<template>
  <div
    class="schedule-page clipr-theme twitch-theme"
    :class="{
      'schedule-page--embedded': embedded,
      'schedule-page--collection-detail': isCollectionDetailActive,
    }"
    @click="closeCollectionActionsMenu"
  >
    <clipr-page-load-overlay
      v-if="(!embedded && (isInitialPageLoading || initialPageLoadError)) || shouldShowCreateClipsRouteLoader"
      :loading="isInitialPageLoading || shouldShowCreateClipsRouteLoader"
      :error="(!embedded && initialPageLoadError && !isInitialPageLoading && !shouldShowCreateClipsRouteLoader) ? tt('common.refreshPageHint', 'Something went wrong while loading the page. Please refresh the page.') : ''"
      :title="tt('common.loading', 'Cargando...')"
      :error-message="tt('common.refreshPageHint', 'Something went wrong while loading the page. Please refresh the page.')"
      :refresh-label="tt('common.refresh', 'Refresh')"
      :fixed="embedded"
      @refresh="reloadCurrentPage"
    />
    <!-- Fila 1: Streams / jobs listos (patrón VODClips) -->
    <div
      v-if="(!collectionDetailView || !selectedCollectionJobId) && !isClipTimeEditorVisible"
      class="schedule-row schedule-row-batches"
    >
      <div v-if="shouldShowScheduleFilters" class="schedule-row-header schedule-row-header--streams">
        <div class="schedule-row-header-left">
          <div
            v-if="shouldShowScheduleFilters"
            class="schedule-period-filter"
            role="group"
            :aria-label="tt('common.period', 'Período')"
          >
            <span class="schedule-period-filter-label">
              <i class="ti-calendar" aria-hidden="true"></i>
              {{ tt("common.period", "Período") }}
            </span>
            <button
              v-for="opt in scheduleDaysFilterOptions"
              :key="`sched-day-${opt.value}`"
              type="button"
              class="schedule-period-pill"
              :class="{ 'is-active': scheduleDaysFilter === opt.value }"
              :disabled="loadingClips"
              @click="scheduleDaysFilter = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="shouldShowScheduleFilters && (canViewAllUsers || isAdminUser)"
        class="schedule-collections-filters"
      >
        <div class="schedule-filters-admin-panel">
        <div class="schedule-filters-toolbar">
          <div class="schedule-filters-toolbar-left">
            <span class="schedule-filters-toolbar-icon" aria-hidden="true">
              <i class="ti-filter"></i>
            </span>
            <span class="schedule-filters-toolbar-title">{{ tt("common.filters", "Filtros") }}</span>
          </div>
        </div>
        <div class="schedule-filters-body">
        <div v-if="canViewAllUsers" class="schedule-filters-row schedule-filters-row--admin">
          <div class="schedule-filters-admin-rail" aria-hidden="true">
            <span class="schedule-filters-admin-badge">{{ tt("common.admin", "Admin") }}</span>
          </div>
          <div class="schedule-filters-admin-grid">
            <div class="schedule-filters-block schedule-filters-block--scope">
              <span class="schedule-filters-block-label schedule-clips-filters-label">
                <i class="ti-world schedule-filters-block-icon" aria-hidden="true"></i>
                {{ tt("common.scope", "Alcance") }}
              </span>
              <div class="schedule-filters-chips">
                <button
                  v-for="opt in scheduleClipScopeOptions"
                  :key="`sched-scope-${opt.value}`"
                  type="button"
                  class="schedule-filter-chip"
                  :class="{ 'schedule-filter-chip--active': scheduleClipScope === opt.value }"
                  :disabled="loadingClips"
                  @click="scheduleClipScope = opt.value"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
            <div class="schedule-filters-block schedule-filters-block--search">
              <span class="schedule-filters-block-label schedule-clips-filters-label">
                <i class="ti-search schedule-filters-block-icon" aria-hidden="true"></i>
                {{ tt("schedulePost.streamUser", "Usuario (stream)") }}
              </span>
              <div class="schedule-filters-search-wrap">
                <i class="ti-search schedule-filters-search-icon" aria-hidden="true"></i>
                <input
                  v-model.trim="scheduleOwnerNameFilter"
                  type="text"
                  class="schedule-filters-search-input"
                  :placeholder="tt('common.filterByUsername', 'Filtrar por nombre de usuario')"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="isAdminUser" class="schedule-filters-row schedule-filters-row--admin schedule-filters-row--status">
          <div class="schedule-filters-admin-rail" aria-hidden="true">
            <span class="schedule-filters-admin-badge">{{ tt("common.admin", "Admin") }}</span>
          </div>
          <div class="schedule-filters-block schedule-filters-block--status">
            <span class="schedule-filters-block-label schedule-clips-filters-label">
              <i class="ti-layers schedule-filters-block-icon" aria-hidden="true"></i>
              {{ tt("vodClips.vodStatus", "Estatus VOD") }}
            </span>
            <div class="schedule-filters-chips schedule-filters-chips--status">
              <button
                v-for="opt in scheduleVodStatusFilterOptions"
                :key="`sched-status-${opt.value == null ? 'all' : opt.value}`"
                type="button"
                class="schedule-filter-chip schedule-filter-chip--status"
                :class="{ 'schedule-filter-chip--active': scheduleVodStatusFilter === opt.value }"
                :data-status="opt.value == null ? 'all' : opt.value"
                :disabled="loadingClips"
                @click="scheduleVodStatusFilter = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
        </div>
        </div>
        </div>
      </div>

      <div class="schedule-row-body">
        <card class="card schedule-collections-card mb-3">
          <div class="schedule-collections-body">
            <clipr-loader
              v-if="loadingClips && visibleClipCollections.length > 0"
              variant="inline"
              :title="tt('common.loading', 'Cargando...')"
            />
            <clipr-loader
              v-else-if="loadingClips && visibleClipCollections.length === 0 && !demoAssetsLoading"
              :title="tt('common.loading', 'Cargando...')"
            />
            <clipr-loader
              v-else-if="demoAssetsLoading && visibleClipCollections.length === 0"
              :title="tt('common.loading', 'Cargando...')"
            />
            <div v-else-if="loadClipsError" class="schedule-row-empty text-center p-3">
              <i
                class="ti-alert-circle"
                style="font-size: 36px; color: #f87171; margin-bottom: 10px; display: block;"
              ></i>
              <p class="small text-danger mb-1">{{ loadClipsError }}</p>
              <p class="text-muted" style="font-size: 12px; margin-top: 5px;">
                {{ tt("schedulePost.needRenderedVideos", "Se requieren videos renderizados para programar publicaciones.") }}
              </p>
            </div>
            <div v-else-if="visibleClipCollections.length === 0" class="schedule-row-empty schedule-row-empty--illustrated text-center p-3">
              <div class="schedule-row-empty-icon" aria-hidden="true">
                <i class="ti-video-camera"></i>
              </div>
              <p v-if="demoAssetsError" class="small text-danger mb-2">
                {{ demoAssetsError }}
              </p>
              <p class="small text-muted">
                {{
                  shouldShowEarlyJobStatuses()
                    ? tt("schedulePost.noStreamsFound", "No se encontraron streams para este período")
                    : tt("schedulePost.noCollectionsReady", "No se encontraron colecciones de clips listas")
                }}
              </p>
              <p
                v-if="shouldShowEarlyJobStatuses()"
                class="text-muted"
                style="font-size: 12px; margin-top: 5px;"
              >
                {{ tt("schedulePost.noStreamsFoundHint", "Aquí aparecerán tus clips cuando estén disponibles para programar.") }}
              </p>
              <p
                v-if="!shouldShowEarlyJobStatuses()"
                class="text-muted"
                style="font-size: 12px; margin-top: 5px;"
              >
                {{ tt("schedulePost.collectionsReadyHint", "Las colecciones con clips listos para publicar aparecerán aquí") }}
              </p>
            </div>
            <div
              v-else-if="filteredClipCollections.length === 0"
              class="schedule-row-empty schedule-row-empty--illustrated text-center p-3"
            >
              <div class="schedule-row-empty-icon schedule-row-empty-icon--muted" aria-hidden="true">
                <i class="ti-search"></i>
              </div>
              <p class="small text-muted">{{ tt("schedulePost.noStreamMatchFilter", "Ningún stream coincide con el filtro de usuario.") }}</p>
            </div>
            <div v-else class="collection-row-scroll-wrap">
              <button
                type="button"
                class="scroll-arrow-btn scroll-arrow-btn-left"
                :aria-label="tt('schedulePost.scrollCollectionsLeft', 'Desplazar fila a la izquierda')"
                @click="scrollScheduleJobsRow(-1)"
              >
                <i class="ti-angle-left"></i>
              </button>
              <transition name="collection-page" mode="out-in">
                <div :key="collectionPage" class="collections-row-scroll">
                  <div
                    v-for="collection in paginatedClipCollections"
                    :key="collection.jobId"
                    class="collection-item"
                    :data-job-id="collection.jobId"
                    :class="{
                      'collection-item-active': selectedCollectionJobId === collection.jobId,
                      'collection-item-menu-open': collectionActionsMenuJobId === collection.jobId,
                      [`collection-item--processing-${getJobProcessingPhase(collection.jobStatus)}`]: isJobProcessing(collection.jobStatus)
                    }"
                    @click="handleCollectionItemClick(collection)"
                  >
                  <div class="collection-preview">
                    <span
                      v-if="!isJobProcessing(collection.jobStatus)"
                      class="collection-status-badge-thumbnail"
                      :class="jobStatusClass(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage, collection)"
                    >
                      <template v-if="isDemoCollection(collection)">DEMO</template>
                      <template v-else>• {{ jobStatusLabel(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage, collection) }}</template>
                    </span>
                    <div
                      v-if="isJobProcessing(collection.jobStatus) && effectiveBatchThumbnailUrl(collection)"
                      class="collection-preview-overlay"
                      :class="`collection-preview-overlay--${getJobProcessingPhase(collection.jobStatus)}`"
                    >
                      <div class="collection-processing-stage" aria-hidden="true">
                        <span
                          class="processing-indicator"
                          :class="`processing-indicator--${getJobProcessingPhase(collection.jobStatus)}`"
                        >
                          <span class="processing-indicator-orbit"></span>
                          <span class="processing-indicator-core"></span>
                        </span>
                      </div>
                      <div class="collection-processing-progress" aria-hidden="true">
                        <span class="collection-processing-progress-bar"></span>
                      </div>
                    </div>
                    <div
                      v-else-if="isJobProcessing(collection.jobStatus)"
                      class="collection-preview-icon"
                      :class="`collection-preview-${getJobProcessingPhase(collection.jobStatus)}`"
                    >
                      <span
                        class="processing-indicator"
                        :class="`processing-indicator--${getJobProcessingPhase(collection.jobStatus)}`"
                        aria-hidden="true"
                      >
                        <span class="processing-indicator-orbit"></span>
                        <span class="processing-indicator-core"></span>
                      </span>
                    </div>
                    <img
                      v-if="effectiveBatchThumbnailUrl(collection)"
                      :src="effectiveBatchThumbnailUrl(collection)"
                      class="collection-preview-image"
                      :class="{
                        [`collection-preview-image--${getJobProcessingPhase(collection.jobStatus)}`]: isJobProcessing(collection.jobStatus)
                      }"
                      loading="lazy"
                      decoding="async"
                      alt=""
                      @error="onBatchThumbnailError(collection.jobId)"
                    />
                    <div v-else-if="!isJobProcessing(collection.jobStatus)" class="collection-preview-icon">
                      <i class="ti-video-camera"></i>
                    </div>
                  </div>
                  <div class="collection-content">
                    <div class="collection-content-header">
                      <h5
                        class="collection-name"
                        :title="collection.displayTitle"
                        :class="{
                          'collection-name-running': isJobRunning(collection.jobStatus),
                          'collection-name-downloaded': isJobDownloaded(collection.jobStatus),
                          'collection-name-rendering': isJobRendering(collection.jobStatus),
                          'collection-name-decided': isJobDecided(collection.jobStatus),
                          'collection-name-queued': isJobQueued(collection.jobStatus),
                          'collection-name-insufficient-tokens': isJobInsufficientTokens(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage)
                        }"
                      >
                        <span v-if="isJobRunning(collection.jobStatus)">
                          {{ tt("vodClips.downloading", "Descargando") }}
                          <span class="processing-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </span>
                        <span v-else-if="isJobDownloaded(collection.jobStatus)">
                          {{ tt("vodClips.preparingClips", "Preparando clips") }}
                          <span class="processing-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </span>
                        <span v-else-if="isJobRendering(collection.jobStatus)">
                          {{ tt("vodClips.creatingClips", "Creando tus clips") }}
                          <span class="processing-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </span>
                        <span v-else-if="isJobDecided(collection.jobStatus)">
                          {{ tt("vodClips.inQueue", "En cola") }}
                          <span class="processing-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </span>
                        <span v-else-if="isJobQueued(collection.jobStatus)">
                          {{ tt("vodClips.validating", "Validando") }}
                          <span class="processing-dots" aria-hidden="true">
                            <span>.</span><span>.</span><span>.</span>
                          </span>
                        </span>
                        <span v-else-if="isJobInsufficientTokens(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage)">
                          {{ tt("vodClips.statusInsufficientTokens", "Tokens insuficientes") }}
                        </span>
                        <span v-else>{{ collection.displayTitle }}</span>
                      </h5>
                      <div
                        v-if="!isDemoCollection(collection)"
                        class="collection-actions"
                        @click.stop
                      >
                        <button
                          type="button"
                          class="collection-actions-toggle"
                          :aria-label="tt('schedulePost.collectionActionsMenuAria', 'Abrir acciones de la colección')"
                          :aria-expanded="collectionActionsMenuJobId === collection.jobId ? 'true' : 'false'"
                          @click.stop="toggleCollectionActionsMenu(collection.jobId)"
                        >
                          <i class="ti-more-alt"></i>
                        </button>
                        <div
                          v-if="collectionActionsMenuJobId === collection.jobId"
                          class="collection-actions-menu"
                          role="menu"
                        >
                          <button
                            type="button"
                            class="collection-actions-menu-item collection-actions-menu-item--danger"
                            role="menuitem"
                            :disabled="isCollectionActionBusy"
                            @click.stop="requestDeleteCollection(collection)"
                          >
                            <i class="ti-trash"></i>
                            <span>{{ tt("vodClips.deleteVod", "Eliminar VOD") }}</span>
                          </button>
                          <button
                            v-if="canUseAdminCollectionActions"
                            type="button"
                            class="collection-actions-menu-item"
                            role="menuitem"
                            :disabled="isCollectionActionBusy"
                            @click.stop="handleReassignVod(collection)"
                          >
                            <i class="ti-exchange-vertical"></i>
                            <span>{{ tt("vodClips.reassignVod", "Reasignar VOD") }}</span>
                          </button>
                          <button
                            v-if="canReleaseStuckCollection(collection)"
                            type="button"
                            class="collection-actions-menu-item"
                            role="menuitem"
                            :disabled="isCollectionActionBusy"
                            @click.stop="requestReleaseStuckCollection(collection)"
                          >
                            <i class="ti-alert"></i>
                            <span>{{ tt("vodClips.sendToError", "Enviar a error") }}</span>
                          </button>
                          <button
                            v-if="canFullRerenderCollection(collection)"
                            type="button"
                            class="collection-actions-menu-item"
                            role="menuitem"
                            :disabled="isCollectionActionBusy"
                            @click.stop="requestFullRerenderCollection(collection)"
                          >
                            <i class="ti-reload"></i>
                            <span>{{ tt("vodClips.rerenderVodFull", "Volver a descargar y renderear") }}</span>
                          </button>
                          <button
                            v-if="canRerenderFailedClipsCollection(collection)"
                            type="button"
                            class="collection-actions-menu-item"
                            role="menuitem"
                            :disabled="isCollectionActionBusy"
                            @click.stop="requestRerenderFailedClipsCollection(collection)"
                          >
                            <i class="ti-reload"></i>
                            <span>{{ tt("vodClips.rerenderFailedClips", "Re-renderear clips fallidos") }}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <p
                      v-if="collectionFailureLegend(collection)"
                      class="collection-failure-legend"
                      :title="collectionFailureLegend(collection)"
                    >
                      {{ collectionFailureLegend(collection) }}
                    </p>
                    <p class="collection-subtitle">
                      {{ getCollectionSubtitleUsername(collection) }}
                    </p>
                    <p class="collection-updated">
                      {{ collection.totalClips }} {{ tt("schedulePost.clip", "clip") }}{{ collection.totalClips === 1 ? "" : "s" }}
                      · {{ collection.readyCount || 0 }} {{ tt("schedulePost.readyToPost", "listos para publicar") }}
                      <template v-if="isAdminUser && Number(collection.failedClipCount || 0) > 0">
                        · {{ collection.failedClipCount }} fallido{{ Number(collection.failedClipCount || 0) === 1 ? "" : "s" }}
                      </template>
                    </p>
                    <p
                      v-if="isAdminUser && collectionMediaStatusLabel(collection)"
                      class="collection-updated"
                    >
                      Media · {{ collectionMediaStatusLabel(collection) }}
                    </p>
                  </div>
                  </div>
                </div>
              </transition>
              <button
                type="button"
                class="scroll-arrow-btn"
                :aria-label="tt('schedulePost.scrollCollectionsRight', 'Desplazar fila a la derecha')"
                @click="scrollScheduleJobsRow(1)"
              >
                <i class="ti-angle-right"></i>
              </button>
            </div>
            <div v-if="collectionPageCount > 1" class="clip-workspace-pagination collection-pagination">
              <button
                type="button"
                class="clip-workspace-pagination-button"
                :disabled="collectionPage === 1"
                :aria-label="tt('schedulePost.previousStreamsPage', 'Página anterior de streams')"
                @click="collectionPage -= 1"
              >
                <i class="ti-angle-left"></i>
              </button>
              <span class="clip-workspace-pagination-label">
                Página {{ collectionPage }} de {{ collectionPageCount }}
              </span>
              <button
                type="button"
                class="clip-workspace-pagination-button"
                :disabled="collectionPage === collectionPageCount"
                :aria-label="tt('schedulePost.nextStreamsPage', 'Página siguiente de streams')"
                @click="collectionPage += 1"
              >
                <i class="ti-angle-right"></i>
              </button>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Fila 2: Clips de la collection seleccionada -->
    <div
      v-if="(!collectionDetailView || selectedCollectionJobId) && !isClipTimeEditorVisible"
      class="schedule-row schedule-row-videos"
    >
      <div v-if="!selectedCollectionJobId || filteredCollectionClips.length === 0" class="schedule-row-header">
        <div class="schedule-row-header-left">
          <h4 class="schedule-row-title">{{ tt("schedulePost.clips", "Clips") }}</h4>
          <p v-if="!selectedCollectionJobId" class="schedule-row-subtitle text-muted">
            {{ tt("schedulePost.selectStreamAbove", "Selecciona un stream arriba para ver sus clips.") }}
          </p>
        </div>
      </div>

      <div class="schedule-row-body">
        <div v-if="filteredCollectionClips.length > 0" class="clip-workspace">
          <aside
            ref="workspaceClipsPanel"
            id="mobile-workspace-clips"
            class="clip-workspace-list"
            :class="{ 'is-mobile-panel-open': mobileWorkspacePanel === 'clips' }"
            :aria-label="tt('schedulePost.clips', 'Clips')"
            :aria-modal="mobileWorkspacePanel === 'clips' ? 'true' : null"
            :role="mobileWorkspacePanel === 'clips' ? 'dialog' : null"
            tabindex="-1"
            @keydown.tab="trapMobileWorkspaceFocus"
          >
            <button
              ref="mobileClipsClose"
              type="button"
              class="clip-workspace-mobile-panel-close"
              :aria-label="tt('common.close', 'Cerrar')"
              @click="closeMobileWorkspacePanel()"
            >
              <i class="ti-close" aria-hidden="true"></i>
            </button>
            <div v-if="selectedCollection" class="clip-workspace-vod-card">
              <div class="clip-workspace-vod-thumb">
                <img
                  v-if="effectiveBatchThumbnailUrl(selectedCollection)"
                  :src="effectiveBatchThumbnailUrl(selectedCollection)"
                  :alt="selectedCollectionTitle"
                  loading="lazy"
                  decoding="async"
                  @error="onBatchThumbnailError(selectedCollection.jobId)"
                />
                <i v-else class="ti-video-camera"></i>
              </div>
              <div class="clip-workspace-vod-copy">
                <strong :title="selectedCollectionTitle">{{ selectedCollectionTitle }}</strong>
                <p class="clip-workspace-vod-meta">
                  <span class="clip-workspace-vod-meta-chip">
                    {{ filteredCollectionClips.length }}
                    {{ filteredCollectionClips.length === 1 ? tt("schedulePost.clip", "clip") : tt("schedulePost.clips", "Clips") }}
                  </span>
                  <span
                    v-if="getCollectionRelativeTime(selectedCollection)"
                    class="clip-workspace-vod-meta-time"
                  >
                    {{ getCollectionRelativeTime(selectedCollection) }}
                  </span>
                </p>
              </div>
              <div class="clip-workspace-vod-download-actions">
                <button
                  type="button"
                  class="clip-workspace-vod-select-all"
                  :disabled="!getDownloadableCollectionClips(selectedCollection).length"
                  @click="toggleAllCollectionZipClips(selectedCollection)"
                >
                  <i :class="areAllCollectionZipClipsSelected(selectedCollection) ? 'ti-close' : 'ti-check'"></i>
                  {{ areAllCollectionZipClipsSelected(selectedCollection) ? tt("schedulePost.clearSelection", "Quitar") : tt("schedulePost.selectAll", "Todos") }}
                </button>
                <button
                  type="button"
                  class="clip-workspace-vod-download-zip"
                  :disabled="isCollectionZipDownloading(selectedCollection) || !selectedCollectionZipClips.length"
                  @click="downloadCollectionZip(selectedCollection, selectedCollectionZipClips)"
                >
                  <i :class="isCollectionZipDownloading(selectedCollection) ? 'ti-reload rotating' : 'ti-download'"></i>
                  <span>{{ collectionZipDownloadLabel(selectedCollection, selectedCollectionZipClips) }}</span>
                </button>
              </div>
            </div>
            <p class="clip-workspace-list-label">
              {{ tt("schedulePost.autoGeneratedClips", "Clips creados automáticamente") }}
            </p>
            <div class="clip-workspace-list-scroll">
              <transition-group name="clip-list-page" tag="div" class="clip-workspace-list-items">
                <div
                  v-for="clip in filteredCollectionClips"
                  :key="clip.id"
                  class="clip-workspace-list-entry"
                  :class="{
                    'is-zip-selected': isClipSelectedForZip(clip),
                  }"
                >
                  <label class="clip-workspace-list-checkbox">
                    <input
                      type="checkbox"
                      :checked="isClipSelectedForZip(clip)"
                      :disabled="!isClipDownloadable(clip)"
                      @change="toggleClipZipSelection(clip)"
                    />
                    <span class="clip-workspace-list-checkbox-mark" aria-hidden="true">
                      <i class="ti-check"></i>
                    </span>
                    <span class="sr-only">{{ tt("schedulePost.selectClipForDownload", "Seleccionar clip para descargar") }}</span>
                  </label>
                  <button
                    type="button"
                    class="clip-workspace-list-item"
                    :class="{
                      'is-selected': selectedWorkspaceClip && selectedWorkspaceClip.id === clip.id,
                      'is-rerendering': isClipRendering(clip),
                      'is-scheduled-to-post': isClipReadyToPost(clip),
                      'is-posted': isClipPosted(clip),
                    }"
                    @click="handleClipCardClick(clip)"
                  >
                  <span class="clip-workspace-list-thumb">
                    <img
                      v-if="clip.thumbnailUrl"
                      :src="clip.thumbnailUrl"
                      :alt="`${tt('vodClips.thumbnailAltPrefix', 'Thumbnail')} ${clip.id}`"
                      :class="{ 'clip-workspace-list-thumb-image--rendering': isClipRendering(clip) }"
                      loading="lazy"
                    />
                    <i v-else-if="!isClipRendering(clip)" class="ti-video-camera"></i>
                    <div
                      v-if="isClipRendering(clip)"
                      class="clip-workspace-rerender-overlay collection-preview-overlay collection-preview-overlay--rendering"
                      aria-hidden="true"
                    >
                      <div class="collection-processing-stage">
                        <span class="processing-indicator processing-indicator--rendering">
                          <span class="processing-indicator-orbit"></span>
                          <span class="processing-indicator-core"></span>
                        </span>
                      </div>
                      <div class="collection-processing-progress">
                        <span class="collection-processing-progress-bar"></span>
                      </div>
                    </div>
                  </span>
                  <span class="clip-workspace-list-copy">
                    <span class="clip-workspace-list-title">{{ clip.clipTitle || getClipDisplayName(clip) }}</span>
                    <span class="clip-workspace-list-meta">
                      <template v-if="isClipRendering(clip)">
                        {{ tt('vodClips.creatingClips', 'Creando tus clips') }}
                        <span class="processing-dots" aria-hidden="true">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </template>
                      <template v-else>
                        {{ clip.timeRangeLabel || clip.durationLabel || tt('schedulePost.clip', 'Clip') }}
                      </template>
                    </span>
                  </span>
                  <span class="clip-workspace-list-stats">
                    <span
                      class="clip-workspace-list-score"
                      :class="[
                        clip.confidence != null ? getScoreClass(clip.confidence) : '',
                        { 'is-placeholder': clip.confidence == null || isClipRendering(clip) },
                      ]"
                    >
                      <template v-if="clip.confidence != null && !isClipRendering(clip)">
                        ✨{{ (clip.confidence * 100).toFixed(0) }}
                      </template>
                      <template v-else>✨--</template>
                    </span>
                    <span
                      class="clip-workspace-list-duration"
                      :class="{ 'is-placeholder': isClipRendering(clip) }"
                    >
                      {{ isClipRendering(clip) ? '--' : getClipDurationShort(clip) }}
                    </span>
                  </span>
                  </button>
                </div>
              </transition-group>
            </div>
          </aside>

          <main v-if="selectedWorkspaceClip" class="clip-workspace-preview">
            <div class="clip-workspace-mobile-toolbar">
              <button
                type="button"
                class="clip-workspace-mobile-back"
                @click="goBackToCollectionList"
              >
                <i class="ti-arrow-left" aria-hidden="true"></i>
                {{ tt('schedulePost.backToVods', 'Volver a VODs') }}
              </button>
              <button
                ref="mobileActionsTrigger"
                type="button"
                class="clip-workspace-mobile-actions"
                :aria-expanded="mobileWorkspacePanel === 'actions' ? 'true' : 'false'"
                aria-controls="mobile-workspace-actions"
                @click="openMobileWorkspacePanel('actions', $event)"
              >
                <i class="ti-settings" aria-hidden="true"></i>
                {{ tt('schedulePost.clipActionsShort', 'Acciones') }}
              </button>
              <div
                class="clip-workspace-mobile-clip-navigation"
                role="group"
                :aria-label="tt('schedulePost.clips', 'Clips')"
              >
                <button
                  type="button"
                  class="clip-workspace-mobile-previous"
                  :disabled="!previousWorkspaceClip"
                  :aria-label="tt('schedulePost.previousClip', 'Clip anterior')"
                  @click="selectPreviousWorkspaceClip"
                >
                  <i class="ti-angle-left" aria-hidden="true"></i>
                </button>
                <button
                  ref="mobileClipsTrigger"
                  type="button"
                  class="clip-workspace-mobile-clips"
                  :aria-expanded="mobileWorkspacePanel === 'clips' ? 'true' : 'false'"
                  aria-controls="mobile-workspace-clips"
                  @click="openMobileWorkspacePanel('clips', $event)"
                >
                  {{ tt('schedulePost.viewClips', 'Ver clips') }} ({{ filteredCollectionClips.length }})
                </button>
                <button
                  type="button"
                  class="clip-workspace-mobile-next"
                  :disabled="!nextWorkspaceClip"
                  :aria-label="tt('schedulePost.nextClip', 'Siguiente clip')"
                  @click="selectNextWorkspaceClip"
                >
                  <i class="ti-angle-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div class="clip-workspace-selected-clip-info">
              <span class="clip-workspace-selected-clip-index">
                {{ tt('schedulePost.clip', 'Clip') }} {{ selectedWorkspaceClipIndex }}
                {{ tt('schedulePost.of', 'de') }} {{ filteredCollectionClips.length }}
              </span>
              <span class="clip-workspace-selected-clip-score">
                <i class="ti-star" aria-hidden="true"></i>
                {{ tt('publicResults.score', 'Puntaje') }}
                {{ selectedWorkspaceClip.confidence != null ? `${(selectedWorkspaceClip.confidence * 100).toFixed(0)}%` : '—' }}
              </span>
            </div>
            <div
              class="clip-workspace-player video-card-playback"
              :class="{
                'video-card-playback--fullscreen': isClipInFullscreen(selectedWorkspaceClip.id),
                'clip-workspace-player--empty': !selectedWorkspaceClip.playbackUrl && !isClipRendering(selectedWorkspaceClip),
                'clip-workspace-player--rendering': isClipRendering(selectedWorkspaceClip),
              }"
              :data-playback-clip-id="String(selectedWorkspaceClip.id)"
            >
              <video
                v-if="selectedWorkspaceClip.playbackUrl"
                :src="selectedWorkspaceClip.playbackUrl"
                :class="{ 'clip-workspace-player-video--rendering': isClipRendering(selectedWorkspaceClip) }"
                :muted="isClipMuted(selectedWorkspaceClip)"
                :controls="!isClipRendering(selectedWorkspaceClip)"
                controlslist="nodownload noremoteplayback"
                disablepictureinpicture
                playsinline
                preload="metadata"
                @play="onClipPlay(selectedWorkspaceClip, $event)"
                @pause="onClipPause(selectedWorkspaceClip, $event)"
                @ended="onClipEnded(selectedWorkspaceClip, $event)"
                @loadedmetadata="onClipLoadedMetadata(selectedWorkspaceClip, $event)"
              ></video>
              <img
                v-else-if="isClipRendering(selectedWorkspaceClip) && selectedWorkspaceClip.thumbnailUrl"
                class="clip-workspace-player-poster clip-workspace-player-video--rendering"
                :src="selectedWorkspaceClip.thumbnailUrl"
                :alt="selectedCollectionTitle"
                loading="lazy"
                decoding="async"
              />
              <div
                v-if="isClipRendering(selectedWorkspaceClip)"
                class="clip-workspace-player-rendering collection-preview-overlay collection-preview-overlay--rendering"
                role="status"
                :aria-label="tt('vodClips.creatingClips', 'Creando tus clips')"
              >
                <div class="collection-processing-stage">
                  <span class="processing-indicator processing-indicator--rendering">
                    <span class="processing-indicator-orbit"></span>
                    <span class="processing-indicator-core"></span>
                  </span>
                </div>
                <p class="clip-workspace-player-rendering-label">
                  {{ tt('vodClips.creatingClips', 'Creando tus clips') }}
                  <span class="processing-dots" aria-hidden="true">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                </p>
                <div class="collection-processing-progress" aria-hidden="true">
                  <span class="collection-processing-progress-bar"></span>
                </div>
              </div>
              <template v-else-if="!selectedWorkspaceClip.playbackUrl">
                <i class="ti-video-camera"></i>
                <span>{{ tt('schedulePost.noPreviewAvailable', 'La vista previa no está disponible') }}</span>
              </template>
            </div>
          </main>

          <aside
            v-if="selectedWorkspaceClip"
            ref="workspaceActionsPanel"
            id="mobile-workspace-actions"
            class="clip-workspace-actions"
            :class="{ 'is-drawer-open': mobileWorkspacePanel === 'actions' || mobileWorkspacePanel === 'schedule' }"
            :aria-modal="mobileWorkspacePanel === 'actions' || mobileWorkspacePanel === 'schedule' ? 'true' : null"
            :aria-label="mobileWorkspacePanel === 'schedule' ? tt('schedulePost.scheduleClip', 'Programar clip') : tt('schedulePost.clipActions', 'Acciones')"
            :role="mobileWorkspacePanel === 'actions' || mobileWorkspacePanel === 'schedule' ? 'dialog' : null"
            tabindex="-1"
            @keydown.tab="trapMobileWorkspaceFocus"
          >
            <button
              ref="mobileActionsClose"
              type="button"
              class="clip-workspace-drawer-close"
              :aria-label="tt('common.close', 'Cerrar')"
              @click="closeMobileWorkspacePanel()"
            >
              <i class="ti-close" aria-hidden="true"></i>
            </button>
            <div
              v-if="mobileScheduleOpening"
              class="clip-workspace-mobile-panel-loading"
              role="status"
            >
              <i class="ti-reload rotating" aria-hidden="true"></i>
              <span>{{ tt('schedulePost.checkingConnection', 'Verificando...') }}</span>
            </div>
            <section v-if="!showScheduleModal" class="clip-workspace-section">
              <h5 class="clip-workspace-section-title">
                {{ tt('schedulePost.clipDetails', 'Detalles del clip') }}
              </h5>
              <section class="clip-workspace-metadata">
              <label>
                <span class="clip-workspace-field-label">
                  {{ tt('schedulePost.title', 'Título') }}
                  <span class="clip-workspace-char-count">{{ workspaceMetadataDraft.title.length }}/100</span>
                </span>
                <input
                  v-model="workspaceMetadataDraft.title"
                  class="form-control"
                  maxlength="100"
                  :disabled="!isWorkspaceMetadataEditable(selectedWorkspaceClip) || workspaceMetadataSaving || savingMetadata || postingAssigning"
                  @blur="onWorkspaceMetadataBlur('title')"
                />
              </label>
              <label>
                <span class="clip-workspace-field-label">
                  {{ tt('schedulePost.description', 'Descripción') }}
                  <span class="clip-workspace-char-count">{{ workspaceMetadataDraft.description.length }}/500</span>
                </span>
                <textarea
                  v-model="workspaceMetadataDraft.description"
                  class="form-control"
                  rows="3"
                  maxlength="500"
                  :disabled="!isWorkspaceMetadataEditable(selectedWorkspaceClip) || workspaceMetadataSaving || savingMetadata || postingAssigning"
                  @blur="onWorkspaceMetadataBlur('description')"
                ></textarea>
              </label>
              <label>
                <span class="clip-workspace-field-label">
                  {{ tt('schedulePost.tags', 'Etiquetas') }}
                  <span class="clip-workspace-char-count">{{ workspaceMetadataDraft.hashtags.length }}/200</span>
                </span>
                <div
                  class="clip-workspace-tag-editor form-control"
                  :class="{ 'is-disabled': !isWorkspaceMetadataEditable(selectedWorkspaceClip) || workspaceMetadataSaving || savingMetadata || postingAssigning }"
                  @click="focusWorkspaceHashtagInput"
                >
                  <span
                    v-for="(tag, index) in workspaceHashtagTags"
                    :key="`${tag}-${index}`"
                    class="clip-workspace-tag-pill"
                  >
                    #{{ tag }}
                  </span>
                  <input
                    ref="workspaceHashtagInput"
                    v-model="workspaceHashtagInput"
                    class="clip-workspace-tag-input"
                    type="text"
                    :maxlength="getWorkspaceHashtagInputMaxLength()"
                    :placeholder="workspaceHashtagTags.length ? '' : tt('schedulePost.tagsHashtagPlaceholder', '#gaming #clips #stream')"
                    :disabled="!isWorkspaceMetadataEditable(selectedWorkspaceClip) || workspaceMetadataSaving || savingMetadata || postingAssigning"
                    @input="syncWorkspaceHashtagsDraft"
                    @keydown="onWorkspaceHashtagKeydown"
                    @blur="onWorkspaceHashtagEditorBlur"
                  />
                </div>
              </label>
              </section>
            </section>
            <template v-if="!showScheduleModal">
              <div class="clip-workspace-actions-stack">
                <div class="clip-workspace-secondary-actions">
                  <button
                    type="button"
                    class="clip-workspace-action clip-workspace-action--secondary"
                    :disabled="isClipDownloading(selectedWorkspaceClip) || isClipDeleting(selectedWorkspaceClip) || isClipInteractionLocked(selectedWorkspaceClip)"
                    @click="downloadClipAsset(selectedWorkspaceClip)"
                  >
                    <i :class="isClipDownloading(selectedWorkspaceClip) ? 'ti-reload rotating' : 'ti-download'"></i>
                    {{ isClipDownloading(selectedWorkspaceClip) ? tt('schedulePost.downloading', 'Descargando...') : tt('publicResults.download', 'Descargar') }}
                  </button>
                  <button
                    type="button"
                    class="clip-workspace-action clip-workspace-action--secondary"
                    :disabled="isClipInteractionLocked(selectedWorkspaceClip)"
                    @click="handleClipEditorAction(selectedWorkspaceClip)"
                  >
                    <i class="ti-pencil"></i>
                    {{ tt('schedulePost.editClip', 'Editar clip') }}
                    <span v-if="!isAdminUser && !scheduleSubscriptionHasActivePlan" class="clip-workspace-pro">PRO</span>
                  </button>
                  <button
                    type="button"
                    class="clip-workspace-action clip-workspace-action--secondary clip-workspace-action--danger"
                    :disabled="isClipDeleting(selectedWorkspaceClip) || isClipDownloading(selectedWorkspaceClip) || isClipInteractionLocked(selectedWorkspaceClip)"
                    @click="openClipDeleteModal(selectedWorkspaceClip)"
                  >
                    <i :class="isClipDeleting(selectedWorkspaceClip) ? 'ti-reload rotating' : 'ti-trash'"></i>
                    {{ isClipDeleting(selectedWorkspaceClip) ? tt('schedulePost.deleting', 'Eliminando...') : tt('common.delete', 'Eliminar') }}
                  </button>
                </div>
                <button
                  type="button"
                  class="clip-workspace-action clip-workspace-action--publish clip-workspace-action--primary"
                  :disabled="isClipInteractionLocked(selectedWorkspaceClip)"
                  @click="openPrimaryWorkspaceAction(selectedWorkspaceClip, $event)"
                >
                  <i :class="isClipManageable(selectedWorkspaceClip) ? 'ti-settings' : 'ti-share'"></i>
                  {{ isClipManageable(selectedWorkspaceClip) ? tt('common.manage', 'Administrar') : tt('schedulePost.publishNow', 'Programar') }}
                  <span v-if="!isAdminUser && !scheduleSubscriptionHasActivePlan" class="clip-workspace-pro">PRO</span>
                </button>
              </div>
            </template>

            <form v-else-if="clipMetadataDraft" class="clip-workspace-schedule" @submit.prevent="saveAndScheduleClip">
              <div class="clip-workspace-schedule-title">
                <button
                  type="button"
                  class="clip-workspace-schedule-back"
                  :aria-label="tt('common.back', 'Atrás')"
                  @click="closeScheduleModal"
                >
                  <i class="ti-arrow-left"></i>
                  <span>{{ tt('common.back', 'Atrás') }}</span>
                </button>
                <h5>{{ tt('schedulePost.scheduleClip', 'Programar clip') }}</h5>
              </div>
              <div class="clip-workspace-schedule-field">
                <span class="clip-workspace-schedule-label">
                  {{ tt('schedulePost.selectPublishNetworks', 'Selecciona las redes de publicación') }}
                </span>
                <div
                  v-if="scheduleConnectionsLoading"
                  class="clip-workspace-schedule-platforms-loading"
                  role="status"
                >
                  <i class="ti-reload rotating" aria-hidden="true"></i>
                  <span>{{ tt('schedulePost.checkingConnection', 'Verificando...') }}</span>
                </div>
                <p
                  v-else-if="connectedSchedulePlatforms.length === 0"
                  class="clip-workspace-schedule-platforms-empty"
                >
                  {{ tt('schedulePost.errorNoConnectedPlatforms', 'No tienes redes sociales conectadas para programar este clip. Conecta al menos una desde tu perfil.') }}
                </p>
                <div
                  v-else
                  class="clip-workspace-schedule-platforms"
                  role="group"
                  :aria-label="tt('schedulePost.selectPublishNetworks', 'Selecciona las redes de publicación')"
                >
                  <button
                    v-for="platform in connectedSchedulePlatforms"
                    :key="platform"
                    type="button"
                    class="clip-workspace-schedule-platform-btn"
                    :class="{ 'is-selected': clipMetadataDraft.platforms.includes(platform) }"
                    :disabled="scheduleConnectOpening"
                    :aria-pressed="clipMetadataDraft.platforms.includes(platform)"
                    :title="getPlatformLabel(platform)"
                    @click="toggleSchedulePlatform(platform)"
                  >
                    <img
                      :src="getPlatformLogoUrl(platform)"
                      :alt="getPlatformLabel(platform)"
                      class="clip-workspace-schedule-platform-logo"
                    />
                  </button>
                </div>
              </div>
              <label v-if="clipMetadataDraft.platforms.includes('facebook')" class="clip-workspace-schedule-field">
                <span class="clip-workspace-schedule-label">{{ tt('schedulePost.facebookPageId', 'Página de Facebook') }}</span>
                <select
                  v-model="clipMetadataDraft.platform_metadata.facebook.facebook_page_id"
                  class="form-control"
                  :disabled="scheduleFacebookPagesLoading || !scheduleFacebookPageOptions.length"
                >
                  <option value="" disabled>{{ tt('schedulePost.facebookPageSelectPlaceholder', 'Selecciona una página') }}</option>
                  <option v-for="page in scheduleFacebookPageOptions" :key="page.id" :value="page.id">{{ page.name }}</option>
                </select>
              </label>
              <label class="clip-workspace-schedule-field">
                <span class="clip-workspace-schedule-label">{{ tt('schedulePost.selectDate', 'Selecciona la fecha') }}</span>
                <input v-model="pendingScheduleData.date" type="date" class="form-control" :min="getPostingTodayInTimezone()" required />
              </label>
              <div class="clip-workspace-schedule-field">
                <span class="clip-workspace-schedule-label">{{ tt('schedulePost.selectTime', 'Selecciona la hora') }}</span>
                <div class="clip-workspace-schedule-time-picker">
                  <div class="clip-workspace-schedule-time-controls">
                    <div class="clip-workspace-schedule-time-fields">
                      <label class="clip-workspace-schedule-time-select">
                        <span class="clip-workspace-schedule-time-select-label">{{ tt('schedulePost.hour', 'Hora') }}</span>
                        <div class="clip-workspace-schedule-time-select-wrap">
                          <select
                            class="clip-workspace-schedule-time-input"
                            :value="postingSelectedPostingHour === null ? '' : postingSelectedPostingHour"
                            @change="onPostingHourSelect($event)"
                          >
                            <option value="" disabled>{{ tt('schedulePost.pickHour', 'Elige hora') }}</option>
                            <option v-for="hour in postingHourOptions" :key="`hour-${hour}`" :value="hour">{{ hour }}</option>
                          </select>
                          <i class="ti-angle-down clip-workspace-schedule-time-chevron" aria-hidden="true"></i>
                        </div>
                      </label>
                      <span class="clip-workspace-schedule-time-colon" aria-hidden="true">:</span>
                      <label class="clip-workspace-schedule-time-select">
                        <span class="clip-workspace-schedule-time-select-label">{{ tt('schedulePost.minute', 'Minuto') }}</span>
                        <div class="clip-workspace-schedule-time-select-wrap">
                          <select
                            class="clip-workspace-schedule-time-input"
                            :value="postingSelectedPostingMinute === null ? '' : postingSelectedPostingMinute"
                            @change="onPostingMinuteSelect($event)"
                          >
                            <option value="" disabled>{{ tt('schedulePost.pickMinute', 'Elige minuto') }}</option>
                            <option
                              v-for="minute in postingMinuteOptions"
                              :key="`minute-${minute}`"
                              :value="minute"
                            >
                              {{ String(minute).padStart(2, '0') }}
                            </option>
                          </select>
                          <i class="ti-angle-down clip-workspace-schedule-time-chevron" aria-hidden="true"></i>
                        </div>
                      </label>
                    </div>
                    <div
                      class="clip-workspace-schedule-time-period"
                      role="group"
                      :aria-label="tt('schedulePost.dayPeriod', 'Periodo del día')"
                    >
                      <button
                        type="button"
                        class="clip-workspace-schedule-period-btn"
                        :class="{ 'is-selected': postingSelectedPostingPeriod === 'AM' }"
                        :aria-pressed="postingSelectedPostingPeriod === 'AM'"
                        @click="postingSelectedPostingPeriod = 'AM'"
                      >
                        AM
                      </button>
                      <button
                        type="button"
                        class="clip-workspace-schedule-period-btn"
                        :class="{ 'is-selected': postingSelectedPostingPeriod === 'PM' }"
                        :aria-pressed="postingSelectedPostingPeriod === 'PM'"
                        @click="postingSelectedPostingPeriod = 'PM'"
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <label class="clip-workspace-schedule-field">
                <span class="clip-workspace-schedule-label">{{ tt('schedulePost.selectTimezoneLabel', 'Selecciona la zona horaria') }}</span>
                <select v-model="postingSelectedTimezone" class="form-control" required>
                  <option value="" disabled>{{ tt('schedulePost.selectTimezone', 'Selecciona zona horaria') }}</option>
                  <option v-for="option in postingTimezoneOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>
              </label>
              <p v-if="scheduleError || postingAssignError" class="text-danger small">{{ scheduleError || postingAssignError }}</p>
              <button
                type="submit"
                class="clip-workspace-action clip-workspace-action--primary clip-workspace-schedule-submit"
                :disabled="savingMetadata || postingAssigning"
              >
                <i :class="savingMetadata || postingAssigning ? 'ti-reload rotating' : 'ti-calendar'"></i>
                {{ savingMetadata || postingAssigning ? tt('schedulePost.scheduling', 'Programando...') : tt('schedulePost.saveAndSchedule', 'Guardar y programar') }}
              </button>
            </form>
          </aside>
        </div>

        <div v-else-if="selectedCollectionJobId" class="schedule-row-empty text-center p-4">
          <i
            class="ti-video-camera"
            style="font-size: 48px; color: #ccc; margin-bottom: 10px; display: block;"
          ></i>
          <p class="text-muted small">
            {{ tt("schedulePost.noReadyClipsInCollection", "No hay clips listos en esta colección.") }}
          </p>
        </div>

        <div v-else class="schedule-row-empty text-center p-4">
          <i
            class="ti-video-camera"
            style="font-size: 48px; color: #ccc; margin-bottom: 10px; display: block;"
          ></i>
          <p class="text-muted small">{{ tt("schedulePost.selectStreamAbove", "Selecciona un stream arriba para ver sus clips.") }}</p>
        </div>
      </div>
    </div>

    <button
      v-if="mobileWorkspacePanel"
      type="button"
      class="clip-workspace-mobile-sheet-backdrop"
      :aria-label="tt('common.close', 'Cerrar')"
      @click="closeMobileWorkspacePanel()"
    ></button>

    <!-- Fila 3: Línea temporal del calendario -->
    <div
      v-if="shouldShowMainTimeline && !isClipTimeEditorVisible"
      class="schedule-row schedule-row-timeline assign-cycle-timeline assign-cycle-timeline--posting"
    >
      <div class="calendar-timeline-header">
        <div class="calendar-timeline-title">
          <label class="assign-cycle-time-label schedule-timeline-posting-date-label">
            <i class="ti-calendar" style="margin-right: 6px;"></i>
            {{ tt("schedulePost.postingDateLabel", "Posting timeline") }}:
          </label>
          <div class="calendar-timeline-month">
            {{ timelineMonthLabel }}
          </div>
        </div>
      </div>

      <div class="calendar-timeline-body">
        <template v-if="loadingPostingTimeline">
          <clipr-loader
            variant="panel"
            class="clipr-loader--compact"
            :title="tt('common.loading', 'Cargando...')"
          />
        </template>
        <template v-else-if="postingTimelineError">
          <div class="timeline-loading-state timeline-loading-state--modal" role="status" aria-live="polite">
            <i class="ti-alert-circle timeline-loading-icon"></i>
            <p class="timeline-loading-title">{{ tt("schedulePost.errorLoadAdminTimeline", "No fue posible cargar la timeline.") }}</p>
            <p class="timeline-loading-subtitle">{{ postingTimelineError }}</p>
          </div>
        </template>
        <template v-else>
          <div
            ref="mainTimelineDays"
            class="timeline-days timeline-days--posting"
            @scroll.passive="onMainTimelineScroll"
          >
            <div
              v-for="day in timelineDays"
              :key="day.isoDate"
              :data-iso-date="day.isoDate"
              class="timeline-day-card"
              :class="{
                'is-today': day.isToday,
                'has-events': day.events && day.events.length > 0
              }"
            >
              <div class="timeline-day-name">
                {{ day.weekdayLabel }}
              </div>
              <div class="timeline-day-number">
                {{ day.dayNumber }}
              </div>
              <div class="timeline-day-events">
                <span
                  v-if="!day.events || day.events.length === 0"
                  class="timeline-day-empty"
                >
                  <i class="ti-calendar" style="font-size: 12px; margin-right: 4px; opacity: 0.5;"></i>
                  {{ tt("schedulePost.noClipsFound", "no clips scheduled") }}
                </span>
                <span
                  v-for="event in day.events"
                  :key="event.id"
                  class="timeline-day-event-pill"
                  :class="{ 'is-posted': isTimelineEventPosted(event) }"
                  :title="getTimelineEventContent(event)"
                  @click.stop="openTimelinePreviewModal(event)"
                >
                  {{ getTimelineEventContent(event) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <transition name="modal-fade">
      <body-portal v-if="previewModalOpen">
        <div class="confirm-modal-overlay" @click.self="closeTimelinePreviewModal">
          <div
            class="confirm-modal confirm-modal--schedule-manage confirm-modal--timeline-preview"
            role="dialog"
            aria-modal="true"
            aria-labelledby="timeline-preview-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="timeline-preview-modal-title" class="confirm-modal-title">
                {{ tt("twitchPostAdmin.previewTitle", "Preview de clip programado") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ selectedPreviewEvent?.title || tt("twitchPostAdmin.untitledClip", "Clip sin título") }}
              </p>
              <button type="button" class="confirm-modal-close" @click="closeTimelinePreviewModal">
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <clipr-loader
                v-if="previewLoading"
                variant="panel"
                class="clipr-loader--compact"
                :title="tt('common.loading', 'Cargando...')"
              />
              <div v-else-if="previewError" class="alert alert-danger mb-0">
                {{ previewError }}
              </div>
              <div v-else-if="previewClip">
                <video
                  v-if="previewClip.videoUrl"
                  class="schedule-timeline-preview-video"
                  :src="previewClip.videoUrl"
                  controls
                  playsinline
                  preload="metadata"
                ></video>
                <div v-else class="timeline-loading-state timeline-loading-state--modal">
                  <i class="ti-video-camera timeline-loading-icon"></i>
                  <p class="timeline-loading-title">{{ tt("twitchPostAdmin.noVideo", "No hay video disponible para este clip.") }}</p>
                </div>
                <div class="scheduled-clip-grid mt-3">
                  <div class="scheduled-clip-item">
                    <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.user", "Usuario") }}</span>
                    <strong class="scheduled-clip-value">{{ selectedPreviewEvent?.user_name || "N/A" }}</strong>
                  </div>
                  <div class="scheduled-clip-item">
                    <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.status", "Estado") }}</span>
                    <strong class="scheduled-clip-value">
                      {{ getClipStatusLabel(previewClip.status || selectedPreviewEvent?.status, null) }}
                    </strong>
                  </div>
                  <div class="scheduled-clip-item">
                    <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.scheduledTime", "Hora programada") }}</span>
                    <strong class="scheduled-clip-value">{{ selectedPreviewEvent?.posting_time || "-" }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                @click="closeTimelinePreviewModal"
              >
                {{ tt("makeClips.close", "Cerrar") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showCollectionProcessingModal">
        <div
          class="confirm-modal-overlay"
          @click.self="closeCollectionProcessingModal"
        >
          <div
            class="confirm-modal confirm-modal--schedule-manage confirm-modal--collection-processing"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-processing-modal-title"
            :class="{
              'confirm-modal--collection-processing-failed': isCollectionProcessingModalFailed(collectionProcessingModalStatus)
            }"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="collection-processing-modal-title" class="confirm-modal-title">
                {{ getCollectionProcessingModalTitle(collectionProcessingModalStatus) }}
              </h3>
              <button type="button" class="confirm-modal-close" @click="closeCollectionProcessingModal">
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body collection-processing-modal-body">
              <div
                v-if="isCollectionProcessingModalFailed(collectionProcessingModalStatus)"
                class="collection-processing-modal-error-icon"
                aria-hidden="true"
              >
                <i class="ti-alert"></i>
              </div>
              <div
                v-else
                class="collection-processing-modal-indicator"
                aria-hidden="true"
              >
                <span
                  class="processing-indicator"
                  :class="`processing-indicator--${getCollectionProcessingModalIndicatorPhase(collectionProcessingModalStatus)}`"
                >
                  <span class="processing-indicator-orbit"></span>
                  <span class="processing-indicator-core"></span>
                </span>
              </div>
              <span
                class="collection-processing-modal-status"
                :class="`collection-processing-modal-status--${getCollectionProcessingModalIndicatorPhase(collectionProcessingModalStatus)}`"
              >
                {{ getCollectionProcessingModalStatusLabel(collectionProcessingModalStatus) }}
              </span>
              <p class="collection-processing-modal-message">
                {{ getCollectionProcessingModalMessage(collectionProcessingModalStatus) }}
              </p>
              <div
                v-if="!isCollectionProcessingModalFailed(collectionProcessingModalStatus)"
                class="collection-processing-modal-progress"
                aria-hidden="true"
              >
                <span
                  class="collection-processing-modal-progress-bar"
                  :class="`collection-processing-modal-progress-bar--${getCollectionProcessingModalIndicatorPhase(collectionProcessingModalStatus)}`"
                ></span>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                @click="closeCollectionProcessingModal"
              >
                {{ tt("common.close", "Cerrar") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showReassignCollectionModal">
        <div class="confirm-modal-overlay" @click.self="closeReassignCollectionModal">
          <div
            class="confirm-modal confirm-modal--schedule-manage confirm-modal--collection-reassign"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-reassign-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="collection-reassign-modal-title" class="confirm-modal-title">
                {{ tt("vodClips.reassignVod", "Reasignar VOD") }}
              </h3>
              <p v-if="reassignCollectionTarget" class="confirm-modal-subtitle">
                {{ reassignCollectionTarget.jobIdShort || reassignCollectionTarget.jobId }}
              </p>
              <p v-else class="confirm-modal-subtitle">
                {{ tt("vodClips.selectUserToReassign", "Selecciona un usuario para reasignar este VOD.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                :disabled="assigningUserInfoId != null"
                @click="closeReassignCollectionModal"
              >
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <div v-if="reassignUsersLoading" class="collection-reassign-state">
                <i class="ti-reload ti-spin"></i>
                <span>{{ tt("usersList.loadingUsers", "Cargando usuarios...") }}</span>
              </div>
              <div v-else-if="reassignUsers.length === 0" class="collection-reassign-state collection-reassign-state--empty">
                <i class="ti-user"></i>
                <span>{{ tt("makeClips.noAssignableUsers", "No hay usuarios disponibles con acceso de Twitch.") }}</span>
              </div>
              <div v-else>
                <div class="collection-reassign-scope-toggle">
                  <button
                    type="button"
                    class="collection-reassign-scope-btn"
                    :class="{ active: reassignUserScope === 'mine' }"
                    @click="setReassignUserScope('mine')"
                  >
                    {{ tt("twitchPostAdmin.showMyUsers", "Mostrar mis usuarios") }}
                  </button>
                  <button
                    type="button"
                    class="collection-reassign-scope-btn"
                    :class="{ active: reassignUserScope === 'all' }"
                    @click="setReassignUserScope('all')"
                  >
                    {{ tt("twitchPostAdmin.showAllUsers", "Mostrar todos los usuarios") }}
                  </button>
                </div>
                <div class="collection-reassign-search owner-search-wrap">
                  <input
                    v-model.trim="reassignUserNameFilter"
                    type="text"
                    class="form-control form-control-sm owner-search-input collection-reassign-search-input"
                    :placeholder="tt('common.filterByNameOrEmail', 'Filtrar por nombre o correo')"
                  />
                  <i class="ti-search owner-search-icon" aria-hidden="true"></i>
                </div>
                <div v-if="filteredReassignUsers.length === 0" class="collection-reassign-state collection-reassign-state--empty">
                  <i class="ti-search"></i>
                  <span>{{ tt("vodClips.noUsersMatchFilter", "Ningún usuario coincide con el filtro.") }}</span>
                </div>
                <div v-else class="collection-reassign-list">
                <button
                  v-for="user in filteredReassignUsers"
                  :key="`schedule-reassign-user-${user.id}`"
                  type="button"
                  class="collection-reassign-user"
                  :disabled="assigningUserInfoId === user.id || assigningUserInfoId != null"
                  @click="assignVodToUser(user)"
                >
                  <span class="collection-reassign-user-copy">
                    <span class="collection-reassign-user-name">{{ user.username || "N/A" }}</span>
                    <small>{{ user.email }}</small>
                  </span>
                  <span class="collection-reassign-user-action">
                    <i v-if="assigningUserInfoId === user.id" class="ti-reload ti-spin"></i>
                    <i v-else class="ti-check"></i>
                    {{ tt("common.assign", "Asignar") }}
                  </span>
                </button>
                </div>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="assigningUserInfoId != null"
                @click="closeReassignCollectionModal"
              >
                {{ tt("makeClips.close", "Cerrar") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showDeleteCollectionModal && deleteCollectionTarget">
        <div class="confirm-modal-overlay" @click.self="closeDeleteCollectionModal">
          <div
            class="confirm-modal confirm-modal--schedule-manage"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-delete-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="collection-delete-modal-title" class="confirm-modal-title">
                {{ tt("vodClips.confirmDeleteVodTitle", "Confirmar eliminación de VOD") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ tt("vodClips.deleteVodPermanent", "Esta acción es permanente y no se puede deshacer.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                :disabled="deletingCollectionJobId != null"
                @click="closeDeleteCollectionModal"
              >
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <div class="delete-confirm-warning-block" role="alert">
                <i class="ti-alert"></i>
                <p>
                  {{ tt("vodClips.deleteVodWarning", "Una vez eliminado, este VOD ya no podrá ser restaurado.") }}
                </p>
              </div>
              <div class="delete-confirm-summary mt-3">
                <span class="delete-confirm-label">{{ tt("vodClips.name", "Nombre") }}</span>
                <p class="delete-confirm-title">{{ deleteCollectionTarget.displayTitle || deleteCollectionTarget.jobTitle || "N/A" }}</p>
              </div>
              <div class="delete-confirm-summary mt-2">
                <span class="delete-confirm-label">{{ tt("twitchPostAdmin.user", "Usuario") }}</span>
                <p class="delete-confirm-title">{{ deleteVodDisplayUser(deleteCollectionTarget) }}</p>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="deletingCollectionJobId != null"
                @click="closeDeleteCollectionModal"
              >
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--danger"
                :class="{ 'is-loading': deletingCollectionJobId != null }"
                :disabled="deletingCollectionJobId != null"
                @click="confirmDeleteCollection"
              >
                <i v-if="deletingCollectionJobId != null" class="ti-reload rotating"></i>
                <i v-else class="ti-trash"></i>
                {{ deletingCollectionJobId != null ? tt("vodClips.deleting", "Eliminando...") : tt("vodClips.deleteVod", "Eliminar VOD") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showReleaseStuckCollectionModal && releaseStuckCollectionTarget">
        <div class="confirm-modal-overlay" @click.self="closeReleaseStuckCollectionModal">
          <div
            class="confirm-modal confirm-modal--schedule-manage"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-release-stuck-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="collection-release-stuck-modal-title" class="confirm-modal-title">
                {{ tt("vodClips.releaseStuckTitle", "Enviar VOD a error") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ tt("vodClips.releaseStuckSubtitle", "Esta acción libera jobs atorados y conserva clips ya renderizados.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                :disabled="releasingStuckCollectionJobId != null"
                @click="closeReleaseStuckCollectionModal"
              >
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <div class="schedule-modal-lede">
                <p class="schedule-modal-lede-text">
                  {{ tt("vodClips.releaseStuckWarning", "Si el job tenía descarga o clips renderizados, quedará en Renderizado y solo se marcarán como fallidos los clips incompletos.") }}
                </p>
              </div>
              <div class="delete-confirm-summary mt-3">
                <span class="delete-confirm-label">{{ tt("vodClips.name", "Nombre") }}</span>
                <p class="delete-confirm-title">{{ releaseStuckCollectionTarget.displayTitle || "N/A" }}</p>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="releasingStuckCollectionJobId != null"
                @click="closeReleaseStuckCollectionModal"
              >
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--danger"
                :class="{ 'is-loading': releasingStuckCollectionJobId != null }"
                :disabled="releasingStuckCollectionJobId != null"
                @click="confirmReleaseStuckCollection"
              >
                <i v-if="releasingStuckCollectionJobId != null" class="ti-reload rotating"></i>
                <i v-else class="ti-alert"></i>
                {{ releasingStuckCollectionJobId != null ? tt("vodClips.sendingToError", "Enviando...") : tt("vodClips.sendToError", "Enviar a error") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showRerenderCollectionModal && rerenderCollectionTarget">
        <div class="confirm-modal-overlay" @click.self="closeRerenderCollectionModal">
          <div
            class="confirm-modal confirm-modal--schedule-manage"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-rerender-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="collection-rerender-modal-title" class="confirm-modal-title">
                {{ isRerenderFailedClipsMode ? tt("vodClips.rerenderFailedClips", "Re-renderear clips fallidos") : tt("vodClips.rerenderVodConfirmTitle", "Volver a descargar y renderear VOD") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ isRerenderFailedClipsMode ? tt("vodClips.rerenderFailedClipsSubtitle", "Solo se volverán a encolar los clips en estado fallido.") : tt("vodClips.rerenderVodConfirmSubtitle", "Se eliminarán los clips actuales y se volverá a encolar el VOD.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                :disabled="rerenderingCollectionJobId != null"
                @click="closeRerenderCollectionModal"
              >
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <div class="schedule-modal-lede">
                <p class="schedule-modal-lede-text">
                  {{ isRerenderFailedClipsMode ? tt("vodClips.rerenderFailedClipsWarning", "Esta opción conserva clips buenos y solo recupera clips fallidos.") : tt("vodClips.rerenderVodWarning", "Solo usa esta opción cuando haya fallado la descarga. El sistema reiniciará el procesamiento desde cero.") }}
                </p>
              </div>
              <div class="delete-confirm-summary mt-3">
                <span class="delete-confirm-label">{{ tt("vodClips.name", "Nombre") }}</span>
                <p class="delete-confirm-title">{{ rerenderCollectionTarget.displayTitle || "N/A" }}</p>
              </div>
              <p
                v-if="collectionFailureLegend(rerenderCollectionTarget)"
                class="collection-rerender-failure-hint"
              >
                <i class="ti-alert-circle"></i>
                {{ collectionFailureLegend(rerenderCollectionTarget) }}
              </p>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="rerenderingCollectionJobId != null"
                @click="closeRerenderCollectionModal"
              >
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                :class="{ 'is-loading': rerenderingCollectionJobId != null }"
                :disabled="rerenderingCollectionJobId != null"
                @click="confirmRerenderCollection"
              >
                <i v-if="rerenderingCollectionJobId != null" class="ti-reload rotating"></i>
                <i v-else class="ti-reload"></i>
                {{ rerenderingCollectionJobId != null ? tt("vodClips.rerenderingVod", "Reencolando...") : (isRerenderFailedClipsMode ? tt("vodClips.rerenderFailedClips", "Re-renderear clips fallidos") : tt("vodClips.rerenderVodFull", "Volver a descargar y renderear")) }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>
 
    <!-- Modal para programar clip con metadatos (estilo MakeClips / Momo) -->
    <transition name="modal-fade">
      <body-portal v-if="showScheduleModal && selectedClipForSchedule && !isInlineSchedulePanel">
        <div
          class="confirm-modal-overlay"
        >
        <div
          class="confirm-modal confirm-modal--schedule-form"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-metadata-modal-title"
        >
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="schedule-metadata-modal-title" class="confirm-modal-title">{{ tt("schedulePost.scheduleClip", "Programar clip") }}</h3>
            <button type="button" class="confirm-modal-close" @click="closeScheduleModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body schedule-modal-scroll-body">
            <div class="schedule-modal-stepper" role="list" :aria-label="tt('schedulePost.scheduleClip', 'Programar clip')">
              <button
                type="button"
                class="schedule-modal-stepper-item"
                :class="{ 'is-active': scheduleModalStep === 1, 'is-complete': scheduleModalStep > 1 }"
                :aria-current="scheduleModalStep === 1 ? 'step' : null"
                role="listitem"
                disabled
                aria-disabled="true"
              >
                <span class="schedule-modal-stepper-index" aria-hidden="true">
                  <i v-if="scheduleModalStep > 1" class="ti-check"></i>
                  <span v-else>1</span>
                </span>
                <span class="schedule-modal-stepper-label">{{ tt("makeClips.connectedAccounts", "Redes conectadas") }}</span>
              </button>
              <span class="schedule-modal-stepper-divider" :class="{ 'is-complete': scheduleModalStep > 1 }"></span>
              <button
                type="button"
                class="schedule-modal-stepper-item"
                :class="{ 'is-active': scheduleModalStep === 2, 'is-complete': scheduleModalStep > 2, 'is-next': scheduleModalStep === 1 }"
                :aria-current="scheduleModalStep === 2 ? 'step' : null"
                role="listitem"
                disabled
                aria-disabled="true"
              >
                <span class="schedule-modal-stepper-index" aria-hidden="true">
                  <i v-if="scheduleModalStep > 2" class="ti-check"></i>
                  <span v-else>2</span>
                </span>
                <span class="schedule-modal-stepper-label">{{ tt("schedulePost.generalSettings", "Details") }}</span>
              </button>
              <span class="schedule-modal-stepper-divider" :class="{ 'is-complete': scheduleModalStep > 2 }"></span>
              <button
                type="button"
                class="schedule-modal-stepper-item"
                :class="{ 'is-active': scheduleModalStep === 3, 'is-next': scheduleModalStep === 2, 'is-pending': scheduleModalStep === 1 }"
                :aria-current="scheduleModalStep === 3 ? 'step' : null"
                role="listitem"
                disabled
                aria-disabled="true"
              >
                <span class="schedule-modal-stepper-index" aria-hidden="true">3</span>
                <span class="schedule-modal-stepper-label">{{ tt("schedulePost.chooseDateTime", "Date and time") }}</span>
              </button>
            </div>

            <section
              v-if="scheduleModalStep === 1 && clipMetadataDraft"
              class="schedule-modal-step-panel schedule-modal-step-panel--social"
            >
              <aside class="schedule-modal-left-rail schedule-modal-left-rail--step">
                <div class="schedule-modal-left-rail-title">{{ tt("makeClips.connectedAccounts", "Redes conectadas") }}</div>
                <p class="schedule-modal-left-rail-hint">
                  {{
                    tt(
                      "schedulePost.networkToggleHint",
                      "Toca una red para activarla o desactivarla y definir dónde se publicará este clip."
                    )
                  }}
                </p>
                <div class="schedule-modal-network-list">
                  <div
                    v-for="platform in metadataPlatforms"
                    :key="platform"
                    class="schedule-modal-network-row"
                    @click="handleScheduleNetworkRowClick(platform)"
                    :class="{
                      'is-on': clipMetadataDraft.platforms.includes(platform),
                      'is-disabled': !isSchedulePlatformConnected(platform),
                      'is-loading': scheduleConnectOpening && scheduleConnectingPlatform === platform,
                    }"
                  >
                    <button
                      type="button"
                      class="schedule-modal-network-item"
                      :class="{
                        'is-on': clipMetadataDraft.platforms.includes(platform),
                        'is-disabled': !isSchedulePlatformConnected(platform),
                        'is-loading': scheduleConnectOpening && scheduleConnectingPlatform === platform,
                      }"
                      :disabled="scheduleConnectionsLoading || scheduleConnectOpening"
                      :aria-pressed="clipMetadataDraft.platforms.includes(platform)"
                      :aria-label="
                        (clipMetadataDraft.platforms.includes(platform)
                          ? tt('schedulePost.platformDisableByTap', 'Toca para desactivar')
                          : tt('schedulePost.platformEnableByTap', 'Toca para activar')) +
                        ' ' +
                        getPlatformLabel(platform)
                      "
                      @click.stop="toggleSchedulePlatform(platform)"
                    >
                      <div class="schedule-modal-network-main">
                        <span class="schedule-social-logo-wrap schedule-social-logo-wrap--card schedule-modal-network-logo">
                          <img
                            class="schedule-social-logo"
                            :src="getPlatformLogoUrl(platform, clipMetadataDraft.platforms.includes(platform) && isSchedulePlatformConnected(platform))"
                            alt=""
                          />
                        </span>
                        <div class="schedule-modal-network-meta">
                          <span class="schedule-modal-network-name">{{ getPlatformLabel(platform) }}</span>
                          <small
                            class="schedule-modal-network-status"
                            :class="isSchedulePlatformConnected(platform) ? 'is-connected' : 'is-disconnected'"
                          >
                            {{
                              isSchedulePlatformConnected(platform)
                                ? tt("userProfile.connected", "Conectado")
                                : tt("schedulePost.platformDisconnected", "No conectada")
                            }}
                          </small>
                          <span
                            class="schedule-modal-network-toggle-pill"
                            :class="{
                              'is-on': clipMetadataDraft.platforms.includes(platform),
                              'is-disabled': !isSchedulePlatformConnected(platform),
                              'is-loading': scheduleConnectOpening && scheduleConnectingPlatform === platform,
                            }"
                          >
                            <template v-if="scheduleConnectOpening && scheduleConnectingPlatform === platform">
                              <i class="ti-reload rotating"></i>
                              {{ tt("schedulePost.openingConnection", "Abriendo conexión...") }}
                            </template>
                            <template v-else>
                              <i
                                :class="
                                  clipMetadataDraft.platforms.includes(platform) && isSchedulePlatformConnected(platform)
                                    ? 'ti-check'
                                    : 'ti-close'
                                "
                                class="schedule-modal-network-toggle-icon"
                                aria-hidden="true"
                              ></i>
                              <span class="schedule-modal-network-toggle-sr-only">
                                {{
                                  !isSchedulePlatformConnected(platform)
                                    ? tt("schedulePost.platformUnavailable", "No disponible")
                                    : clipMetadataDraft.platforms.includes(platform)
                                      ? tt("schedulePost.publishEnabled", "Publicación activa")
                                      : tt("schedulePost.publishDisabled", "Publicación inactiva")
                                }}
                              </span>
                            </template>
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                  <div v-if="isSchedulePlatformConnected('facebook')" class="schedule-modal-facebook-editor schedule-modal-facebook-editor--grid-slot">
                    <label class="schedule-modal-facebook-label">
                      {{ tt("schedulePost.facebookPageId", "Página de Facebook") }}
                    </label>
                    <select
                      class="form-control"
                      v-model="clipMetadataDraft.platform_metadata.facebook.facebook_page_id"
                      :disabled="scheduleFacebookPagesLoading || !scheduleFacebookPageOptions.length"
                    >
                      <option value="" disabled>
                        {{ tt("schedulePost.facebookPageSelectPlaceholder", "Selecciona una página") }}
                      </option>
                      <option
                        v-for="page in scheduleFacebookPageOptions"
                        :key="page.id"
                        :value="page.id"
                      >
                        {{ page.name }}
                      </option>
                    </select>
                    <small v-if="scheduleFacebookPagesLoading" class="form-text text-muted">
                      {{ tt("schedulePost.loadingFacebookPages", "Cargando páginas de Facebook...") }}
                    </small>
                    <small v-else-if="scheduleFacebookPagesLoadError" class="form-text text-danger">
                      {{ tt("schedulePost.facebookPagesLoadError", "No se pudieron cargar tus páginas de Facebook.") }}
                    </small>
                    <small v-else-if="!scheduleFacebookPageOptions.length" class="form-text text-muted">
                      {{ tt("schedulePost.facebookPagesEmpty", "No hay páginas de Facebook disponibles para esta cuenta.") }}
                    </small>
                  </div>
                </div>
              </aside>
            </section>

            <section
              v-if="scheduleModalStep === 2 && clipMetadataDraft"
              class="schedule-modal-step-panel schedule-modal-step-panel--general"
            >
              <div class="schedule-modal-main-column schedule-modal-main-column--step">
                <div class="metadata-section-general schedule-modal-general-card">
                  <div class="schedule-modal-general-layout">
                    <div class="schedule-modal-general-fields">
                      <div class="form-group form-group-full schedule-modal-compact-field">
                        <label><strong>{{ tt("schedulePost.title", "Título") }}</strong> <span class="field-required">*</span></label>
                        <input class="form-control" v-model="clipMetadataDraft.title" :placeholder="tt('schedulePost.titleAllPlatforms', 'Título usado en todas las plataformas')" />
                      </div>
                      <div class="form-group form-group-full schedule-modal-compact-field">
                        <label><strong>{{ tt("schedulePost.description", "Descripción") }}</strong></label>
                        <textarea class="form-control" rows="2" v-model="clipMetadataDraft.description" :placeholder="tt('schedulePost.descriptionPlaceholder', 'Subtítulo / descripción')"></textarea>
                      </div>
                      <div class="form-group form-group-full schedule-modal-compact-field mb-0">
                        <label><strong>{{ tt("schedulePost.tags", "Etiquetas") }}</strong></label>
                        <input
                          class="form-control"
                          v-model="clipMetadataDraft.hashtags"
                          :placeholder="tt('schedulePost.tagsHashtagPlaceholder', '#gaming #clips #stream')"
                        />
                        <small class="form-text text-muted schedule-modal-compact-hint">
                          {{ tt("schedulePost.tagsHashtagHint", "Separa cada etiqueta con #") }}
                        </small>
                      </div>
                      <div class="schedule-modal-preview-social-head">
                        <p class="schedule-modal-preview-social-title">
                          {{ tt("schedulePost.selectedNetwork", "Red seleccionada") }}
                        </p>
                        <div class="schedule-modal-preview-platform-chips">
                          <span
                            v-for="platform in scheduleModalPreviewActivePlatforms"
                            :key="`schedule-preview-platform-${platform}`"
                            class="schedule-modal-preview-platform-chip"
                          >
                            <img
                              class="schedule-social-logo"
                              :src="getPlatformLogoUrl(platform, true)"
                              alt=""
                            />
                            <span>{{ getPlatformLabel(platform) }}</span>
                          </span>
                          <span
                            v-if="!scheduleModalPreviewActivePlatforms.length"
                            class="schedule-modal-preview-platform-chip schedule-modal-preview-platform-chip--empty"
                          >
                            {{ tt("schedulePost.selectPlatformToPreview", "no se tienen redes sociales seleccionadas") }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="schedule-modal-preview-pane">
                      <div class="schedule-modal-preview-phone">
                        <div class="schedule-modal-preview-frame">
                          <img
                            v-if="scheduleModalPreviewThumbnailUrl"
                            :src="scheduleModalPreviewThumbnailUrl"
                            class="schedule-modal-preview-image"
                            alt=""
                          />
                          <div v-else class="schedule-modal-preview-empty">
                            <i class="ti-image"></i>
                            <span>{{ tt("schedulePost.noClipYet", "sin clip aún") }}</span>
                          </div>
                          <div class="schedule-modal-preview-topbar">
                            <span class="schedule-modal-preview-avatar">
                              {{ scheduleModalPreviewAvatarLetter }}
                            </span>
                            <div class="schedule-modal-preview-account-meta">
                              <span class="schedule-modal-preview-account-name">
                                {{ scheduleModalPreviewHandle }}
                              </span>
                              <span class="schedule-modal-preview-account-subtitle">
                                {{ scheduleModalPreviewScheduleLabel }}
                              </span>
                            </div>
                          </div>
                          <div class="schedule-modal-preview-gradient"></div>
                          <div class="schedule-modal-preview-caption">
                            <p class="schedule-modal-preview-caption-title">
                              {{ scheduleModalPreviewTitle }}
                            </p>
                            <p
                              v-if="scheduleModalPreviewDescription"
                              class="schedule-modal-preview-caption-description"
                            >
                              {{ scheduleModalPreviewDescription }}
                            </p>
                            <div
                              v-if="scheduleModalPreviewHashtags.length"
                              class="schedule-modal-preview-tags"
                            >
                              <span
                                v-for="tag in scheduleModalPreviewHashtags"
                                :key="`schedule-preview-tag-${tag}`"
                                class="schedule-modal-preview-tag"
                              >
                                {{ tag }}
                              </span>
                            </div>
                            <div class="schedule-modal-preview-engagement">
                              <span>
                                <i class="ti-heart"></i>
                                {{ tt("schedulePost.likesLabel", "Me gusta") }}
                              </span>
                              <span>
                                <i class="ti-comment-alt"></i>
                                {{ tt("schedulePost.commentsLabel", "Comentarios") }}
                              </span>
                              <span>
                                <i class="ti-share"></i>
                                {{ tt("schedulePost.shareLabel", "Compartir") }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              v-if="scheduleModalStep === 3 && pendingScheduleData"
              class="schedule-modal-step-panel schedule-modal-step-panel--posting"
            >
              <div class="schedule-modal-posting-block schedule-modal-posting-block--step">
                <div class="assign-cycle-timeline assign-cycle-timeline--posting mt-3">
                  <div class="calendar-timeline-header">
                    <div class="calendar-timeline-title">
                      <label class="assign-cycle-time-label schedule-timeline-posting-date-label">
                        <i class="ti-calendar" style="margin-right: 6px;"></i>
                        {{ tt("schedulePost.postingDateLabel", "Posting timeline") }}:
                      </label>
                      <div class="calendar-timeline-month">
                        {{ postingTimelineMonthLabel }}
                      </div>
                    </div>
                  </div>
                  <div class="calendar-timeline-body">
                    <template v-if="loadingPostingTimeline">
                      <clipr-loader
                        variant="panel"
                        class="clipr-loader--compact"
                        :title="tt('common.loading', 'Cargando...')"
                      />
                    </template>
                    <template v-else-if="postingTimelineError">
                      <div class="timeline-loading-state timeline-loading-state--modal" role="status" aria-live="polite">
                        <i class="ti-alert-circle timeline-loading-icon"></i>
                        <p class="timeline-loading-title">{{ tt("schedulePost.errorLoadAdminTimeline", "No fue posible cargar la timeline.") }}</p>
                        <p class="timeline-loading-subtitle">{{ postingTimelineError }}</p>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        ref="postingTimelineDays"
                        class="timeline-days timeline-days--posting"
                        @scroll.passive="onPostingTimelineScroll"
                      >
                        <div
                          v-for="day in postingTimelineDays"
                          :key="day.isoDate"
                          :data-iso-date="day.isoDate"
                          class="timeline-day-card"
                          :class="{
                            'is-today': day.isToday,
                            'has-events': day.events && day.events.length > 0,
                            'is-selected': pendingScheduleData.date === day.isoDate,
                            'is-disabled': isPastPostingTimelineDay(day),
                          }"
                          :aria-disabled="isPastPostingTimelineDay(day) ? 'true' : 'false'"
                          @click="selectPostingDate(day)"
                        >
                          <div class="timeline-day-name">
                            {{ day.weekdayLabel }}
                          </div>
                          <div class="timeline-day-number">
                            {{ day.dayNumber }}
                          </div>
                          <div class="timeline-day-events">
                            <span
                              v-if="!day.events || day.events.length === 0"
                              class="timeline-day-empty"
                            >
                              <i class="ti-calendar" style="font-size: 12px; margin-right: 4px; opacity: 0.5;"></i>
                              {{ tt("schedulePost.noClipsFound", "no clips scheduled") }}
                            </span>
                            <span
                              v-for="event in day.events"
                              :key="event.id"
                              class="timeline-day-event-pill timeline-day-event-pill--static"
                              :title="getTimelineEventContent(event)"
                            >
                              {{ getTimelineEventContent(event) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="schedule-modal-posting-controls schedule-modal-posting-controls--combined mt-3">
                  <div class="assign-cycle-timezone-picker assign-cycle-timezone-picker--compact">
                    <label class="assign-cycle-time-label">
                      <i class="ti-world" style="margin-right: 6px;"></i>
                      {{ tt("schedulePost.timezone", "Zona horaria") }}:
                    </label>
                    <select
                      v-model="postingSelectedTimezone"
                      class="material-timezone-select"
                      :disabled="postingAssigning"
                      @change="onPostingTimezoneChange"
                      @focus="refreshPostingTimezonePreviewTimes"
                      @click="refreshPostingTimezonePreviewTimes"
                    >
                      <option value="" disabled>{{ tt("schedulePost.selectTimezone", "Selecciona zona horaria") }}</option>
                      <option
                        v-for="option in postingTimezoneOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <p v-if="!postingSelectedTimezone" class="assign-cycle-time-hint">
                      {{ tt("schedulePost.selectTimezoneHint", "Selecciona una zona horaria") }}
                    </p>
                  </div>

                  <div class="assign-cycle-time-picker assign-cycle-time-picker--compact">
                    <label class="assign-cycle-time-label">
                      <i class="ti-time" style="margin-right: 6px;"></i>
                      {{ tt("schedulePost.postingTime", "Hora de publicación") }}:
                    </label>
                    <div class="material-time-picker">
                      <div class="material-time-picker-main">
                        <div class="material-time-input-group">
                          <label class="material-time-input-label">{{ tt("schedulePost.hour", "Hora") }}</label>
                          <input
                            type="text"
                            inputmode="numeric"
                            v-model="postingSelectedPostingHourText"
                            class="material-time-input"
                            maxlength="2"
                            placeholder="--"
                            @input="validatePostingHourOnInput"
                            @blur="validatePostingHour"
                          />
                        </div>
                        <span class="material-time-separator">:</span>
                        <div class="material-time-input-group">
                          <label class="material-time-input-label">{{ tt("schedulePost.minute", "Minuto") }}</label>
                          <input
                            type="text"
                            inputmode="numeric"
                            v-model="postingSelectedPostingMinuteText"
                            class="material-time-input"
                            maxlength="2"
                            placeholder="--"
                            @input="validatePostingMinuteOnInput"
                            @blur="validatePostingMinute"
                          />
                        </div>
                        <div class="material-time-period">
                          <button
                            type="button"
                            class="material-period-btn"
                            :class="{ 'is-selected': postingSelectedPostingPeriod === 'AM' }"
                            @click="postingSelectedPostingPeriod = 'AM'"
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            class="material-period-btn"
                            :class="{ 'is-selected': postingSelectedPostingPeriod === 'PM' }"
                            @click="postingSelectedPostingPeriod = 'PM'"
                          >
                            PM
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              v-if="scheduleModalStep === 3 && !pendingScheduleData"
              class="schedule-modal-step-panel schedule-modal-step-panel--empty"
            >
              <div class="schedule-modal-step-placeholder">
                <i class="ti-reload rotating"></i>
                <span>{{ tt("common.loading", "Cargando...") }}</span>
              </div>
            </section>

            <div v-if="scheduleModalStep === 3 && scheduleError" class="alert alert-danger mt-3">
              {{ scheduleError }}
            </div>
            <div v-if="scheduleModalStep === 3 && postingAssignError" class="alert alert-danger mt-3">
              {{ postingAssignError }}
            </div>
          </div>
          <div class="confirm-modal-footer schedule-modal-footer-actions">
            <button
              type="button"
              class="confirm-modal-btn confirm-modal-btn--secondary"
              :disabled="savingMetadata || postingAssigning"
              @click="closeScheduleModal"
            >
              <i class="ti-close"></i>
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              v-if="scheduleModalStep > (scheduleEditingExistingClip ? 2 : 1)"
              type="button"
              class="confirm-modal-btn confirm-modal-btn--secondary"
              :disabled="savingMetadata || postingAssigning"
              @click="goToPreviousScheduleModalStep"
            >
              <i class="ti-angle-left"></i>
              {{ tt("common.previous", "Anterior") }}
            </button>
            <button
              v-if="scheduleModalStep < 3"
              type="button"
              class="confirm-modal-btn confirm-modal-btn--primary"
              :disabled="savingMetadata || postingAssigning || !canGoToNextScheduleModalStep()"
              @click="goToNextScheduleModalStep"
            >
              <i class="ti-angle-right"></i>
              {{ tt("common.next", "Siguiente") }}
            </button>
            <button
              v-else
              type="button"
              class="confirm-modal-btn confirm-modal-btn--primary"
              :class="{ 'is-loading': savingMetadata || postingAssigning }"
              :disabled="
                savingMetadata ||
                postingAssigning ||
                (!scheduleEditingExistingClip && getConnectedSchedulePlatforms().length === 0) ||
                !pendingScheduleData ||
                !pendingScheduleData.date ||
                postingSelectedPostingHour === null ||
                postingSelectedPostingMinute === null ||
                !postingSelectedTimezone
              "
              :title="
                !scheduleEditingExistingClip && getConnectedSchedulePlatforms().length === 0
                  ? tt('schedulePost.errorNoConnectedPlatforms', 'No tienes redes sociales conectadas para programar este clip. Conecta al menos una desde tu perfil.')
                  : ''
              "
              @click="saveAndScheduleClip"
            >
              <i v-if="savingMetadata || postingAssigning" class="ti-reload rotating"></i>
              <i v-else class="ti-calendar"></i>
              {{
                savingMetadata || postingAssigning
                  ? tt("schedulePost.scheduling", "Programando...")
                  : (scheduleEditingExistingClip
                    ? tt("schedulePost.saveScheduledChanges", "Guardar cambios")
                    : tt("schedulePost.saveAndSchedule", "Guardar y programar"))
              }}
            </button>
          </div>
        </div>
        </div>
      </body-portal>
    </transition>

    <schedule-subscription-prompt-modal
      v-model="showScheduleSubscriptionPromptModal"
      :has-active-plan="scheduleSubscriptionHasActivePlan"
      @close="closeScheduleSubscriptionPromptModal"
      @subscribe="openSubscriptionFromSchedulePrompt"
    />

    <transition name="modal-fade">
      <body-portal v-if="showScheduleConnectPromptModal && showScheduleModal">
        <div
          class="confirm-modal-overlay"
          @click.self="closeScheduleConnectPromptModal"
        >
          <div
            class="confirm-modal confirm-modal--schedule-manage"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-connect-social-prompt-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="schedule-connect-social-prompt-title" class="confirm-modal-title">
                {{ tt("schedulePost.connectSocialPromptTitle", "Conecta tus redes sociales") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ tt("schedulePost.connectSocialPromptSubtitle", "Necesitas al menos una red conectada para programar clips.") }}
              </p>
              <button type="button" class="confirm-modal-close" @click="closeScheduleConnectPromptModal">
                <i class="ti-close"></i>
              </button>
            </div>
            <div class="confirm-modal-body schedule-modal-readable-body">
              <div class="schedule-modal-lede">
                <p class="schedule-modal-lede-text">
                  {{ tt("schedulePost.connectSocialPromptBody", "Ve a Configuración para conectar tus redes sociales y volver a programar este clip.") }}
                </p>
              </div>
            </div>
            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                @click="closeScheduleConnectPromptModal"
              >
                <i class="ti-close"></i>
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                @click="goToSocialConnectionsSettings"
              >
                <i class="ti-arrow-right"></i>
                {{ tt("schedulePost.connectSocialPromptContinue", "Continuar") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>

    <!-- Modal para administrar clip ya programado -->
    <transition name="modal-fade">
      <body-portal v-if="showManageScheduledModal && selectedScheduledClip">
        <div
          class="confirm-modal-overlay"
          @click.self="closeManageScheduledModal"
        >
        <div
          class="confirm-modal confirm-modal--schedule-manage"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-manage-modal-title"
        >
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="schedule-manage-modal-title" class="confirm-modal-title">{{ tt("twitchPostAdmin.manageScheduledClip", "Administrar clip programado") }}</h3>
            <p class="confirm-modal-subtitle">{{ tt("schedulePost.manageScheduledSubtitle", "Detalles y acciones para esta publicación.") }}</p>
            <button type="button" class="confirm-modal-close" @click="closeManageScheduledModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body schedule-modal-readable-body">
            <div class="schedule-modal-lede">
              <p class="schedule-modal-lede-text">
                {{ tt("schedulePost.manageScheduledBody", "Estás gestionando la publicación programada de este clip. Los datos se muestran a continuación.") }}
              </p>
              <p class="schedule-modal-clip-title">
                {{ selectedScheduledClip.clipTitle || getClipDisplayName(selectedScheduledClip) }}
              </p>
            </div>

          <div class="scheduled-clip-grid">
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.status", "Estado") }}</span>
              <strong class="scheduled-clip-value">{{ getClipStatusLabel(selectedScheduledClip.status, selectedScheduledClip) }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.scheduledDate", "Fecha programada") }}</span>
              <strong class="scheduled-clip-value">{{ getScheduledClipDateLabel(selectedScheduledClip) }}</strong>
            </div>
            <div class="scheduled-clip-item">
              <span class="scheduled-clip-label">{{ tt("twitchPostAdmin.scheduledTime", "Hora programada") }}</span>
              <strong class="scheduled-clip-value">
                {{ getScheduledClipTimeLabel(selectedScheduledClip) }} ({{ getScheduledClipTimezoneLabel() }})
              </strong>
            </div>
          </div>

          <div v-if="manageScheduledError" class="schedule-modal-inline-error mt-3" role="alert">
            <i class="ti-alert-circle"></i>
            {{ manageScheduledError }}
          </div>
          </div>
          <div class="confirm-modal-footer schedule-manage-footer-actions">
            <button
              v-if="canEditScheduledClip(selectedScheduledClip)"
              type="button"
              class="confirm-modal-btn confirm-modal-btn--primary"
              :disabled="manageScheduledCancelling"
              @click="openScheduledClipEditorFromManage"
            >
              <i class="ti-pencil"></i>
              {{ tt("schedulePost.editScheduledMetadata", "Editar programación") }}
            </button>
            <button
              v-if="!isClipPosted(selectedScheduledClip)"
              type="button"
              class="confirm-modal-btn confirm-modal-btn--danger"
              :class="{ 'is-loading': manageScheduledCancelling }"
              :disabled="manageScheduledCancelling"
              @click="cancelScheduledUpload"
            >
              <i v-if="manageScheduledCancelling" class="ti-reload rotating"></i>
              <i v-else class="ti-na"></i>
              {{ manageScheduledCancelling ? tt("twitchPostAdmin.cancellingUpload", "Cancelando...") : tt("twitchPostAdmin.cancelUpload", "Cancelar subida") }}
            </button>
            <button
              type="button"
              class="confirm-modal-btn confirm-modal-btn--secondary"
              :disabled="manageScheduledCancelling"
              @click="closeManageScheduledModal"
            >
              <i class="ti-close"></i>
              {{ tt("makeClips.close", "Cerrar") }}
            </button>
          </div>
        </div>
        </div>
      </body-portal>
    </transition>

    <transition name="modal-fade">
      <body-portal v-if="showClipDeleteModal && pendingDeleteClip">
        <div
          class="confirm-modal-overlay"
          @click.self="closeClipDeleteModal"
        >
        <div class="confirm-modal schedule-delete-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-delete-confirm-title">
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="schedule-delete-confirm-title" class="confirm-modal-title">
              {{ deleteClipConfirmStep === 1 ? tt("publicResults.confirmDelete", "Confirmar eliminación") : tt("publicResults.lastConfirmation", "Última confirmación") }}
            </h3>
            <p class="confirm-modal-subtitle">
              {{
                deleteClipConfirmStep === 1
                  ? tt("schedulePost.deleteStepOne", "Este clip dejará de estar disponible en la programación.")
                  : tt("publicResults.deleteStepTwo", "Esta acción es permanente y no se podrá deshacer.")
              }}
            </p>
            <button class="confirm-modal-close" type="button" @click="closeClipDeleteModal" :disabled="isDeletingPendingScheduleClip">
              <i class="ti-close"></i>
            </button>
          </div>

          <div class="confirm-modal-body schedule-modal-readable-body">
            <div class="schedule-modal-lede">
              <p class="schedule-modal-lede-text">
                {{ tt("schedulePost.deleteReviewHint", "Revisa el clip antes de confirmar; podrás cancelar con el botón inferior.") }}
              </p>
            </div>
            <div class="delete-confirm-summary">
              <span class="delete-confirm-label">{{ tt("publicResults.selectedClip", "Clip seleccionado") }}</span>
              <p class="delete-confirm-title">{{ pendingDeleteClip.clipTitle || getClipDisplayName(pendingDeleteClip) }}</p>
            </div>

            <div v-if="deleteClipConfirmStep === 2" class="delete-confirm-warning-block" role="alert">
              <i class="ti-alert"></i>
              <p>
                {{ tt("schedulePost.deleteStepTwoWarning", "Si continúas, el clip será eliminado y no volverá a aparecer en esta vista.") }}
              </p>
            </div>

            <p v-if="deleteClipModalError" class="delete-confirm-error">
              <i class="ti-alert-circle"></i> {{ deleteClipModalError }}
            </p>
          </div>

          <div class="confirm-modal-footer">
            <button class="confirm-modal-btn confirm-modal-btn--secondary" type="button" @click="closeClipDeleteModal" :disabled="isDeletingPendingScheduleClip">
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              class="confirm-modal-btn"
              :class="deleteClipConfirmStep === 1 ? 'confirm-modal-btn--primary' : 'confirm-modal-btn--danger'"
              type="button"
              :disabled="isDeletingPendingScheduleClip"
              @click="confirmClipDeleteStep"
            >
              {{ isDeletingPendingScheduleClip
                ? tt("schedulePost.deleting", "Eliminando...")
                : (deleteClipConfirmStep === 1 ? tt("publicResults.yesContinue", "Si, continuar") : tt("publicResults.yesDeletePermanent", "Si, eliminar definitivamente")) }}
            </button>
          </div>
        </div>
        </div>
      </body-portal>
    </transition>

    <div v-if="isClipTimeEditorVisible" class="schedule-editor-page">
      <section
        class="schedule-editor-shell confirm-modal--clip-time-editor"
        :class="{ 'is-read-only': isDemoClip(clipTimeEditor.clip) }"
        role="region"
        aria-labelledby="schedule-clip-time-editor-title"
      >
            <header class="confirm-modal-header confirm-modal-header--centered schedule-editor-header">
              <h3 id="schedule-clip-time-editor-title" class="confirm-modal-title">
                {{ tt("vodClips.clipEditorTitle", "Editor de clips") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ clipTimeEditor.clip.clipTitle || getClipDisplayName(clipTimeEditor.clip) }}
              </p>
            </header>

            <div class="confirm-modal-body schedule-modal-readable-body clip-time-editor-modal-body schedule-editor-body">
              <div class="clip-time-editor-screen">
                <div class="make-flow-subtitles-wrap">
                  <label
                    class="make-flow-subtitles-pill"
                    :class="clipTimeEditor.draftSubtitlesEnabled ? 'make-flow-subtitles-pill--active' : 'make-flow-subtitles-pill--inactive'"
                  >
                    <span class="make-flow-subtitles-pill-row">
                      <span class="make-flow-subtitles-pill-label">
                        {{ tt("makeClips.enableSubtitles", "Agregar subtítulos") }}
                      </span>
                      <span class="make-flow-subtitles-pill-switch">
                        <input
                          v-model="clipTimeEditor.draftSubtitlesEnabled"
                          type="checkbox"
                          :disabled="clipTimeEditorSaving"
                          :aria-label="tt('makeClips.enableSubtitles', 'Agregar subtítulos')"
                        >
                        <span class="make-flow-subtitles-pill-track" aria-hidden="true">
                          <span class="make-flow-subtitles-pill-thumb"></span>
                        </span>
                      </span>
                    </span>
                    <span
                      class="make-flow-subtitles-pill-status"
                      :class="clipTimeEditor.draftSubtitlesEnabled ? 'is-on' : 'is-off'"
                    >
                      {{ clipTimeEditor.draftSubtitlesEnabled
                        ? tt("makeClips.subtitlesStatusOn", "Activados")
                        : tt("makeClips.subtitlesStatusOff", "Desactivados") }}
                    </span>
                  </label>
                  <button
                    type="button"
                    class="clip-transcript-open-btn"
                    :disabled="!clipTimeEditor.draftSubtitlesEnabled || clipTimeEditorSaving || isDemoClip(clipTimeEditor.clip)"
                    @click="openClipTranscriptEditor"
                  >
                    <i class="ti-pencil-alt"></i>
                    {{ tt("vodClips.correctTranscript", "Corregir transcripción") }}
                  </button>
                  <button
                    type="button"
                    class="clip-transcript-open-btn"
                    :disabled="!clipTimeEditor.draftSubtitlesEnabled || clipTimeEditorSaving || isDemoClip(clipTimeEditor.clip)"
                    @click="openClipTimeEditorSubtitleStyleModal"
                  >
                    <i class="ti-palette"></i>
                    {{ tt("userProfile.subtitleStyleEdit", "Editar") }}
                  </button>
                </div>

                <div class="editor-format">
                  <label id="clip-time-editor-video-type-label" class="editor-format-label">
                    {{ tt("vodClips.videoTypeLabel", "Tipo de video") }}
                  </label>
                  <div
                    class="clip-time-editor-video-type-picker"
                    role="radiogroup"
                    aria-labelledby="clip-time-editor-video-type-label"
                  >
                    <button
                      v-for="option in clipTimeEditorVideoTypeOptions"
                      :key="option.value"
                      type="button"
                      class="clip-time-editor-video-type-option"
                      :class="{ 'is-active': clipTimeEditor.draftVideoType === option.value }"
                      :disabled="clipTimeEditorSaving"
                      :aria-pressed="clipTimeEditor.draftVideoType === option.value ? 'true' : 'false'"
                      @click="setClipTimeEditorVideoType(option.value)"
                    >
                      <span class="clip-time-editor-video-type-option-icon">
                        <layout-type-icon :type="option.value" />
                      </span>
                      <span class="clip-time-editor-video-type-option-label">
                        {{ tt(option.labelKey, option.fallback) }}
                      </span>
                    </button>
                  </div>
                  <p v-if="clipTimeEditorSelectedVideoTypeDescription" class="clip-time-editor-video-type-help">
                    {{ clipTimeEditorSelectedVideoTypeDescription }}
                  </p>
                </div>

                <div class="editor-stage">
                <div class="editor-preview-row">
                  <div class="editor-preview-main">
                    <p class="editor-preview-caption editor-preview-caption--source">
                      {{ tt("vodClips.editorOriginalStreamLabel", "Transmisión original") }}
                    </p>
                    <div class="clip-time-editor-video-shell">
                      <div class="clip-time-editor-video-frame">
                        <video
                          ref="clipTimeEditorVideo"
                          class="clip-time-editor-video"
                          :src="clipTimeEditorVideoSrc(clipTimeEditor.clip)"
                          preload="metadata"
                          playsinline
                          @loadedmetadata="onClipTimeEditorLoadedMetadata"
                          @play="onClipTimeEditorPlay"
                          @pause="onClipTimeEditorPause"
                          @timeupdate="onClipTimeEditorTimeUpdate"
                          @volumechange="onClipTimeEditorVolumeChange"
                          @error="onClipTimeEditorVideoError"
                        ></video>
                        <div
                          v-if="clipTimeEditorShowsSourceLayoutControls"
                          class="clip-time-editor-layout-overlay"
                        >
                          <div
                            v-for="handle in clipTimeEditorSourceLayoutHandles()"
                            :key="handle.key"
                            class="clip-time-editor-layout-box"
                            :class="handle.className"
                            :style="clipTimeEditorSourceLayoutHandleStyle(handle)"
                            @pointerdown.stop.prevent="onClipTimeEditorLayoutPointerDown(handle.key, $event)"
                          >
                            <span>{{ handle.label }}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="clipTimeEditorPlaybackLoading"
                        class="clip-time-editor-preview-loader"
                        role="status"
                        aria-live="polite"
                      >
                        <span class="clip-time-editor-preview-spinner" aria-hidden="true">
                          <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--outer"></span>
                          <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--inner"></span>
                        </span>
                        <span class="clip-time-editor-preview-loader-label">{{ tt("common.loading", "Cargando...") }}</span>
                      </div>
                    </div>
                    <p
                      v-if="clipTimeEditorShowsSourceLayoutControls"
                      class="clip-time-editor-video-type-help"
                    >
                      {{ tt("vodClips.layoutDragHint", "Arrastra los recuadros sobre el video horizontal para elegir el encuadre del clip vertical.") }}
                    </p>
                  </div>

                  <div class="editor-preview-vertical">
                    <div class="editor-preview-vertical-stack">
                      <p class="editor-preview-caption editor-preview-caption--clip">
                        {{ tt("vodClips.editorClipPreviewLabel", "Vista previa del clip") }}
                      </p>
                    <div v-if="clipTimeEditorIsIrlVideoType" class="clip-time-editor-context-preview-shell">
                      <div
                        ref="clipTimeEditorContextStage"
                        class="clip-time-editor-context-stage"
                      >
                        <canvas
                          ref="clipTimeEditorPreviewCanvas"
                          class="clip-time-editor-preview-canvas"
                          aria-hidden="true"
                        ></canvas>
                        <div
                          v-if="clipTimeEditorPlaybackLoading"
                          class="clip-time-editor-preview-loader"
                          aria-hidden="true"
                        >
                          <span class="clip-time-editor-preview-spinner">
                            <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--outer"></span>
                            <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--inner"></span>
                          </span>
                          <span class="clip-time-editor-preview-loader-label">{{ tt("common.loading", "Cargando...") }}</span>
                        </div>
                        <div
                          v-if="clipTimeEditorContextEnabled"
                          ref="clipTimeEditorContextOverlay"
                          class="clip-time-editor-context-overlay"
                          :style="clipTimeEditorContextOverlayStyle()"
                          @pointerdown.stop="onClipTimeEditorContextPointerDown"
                        >
                          <span
                            v-for="(line, index) in clipTimeEditorContextPreviewLines"
                            :key="`clip-time-editor-context-preview-line-${index}`"
                            class="clip-time-editor-context-overlay-line"
                          >
                            {{ line }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      ref="clipTimeEditorGenericStage"
                      class="editor-vertical-generic"
                      :class="{ 'editor-vertical-generic--podcast': clipTimeEditorIsPodcastVideoType }"
                    >
                      <canvas
                        ref="clipTimeEditorPreviewCanvas"
                        class="clip-time-editor-preview-canvas"
                        aria-hidden="true"
                      ></canvas>
                      <div
                        v-if="clipTimeEditorPlaybackLoading"
                        class="clip-time-editor-preview-loader"
                        aria-hidden="true"
                      >
                        <span class="clip-time-editor-preview-spinner">
                          <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--outer"></span>
                          <span class="clip-time-editor-preview-spinner-ring clip-time-editor-preview-spinner-ring--inner"></span>
                        </span>
                        <span class="clip-time-editor-preview-loader-label">{{ tt("common.loading", "Cargando...") }}</span>
                      </div>
                    </div>
                      <p class="editor-preview-disclaimer">
                        {{ tt("vodClips.editorRenderVarianceHint", "El render final puede verse un poco distinto a esta vista previa.") }}
                      </p>
                    </div>
                  </div>
                </div>

              <component
                :is="isMobileClipTimeEditorControlsViewport() ? 'body-portal' : 'div'"
                class="clip-time-editor-mobile-control-host editor-stage-controls-host"
              >
              <div class="clip-time-editor-video-controls">
                <button
                  type="button"
                  class="clip-time-editor-control-btn"
                  :class="{ 'clip-time-editor-control-btn--loading': clipTimeEditorPlaybackLoading }"
                  :disabled="clipTimeEditorSaving || clipTimeEditorPlaybackLoading"
                  :aria-label="clipTimeEditorPlaybackLoading
                    ? tt('vodClips.loadingPreview', 'Cargando vista previa')
                    : (clipTimeEditor.isPlaying
                      ? tt('vodClips.pausePreview', 'Pausar vista previa')
                      : tt('vodClips.playPreview', 'Reproducir vista previa'))"
                  @click="toggleClipTimeEditorPlayback"
                >
                  <template v-if="clipTimeEditorPlaybackLoading">
                    <span class="clip-time-editor-control-spinner" aria-hidden="true">
                      <span class="clip-time-editor-control-spinner-ring clip-time-editor-control-spinner-ring--outer"></span>
                      <span class="clip-time-editor-control-spinner-ring clip-time-editor-control-spinner-ring--inner"></span>
                    </span>
                    <span>{{ tt("common.loading", "Cargando...") }}</span>
                  </template>
                  <template v-else>
                    <i :class="clipTimeEditor.isPlaying ? 'ti-control-pause' : 'ti-control-play'"></i>
                    <span>{{ clipTimeEditor.isPlaying ? tt("vodClips.pause", "Pausar") : tt("vodClips.play", "Reproducir") }}</span>
                  </template>
                </button>

                <div class="clip-time-editor-playback-timeline">
                  <input
                    class="clip-time-editor-playback-range"
                    type="range"
                    min="0"
                    :max="clipTimeEditorPlaybackDurationSeconds"
                    step="0.1"
                    :value="clipTimeEditorPlaybackCurrentSeconds"
                    :disabled="clipTimeEditorSaving || clipTimeEditorPlaybackDurationSeconds <= 0"
                    :aria-label="tt('vodClips.previewTimeline', 'Linea de tiempo de la vista previa')"
                    @pointerdown="startClipTimeEditorPlaybackScrub"
                    @pointerup="commitClipTimeEditorPlaybackScrub"
                    @pointercancel="commitClipTimeEditorPlaybackScrub"
                    @input="onClipTimeEditorPlaybackScrub"
                    @change="commitClipTimeEditorPlaybackScrub"
                  />
                  <div class="clip-time-editor-playback-time">
                    <span>{{ formatClipEditorSeconds(clipTimeEditorPlaybackCurrentSeconds) }}</span>
                    <span>{{ formatClipEditorSeconds(clipTimeEditorPlaybackDurationSeconds) }}</span>
                  </div>
                </div>

                <button
                  type="button"
                  class="clip-time-editor-control-btn clip-time-editor-control-btn--mute"
                  :disabled="clipTimeEditorSaving"
                  :aria-pressed="clipTimeEditor.isMuted ? 'true' : 'false'"
                  :aria-label="clipTimeEditor.isMuted ? tt('vodClips.unmutePreview', 'Activar sonido de la vista previa') : tt('vodClips.mutePreview', 'Silenciar vista previa')"
                  @click="toggleClipTimeEditorMute"
                >
                  <i class="ti-volume"></i>
                  <span>{{ clipTimeEditor.isMuted ? tt("vodClips.unmute", "Activar sonido") : tt("vodClips.mute", "Silenciar") }}</span>
                </button>
              </div>
              </component>

                <section class="editor-trim">
              <div class="clip-time-editor-grid">
                <div class="clip-time-editor-stat">
                  <span class="clip-time-editor-stat-label">{{ tt("vodClips.currentRange", "Rango actual") }}</span>
                  <strong class="clip-time-editor-stat-value">
                    {{ formatClipEditorTimelineSeconds(clipTimeEditor.baseStart) }} - {{ formatClipEditorTimelineSeconds(clipTimeEditor.baseEnd) }}
                  </strong>
                </div>
                <div class="clip-time-editor-stat">
                  <span class="clip-time-editor-stat-label">{{ tt("vodClips.newRange", "Nuevo rango") }}</span>
                  <strong class="clip-time-editor-stat-value">
                    {{ formatClipEditorTimelineSeconds(clipTimeEditor.draftStart) }} - {{ formatClipEditorTimelineSeconds(clipTimeEditor.draftEnd) }}
                  </strong>
                </div>
                <div class="clip-time-editor-stat">
                  <span class="clip-time-editor-stat-label">{{ tt("vodClips.duration", "Duración") }}</span>
                  <strong class="clip-time-editor-stat-value">
                    {{ formatClipEditorSeconds(Math.max(0, Number(clipTimeEditor.draftEnd || 0) - Number(clipTimeEditor.draftStart || 0))) }}
                  </strong>
                </div>
              </div>

              <component
                :is="isMobileClipTimeEditorControlsViewport() ? 'body-portal' : 'div'"
                class="clip-time-editor-mobile-control-host"
              >
              <div class="clip-time-editor-timeline">
                <div class="clip-time-editor-timeline-header">
                  <span>{{ formatClipEditorSeconds(0) }}</span>
                  <span>{{ tt("vodClips.currentRange", "Rango actual") }}</span>
                  <span>{{ formatClipEditorSeconds(clipTimeEditorRangeSeconds()) }}</span>
                </div>

                <div
                  class="clip-time-editor-track"
                  ref="clipTimeEditorTrack"
                  :style="clipTimeEditorTrackStyle()"
                  @pointerdown="onClipTimeEditorTrackPointerDown"
                >
                  <div class="clip-time-editor-minor-layer">
                    <div
                      v-for="marker in clipTimeEditorMinorMarkers()"
                      :key="`clip-editor-minor-${marker.time}`"
                      class="clip-time-editor-minor-marker"
                      :style="{ left: marker.percent + '%' }"
                    >
                      <div class="clip-time-editor-minor-marker-line"></div>
                    </div>
                  </div>

                  <div
                    v-for="marker in clipTimeEditorMarkers()"
                    :key="`clip-editor-marker-${marker.time}`"
                    class="clip-time-editor-marker"
                    :style="{ left: marker.percent + '%' }"
                    :title="formatClipEditorSeconds(marker.time)"
                  >
                    <div class="clip-time-editor-marker-line"></div>
                    <div class="clip-time-editor-marker-label">{{ formatClipEditorSeconds(marker.time) }}</div>
                  </div>

                  <div
                    class="clip-time-editor-selection"
                    :style="clipTimeEditorSelectionStyle()"
                    :title="tt('vodClips.dragToMoveSelection', 'Arrastra para mover la selección')"
                    @pointerdown.stop="onClipTimeEditorSelectionPointerDown"
                  >
                    <div
                      class="clip-time-editor-handle clip-time-editor-handle-left"
                      :title="tt('vodClips.adjustStartDrag', 'Ajustar inicio (arrastra)')"
                      @pointerdown.stop="onClipTimeEditorHandlePointerDown('start', $event)"
                    ></div>

                    <div class="clip-time-editor-selection-label">
                      {{ tt("vodClips.selected", "Seleccionado") }}
                    </div>

                    <div
                      class="clip-time-editor-handle clip-time-editor-handle-right"
                      :title="tt('vodClips.adjustEndDrag', 'Ajustar fin (arrastra)')"
                      @pointerdown.stop="onClipTimeEditorHandlePointerDown('end', $event)"
                    ></div>
                  </div>
                </div>
              </div>
              </component>
                </section>
                </div>

                <section v-if="clipTimeEditorIsIrlVideoType" class="editor-context-block">
                <p class="clip-time-editor-context-help">
                  {{ tt("vodClips.contextPreviewHint", "Esta vista previa usa el video de edición para simular el layout IRL vertical. El resultado final puede variar después del re-render.") }}
                </p>
                  <label class="clip-time-editor-context-toggle">
                    <input
                      type="checkbox"
                      :checked="clipTimeEditorContextEnabled"
                      :disabled="clipTimeEditorSaving"
                      @change="setClipTimeEditorContextEnabled($event.target.checked)"
                    />
                    <span>{{ tt("vodClips.contextBoxToggleLabel", "Mostrar cuadro de contexto") }}</span>
                  </label>
                  <p
                    v-if="!clipTimeEditorContextEnabled"
                    class="clip-time-editor-context-empty"
                  >
                    {{ tt("vodClips.contextBoxDisabledHint", "El clip IRL se renderizará sin cuadro de contexto.") }}
                  </p>
                  <div v-if="clipTimeEditorContextEnabled" class="clip-time-editor-context-text-group">
                    <label class="clip-time-editor-context-text-label" for="clip-time-editor-context-input">
                      {{ tt("vodClips.contextTextInputLabel", "Texto de contexto") }}
                    </label>
                    <textarea
                      id="clip-time-editor-context-input"
                      ref="clipTimeEditorContextInput"
                      v-model="clipTimeEditor.draftContextText"
                      class="form-control clip-time-editor-context-textarea"
                      :disabled="clipTimeEditorSaving"
                      rows="3"
                      :placeholder="tt('vodClips.contextTextInputPlaceholder', 'Escribe o mejora el contexto que aparecerá en el clip IRL.')"
                    ></textarea>
                    <div class="clip-time-editor-context-emoji-section">
                      <div class="clip-time-editor-context-emoji-header">
                        <span>{{ tt("vodClips.contextCompatibleEmojiTitle", "Emojis compatibles") }}</span>
                        <small>{{ tt("vodClips.contextCompatibleEmojiHint", "Estos símbolos sí se renderizan en el video final.") }}</small>
                      </div>
                      <div class="clip-time-editor-context-emoji-grid">
                        <button
                          v-for="emoji in clipTimeEditorContextCompatibleEmojis"
                          :key="emoji.symbol"
                          type="button"
                          class="clip-time-editor-context-emoji-btn"
                          :disabled="clipTimeEditorSaving"
                          :title="emoji.label"
                          :aria-label="tt('vodClips.insertContextEmoji', 'Insertar emoji') + ' ' + clipTimeEditorContextEmojiLabel(emoji)"
                          @click="insertClipTimeEditorContextEmoji(emoji.symbol)"
                        >
                          {{ emoji.symbol }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-if="clipTimeEditorContextEnabled && !clipTimeEditorContextHasText" class="clip-time-editor-context-empty">
                    {{ tt("vodClips.contextTextMissing", "Este clip no tiene texto de contexto generado por IA.") }}
                  </p>
                  <p v-if="clipTimeEditorContextEnabled" class="clip-time-editor-context-help">
                    {{ tt("vodClips.contextDragHint", "Arrastra la caja para elegir dónde aparecerá el contexto en clips IRL.") }}
                  </p>
                </section>

                <section v-if="canEditEndcardPersonalization" class="clip-time-editor-endcard-panel">
                  <personalization-card
                    ref="clipTimeEditorPersonalizationCard"
                    :is-twitch-user="true"
                    :even-column-split="true"
                    variant="full"
                    logo-preview-mode="endcard"
                  />
                </section>

              <div
                v-if="clipTimeEditor.errorMessage"
                class="schedule-modal-inline-error clip-time-editor-error"
                role="alert"
              >
                <i class="ti-alert-circle"></i>
                {{ clipTimeEditor.errorMessage }}
              </div>
              </div>
            </div>

            <footer class="confirm-modal-footer clip-time-editor-footer schedule-editor-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="clipTimeEditorSaving"
                @click="closeClipTimeEditorModal"
              >
                <i class="ti-close"></i>
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                :class="{ 'is-loading': clipTimeEditorSaving }"
                :disabled="isDemoClip(clipTimeEditor.clip) || clipTimeEditorSaving || !clipTimeEditorHasChanges()"
                @click="saveClipTimeEditor"
              >
                <i v-if="clipTimeEditorSaving" class="ti-reload rotating"></i>
                <i v-else class="ti-check"></i>
                {{ clipTimeEditorSaving ? tt("vodClips.rerenderingVod", "Reencolando...") : tt("vodClips.rerenderVod", "Volver a renderear") }}
              </button>
            </footer>
      </section>
    </div>

    <transition name="modal-fade">
      <body-portal v-if="showClipTranscriptEditorModal">
        <div class="confirm-modal-overlay clip-transcript-modal-overlay" @click.self="closeClipTranscriptEditor">
          <section
            class="confirm-modal clip-transcript-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clip-transcript-modal-title"
          >
            <header class="confirm-modal-header confirm-modal-header--centered">
              <h3 id="clip-transcript-modal-title" class="confirm-modal-title">
                {{ tt("vodClips.correctTranscript", "Corregir transcripción") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ tt("vodClips.transcriptVisibleRange", "Solo se muestran los bloques dentro del rango seleccionado.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                @click="closeClipTranscriptEditor"
              >
                <i class="ti-close"></i>
              </button>
            </header>

            <div class="confirm-modal-body clip-transcript-modal-body">
              <div class="clip-transcript-warning" role="note">
                <i class="ti-alert-circle"></i>
                <div>
                  <strong>{{ tt("vodClips.transcriptCorrectionOnly", "Uso exclusivo para corregir malas interpretaciones, ortografía o censurar.") }}</strong>
                  <p>{{ tt("vodClips.transcriptSyncWarning", "Agregar o eliminar palabras puede desincronizar los subtítulos.") }}</p>
                  <p>
                    {{ tt("vodClips.transcriptCensorHint", "Tres o más asteriscos consecutivos silencian la zona central de esa palabra.") }}
                    <span class="clip-transcript-examples">{{ tt("vodClips.transcriptCensorExamples", "Ejemplos: p***ta, mier****.") }}</span>
                  </p>
                </div>
              </div>

              <clipr-loader
                v-if="clipTranscriptLoading"
                variant="panel"
                class="clipr-loader--compact"
                :title="tt('common.loading', 'Cargando...')"
              />

              <div v-else-if="clipTranscriptError" class="schedule-modal-inline-error" role="alert">
                <i class="ti-alert-circle"></i>
                <span>{{ clipTranscriptError }}</span>
                <button type="button" class="clip-transcript-retry-btn" @click="loadClipTranscript">
                  {{ tt("common.retry", "Reintentar") }}
                </button>
              </div>

              <template v-else>
                <p v-if="isDemoClip(clipTimeEditor.clip)" class="clip-transcript-read-only-note">
                  <i class="ti-lock"></i>
                  {{ tt("common.demoReadOnly", "Demo de solo lectura.") }}
                </p>
                <div v-if="clipTranscriptVisibleBlocks.length" class="clip-transcript-blocks">
                  <label
                    v-for="block in clipTranscriptVisibleBlocks"
                    :key="block.id"
                    class="clip-transcript-block"
                  >
                    <span class="clip-transcript-block-time">
                      {{ formatClipEditorTimelineSeconds(block.start) }} – {{ formatClipEditorTimelineSeconds(block.end) }}
                    </span>
                    <textarea
                      v-model="block.text"
                      class="form-control clip-transcript-textarea"
                      rows="3"
                      :readonly="isDemoClip(clipTimeEditor.clip)"
                      :disabled="clipTimeEditorSaving"
                    ></textarea>
                  </label>
                </div>
                <p v-else class="clip-transcript-empty">
                  {{ tt("vodClips.transcriptNoBlocksInRange", "No hay bloques de transcripción dentro del rango seleccionado.") }}
                </p>
              </template>
            </div>

            <footer class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                @click="closeClipTranscriptEditor"
              >
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                :disabled="clipTranscriptLoading || !!clipTranscriptError || isDemoClip(clipTimeEditor.clip)"
                @click="applyClipTranscriptChanges"
              >
                <i class="ti-check"></i>
                {{ tt("vodClips.applyTranscriptChanges", "Aplicar al editor") }}
              </button>
            </footer>
          </section>
        </div>
      </body-portal>
    </transition>

    <transition name="snackbar-fade">
      <body-portal v-if="clipActionSnackbarVisible">
        <div
          class="schedule-snackbar"
          :class="clipActionSnackbarClass"
        >
          <i :class="clipActionSnackbarIcon"></i>
          <span>{{ clipActionSnackbarMessage }}</span>
        </div>
      </body-portal>
    </transition>
    <body-portal v-if="subtitleStyleModalOpen && isMobileClipTimeEditorControlsViewport()">
      <subtitle-style-modal
        class="clip-time-editor-subtitle-style-modal"
        :visible="true"
        :saving="subtitleStyleSaving"
        :value="clipTimeEditor.draftSubtitleStyle"
        @close="subtitleStyleModalOpen = false"
        @save="saveClipTimeEditorSubtitleStyle"
      />
    </body-portal>
    <subtitle-style-modal
      v-else
      class="clip-time-editor-subtitle-style-modal"
      :visible="subtitleStyleModalOpen"
      :saving="subtitleStyleSaving"
      :value="clipTimeEditor.draftSubtitleStyle"
      @close="subtitleStyleModalOpen = false"
      @save="saveClipTimeEditorSubtitleStyle"
    />

  </div>
</template>

<script>
import { supabase } from "@/supabase";
import {
  buildAuthHeaders,
  getEffectiveUserId,
  isImpersonating,
} from "@/utils/impersonation";
import escapeToCloseMixin from "@/mixins/escapeToCloseMixin";
import ScheduleSubscriptionPromptModal from "@/components/common/ScheduleSubscriptionPromptModal.vue";
import CliprPageLoadOverlay from "@/components/clipr/CliprPageLoadOverlay.vue";
import CliprLoader from "@/components/clipr/CliprLoader.vue";
import SubtitleStyleModal from "@/components/clipr/SubtitleStyleModal.vue";
import LayoutTypeIcon from "@/components/common/LayoutTypeIcon.vue";
import PersonalizationCard from "@/pages/UserProfile/PersonalizationCard.vue";
import { useCurrentUserStore } from "@/stores/currentUser";
import { useSocialProfileStore } from "@/stores/socialProfile";
import { useFacebookPageIdStore } from "@/stores/facebookPageId";
import { syncModalScrollLockState } from "@/utils/modalScrollLock";
import { getApiUrl as resolveApiUrl } from "@/api/client";
import { fetchFacebookPages } from "@/api/social";
import JSZip from "jszip";
import {
  buildTwitchClipPrivateMediaUrl,
  deleteTwitchClip,
  fetchTwitchClipTranscript,
  fetchTwitchClipLibrary,
  fetchTwitchJobClips,
  fetchTwitchJobMediaSyncStatus,
  fetchTwitchJobs,
  fetchTwitchMediaExpiryStatus,
  fetchTwitchPostingTimeline,
  fetchTwitchSchedulePreview,
  requestTwitchClipDownload,
  updateTwitchClip,
} from "@/api/twitch";
import {
  CLIPR_DEMO_COLLECTION_JOB_ID,
  fetchCliprDemoAssets,
  mapDemoAssetsToScheduleCollection,
} from "@/api/clipr";
import {
  CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT,
  CREATE_CLIPS_SIDEBAR_RESET_EVENT,
} from "@/config/navRoutes";
import {
  didScheduleClipMediaRevisionChange,
  didScheduleClipLibraryMediaRevisionChange,
  invalidateScheduleClipLibraryCache,
  readScheduleClipLibraryCache,
  writeScheduleClipLibraryCache,
} from "@/utils/scheduleClipLibraryCache";
import youtubePlatformIcon from "@/assets/img/platform-icons/youtube.svg";
import tiktokPlatformIcon from "@/assets/img/platform-icons/tiktok.svg";
import facebookPlatformIcon from "@/assets/img/platform-icons/facebook.svg";
import instagramPlatformIcon from "@/assets/img/platform-icons/instagram.svg";
import xPlatformIcon from "@/assets/img/platform-icons/x.svg";

const WORKSPACE_PLATFORM_ICON_URLS = {
  youtube: youtubePlatformIcon,
  tiktok: tiktokPlatformIcon,
  facebook: facebookPlatformIcon,
  instagram: instagramPlatformIcon,
  x: xPlatformIcon,
};
const WORKSPACE_PUBLISH_PLATFORMS = ["youtube", "tiktok", "facebook", "instagram", "x"];

const LOCAL_STORAGE_TWITCH_TIMEZONE_KEY = "momo:twitch:timezone";
const CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X = 0.5;
const CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y = 120 / 1920;
const CLIP_TIME_EDITOR_CONTEXT_EPSILON = 0.0005;
const CLIP_TIME_EDITOR_IRL_OUTPUT_WIDTH = 1080;
const CLIP_TIME_EDITOR_IRL_OUTPUT_HEIGHT = 1920;
const CLIP_TIME_EDITOR_CAPTION_OUTPUT_WIDTH = 1080;
const CLIP_TIME_EDITOR_CAPTION_OUTPUT_HEIGHT = 1920;
const CLIP_TIME_EDITOR_CAPTION_FONT_SIZE = 75;
const CLIP_TIME_EDITOR_CAPTION_OUTLINE = 8;
const CLIP_TIME_EDITOR_CAPTION_BACKGROUND_OUTLINE = 20;
const CLIP_TIME_EDITOR_CAPTION_FONT_SIZES = Object.freeze({
  Anton: 78,
  "Archivo Black": 75,
  "Bebas Neue": 82,
  "Bricolage Grotesque": 78,
  "DejaVu Sans": 75,
  "DejaVu Sans Condensed": 75,
  "League Spartan": 78,
  "Liberation Sans": 75,
  Oswald: 82,
});
const CLIP_TIME_EDITOR_CAPTION_FONT_FAMILIES = Object.freeze({
  Anton: "Anton",
  "Archivo Black": "Archivo Black",
  "Bebas Neue": "Bebas Neue",
  "Bricolage Grotesque": "Bricolage Grotesque",
  "DejaVu Sans": "Clipr Subtitle DejaVu Sans",
  "DejaVu Sans Condensed": "Clipr Subtitle DejaVu Sans Condensed",
  "League Spartan": "League Spartan",
  "Liberation Sans": "Clipr Subtitle Liberation Sans",
  Oswald: "Oswald",
});
const CLIP_TIME_EDITOR_GAMEPLAY_CAPTION_Y = 1 - 0.35;
const CLIP_TIME_EDITOR_JUST_CHATTING_CAPTION_Y = 0.78;
const CLIP_TIME_EDITOR_IRL_CAPTION_Y = 1445 / CLIP_TIME_EDITOR_IRL_OUTPUT_HEIGHT;
const CLIP_TIME_EDITOR_IRL_CONTEXT_FONT_SIZE = 56;
const CLIP_TIME_EDITOR_IRL_CONTEXT_MAX_LINE_CHARS = 28;
const CLIP_TIME_EDITOR_IRL_CONTEXT_LINE_SPACING = 12;
const CLIP_TIME_EDITOR_IRL_CONTEXT_BOX_BORDER = 24;
const CLIP_TIME_EDITOR_CONTEXT_COMPATIBLE_EMOJIS = Object.freeze([
  { symbol: "☺", labelKey: "happy", label: "Feliz" },
  { symbol: "☹", labelKey: "sad", label: "Triste" },
  { symbol: "❤", labelKey: "heart", label: "Corazón" },
  { symbol: "♥", labelKey: "classicHeart", label: "Corazón clásico" },
  { symbol: "★", labelKey: "star", label: "Estrella" },
  { symbol: "☆", labelKey: "starOutline", label: "Estrella contorno" },
  { symbol: "✦", labelKey: "sparkle", label: "Destello" },
  { symbol: "✧", labelKey: "sparkleOutline", label: "Destello contorno" },
  { symbol: "☀", labelKey: "sun", label: "Sol" },
  { symbol: "☁", labelKey: "cloud", label: "Nube" },
  { symbol: "☂", labelKey: "rain", label: "Lluvia" },
  { symbol: "☃", labelKey: "snow", label: "Nieve" },
  { symbol: "⚡", labelKey: "lightning", label: "Rayo" },
  { symbol: "☕", labelKey: "coffee", label: "Café" },
  { symbol: "♫", labelKey: "music", label: "Música" },
  { symbol: "♪", labelKey: "musicNote", label: "Nota musical" },
  { symbol: "✓", labelKey: "check", label: "Check" },
  { symbol: "✕", labelKey: "cross", label: "Equis" },
  { symbol: "✈", labelKey: "plane", label: "Avión" },
  { symbol: "⚑", labelKey: "flag", label: "Bandera" },
  { symbol: "⚐", labelKey: "flagOutline", label: "Bandera contorno" },
  { symbol: "☘", labelKey: "clover", label: "Trébol" },
  { symbol: "☾", labelKey: "moon", label: "Luna" },
  { symbol: "☽", labelKey: "moonOutline", label: "Luna contorno" },
  { symbol: "♕", labelKey: "crown", label: "Corona" },
  { symbol: "♛", labelKey: "blackCrown", label: "Corona negra" },
  { symbol: "⚙", labelKey: "gear", label: "Engrane" },
  { symbol: "⚔", labelKey: "swords", label: "Espadas" },
  { symbol: "⚓", labelKey: "anchor", label: "Ancla" },
  { symbol: "⚠", labelKey: "warning", label: "Advertencia" },
]);
const CLIP_TIME_EDITOR_PREVIEW_MAX_DEVICE_PIXEL_RATIO = 2;
const CLIP_TIME_EDITOR_VERTICAL_SOURCE_ASPECT = 9 / 16;
const CLIP_TIME_EDITOR_GAMEPLAY_FACE_SIDE_RATIO = 0.18;
// Tope de clips cuando la biblioteca se pide sin filtro de días (p. ej. rutas con jobId),
// para evitar descargar el historial completo en memoria.
const CLIP_LIBRARY_UNSCOPED_CLIP_LIMIT = 500;

function createDefaultClipTimeEditorPreviewLayout() {
  return {
    version: 1,
    irl: {
      background: { x: 0, y: 0, width: 1, height: 1 },
      foreground: { x: 0, y: 0, width: 1, height: 1 },
    },
    just_chatting: {
      crop: { x: 0, y: 0, width: 1, height: 1 },
    },
    gameplay: {
      face: { x: 0, y: 0, width: 0.18, height: 0.18 },
      gameplay: { x: 0, y: 0, width: 1, height: 1 },
    },
  };
}

export default {
  name: 'ScheduleCollections',
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    minimalClipActions: {
      type: Boolean,
      default: false,
    },
    collectionDetailView: {
      type: Boolean,
      default: false,
    },
    showEarlyJobStatuses: {
      type: Boolean,
      default: false,
    },
    blockOpenUntilRendered: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [escapeToCloseMixin],
  components: {
    ScheduleSubscriptionPromptModal,
    PersonalizationCard,
    LayoutTypeIcon,
    CliprPageLoadOverlay,
    CliprLoader,
    SubtitleStyleModal,
  },
  data() {
    return {
      events: [],
      postingTimelineRawEvents: [],
      postingTimelineUserName: "",
      postingTimelineUserTimezone: "",
      loadingPostingTimeline: false,
      postingTimelineError: "",
      userId: null,
      userRole: null,
      canViewAllUsers: false,
      mediaStatusByJobId: {},
      mediaExpiryByJobId: {},
      jobs: [],
      jobsById: {},
      clips: [],
      clipCollections: [],
      demoCollection: null,
      loadingClips: false,
      loadClipsError: null,
      hasLoadedClipLibrary: false,
      loadedClipLibraryDaysFilter: null,
      demoAssetsLoading: false,
      demoAssetsError: null,
      isInitialPageLoading: true,
      initialPageLoadError: null,
      isScheduleCollectionsDestroyed: false,
      showCollectionProcessingModal: false,
      collectionProcessingModalStatus: null,
      collectionActionsMenuJobId: null,
      showDeleteCollectionModal: false,
      deleteCollectionTarget: null,
      deletingCollectionJobId: null,
      showReleaseStuckCollectionModal: false,
      releaseStuckCollectionTarget: null,
      releasingStuckCollectionJobId: null,
      showReassignCollectionModal: false,
      reassignCollectionTarget: null,
      reassignUsers: [],
      reassignUserNameFilter: "",
      reassignUserScope: "mine",
      reassignUsersLoading: false,
      assigningUserInfoId: null,
      showRerenderCollectionModal: false,
      rerenderCollectionTarget: null,
      rerenderCollectionActionType: "full",
      rerenderingCollectionJobId: null,
      selectedCollectionJobId: null,
      collectionClipOrderByJob: {},
      collectionPage: 1,
      collectionsPerPage: 4,
      selectedClipId: null,
      selectedClipDetails: null,
      isInlineSchedulePanel: false,
      pendingClipEditorRouteClipId: "",
      clipEditorRouteFailedClipId: "",
      loadingClipDetail: false,
      clipPlayback: {}, // Track which clips are actively playing
      videoElements: {}, // Store references to video elements
      clipMutedState: {}, // Track per-clip mute state
      fullscreenClipId: null,
      clipPlaybackSessionId: 0,
      // Modal de programación de clips
      showScheduleModal: false,
      mobileWorkspacePanel: null,
      mobileWorkspacePanelTrigger: null,
      mobileScheduleOpening: false,
      scheduleModalStep: 1,
      scheduleEditingExistingClip: false,
      selectedClipForSchedule: null,
      clipMetadataDraft: null,
      workspaceMetadataDraft: {
        clipId: null,
        title: "",
        description: "",
        hashtags: "",
      },
      workspaceHashtagTags: [],
      workspaceHashtagInput: "",
      workspaceMetadataBaseline: "",
      workspaceMetadataSaving: false,
      metadataPlatforms: ["instagram", "tiktok", "youtube", "x", "facebook"],
      activePlatformTab: "instagram", // Tab activa por defecto
      savingMetadata: false,
      scheduleError: null,
      scheduleFacebookPageIdBaseline: "",
      scheduleFacebookPages: [],
      scheduleFacebookPagesLoading: false,
      scheduleFacebookPagesLoadError: "",
      scheduleFacebookPagesRequestedForCurrentView: false,
      scheduleConnectionsLoading: false,
      scheduleConnectOpening: false,
      scheduleConnectingPlatform: null,
      showScheduleConnectPromptModal: false,
      showScheduleSubscriptionPromptModal: false,
      scheduleSubscriptionHasActivePlan: false,
      scheduleConnectedPlatforms: {
        instagram: false,
        tiktok: false,
        youtube: false,
        facebook: false,
        x: false,
      },
      // Modal de scheduling para publicación
      showPostingScheduleModal: false,
      pendingScheduleData: null, // { date, clipId, label }
      postingAssigning: false,
      postingAssignError: null,
      postingSelectedPostingHour: null,
      postingSelectedPostingMinute: null,
      postingSelectedPostingPeriod: "AM",
      postingSelectedTimezone: "",
      postingTimezonePreviewTimes: {},
      postingSelectedPostingHourText: "",
      postingSelectedPostingMinuteText: "",
      postingUserTimezone: "",
      postingTimelineStartDate: "",
      // Modal para administrar clips ya programados
      showManageScheduledModal: false,
      selectedScheduledClip: null,
      manageScheduledCancelling: false,
      manageScheduledError: null,
      previewModalOpen: false,
      previewLoading: false,
      previewError: "",
      selectedPreviewEvent: null,
      previewClip: null,
      downloadingClipIds: {},
      downloadingCollectionZips: {},
      selectedCollectionZipClipIds: {},
      deletingClipIds: {},
      rerenderingClipIds: {},
      showClipDeleteModal: false,
      pendingDeleteClip: null,
      deleteClipConfirmStep: 1,
      deleteClipModalError: "",
      showClipTimeEditorModal: false,
      showClipTranscriptEditorModal: false,
      clipTranscriptLoading: false,
      clipTranscriptError: "",
      clipTranscriptBase: null,
      clipTranscriptDraft: null,
      clipTranscriptModalBlocks: [],
      clipTimeEditorSaving: false,
      subtitleStyleModalOpen: false,
      subtitleStyleSaving: false,
      clipTimeEditorSubtitleStyleTouched: false,
      clipTimeEditorPreviewRendererRunning: false,
      clipTimeEditorPreviewRenderRequestId: null,
      clipTimeEditorPreviewRenderRequestType: null,
      clipTimeEditorPreviewCanvasSizeKey: "",
      clipTimeEditorPreviewCanvasSize: null,
      clipTimeEditorPreviewHasFrame: false,
      clipTimeEditorIrlBlurCache: null,
      clipTimeEditorUndoSnapshot: null,
      clipTimeEditorUndoRestoring: false,
      clipTimeEditorMarkerCache: {
        majorKey: "",
        major: [],
        minorKey: "",
        minor: [],
      },
      clipTimeEditorDragFrameRequestId: null,
      clipTimeEditorDragPendingClientX: null,
      clipTimeEditorLayoutDragFrameRequestId: null,
      clipTimeEditorLayoutDragPendingPoint: null,
      clipTimeEditorContextDragFrameRequestId: null,
      clipTimeEditorContextDragPendingPoint: null,
      clipTimeEditorPlaybackLoading: false,
      clipTimeEditorPlaybackLoadingTimer: null,
      clipTimeEditorPlaybackSeekOperationId: 0,
      clipTimeEditorPlaybackSeekReadyCancel: null,
      clipTimeEditorPlaybackScrubFrameRequestId: null,
      clipTimeEditorPlaybackScrubPendingAbsolute: null,
      clipTimeEditorContextCompatibleEmojis: CLIP_TIME_EDITOR_CONTEXT_COMPATIBLE_EMOJIS,
      clipTimeEditor: {
        clip: null,
        privateMediaUrl: "",
        baseStart: 0,
        baseEnd: 0,
        baseVideoType: "auto",
        baseSubtitlesEnabled: true,
        baseSubtitlesEnabled: true,
        editWindowStart: 0,
        editWindowEnd: 0,
        minTime: 0,
        maxTime: 0,
        draftStart: 0,
        draftEnd: 0,
        draftVideoType: "auto",
        draftSubtitlesEnabled: true,
        draftSubtitlesEnabled: true,
        draftSubtitleStyle: {
          font: "DejaVu Sans",
          activeColor: "#FFF302",
          activeEffect: "text_color",
        },
        basePreviewLayout: createDefaultClipTimeEditorPreviewLayout(),
        draftPreviewLayout: createDefaultClipTimeEditorPreviewLayout(),
        baseContextEnabled: false,
        draftContextEnabled: false,
        baseContextText: "",
        draftContextText: "",
        baseContextPosition: {
          x: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          y: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
        },
        draftContextPosition: {
          x: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          y: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
        },
        videoTypeTouched: false,
        previewStart: 0,
        currentAbsolute: 0,
        videoDuration: null,
        isPlaying: false,
        isMuted: false,
        playbackScrubActive: false,
        playbackScrubWasPlaying: false,
        errorMessage: "",
        drag: {
          active: false,
          mode: null, // 'move' | 'start' | 'end'
          startX: 0,
          startDraftStart: 0,
          startDraftEnd: 0,
          trackLeft: 0,
          trackWidth: 0,
          trackRange: 0,
        },
        contextDrag: {
          active: false,
          startClientX: 0,
          startClientY: 0,
          startX: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          startY: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
          stageWidth: 0,
          stageHeight: 0,
          overlayWidth: 0,
          overlayHeight: 0,
        },
        layoutDrag: {
          active: false,
          target: null,
          startClientX: 0,
          startClientY: 0,
          startRect: null,
          videoWidth: 0,
          videoHeight: 0,
          wasPlaying: false,
        },
      },
      isModalScrollLocked: false,
      modalLockWrapper: null,
      modalLockWrapperTransform: "",
      modalLockWrapperWebkitTransform: "",
      modalLockMainPanel: null,
      modalLockMainPanelOverflow: "",
      modalLockMainPanelScrollTop: 0,
      modalLockMainPanelTransform: "",
      modalLockMainPanelWebkitTransform: "",
      modalLockBodyOverflow: "",
      modalLockBodyPosition: "",
      modalLockBodyTop: "",
      modalLockBodyLeft: "",
      modalLockBodyRight: "",
      modalLockBodyWidth: "",
      modalLockBodyScrollY: 0,
      modalLockBodyIsFixed: false,
      clipActionSnackbarVisible: false,
      clipActionSnackbarMessage: "",
      clipActionSnackbarType: "download",
      clipActionSnackbarTimeout: null,
      // Timeline principal (scroll horizontal arrancando en hoy, con 7 días previos)
      timelineStartDate: (() => {
        const today = new Date();
        today.setDate(today.getDate() - 7);
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      })(),
      timelineVisibleMonthIso: "",
      mainTimelineInitialScrollPending: true,
      postingTimelineVisibleMonthIso: "",
      scheduleTimelineLayoutRefreshFrameId: null,
      failedBatchThumbnailJobIds: new Set(),
      // Filtros (período / alcance admin / estado clip / búsqueda por usuario en streams)
      scheduleDaysFilter: 1,
      scheduleDaysFilterOptions: [
        { value: 1, label: this.tt("schedulePost.period1d", "1 día") },
        { value: 3, label: this.tt("schedulePost.period3d", "3 días") },
        { value: 7, label: this.tt("schedulePost.period7d", "7 días") },
      ],
      scheduleClipScope: "mine",
      scheduleClipScopeOptions: [
        { value: "all", label: this.tt("schedulePost.scopeAllUsers", "Todos los usuarios") },
        { value: "mine", label: this.tt("schedulePost.scopeMyClips", "Solo mis clips") },
        { value: "my-users", label: this.tt("schedulePost.scopeMyUsers", "Solo mis usuarios") },
      ],
      scheduleVodStatusFilter: null,
      scheduleVodStatusFilterOptions: [
        { value: null, label: this.tt("vodClips.statusAll", "Todos") },
        { value: "1_queued", label: this.tt("vodClips.statusQueued", "En cola") },
        { value: "2_running", label: this.tt("vodClips.statusDownloading", "Descargando") },
        { value: "5_rendering", label: this.tt("vodClips.statusRendering", "Renderizando") },
        { value: "3_downloaded", label: this.tt("vodClips.statusDownloaded", "Descargado") },
        { value: "4_decided", label: this.tt("vodClips.statusDecided", "Decidido") },
        { value: "6_rendered", label: this.tt("vodClips.statusRendered", "Renderizado") },
        { value: "7_scheduling", label: this.tt("vodClips.statusScheduling", "Programando") },
        { value: "8_published", label: this.tt("vodClips.statusPublished", "Publicado") },
        { value: "expired", label: this.tt("vodClips.statusExpired", "Expirado") },
        { value: "failed", label: this.tt("vodClips.statusFailed", "Fallido") },
        { value: "insufficient_tokens", label: this.tt("vodClips.statusInsufficientTokens", "Tokens insuficientes") },
      ],
      scheduleOwnerNameFilter: "",
      scheduleFilterGateVodCount: 0,
      userHasEverRenderedStream: null,
      pendingScheduleFilterTransitionReload: false,
      suppressNextScheduleDaysFilterReload: false,
      realtimeCollectionsChannel: null,
      realtimeCollectionsSubscriptionKey: "",
      realtimeCollectionsRefreshDebounceTimer: null,
      realtimeCollectionsRefreshQueued: false,
      realtimeCollectionsRefreshInFlight: false,
      realtimeCollectionsLastPayload: null,
      refundClockNow: Date.now(),
      refundClockTimer: null,
    }
  },
  async mounted() {
    this.isScheduleCollectionsDestroyed = false;
    this.refundClockTimer = window.setInterval(() => {
      this.refundClockNow = Date.now();
    }, 1000);
    if (this.embedded && !this.getCollectionJobIdFromRoute()) {
      this.isInitialPageLoading = false;
    }
    try {
      this.refreshPostingTimezonePreviewTimes();
      const { data: { session: initialSession } } = await supabase.auth.getSession();
      if (initialSession?.user) {
        this.userId = getEffectiveUserId(initialSession.user.id);
      }
      this.applyScheduleDaysFilterFromRoute();
      {
        const { minIso, todayIso } = this.getMainTimelineAllowedRange();
        this.timelineStartDate = minIso;
        this.timelineVisibleMonthIso = todayIso;
        this.mainTimelineInitialScrollPending = true;
      }
      let hydratedFromCache = this.hydrateClipLibraryFromCache();
      const userSessionPromise = this.loadUserSession();
      const postingTimezonePromise = this.loadPostingTimezone();
      await userSessionPromise;
      this.fetchSchedulePlatformConnections(initialSession).catch(() => {});
      if (this.isScheduleCollectionsDestroyed) return;
      if (!hydratedFromCache) {
        hydratedFromCache = this.hydrateClipLibraryFromCache();
      }
      if (this.isClipEditorRouteContext()) {
        await this.openClipTimeEditorFromRouteRefresh();
        if (this.isScheduleCollectionsDestroyed) return;
        await this.loadClips({ throwOnError: true, silent: hydratedFromCache });
      } else {
        await this.loadClips({ throwOnError: true, silent: hydratedFromCache });
      }
      if (this.isScheduleCollectionsDestroyed) return;
      if (this.isAdminUser) {
        await this.refreshAdminMediaExpiryStatus();
      }
      this.syncCreateClipsRouteState();
      this.$nextTick(() => {
        this.syncSelectedClipPlaybackSession();
      });
      void postingTimezonePromise;
      void this.loadPostingTimeline();
    } catch (err) {
      this.initialPageLoadError = this.tt(
        "common.refreshPageHint",
        "Something went wrong while loading the page. Please refresh the page."
      );
    } finally {
      this.isInitialPageLoading = false;
    }
    if (this.isScheduleCollectionsDestroyed) return;
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.scheduleTimelineLayoutRefresh);
      window.addEventListener('resize', this.closeMobileWorkspacePanelOnDesktop);
      window.addEventListener('keydown', this.onVideoCardKeydown);
      window.addEventListener(
        CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT,
        this.closeMobileWorkspaceForSidebar
      );
      window.addEventListener(CREATE_CLIPS_SIDEBAR_RESET_EVENT, this.onCreateClipsSidebarReset);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('fullscreenchange', this.onVideoCardFullscreenChange);
    }
    this.scheduleTimelineLayoutRefresh();
    await this.setupRealtimeCollectionsSubscription();
  },
  beforeDestroy() {
    this.isScheduleCollectionsDestroyed = true;
    if (this.refundClockTimer) {
      window.clearInterval(this.refundClockTimer);
      this.refundClockTimer = null;
    }
    this.teardownRealtimeCollectionsSubscription();
    if (
      this.scheduleTimelineLayoutRefreshFrameId != null
      && typeof window !== 'undefined'
      && typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(this.scheduleTimelineLayoutRefreshFrameId);
      this.scheduleTimelineLayoutRefreshFrameId = null;
    }
    this.stopClipTimeEditorDrag();
    this.stopClipTimeEditorLayoutDrag();
    this.stopClipTimeEditorContextDrag();
    this.clearClipTimeEditorPreviewCaches();
    this.cancelClipTimeEditorPlaybackScrubSeek();
    this.pauseClipTimeEditorMainVideo({ render: false });
    this.releaseClipTimeEditorMediaResources();
    this.clearClipTimeEditorPlaybackLoadingState();
    this.stopClipTimeEditorPreviewRenderer();
    this.syncModalScrollLock(false);
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.scheduleTimelineLayoutRefresh);
      window.removeEventListener('resize', this.closeMobileWorkspacePanelOnDesktop);
      window.removeEventListener('keydown', this.onVideoCardKeydown);
      window.removeEventListener(
        CREATE_CLIPS_MOBILE_WORKSPACE_CLOSE_EVENT,
        this.closeMobileWorkspaceForSidebar
      );
      window.removeEventListener(CREATE_CLIPS_SIDEBAR_RESET_EVENT, this.onCreateClipsSidebarReset);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('fullscreenchange', this.onVideoCardFullscreenChange);
    }
    if (this.clipActionSnackbarTimeout) {
      clearTimeout(this.clipActionSnackbarTimeout);
      this.clipActionSnackbarTimeout = null;
    }
    if (this.fullscreenClipId) {
      this.exitClipFullscreen();
      this.fullscreenClipId = null;
    }
  },
  computed: {
    escapeToCloseModals() {
      return [
        { show: "showClipTranscriptEditorModal", close: "closeClipTranscriptEditor" },
        { show: "showCollectionProcessingModal", close: "closeCollectionProcessingModal" },
        { show: "showReassignCollectionModal", close: "closeReassignCollectionModal" },
        { show: "showDeleteCollectionModal", close: "closeDeleteCollectionModal" },
        { show: "showReleaseStuckCollectionModal", close: "closeReleaseStuckCollectionModal" },
        { show: "showRerenderCollectionModal", close: "closeRerenderCollectionModal" },
        { show: "previewModalOpen", close: "closeTimelinePreviewModal" },
        { show: "showScheduleConnectPromptModal", close: "closeScheduleConnectPromptModal" },
        { show: "showScheduleSubscriptionPromptModal", close: "closeScheduleSubscriptionPromptModal" },
        { show: "showScheduleModal", close: "closeScheduleModal" },
        { show: "showPostingScheduleModal", close: "cancelPostingScheduleModal" },
        { show: "showManageScheduledModal", close: "closeManageScheduledModal" },
        { show: "showClipDeleteModal", close: "closeClipDeleteModal" },
        { show: "mobileWorkspacePanel", close: "closeMobileWorkspacePanel" },
      ];
    },
    isAnyModalOpen() {
      return this.showClipTranscriptEditorModal
        || this.showCollectionProcessingModal
        || this.showReassignCollectionModal
        || this.showDeleteCollectionModal
        || this.showReleaseStuckCollectionModal
        || this.showRerenderCollectionModal
        || this.previewModalOpen
        || (this.showScheduleModal && !this.isInlineSchedulePanel)
        || this.showScheduleSubscriptionPromptModal
        || this.showScheduleConnectPromptModal
        || this.showPostingScheduleModal
        || this.showManageScheduledModal
        || this.showClipDeleteModal
        || this.mobileWorkspacePanel !== null;
    },
    isClipTimeEditorVisible() {
      return this.showClipTimeEditorModal && !!this.clipTimeEditor?.clip;
    },
    clipTranscriptVisibleBlocks() {
      const draftStart = Number(this.clipTimeEditor?.draftStart || 0);
      const draftEnd = Number(this.clipTimeEditor?.draftEnd || 0);
      return (this.clipTranscriptModalBlocks || []).filter((block) => (
        Number(block?.end || 0) > draftStart
        && Number(block?.start || 0) < draftEnd
      ));
    },
    isCollectionActionBusy() {
      return this.deletingCollectionJobId != null
        || this.releasingStuckCollectionJobId != null
        || this.rerenderingCollectionJobId != null
        || this.assigningUserInfoId != null;
    },
    isRerenderFailedClipsMode() {
      return this.rerenderCollectionActionType === "failed_clips";
    },
    shouldPromptScheduleSocialConnections() {
      return this.connectedSchedulePlatforms.length === 0;
    },
    connectedSchedulePlatforms() {
      return this.metadataPlatforms.filter((platform) =>
        this.isSchedulePlatformConnected(platform)
      );
    },
    scheduleFacebookPageOptions() {
      const options = [];
      const seenIds = new Set();

      (this.scheduleFacebookPages || []).forEach((page) => {
        const pageId = this.normalizeFacebookPageId(page?.id);
        if (!pageId || seenIds.has(pageId)) return;
        const pageName = String(page?.name || "").trim() || pageId;
        options.push({
          id: pageId,
          name: pageName,
        });
        seenIds.add(pageId);
      });

      const selectedPageId = this.getDraftFacebookPageId();
      if (selectedPageId && !seenIds.has(selectedPageId)) {
        options.unshift({
          id: selectedPageId,
          name: this.tt("schedulePost.facebookPageUnknownName", "Página guardada"),
        });
      }

      return options;
    },
    scheduleModalPreviewThumbnailUrl() {
      const clip = this.selectedClipForSchedule || {};
      const fromClip =
        clip.thumbnailUrl ||
        clip.thumbnail_url ||
        clip.thumbnail ||
        clip.poster_url ||
        "";
      if (fromClip) return fromClip;

      const jobThumb = this.normalizeBatchThumbnailUrl(
        clip.job_thumbnail_url
      );
      if (jobThumb) return jobThumb;

      if (this.selectedCollection) {
        return this.getCollectionThumbnailUrl(this.selectedCollection);
      }
      return "";
    },
    scheduleModalPreviewActivePlatforms() {
      const draftPlatforms = Array.isArray(this.clipMetadataDraft?.platforms)
        ? this.clipMetadataDraft.platforms
        : [];
      return this.metadataPlatforms.filter((platform) =>
        draftPlatforms.includes(platform)
      );
    },
    scheduleModalPreviewTitle() {
      const draftTitle = String(this.clipMetadataDraft?.title || "").trim();
      if (draftTitle) return draftTitle;
      const clip = this.selectedClipForSchedule;
      if (!clip) return this.tt("schedulePost.previewDefaultTitle", "Tu clip está listo para publicarse");
      return clip.clipTitle || this.getClipDisplayName(clip);
    },
    scheduleModalPreviewDescription() {
      return String(this.clipMetadataDraft?.description || "").trim();
    },
    scheduleModalPreviewHashtags() {
      const hashtags = this.parseHashtagList(this.clipMetadataDraft?.hashtags || "");
      return hashtags
        .slice(0, 4)
        .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`));
    },
    scheduleModalPreviewHandle() {
      const sourceUsername = String(this.selectedClipForSchedule?.source_username || "").trim();
      const collectionUsername = String(this.selectedCollection?.sourceUsername || "").trim();
      const baseHandle = sourceUsername || collectionUsername || this.tt("schedulePost.previewDefaultHandle", "tu_canal");
      return baseHandle.startsWith("@") ? baseHandle : `@${baseHandle}`;
    },
    scheduleModalPreviewAvatarLetter() {
      const normalized = String(this.scheduleModalPreviewHandle || "")
        .replace(/^@+/, "")
        .trim();
      return (normalized.charAt(0) || "C").toUpperCase();
    },
    scheduleModalPreviewScheduleLabel() {
      const selectedDate = this.pendingScheduleData?.date;
      if (!selectedDate) {
        return this.tt("schedulePost.previewPickDateTime", "Elige fecha y hora para publicar");
      }
      const dateLabel = this.formatDate(selectedDate);
      if (
        this.postingSelectedPostingHour === null
        || this.postingSelectedPostingMinute === null
      ) {
        return `${dateLabel} · ${this.tt("schedulePost.previewPendingTime", "hora pendiente")}`;
      }
      const hour = String(this.postingSelectedPostingHour).padStart(2, "0");
      const minute = String(this.postingSelectedPostingMinute).padStart(2, "0");
      const timezone = this.postingSelectedTimezone ? ` · ${this.postingSelectedTimezone}` : "";
      return `${dateLabel} · ${hour}:${minute} ${this.postingSelectedPostingPeriod}${timezone}`;
    },
    postingHourOptions() {
      return Array.from({ length: 12 }, (_, index) => index + 1);
    },
    postingMinuteOptions() {
      const baseMinutes = Array.from({ length: 12 }, (_, index) => index * 5);
      const currentMinute = this.postingSelectedPostingMinute;
      if (currentMinute !== null && !baseMinutes.includes(currentMinute)) {
        return [...baseMinutes, currentMinute].sort((a, b) => a - b);
      }
      return baseMinutes;
    },
    filteredCollectionClips() {
      if (!this.selectedCollectionJobId) return [];
      const jobId = this.selectedCollectionJobId;
      const scopedClips = this.getScopedCollectionClips(jobId);
      if (!scopedClips.length) return [];

      const order = this.collectionClipOrderByJob[jobId];
      if (!Array.isArray(order) || order.length === 0) {
        return scopedClips.slice().sort((a, b) => this.compareClipsByScore(a, b));
      }

      const clipsById = new Map(scopedClips.map((clip) => [String(clip.id), clip]));
      const orderedClips = order
        .map((clipId) => clipsById.get(String(clipId)))
        .filter(Boolean);

      if (orderedClips.length === scopedClips.length) {
        return orderedClips;
      }

      const orderedIds = new Set(orderedClips.map((clip) => String(clip.id)));
      const missingClips = scopedClips
        .filter((clip) => !orderedIds.has(String(clip.id)))
        .sort((a, b) => this.compareClipsByScore(a, b));

      return orderedClips.concat(missingClips);
    },
    selectedCollectionZipClips() {
      return this.filteredCollectionClips.filter(
        (clip) => this.isClipDownloadable(clip) && this.isClipSelectedForZip(clip)
      );
    },
    selectedWorkspaceClip() {
      const selectedId = String(this.selectedClipId || "");
      const clips = this.filteredCollectionClips;

      if (selectedId) {
        const selectedInFiltered = clips.find((clip) => String(clip.id) === selectedId);
        if (selectedInFiltered) return selectedInFiltered;

        if (this.selectedCollectionJobId) {
          const selectedInCollection = (this.clips || []).find(
            (clip) =>
              String(clip.id) === selectedId
              && String(clip.job_id) === String(this.selectedCollectionJobId)
              && this.isClipActive(clip)
          );
          if (selectedInCollection) return selectedInCollection;
        }

        return null;
      }

      return clips.length ? clips[0] : null;
    },
    nextWorkspaceClip() {
      const selectedClipId = String(this.selectedWorkspaceClip?.id || "");
      const selectedIndex = this.filteredCollectionClips.findIndex(
        (clip) => String(clip.id) === selectedClipId
      );
      return this.filteredCollectionClips[selectedIndex + 1] || null;
    },
    previousWorkspaceClip() {
      const selectedClipId = String(this.selectedWorkspaceClip?.id || "");
      const selectedIndex = this.filteredCollectionClips.findIndex(
        (clip) => String(clip.id) === selectedClipId
      );
      return selectedIndex > 0 ? this.filteredCollectionClips[selectedIndex - 1] : null;
    },
    selectedWorkspaceClipIndex() {
      const selectedClipId = String(this.selectedWorkspaceClip?.id || "");
      return this.filteredCollectionClips.findIndex(
        (clip) => String(clip.id) === selectedClipId
      ) + 1;
    },
    workspacePublishPlatforms() {
      return WORKSPACE_PUBLISH_PLATFORMS;
    },
    realClipCollections() {
      return Array.isArray(this.clipCollections)
        ? this.clipCollections.filter((collection) => !this.isDemoCollection(collection))
        : [];
    },
    hasRealRenderedVodCollections() {
      return this.realClipCollections.length > 0;
    },
    shouldShowDemoCollection() {
      if (this.isAdminUser) return false;
      return !this.hasRealRenderedVodCollections && !!this.demoCollection;
    },
    visibleClipCollections() {
      const list = [];
      if (this.shouldShowDemoCollection) {
        list.push(this.demoCollection);
      }
      return list.concat(this.realClipCollections);
    },
    filteredClipCollections() {
      const pinnedCollections = this.visibleClipCollections.filter((collection) => {
        return this.isDemoCollection(collection);
      });
      let list = this.visibleClipCollections.filter((collection) => {
        return !this.isDemoCollection(collection);
      });
      if (this.isAdminUser && this.scheduleVodStatusFilter != null) {
        list = list.filter((collection) =>
          this.matchesScheduleVodStatusFilter(collection, this.scheduleVodStatusFilter)
        );
      }
      const q = String(this.scheduleOwnerNameFilter || "").trim().toLowerCase();
      if (!this.canViewAllUsers || !q) return pinnedCollections.concat(list);
      const filteredList = list.filter((c) =>
        String(c.sourceUsername || "")
          .toLowerCase()
          .includes(q)
      );
      return pinnedCollections.concat(filteredList);
    },
    collectionPageCount() {
      return Math.ceil(this.filteredClipCollections.length / this.collectionsPerPage);
    },
    paginatedClipCollections() {
      const start = (this.collectionPage - 1) * this.collectionsPerPage;
      return this.filteredClipCollections.slice(start, start + this.collectionsPerPage);
    },
    filteredReassignUsers() {
      const users = this.scopeFilteredReassignUsers;
      const query = String(this.reassignUserNameFilter || "").trim().toLowerCase();
      if (!query) return users;
      return users.filter((user) =>
        String(user?.username || "").toLowerCase().includes(query) ||
        String(user?.email || "").toLowerCase().includes(query)
      );
    },
    currentAdminUserId() {
      return useCurrentUserStore().currentUserId || "";
    },
    scopeFilteredReassignUsers() {
      const users = this.reassignUsers || [];
      if (this.reassignUserScope !== "mine") return users;
      const currentAdminUserId = this.normalizeReassignValue(this.currentAdminUserId);
      if (!currentAdminUserId) return [];
      return users.filter(
        (user) => this.normalizeReassignValue(user?.assigned_admin) === currentAdminUserId
      );
    },
    selectedCollection() {
      if (!this.selectedCollectionJobId) return null;
      return (this.visibleClipCollections || []).find(
        (collection) => collection.jobId === this.selectedCollectionJobId
      ) || null;
    },
    selectedCollectionTitle() {
      return this.selectedCollection?.displayTitle || "";
    },
    isCollectionDetailActive() {
      return this.collectionDetailView && !!this.selectedCollectionJobId;
    },
    // Días renderizados en la timeline principal usando fechas locales coherentes
    timelineDays() {
      const days = [];
      if (!this.timelineStartDate) return days;

      // Parsear YYYY-MM-DD manualmente para evitar problemas de zona horaria
      const parts = String(this.timelineStartDate).split('-').map(Number);
      if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return days;
      const [year, month, day] = parts;
      const start = new Date(year, month - 1, day);

      const { todayIso } = this.getMainTimelineAllowedRange();
      const renderDays = this.getTimelineRenderDaysCount();
      for (let i = 0; i < renderDays; i += 1) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);

        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const isoDate = `${y}-${m}-${dd}`;

        const weekdayLabel = d
          .toLocaleDateString(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { weekday: 'long' })
          .toUpperCase();
        const dayNumber = d.getDate();

        const eventsForDay = (this.events || [])
          .filter(ev => {
            if (!ev.start) return false;
            const evDate = String(ev.start).slice(0, 10);
            return evDate === isoDate;
          })
          .sort((a, b) => {
            const isPostedA = a.extendedProps?.isPosted ? 1 : 0;
            const isPostedB = b.extendedProps?.isPosted ? 1 : 0;
            if (isPostedA !== isPostedB) {
              return isPostedA - isPostedB;
            }
            // Ordenar por clipId (ascendente)
            const clipIdA = a.extendedProps?.clipId || 0;
            const clipIdB = b.extendedProps?.clipId || 0;
            if (clipIdA !== clipIdB) {
              return clipIdA - clipIdB;
            }
            // Si tienen el mismo clipId, ordenar por hora
            const timeA = a.extendedProps?.postingTime || '';
            const timeB = b.extendedProps?.postingTime || '';
            return timeA.localeCompare(timeB);
          });

        days.push({
          dateObj: d,
          isoDate,
          weekdayLabel,
          dayNumber,
          isToday: isoDate === todayIso,
          events: eventsForDay
        });
      }
      return days;
    },
    timelineMonthLabel() {
      if (this.timelineVisibleMonthIso) {
        const parsed = this.parseIsoDateToLocal(this.timelineVisibleMonthIso);
        if (parsed) {
          return parsed
            .toLocaleDateString(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { month: "long" })
            .toUpperCase();
        }
      }
      if (!this.timelineDays || this.timelineDays.length === 0) return '';
      const first = this.timelineDays[0].dateObj;
      return first
        .toLocaleDateString(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { month: 'long' })
        .toUpperCase();
    },
    postingTimelineDays() {
      const days = [];
      if (!this.postingTimelineStartDate) return days;
      const tzIana = this.getPostingIanaTimezone(this.postingUserTimezone);
      const todayIso = this.getPostingTodayInTimezone();
      const renderDays = this.getPostingTimelineRenderDaysCount();
      const { minIso } = this.getTimelineAllowedRange();
      const safeStartIso = this.postingTimelineStartDate < minIso
        ? minIso
        : this.postingTimelineStartDate;
      for (let i = 0; i < renderDays; i += 1) {
        const isoDate = this.addDaysToIsoDate(safeStartIso, i);
        if (!isoDate) continue;
        const parsedDate = this.parseIsoDateToLocal(isoDate);
        if (!parsedDate) continue;
        const d = new Date(
          parsedDate.getFullYear(),
          parsedDate.getMonth(),
          parsedDate.getDate(),
          12,
          0,
          0
        );

        const weekdayLabel = new Intl.DateTimeFormat(this.$i18n?.locale === "en" ? "en-US" : "es-ES", {
          timeZone: tzIana,
          weekday: "long",
        })
          .format(d)
          .toUpperCase();

        const dayNumber = Number(
          new Intl.DateTimeFormat(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { timeZone: tzIana, day: "numeric" }).format(d)
        );

        const eventsForDay = (this.events || [])
          .filter((ev) => {
            if (!ev.start) return false;
            const evDate = String(ev.start).slice(0, 10);
            return evDate === isoDate;
          })
          .sort((a, b) => {
            const isPostedA = a.extendedProps?.isPosted ? 1 : 0;
            const isPostedB = b.extendedProps?.isPosted ? 1 : 0;
            if (isPostedA !== isPostedB) {
              return isPostedA - isPostedB;
            }
            const timeA = a.extendedProps?.postingTime || "";
            const timeB = b.extendedProps?.postingTime || "";
            return timeA.localeCompare(timeB);
          });

        days.push({
          dateObj: d,
          isoDate,
          weekdayLabel,
          dayNumber,
          isToday: isoDate === todayIso,
          events: eventsForDay,
        });
      }
      return days;
    },
    postingTimelineMonthLabel() {
      if (this.isMobileTimelineMode() && this.postingTimelineVisibleMonthIso) {
        const tzIana = this.getPostingIanaTimezone(this.postingUserTimezone);
        const parsed = this.parseIsoDateToLocal(this.postingTimelineVisibleMonthIso);
        if (parsed) {
          return new Intl.DateTimeFormat(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { timeZone: tzIana, month: "long" })
            .format(parsed)
            .toUpperCase();
        }
      }
      if (!this.postingTimelineDays || this.postingTimelineDays.length === 0) return "";
      const first = this.postingTimelineDays[0].dateObj;
      const tzIana = this.getPostingIanaTimezone(this.postingUserTimezone);
      return new Intl.DateTimeFormat(this.$i18n?.locale === "en" ? "en-US" : "es-ES", { timeZone: tzIana, month: "long" })
        .format(first)
        .toUpperCase();
    },
    isDeletingPendingScheduleClip() {
      return this.isClipDeleting(this.pendingDeleteClip);
    },
    postingTimezoneOptions() {
      return [
        { value: "EST", baseLabel: "EST (Eastern Standard Time)" },
        { value: "PST", baseLabel: "PST (Pacific Standard Time)" },
        { value: "CST", baseLabel: "CST (Central Standard Time)" },
        { value: "MST", baseLabel: "MST (Mountain Standard Time)" },
        { value: "UTC", baseLabel: "UTC" },
      ].map((option) => {
        const previewTime = this.postingTimezonePreviewTimes[option.value];
        return {
          value: option.value,
          label: previewTime ? `${option.baseLabel} - ${previewTime}` : option.baseLabel,
        };
      });
    },
    clipTimeEditorSelectedVideoTypeLabel() {
      const selectedVideoType = this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType);
      const labels = {
        auto: this.tt("vodClips.videoTypeAuto", "Auto"),
        gameplay: this.tt("vodClips.videoTypeGameplay", "Pantalla dividida"),
        just_chatting: this.tt("vodClips.videoTypeJustChatting", "Estándar"),
        irl: this.tt("vodClips.videoTypeIRL", "Video completo"),
        podcast: this.tt("vodClips.videoTypePodcast", "Podcast"),
      };
      return labels[selectedVideoType] || "";
    },
    clipTimeEditorVideoTypeOptions() {
      return [
        {
          value: "just_chatting",
          labelKey: "vodClips.videoTypeJustChatting",
          fallback: "Estándar",
        },
        {
          value: "gameplay",
          labelKey: "vodClips.videoTypeGameplay",
          fallback: "Pantalla dividida",
        },
        {
          value: "podcast",
          labelKey: "vodClips.videoTypePodcast",
          fallback: "Podcast",
        },
        {
          value: "irl",
          labelKey: "vodClips.videoTypeIRL",
          fallback: "Video completo",
        },
      ];
    },
    clipTimeEditorSelectedVideoTypeDescription() {
      const selectedVideoType = this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType);
      const descriptions = {
        gameplay: this.tt("vodClips.videoTypeGameplayDescription", "Esta categoría se usa cuando quieres ver al mismo tiempo tu rostro y el gameplay. Ideal para streams de videojuegos con facecam."),
        just_chatting: this.tt("vodClips.videoTypeJustChattingDescription", "Esta categoría prioriza un recorte vertical centrado en tu rostro para clips de conversación, reacción o storytelling."),
        irl: this.tt("vodClips.videoTypeIRLDescription", "Esta categoría aplica el modo IRL, diseñado para contenido en cámara real con composición dedicada y ajustes específicos de ese formato."),
        podcast: this.tt("vodClips.videoTypePodcastDescription", "Esta categoría está pensada para podcasts o entrevistas y permite layouts multi-rostro con hasta 4 personas visibles en pantalla."),
      };
      return descriptions[selectedVideoType] || "";
    },
    clipTimeEditorIsIrlVideoType() {
      return this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType) === "irl";
    },
    clipTimeEditorIsJustChattingVideoType() {
      return this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType) === "just_chatting";
    },
    clipTimeEditorIsGameplayVideoType() {
      return this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType) === "gameplay";
    },
    clipTimeEditorIsPodcastVideoType() {
      return this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType) === "podcast";
    },
    clipTimeEditorShowsSourceLayoutControls() {
      return this.clipTimeEditorIsJustChattingVideoType || this.clipTimeEditorIsGameplayVideoType;
    },
    clipTimeEditorAllowsContextEditing() {
      return this.clipTimeEditorIsIrlVideoType;
    },
    clipTimeEditorContextEnabled() {
      return this.clipTimeEditorAllowsContextEditing && Boolean(this.clipTimeEditor?.draftContextEnabled);
    },
    clipTimeEditorContextHasText() {
      return this.clipTimeEditorContextEnabled && String(this.clipTimeEditor?.draftContextText || "").trim().length > 0;
    },
    clipTimeEditorContextPreviewText() {
      const text = String(this.clipTimeEditor?.draftContextText || "").trim();
      if (text) return text;
      return this.tt("vodClips.contextTextMissing", "Este clip no tiene texto de contexto generado por IA.");
    },
    clipTimeEditorContextPreviewLines() {
      return this.wrapClipTimeEditorContextLines(
        this.clipTimeEditorContextPreviewText,
        CLIP_TIME_EDITOR_IRL_CONTEXT_MAX_LINE_CHARS
      );
    },
    clipTimeEditorPlaybackDurationSeconds() {
      return Math.max(
        0,
        Number(this.clipTimeEditor?.draftEnd || 0) - Number(this.clipTimeEditor?.draftStart || 0)
      );
    },
    clipTimeEditorPlaybackCurrentSeconds() {
      const draftStart = Number(this.clipTimeEditor?.draftStart || 0);
      const duration = this.clipTimeEditorPlaybackDurationSeconds;
      const currentAbsolute = Number(this.clipTimeEditor?.currentAbsolute || draftStart);
      if (!Number.isFinite(currentAbsolute)) return 0;
      return Math.max(0, Math.min(duration, currentAbsolute - draftStart));
    },
    clipActionSnackbarClass() {
      if (this.clipActionSnackbarType === "delete") {
        return "schedule-snackbar--delete";
      }
      if (this.clipActionSnackbarType === "info") {
        return "schedule-snackbar--info";
      }
      return "schedule-snackbar--download";
    },
    clipActionSnackbarIcon() {
      if (this.clipActionSnackbarType === "delete") {
        return "ti-trash";
      }
      if (this.clipActionSnackbarType === "info") {
        return "ti-info-alt";
      }
      return "ti-download";
    },
    isAdminUser() {
      return String(this.userRole || "").trim().toLowerCase() === "admin";
    },
    canEditEndcardPersonalization() {
      return this.isAdminUser || isImpersonating();
    },
    canUseAdminCollectionActions() {
      return this.isAdminUser && this.canViewAllUsers;
    },
    isScheduleOwnClipsScope() {
      return this.scheduleClipScope === "mine";
    },
    isScheduleAssignedUsersScope() {
      return this.scheduleClipScope === "my-users";
    },
    normalizedScheduleDaysFilter() {
      return this.scheduleDaysFilter && [1, 3, 7].includes(Number(this.scheduleDaysFilter))
        ? Number(this.scheduleDaysFilter)
        : 7;
    },
    nonAdminVodCollectionsCount() {
      return Number(this.scheduleFilterGateVodCount) || 0;
    },
    shouldShowScheduleFilters() {
      if (this.isAdminUser) return true;
      return this.nonAdminVodCollectionsCount > 10;
    },
    shouldShowCreateClipsRouteLoader() {
      if (!this.embedded || !this.isCreateClipsRouteContext()) return false;
      if (!this.getCollectionJobIdFromRoute()) return false;
      if (this.isClipTimeEditorVisible) return false;
      if (this.loadingClips || this.isInitialPageLoading) return true;
      if (this.isClipEditorRouteContext()) {
        return !!this.pendingClipEditorRouteClipId;
      }
      return false;
    },
    hasTimelineEligibleJobs() {
      return (this.clipCollections || []).some((collection) => {
        const status = this.normalizeJobStatus(collection?.jobStatus);
        return status === "7_scheduling" || status === "8_published";
      });
    },
    hasProcessingCollections() {
      return (this.clipCollections || []).some((collection) => this.isJobProcessing(collection?.jobStatus));
    },
    shouldShowMainTimeline() {
      return false;
    },
  },
  watch: {
    "$i18n.locale"() {
      if (!this.hasLoadedClipLibrary || this.loadingClips) return;
      if (this.isAdminUser || this.realClipCollections.length > 0) return;
      void this.reloadDemoCollectionForInterfaceLanguage();
    },
    collectionPageCount(pageCount) {
      if (pageCount > 0 && this.collectionPage > pageCount) {
        this.collectionPage = pageCount;
      }
    },
    selectedCollectionJobId(jobId) {
      if (!jobId) {
        this.closeMobileWorkspacePanel(false);
        return;
      }
      this.syncCollectionClipOrder(jobId);
      this.fetchSchedulePlatformConnections().catch(() => {});
    },
    showScheduleModal(isOpen) {
      if (!this.isMobileWorkspaceViewport()) return;
      if (isOpen) {
        this.mobileScheduleOpening = false;
        this.openMobileWorkspacePanel("schedule");
        return;
      }
      if (this.mobileWorkspacePanel === "schedule") {
        this.closeMobileWorkspacePanel();
      }
    },
    clips: {
      deep: true,
      handler() {
        if (!this.selectedCollectionJobId) return;
        this.syncCollectionClipOrder(this.selectedCollectionJobId);
      },
    },
    selectedWorkspaceClip: {
      immediate: true,
      handler(nextClip, previousClip) {
        const nextId = String(nextClip?.id || "");
        const previousId = String(previousClip?.id || "");
        if (previousId && previousId !== nextId && this.isWorkspaceMetadataDirty()) {
          this.saveWorkspaceClipColumns({ clip: previousClip, silent: true });
        }
        this.hydrateWorkspaceMetadataDraft(nextClip);
        if (!nextId || nextId === previousId) return;
        this.$nextTick(() => {
          if (String(this.selectedWorkspaceClip?.id || "") !== nextId) return;
          this.syncSelectedClipPlaybackSession();
        });
      },
    },
    isClipTimeEditorVisible: {
      immediate: true,
      handler(isVisible) {
        const clip = isVisible ? this.clipTimeEditor?.clip : null;
        this.$emit("clip-editor-visibility-change", {
          isVisible: !!isVisible,
          clipId: clip?.id || null,
        });
      },
    },
    isCollectionDetailActive: {
      immediate: true,
      handler(isActive) {
        this.emitCollectionFocusChange(isActive);
      },
    },
    selectedCollection() {
      if (!this.isCollectionDetailActive) return;
      this.emitCollectionFocusChange(true);
    },
    isAnyModalOpen: {
      immediate: true,
      handler(isOpen) {
        this.syncModalScrollLock(isOpen);
      },
    },
    clips() {
      this.openPendingClipTimeEditorFromRoute();
    },
    '$route.query.jobId'() {
      this.syncCreateClipsRouteState();
    },
    '$route.query.job_id'() {
      this.syncCreateClipsRouteState();
    },
    '$route.query.days'() {
      this.applyScheduleDaysFilterFromRoute({ reload: true });
    },
    '$route.query.scheduleDays'() {
      this.applyScheduleDaysFilterFromRoute({ reload: true });
    },
    '$route.params.jobId'() {
      this.syncCreateClipsRouteState();
    },
    '$route.params.clipId'() {
      this.syncCreateClipsRouteState();
    },
    '$route.path'() {
      this.syncCreateClipsRouteState();
    },
    userId() {
      this.setupRealtimeCollectionsSubscription();
    },
    canViewAllUsers() {
      this.setupRealtimeCollectionsSubscription();
    },
    shouldShowScheduleFilters(next, previous) {
      if (next === previous) return;
      this.setupRealtimeCollectionsSubscription();
      if (this.loadingClips) {
        this.pendingScheduleFilterTransitionReload = true;
        return;
      }
      this.loadClips();
    },
    scheduleDaysFilter(next, previous) {
      if (next === previous) return;
      this.collectionPage = 1;
      const shouldSuppressReload = this.suppressNextScheduleDaysFilterReload;
      this.suppressNextScheduleDaysFilterReload = false;
      if (!this.shouldShowScheduleFilters) return;
      if (shouldSuppressReload) {
        return;
      }
      this.syncScheduleDaysFilterToRoute();
      this.loadClips();
    },
    scheduleClipScope() {
      this.collectionPage = 1;
      if (this.canViewAllUsers && this.shouldShowScheduleFilters) {
        this.loadClips();
      }
      this.setupRealtimeCollectionsSubscription();
    },
    scheduleVodStatusFilter() {
      this.collectionPage = 1;
    },
    shouldShowMainTimeline(isVisible) {
      if (!isVisible) return;
      const { minIso, todayIso } = this.getMainTimelineAllowedRange();
      this.timelineStartDate = minIso;
      this.timelineVisibleMonthIso = todayIso;
      this.mainTimelineInitialScrollPending = true;
      this.$nextTick(() => {
        this.scheduleTimelineLayoutRefresh();
      });
    },
    showPostingScheduleModal(isOpen) {
      if (!isOpen) return;
      this.$nextTick(() => {
        this.recenterTimelineOnMobile("postingTimelineDays");
      });
    },
    scheduleOwnerNameFilter() {
      this.collectionPage = 1;
      this.$nextTick(() => this.ensureSelectedCollectionStillVisible());
    },
    'clipMetadataDraft.platforms'(newPlatforms) {
      // Si la pestaña activa ya no está seleccionada, cambiar a la primera disponible
      if (newPlatforms && newPlatforms.length > 0 && !newPlatforms.includes(this.activePlatformTab)) {
        this.activePlatformTab = newPlatforms[0];
      } else if (!newPlatforms || newPlatforms.length === 0) {
        // Si no hay plataformas seleccionadas, establecer la primera disponible
        this.activePlatformTab = this.metadataPlatforms[0];
      }
      if (this.showScheduleModal && Array.isArray(newPlatforms) && newPlatforms.includes("facebook")) {
        this.ensureScheduleFacebookPagesLoaded();
      }
    },
    'clipTimeEditor.draftVideoType'(next, previous) {
      if (!this.shouldStageClipTimeEditorUndoFromWatcher(next, previous)) return;
      this.stageClipTimeEditorUndoSnapshot({ draftVideoType: previous });
    },
    'clipTimeEditor.draftSubtitlesEnabled'(next, previous) {
      this.$nextTick(() => {
        this.renderClipTimeEditorPreviewFrame();
      });
      if (!this.shouldStageClipTimeEditorUndoFromWatcher(next, previous)) return;
      this.stageClipTimeEditorUndoSnapshot({ draftSubtitlesEnabled: previous });
    },
    'clipTimeEditor.draftContextEnabled'(next, previous) {
      if (!this.shouldStageClipTimeEditorUndoFromWatcher(next, previous)) return;
      this.stageClipTimeEditorUndoSnapshot({ draftContextEnabled: previous });
    },
    'clipTimeEditor.draftContextText'(next, previous) {
      if (!this.shouldStageClipTimeEditorUndoFromWatcher(next, previous)) return;
      this.stageClipTimeEditorUndoSnapshot({ draftContextText: previous });
    },
  },
  methods: {
    closeMobileWorkspaceForSidebar() {
      if (!this.isCreateClipsRouteContext()) return;
      this.closeMobileWorkspacePanel(false);
    },
    onCreateClipsSidebarReset() {
      if (!this.embedded) return;
      if (!this.isCreateClipsRouteContext()) return;
      this.resetEmbeddedCollectionSelection();
    },
    resetEmbeddedCollectionSelection() {
      const hadSelection = Boolean(this.selectedCollectionJobId);
      const hadEditorOpen = this.showClipTimeEditorModal;
      this.closeCollectionActionsMenu();
      this.stopVideoCardMediaPlayback();
      if (hadEditorOpen) {
        this.closeClipTimeEditorModal(true);
      }
      this.selectedCollectionJobId = null;
      this.selectedClipId = null;
      this.selectedClipDetails = null;
      if (hadSelection && this.isCreateClipsRouteContext()) {
        this.syncCollectionSelectionToRoute(null);
      }
    },
    syncModalScrollLock(shouldLock) {
      syncModalScrollLockState(this, shouldLock);
    },
    normalizeScheduleDaysFilterValue(value) {
      const rawValue = Array.isArray(value) ? value[0] : value;
      const days = Number(rawValue);
      return [1, 3, 7].includes(days) ? days : null;
    },
    getRouteScheduleDaysFilter() {
      const query = (this.$route && this.$route.query) || {};
      return this.normalizeScheduleDaysFilterValue(
        query.days != null ? query.days : query.scheduleDays
      );
    },
    getClipLibraryDaysFilter() {
      const routeDays = this.getRouteScheduleDaysFilter();
      if (routeDays) return routeDays;
      if (this.getCollectionJobIdFromRoute()) return null;
      return this.shouldShowScheduleFilters
        ? this.normalizedScheduleDaysFilter
        : null;
    },
    ensureClipLibraryDaysFilterMatchesRoute() {
      if (this.loadingClips || !this.hasLoadedClipLibrary) return;
      const expectedDays = this.getClipLibraryDaysFilter();
      if (this.loadedClipLibraryDaysFilter === expectedDays) return;
      this.loadClips();
    },
    applyScheduleDaysFilterFromRoute({ reload = false } = {}) {
      const routeDays = this.getRouteScheduleDaysFilter();
      if (!routeDays || routeDays === this.scheduleDaysFilter) return false;
      this.suppressNextScheduleDaysFilterReload = true;
      this.scheduleDaysFilter = routeDays;
      if (reload && this.shouldShowScheduleFilters) {
        if (this.loadingClips) {
          this.pendingScheduleFilterTransitionReload = true;
        } else {
          this.loadClips();
        }
      }
      return true;
    },
    syncScheduleDaysFilterToRoute() {
      if (!this.$router || !this.$route) return;
      if (!this.isCreateClipsRouteContext()) return;

      const currentQuery = (this.$route && this.$route.query) || {};
      const nextQuery = { ...currentQuery };
      const nextDays = this.normalizedScheduleDaysFilter;
      delete nextQuery.scheduleDays;
      if (nextDays === 1) {
        delete nextQuery.days;
      } else {
        nextQuery.days = String(nextDays);
      }

      const currentDays = this.normalizeScheduleDaysFilterValue(currentQuery.days);
      const hasLegacyScheduleDays = currentQuery.scheduleDays != null;
      if (!hasLegacyScheduleDays && currentDays === nextDays) return;

      const handleNavigationError = (error) => {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          console.warn("No se pudo sincronizar el filtro de días:", error);
        }
      };
      try {
        const navigation = this.$router.replace({
          path: this.$route.path,
          query: nextQuery,
        });
        if (navigation && typeof navigation.catch === "function") {
          navigation.catch(handleNavigationError);
        }
      } catch (error) {
        handleNavigationError(error);
      }
    },
    getCollectionJobIdFromRoute() {
      const routeParams = (this.$route && this.$route.params) || {};
      const routeJobIdRaw = routeParams.jobId;
      const routeJobId = Array.isArray(routeJobIdRaw) ? routeJobIdRaw[0] : routeJobIdRaw;
      const routeJobIdString = typeof routeJobId === "string" ? routeJobId.trim() : "";
      if (routeJobIdString.length > 0) return routeJobIdString;

      const q = (this.$route && this.$route.query) || {};
      const raw = q.job_id != null ? q.job_id : q.jobId;
      const value = Array.isArray(raw) ? raw[0] : raw;
      const queryJobIdString = typeof value === "string" ? value.trim() : "";
      return queryJobIdString.length > 0 ? queryJobIdString : "";
    },
    async syncCollectionSelectionToRoute(jobId = null) {
      if (!this.$router || !this.$route) return;
      const routePath = String(this.$route.path || "");
      const isCreateClipsRoute = this.$route.name === "make clips" || routePath.startsWith("/create-clips");
      if (!isCreateClipsRoute) return;

      const nextJobIdRaw = typeof jobId === "string" ? jobId.trim() : "";
      const nextJobId = nextJobIdRaw;
      const currentJobId = this.getCollectionJobIdFromRoute();
      const currentQuery = (this.$route && this.$route.query) || {};
      const hasLegacyQueryJobId = currentQuery.jobId != null || currentQuery.job_id != null;
      const isEditorRoute = this.isClipEditorRouteContext();
      if (!hasLegacyQueryJobId && !isEditorRoute && currentJobId === nextJobId) return;

      const nextQuery = { ...currentQuery };
      delete nextQuery.jobId;
      delete nextQuery.job_id;
      const nextPath = nextJobId
        ? `/create-clips/${encodeURIComponent(nextJobId)}`
        : "/create-clips";
      const shouldPush =
        !!nextJobId &&
        currentJobId !== nextJobId;

      try {
        const navigate = shouldPush ? this.$router.push : this.$router.replace;
        await navigate.call(this.$router, {
          path: nextPath,
          query: nextQuery,
        });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          console.warn("No se pudo sincronizar la ruta de collection:", error);
        }
      }
    },
    syncCreateClipsRouteState() {
      this.applyRouteSelectedCollection();
      this.syncClipTimeEditorFromRoute();
      this.ensureClipLibraryDaysFilterMatchesRoute();
    },
    isClipEditorRouteContext() {
      if (!this.isCreateClipsRouteContext()) return false;
      const routeParams = (this.$route && this.$route.params) || {};
      const rawClipId = routeParams.clipId;
      const clipId = Array.isArray(rawClipId) ? rawClipId[0] : rawClipId;
      return typeof clipId === "string" && clipId.trim().length > 0;
    },
    getClipEditorRouteClipId() {
      if (!this.isClipEditorRouteContext()) return "";
      const routeParams = (this.$route && this.$route.params) || {};
      const rawClipId = routeParams.clipId;
      const clipId = Array.isArray(rawClipId) ? rawClipId[0] : rawClipId;
      return typeof clipId === "string" ? clipId.trim() : "";
    },
    getClipById(clipId) {
      const normalizedClipId = String(clipId || "").trim();
      if (!normalizedClipId) return null;
      return (this.clips || []).find((clip) => String(clip?.id || "") === normalizedClipId) || null;
    },
    syncClipTimeEditorFromRoute() {
      if (!this.isCreateClipsRouteContext()) return;
      if (this.isClipEditorRouteContext()) {
        const routeClipId = this.getClipEditorRouteClipId();
        const currentClipId = String(this.clipTimeEditor?.clip?.id || "").trim();
        if (this.showClipTimeEditorModal && currentClipId === routeClipId) return;
        this.openClipTimeEditorFromRoute();
        return;
      }
      this.pendingClipEditorRouteClipId = "";
      this.clipEditorRouteFailedClipId = "";
      if (this.showClipTimeEditorModal) {
        this.closeClipTimeEditorModal(true, {
          syncRoute: false,
        });
      }
    },
    async openClipTimeEditorFromRoute() {
      const clipId = this.getClipEditorRouteClipId();
      if (!clipId) {
        this.pendingClipEditorRouteClipId = "";
        return;
      }
      if (clipId === this.clipEditorRouteFailedClipId) {
        this.pendingClipEditorRouteClipId = "";
        return;
      }
      const clip = this.getClipById(clipId);
      if (!clip) {
        const isRouteLibraryScopeLoaded =
          this.hasLoadedClipLibrary
          && !this.loadingClips
          && this.loadedClipLibraryDaysFilter === this.getClipLibraryDaysFilter();
        if (isRouteLibraryScopeLoaded) {
          // La librería vigente ya cargó y el clip no existe: no dejar el estado pendiente prendido.
          this.pendingClipEditorRouteClipId = "";
          this.clipEditorRouteFailedClipId = clipId;
          this.showClipActionSnackbar(this.tt("schedulePost.clipNotFound", "Clip no encontrado"), "info");
          return;
        }
        this.pendingClipEditorRouteClipId = clipId;
        return;
      }
      const routeJobId = this.getCollectionJobIdFromRoute();
      const clipJobId = String(clip?.job_id ?? clip?.jobId ?? "").trim();
      if (routeJobId && clipJobId && routeJobId !== clipJobId) {
        this.pendingClipEditorRouteClipId = "";
        return;
      }
      if (clipJobId) {
        this.selectedCollectionJobId = clipJobId;
      }
      this.pendingClipEditorRouteClipId = "";
      await this.openClipTimeEditorModal(clip, { syncRoute: false });
      if (this.isScheduleCollectionsDestroyed) return;
      if (!this.isClipTimeEditorVisible) {
        // openClipTimeEditorModal ya mostró el mensaje correspondiente; evita reintentos.
        this.clipEditorRouteFailedClipId = clipId;
      }
    },
    async openClipTimeEditorFromRouteRefresh() {
      if (!this.isClipEditorRouteContext()) return;
      if (this.showClipTimeEditorModal) return;
      const clipId = this.getClipEditorRouteClipId();
      const routeJobId = this.getCollectionJobIdFromRoute();
      if (!clipId || !routeJobId) return;
      this.pendingClipEditorRouteClipId = clipId;
      let jobClips = null;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (this.isScheduleCollectionsDestroyed) return;
        if (!session) return;
        const payload = await fetchTwitchJobClips(session, routeJobId);
        if (this.isScheduleCollectionsDestroyed) return;
        jobClips = Array.isArray(payload?.clips) ? payload.clips : [];
      } catch (error) {
        // Deja el estado pendiente para que loadClips() abra el editor al terminar.
        console.warn("No se pudo precargar el clip del editor desde la ruta:", error);
        return;
      }
      const clip = jobClips.find((row) => String(row?.id || "").trim() === clipId) || null;
      if (!clip || !this.isClipActive(clip)) {
        this.pendingClipEditorRouteClipId = "";
        this.clipEditorRouteFailedClipId = clipId;
        this.showClipActionSnackbar(this.tt("schedulePost.clipNotFound", "Clip no encontrado"), "info");
        return;
      }
      if (!this.isClipStatusVisibleInSchedule(clip)) {
        this.pendingClipEditorRouteClipId = "";
        this.clipEditorRouteFailedClipId = clipId;
        this.showClipActionSnackbar(this.clipEditorUnavailableMessage(clip), "info");
        return;
      }
      this.selectedCollectionJobId = routeJobId;
      this.pendingClipEditorRouteClipId = "";
      await this.openClipTimeEditorModal(clip, { syncRoute: false });
      if (this.isScheduleCollectionsDestroyed) return;
      if (!this.isClipTimeEditorVisible) {
        // openClipTimeEditorModal ya mostró el mensaje correspondiente; evita reintentos.
        this.clipEditorRouteFailedClipId = clipId;
      }
    },
    openPendingClipTimeEditorFromRoute() {
      if (this.showClipTimeEditorModal) return;
      if (!this.isCreateClipsRouteContext()) return;
      if (!this.pendingClipEditorRouteClipId) return;
      if (!this.isClipEditorRouteContext()) {
        this.pendingClipEditorRouteClipId = "";
        return;
      }
      this.openClipTimeEditorFromRoute();
    },
    async syncClipEditorRouteState(isEditorVisible, { replace = false, clipId = "" } = {}) {
      if (!this.$router || !this.$route) return;
      if (!this.isCreateClipsRouteContext()) return;

      const editorClipId = String(
        clipId
        || this.clipTimeEditor?.clip?.id
        || this.selectedClipId
        || ""
      ).trim();
      const editorJobId = String(
        this.clipTimeEditor?.clip?.job_id
        ?? this.clipTimeEditor?.clip?.jobId
        ?? this.selectedCollectionJobId
        ?? this.getCollectionJobIdFromRoute()
        ?? ""
      ).trim();
      if (isEditorVisible && (!editorJobId || !editorClipId)) return;

      const currentQuery = (this.$route && this.$route.query) || {};
      const nextQuery = { ...currentQuery };
      delete nextQuery.jobId;
      delete nextQuery.job_id;

      const nextPath = isEditorVisible
        ? `/create-clips/${encodeURIComponent(editorJobId)}/editor/${encodeURIComponent(editorClipId)}`
        : editorJobId
          ? `/create-clips/${encodeURIComponent(editorJobId)}`
          : "/create-clips";
      const hasLegacyQueryJobId = currentQuery.jobId != null || currentQuery.job_id != null;
      if (!hasLegacyQueryJobId && String(this.$route.path || "") === nextPath) return;

      try {
        const navigate = replace ? this.$router.replace : this.$router.push;
        await navigate.call(this.$router, {
          path: nextPath,
          query: nextQuery,
        });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          console.warn("No se pudo sincronizar la ruta del editor:", error);
        }
      }
    },
    isCreateClipsRouteContext() {
      const routePath = String(this.$route?.path || "");
      return this.$route?.name === "make clips" || routePath.startsWith("/create-clips");
    },
    emitCollectionFocusChange(isActive = this.isCollectionDetailActive) {
      const selectedCollection = isActive
        ? this.selectedCollection
        : null;
      this.$emit("collection-focus-change", {
        isFocused: !!isActive,
        selectedCollection: selectedCollection
          ? {
              jobId: selectedCollection.jobId || null,
              displayTitle: selectedCollection.displayTitle || "",
              sourceUsername: selectedCollection.sourceUsername || "",
            }
          : null,
      });
    },
    tt(key, fallback, params) {
      if (typeof this.$t === "function") {
        return this.$t(key, params || {});
      }
      if (params && fallback) {
        return Object.entries(params).reduce(
          (text, [paramKey, value]) => text.split(`{${paramKey}}`).join(String(value ?? "")),
          fallback
        );
      }
      return fallback;
    },
    async saveClipTimeEditorSubtitleStyle(style) {
      if (this.subtitleStyleSaving) return;
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) {
        this.showClipActionSnackbar(this.tt("vodClips.authRequired", "Se requiere autenticación"), "info");
        return;
      }
      this.subtitleStyleSaving = true;
      try {
        const { data, error } = await supabase
          .from("user_info")
          .update({
            subtitle_font: style.font,
            subtitle_active_color: style.activeColor,
            subtitle_active_effect: style.activeEffect,
          })
          .eq("user_id", userId)
          .select("subtitle_font, subtitle_active_color, subtitle_active_effect")
          .single();
        if (error) throw error;
        this.clipTimeEditor.draftSubtitleStyle = {
          font: data.subtitle_font,
          activeColor: data.subtitle_active_color,
          activeEffect: data.subtitle_active_effect,
        };
        const currentUserStore = useCurrentUserStore();
        if (currentUserStore.profile) {
          currentUserStore.profile.subtitle_font = data.subtitle_font;
          currentUserStore.profile.subtitle_active_color = data.subtitle_active_color;
          currentUserStore.profile.subtitle_active_effect = data.subtitle_active_effect;
        }
        this.clipTimeEditorSubtitleStyleTouched = true;
        this.subtitleStyleModalOpen = false;
      } catch (error) {
        this.showClipActionSnackbar(
          error?.message || this.tt("common.genericError", "Ocurrió un error"),
          "error"
        );
      } finally {
        this.subtitleStyleSaving = false;
      }
    },
    async openClipTimeEditorSubtitleStyleModal() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) {
        this.showClipActionSnackbar(this.tt("vodClips.authRequired", "Se requiere autenticación"), "info");
        return;
      }
      const { data, error } = await supabase
        .from("user_info")
        .select("subtitle_font, subtitle_active_color, subtitle_active_effect")
        .eq("user_id", userId)
        .single();
      if (error) {
        this.showClipActionSnackbar(error.message, "error");
        return;
      }
      this.clipTimeEditor.draftSubtitleStyle = {
        font: data.subtitle_font,
        activeColor: data.subtitle_active_color,
        activeEffect: data.subtitle_active_effect,
      };
      this.subtitleStyleModalOpen = true;
    },
    clearClipTimeEditorPreviewCaches() {
      this.clipTimeEditorPreviewCanvasSizeKey = "";
      this.clipTimeEditorPreviewCanvasSize = null;
      this.clipTimeEditorPreviewHasFrame = false;
      this.clipTimeEditorIrlBlurCache = null;
    },
    releaseClipTimeEditorMediaResources() {
      // Libera buffers del <video> y del canvas del editor para que el navegador
      // pueda reclamar la memoria al cerrar (no basta con pausar).
      const videoRef = this.$refs.clipTimeEditorVideo;
      const video = Array.isArray(videoRef) ? videoRef[0] : videoRef;
      if (video && video.tagName === "VIDEO") {
        try {
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch (_err) {
          // noop
        }
      }
      const canvas = this.resolveClipTimeEditorPreviewCanvasElement();
      if (canvas) {
        try {
          const context = typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
          if (context) {
            context.clearRect(0, 0, canvas.width, canvas.height);
          }
          canvas.width = 0;
          canvas.height = 0;
        } catch (_err) {
          // noop
        }
      }
    },
    resetClipTimeEditorMarkerCache() {
      this.clipTimeEditorMarkerCache = {
        majorKey: "",
        major: [],
        minorKey: "",
        minor: [],
      };
    },
    clearClipTimeEditorUndoSnapshot() {
      this.clipTimeEditorUndoSnapshot = null;
    },
    shouldStageClipTimeEditorUndoFromWatcher(next, previous) {
      return this.isClipTimeEditorVisible
        && !this.clipTimeEditorUndoRestoring
        && next !== previous;
    },
    buildClipTimeEditorUndoSnapshot(overrides = {}) {
      const contextPosition = this.cloneClipTimeEditorContextPosition(
        overrides.draftContextPosition ?? this.clipTimeEditor?.draftContextPosition
      );
      const previewLayout = this.cloneClipTimeEditorPreviewLayout(
        overrides.draftPreviewLayout ?? this.clipTimeEditor?.draftPreviewLayout
      );
      return {
        draftStart: Number(overrides.draftStart ?? this.clipTimeEditor?.draftStart ?? 0),
        draftEnd: Number(overrides.draftEnd ?? this.clipTimeEditor?.draftEnd ?? 0),
        draftVideoType: this.normalizeClipVideoType(
          overrides.draftVideoType ?? this.clipTimeEditor?.draftVideoType
        ),
        draftSubtitlesEnabled: Boolean(
          overrides.draftSubtitlesEnabled ?? this.clipTimeEditor?.draftSubtitlesEnabled
        ),
        draftContextEnabled: Boolean(
          overrides.draftContextEnabled ?? this.clipTimeEditor?.draftContextEnabled
        ),
        draftContextText: String(
          overrides.draftContextText ?? this.clipTimeEditor?.draftContextText ?? ""
        ),
        draftContextPosition: contextPosition,
        draftPreviewLayout: previewLayout,
      };
    },
    stageClipTimeEditorUndoSnapshot(overrides = {}) {
      if (!this.isClipTimeEditorVisible || this.clipTimeEditorSaving) return;
      this.clipTimeEditorUndoSnapshot = this.buildClipTimeEditorUndoSnapshot(overrides);
    },
    applyClipTimeEditorUndoSnapshot(snapshot) {
      if (!snapshot || !this.isClipTimeEditorVisible || this.clipTimeEditorSaving) return false;
      this.clipTimeEditorUndoRestoring = true;
      try {
        this.clipTimeEditor.draftStart = Number(snapshot.draftStart || 0);
        this.clipTimeEditor.draftEnd = Number(snapshot.draftEnd || 0);
        this.clipTimeEditor.draftVideoType = this.normalizeClipVideoType(snapshot.draftVideoType);
        this.clipTimeEditor.draftSubtitlesEnabled = Boolean(snapshot.draftSubtitlesEnabled);
        this.clipTimeEditor.draftContextEnabled = Boolean(snapshot.draftContextEnabled);
        this.clipTimeEditor.draftContextText = String(snapshot.draftContextText || "");
        this.clipTimeEditor.draftContextPosition = this.cloneClipTimeEditorContextPosition(
          snapshot.draftContextPosition
        );
        this.clipTimeEditor.draftPreviewLayout = this.cloneClipTimeEditorPreviewLayout(
          snapshot.draftPreviewLayout
        );
        this.clampClipTimeEditorRange();
      } finally {
        this.clipTimeEditorUndoRestoring = false;
        this.clearClipTimeEditorUndoSnapshot();
      }
      this.clearClipTimeEditorPreviewCaches();
      this.previewClipTimeEditorDraft({ shouldPlay: false });
      return true;
    },
    handleClipTimeEditorUndoKeydown(event) {
      if (!event || !this.isClipTimeEditorVisible) return false;
      const key = String(event.key || "").toLowerCase();
      const isUndoKey = key === "z" && (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey;
      if (!isUndoKey) return false;
      event.preventDefault();
      event.stopPropagation();
      const snapshot = this.clipTimeEditorUndoSnapshot;
      if (!snapshot) return true;
      this.applyClipTimeEditorUndoSnapshot(snapshot);
      return true;
    },
    pruneScheduleCollectionRuntimeCaches(clips = this.clips, collections = this.clipCollections) {
      const clipIds = new Set(
        (Array.isArray(clips) ? clips : [])
          .map((clip) => String(clip?.id || "").trim())
          .filter(Boolean)
      );
      const jobIds = new Set(
        (Array.isArray(collections) ? collections : [])
          .map((collection) => String(collection?.jobId || "").trim())
          .filter(Boolean)
      );
      (Array.isArray(this.jobs) ? this.jobs : []).forEach((job) => {
        const jobId = String(job?.id || "").trim();
        if (jobId) jobIds.add(jobId);
      });

      const activePlaybackId = this.getActiveClipPlaybackId();
      if (activePlaybackId && !clipIds.has(activePlaybackId)) {
        this.stopVideoCardMediaPlayback();
      }

      Object.keys(this.videoElements || {}).forEach((key) => {
        const clipId = String(key || "").replace(/^clip-/, "");
        if (!clipIds.has(clipId)) {
          this.$delete(this.videoElements, key);
        }
      });
      [
        this.clipMutedState,
        this.clipPlayback,
        this.downloadingClipIds,
        this.deletingClipIds,
        this.rerenderingClipIds,
      ].forEach((cache) => {
        Object.keys(cache || {}).forEach((clipId) => {
          if (!clipIds.has(String(clipId))) {
            this.$delete(cache, clipId);
          }
        });
      });
      [this.mediaStatusByJobId, this.mediaExpiryByJobId].forEach((cache) => {
        Object.keys(cache || {}).forEach((jobId) => {
          if (!jobIds.has(String(jobId))) {
            this.$delete(cache, jobId);
          }
        });
      });
      if (this.failedBatchThumbnailJobIds instanceof Set) {
        this.failedBatchThumbnailJobIds = new Set(
          Array.from(this.failedBatchThumbnailJobIds).filter((jobId) => jobIds.has(String(jobId)))
        );
      }
      Object.keys(this.collectionClipOrderByJob || {}).forEach((jobId) => {
        if (!jobIds.has(String(jobId))) {
          this.$delete(this.collectionClipOrderByJob, jobId);
        }
      });
    },
    async resolveScheduleSubscriptionGate(session = null) {
      if (isImpersonating()) {
        this.scheduleSubscriptionHasActivePlan = true;
        return true;
      }
      try {
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(session, { syncCommerce: true, forceCommerce: true });
        const resolvedRole = String(currentUserStore.useRole || "").trim().toLowerCase();
        this.userRole = currentUserStore.useRole || this.userRole;
        this.canViewAllUsers = currentUserStore.hasAdminAccess;
        if (resolvedRole === "admin") {
          this.scheduleSubscriptionHasActivePlan = true;
          return true;
        }
        this.scheduleSubscriptionHasActivePlan = currentUserStore.canSchedulePosts;
        return this.scheduleSubscriptionHasActivePlan;
      } catch (error) {
        console.warn("No se pudo validar la suscripción antes de programar o conectar redes:", error);
        this.scheduleSubscriptionHasActivePlan = false;
        return false;
      }
    },
    openScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = true;
    },
    closeScheduleSubscriptionPromptModal() {
      this.showScheduleSubscriptionPromptModal = false;
      this.scheduleSubscriptionHasActivePlan = false;
    },
    async openSubscriptionFromSchedulePrompt() {
      this.closeScheduleSubscriptionPromptModal();
      if (!this.$router) return;
      try {
        await this.$router.push({ name: "billing" });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          this.$notify({
            message: `${this.tt("common.error", "Error")}: ${message || this.tt("schedulePost.errorOpenSettings", "No se pudo abrir configuración.")}`,
            type: "danger",
          });
        }
      }
    },
    goToScheduleModalStep(step) {
      const parsedStep = Number(step);
      if (!Number.isFinite(parsedStep)) return;
      const boundedStep = Math.min(3, Math.max(1, Math.floor(parsedStep)));
      if (boundedStep === this.scheduleModalStep) return;
      this.scheduleModalStep = boundedStep;
      this.$nextTick(() => {
        const body = this.$el?.querySelector?.(".confirm-modal--schedule-form .schedule-modal-scroll-body");
        if (body) body.scrollTop = 0;
      });
    },
    goToNextScheduleModalStep() {
      if (!this.canGoToNextScheduleModalStep()) return;
      this.goToScheduleModalStep(this.scheduleModalStep + 1);
    },
    canGoToNextScheduleModalStep() {
      if (this.scheduleModalStep !== 1) return true;
      const selectedPlatforms = Array.isArray(this.clipMetadataDraft?.platforms)
        ? this.clipMetadataDraft.platforms
        : [];
      return selectedPlatforms.length > 0;
    },
    goToPreviousScheduleModalStep() {
      this.goToScheduleModalStep(this.scheduleModalStep - 1);
    },
    toggleCollectionActionsMenu(jobId) {
      if (!jobId) return;
      this.collectionActionsMenuJobId =
        this.collectionActionsMenuJobId === jobId ? null : jobId;
    },
    closeCollectionActionsMenu() {
      this.collectionActionsMenuJobId = null;
    },
    canReleaseStuckCollection(collection) {
      if (!this.canUseAdminCollectionActions) return false;
      if (!collection || !collection.jobId) return false;
      const normalizedStatus = this.normalizeJobStatus(collection.jobStatus);
      return [
        "1_queued",
        "2_running",
        "3_downloaded",
        "4_decided",
        "5_rendering",
      ].includes(normalizedStatus);
    },
    collectionHasDownloadSource(collection) {
      if (!collection) return false;
      const downloadPath = String(collection.downloadPath || "").trim();
      const downloadUrl = String(collection.downloadUrl || "").trim();
      return downloadPath !== "" || downloadUrl !== "";
    },
    collectionHasLocalOutputsReady(collection) {
      if (!collection) return false;
      if (collection.localOutputsReady === true) return true;
      const jobId = String(collection.jobId || "").trim();
      const job = jobId ? this.jobsById?.[jobId] : null;
      return job?.local_outputs_ready === true;
    },
    isCollectionClipUsableStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return normalized === "4_rendered"
        || normalized === "4.3_rendered"
        || normalized === "5_scheduled_to_post"
        || normalized === "posted"
        || normalized === "6_posted"
        || normalized === "7_posted";
    },
    isCollectionClipFailedStatus(status) {
      return this.normalizeClipStatus(status) === "3.1_failed";
    },
    collectionHasUsableClips(collection) {
      if (!collection) return false;
      const directCount = Number(collection.usableClipCount || 0);
      if (directCount > 0) return true;
      const clips = Array.isArray(collection.clips) ? collection.clips : [];
      return clips.some((clip) => this.isCollectionClipUsableStatus(clip?.status));
    },
    collectionFailedClipsCount(collection) {
      if (!collection) return 0;
      const directCount = Number(collection.failedClipCount || 0);
      if (Number.isFinite(directCount) && directCount > 0) return directCount;
      const clips = Array.isArray(collection.clips) ? collection.clips : [];
      return clips.reduce((count, clip) => {
        return this.isCollectionClipFailedStatus(clip?.status) ? count + 1 : count;
      }, 0);
    },
    canFullRerenderCollection(collection) {
      if (!this.canUseAdminCollectionActions) return false;
      if (!collection || !collection.jobId) return false;
      const normalizedStatus = this.normalizeJobStatus(collection.jobStatus);
      if (!this.isJobFailed(normalizedStatus)) return false;
      if (this.collectionHasDownloadSource(collection)) return false;
      if (this.collectionHasUsableClips(collection)) return false;
      return true;
    },
    canRerenderFailedClipsCollection(collection) {
      if (!this.canUseAdminCollectionActions) return false;
      if (!collection || !collection.jobId) return false;
      const normalizedStatus = this.normalizeJobStatus(collection.jobStatus);
      if (normalizedStatus !== "6_rendered") return false;
      if (!this.collectionHasDownloadSource(collection)) return false;
      return this.collectionFailedClipsCount(collection) > 0;
    },
    requestDeleteCollection(collection) {
      if (!collection || !collection.jobId) return;
      if (this.deletingCollectionJobId != null) return;
      this.closeCollectionActionsMenu();
      this.deleteCollectionTarget = collection;
      this.showDeleteCollectionModal = true;
    },
    closeDeleteCollectionModal(force = false) {
      if (this.deletingCollectionJobId != null && !force) return;
      this.showDeleteCollectionModal = false;
      this.deleteCollectionTarget = null;
    },
    deleteVodDisplayUser(collection) {
      if (!collection) return "N/A";
      return collection.sourceUsername || collection.ownerName || collection.ownerUserId || "N/A";
    },
    resolveJobOwnerName(jobLike) {
      const ownerCandidates = [
        jobLike?.user_info?.use_name,
        jobLike?.owner_name,
        jobLike?.use_name,
      ];
      for (const candidate of ownerCandidates) {
        const normalized = String(candidate || "").trim();
        if (normalized) return normalized;
      }
      return "";
    },
    resolveJobTitle(jobLike) {
      return String(jobLike?.source_meta?.title || "").trim();
    },
    getCollectionSubtitleUsername(collection) {
      if (!collection) return "N/A";
      if (this.isDemoCollection(collection)) return "DEMO";
      if (this.isAdminUser) {
        const ownerName = String(collection.ownerName || "").trim();
        if (ownerName) return ownerName;
      }
      return collection.sourceUsername || "N/A";
    },
    async confirmDeleteCollection() {
      if (!this.deleteCollectionTarget || !this.deleteCollectionTarget.jobId) return;
      await this.deleteCollection(this.deleteCollectionTarget);
    },
    async deleteCollection(collection) {
      if (!collection || !collection.jobId) return;
      if (this.deletingCollectionJobId != null) return;

      const jobId = String(collection.jobId || "");
      if (!jobId) return;

      this.deletingCollectionJobId = jobId;
      this.closeCollectionActionsMenu();
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw sessionError || new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }

        const response = await fetch(
          `${this.getApiUrl()}/momo/twitch/jobs/${encodeURIComponent(jobId)}`,
          {
            method: "DELETE",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          }
        );

        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          const errorDetail = String(errData?.detail || "").toLowerCase();
          const isAlreadyGone =
            response.status === 404 &&
            (errorDetail.includes("job not found") || errorDetail.includes("not found"));

          if (!isAlreadyGone) {
            throw new Error(
              errData?.detail
                || response.statusText
                || this.tt("vodClips.deleteVodError", "Error al eliminar el VOD")
            );
          }
        }

        if (String(this.selectedCollectionJobId || "") === jobId) {
          this.selectedCollectionJobId = null;
          this.selectedClipId = null;
          this.selectedClipDetails = null;
          this.syncCollectionSelectionToRoute(null);
        }
        this.$delete(this.mediaStatusByJobId, jobId);
        this.$delete(this.mediaExpiryByJobId, jobId);

        this.closeDeleteCollectionModal(true);
        this.$emit("collection-deleted", { jobId });
        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });

        if (this.$notify) {
          this.$notify({
            message: this.tt("vodClips.vodDeleted", "VOD eliminado."),
            type: "success",
          });
        }
      } catch (error) {
        console.error("Error deleting VOD from schedule:", error);
        if (this.$notify) {
          this.$notify({
            message:
              error?.message
              || this.tt("vodClips.deleteVodRetryError", "Error al eliminar el VOD. Por favor, inténtalo de nuevo."),
            type: "danger",
          });
        }
      } finally {
        this.deletingCollectionJobId = null;
      }
    },
    async handleReassignVod(collection) {
      if (!this.canUseAdminCollectionActions) return;
      if (!collection || !collection.jobId) return;
      this.closeCollectionActionsMenu();
      this.reassignCollectionTarget = collection;
      this.reassignUserNameFilter = "";
      this.reassignUserScope = "mine";
      this.showReassignCollectionModal = true;
      await this.fetchReassignUsers();
    },
    closeReassignCollectionModal(force = false) {
      if (this.assigningUserInfoId != null && !force) return;
      this.showReassignCollectionModal = false;
      this.reassignCollectionTarget = null;
      this.reassignUsers = [];
      this.reassignUserNameFilter = "";
      this.reassignUserScope = "mine";
      this.reassignUsersLoading = false;
      this.assigningUserInfoId = null;
    },
    async fetchReassignUsers() {
      this.reassignUsersLoading = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = session
          ? buildAuthHeaders(session)
          : { "Content-Type": "application/json" };
        const response = await fetch(`${this.getApiUrl()}/momo/get-users`, {
          method: "GET",
          headers,
        });
        if (!response.ok) {
          throw new Error(
            `${this.tt("vodClips.loadUsersError", "No se pudieron cargar usuarios")}: ${response.statusText}`
          );
        }
        const data = await response.json();
        const rawUsers = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
        this.reassignUsers = rawUsers
          .filter((user) => user && user.id != null && user.twitch_user === true)
          .map((user) => {
            const userInfo = Array.isArray(user?.user_info) ? user.user_info[0] : user?.user_info;
            return {
              id: user.id,
              username: user.username || "",
              email: user.email || "",
              user_id: user.user_id || null,
              assigned_admin: String(user?.assigned_admin || userInfo?.assigned_admin || "").trim(),
            };
          })
          .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      } catch (error) {
        console.error("Error loading reassign users from schedule:", error);
        this.reassignUsers = [];
        if (this.$notify) {
          this.$notify({
            message: this.tt("vodClips.loadUsersErrorShort", "No se pudieron cargar los usuarios."),
            type: "danger",
          });
        }
      } finally {
        this.reassignUsersLoading = false;
      }
    },
    normalizeReassignValue(value) {
      return String(value || "").trim().toLowerCase();
    },
    setReassignUserScope(scope) {
      if (scope !== "mine" && scope !== "all") return;
      this.reassignUserScope = scope;
      this.reassignUserNameFilter = "";
    },
    async assignVodToUser(user) {
      if (!this.canUseAdminCollectionActions) return;
      if (!user || user.id == null) return;
      if (!this.reassignCollectionTarget || !this.reassignCollectionTarget.jobId) return;
      if (this.assigningUserInfoId != null) return;

      const jobId = String(this.reassignCollectionTarget.jobId || "");
      if (!jobId) return;
      this.assigningUserInfoId = user.id;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }

        let targetAuthUserId = user.user_id || null;
        if (!targetAuthUserId) {
          const userIdResponse = await fetch(
            `${this.getApiUrl()}/momo/twitch/user-info/user-id?user_info_id=${encodeURIComponent(user.id)}`,
            {
              method: "GET",
              headers: buildAuthHeaders(session, { includeContentType: false }),
            }
          );
          if (!userIdResponse.ok) {
            const errData = await userIdResponse.json().catch(() => ({ detail: userIdResponse.statusText }));
            throw new Error(
              errData?.detail
              || this.tt("vodClips.selectedUserIdNotFound", "No se encontró el user_id del usuario seleccionado.")
            );
          }
          const userIdPayload = await userIdResponse.json().catch(() => ({}));
          targetAuthUserId = userIdPayload?.user_id || null;
        }

        if (!targetAuthUserId) {
          throw new Error(this.tt("vodClips.selectedUserIdNotFound", "No se encontró el user_id del usuario seleccionado."));
        }

        const response = await fetch(
          `${this.getApiUrl()}/momo/twitch/jobs/${encodeURIComponent(jobId)}/reassign`,
          {
            method: "POST",
            headers: buildAuthHeaders(session),
            body: JSON.stringify({ target_user_id: targetAuthUserId }),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(
            errData?.detail
            || this.tt("vodClips.reassignVodError", "No se pudo reasignar el VOD. Inténtalo de nuevo.")
          );
        }

        if (this.$notify) {
          this.$notify({
            message: `${this.tt("vodClips.vodReassignedTo", "VOD reasignado a")} ${user.username || this.tt("vodClips.selectedUser", "usuario seleccionado")}.`,
            type: "success",
          });
        }

        this.closeReassignCollectionModal(true);
        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });
      } catch (error) {
        console.error("Error reassigning VOD from schedule:", error);
        if (this.$notify) {
          this.$notify({
            message:
              error?.message
              || this.tt("vodClips.reassignVodError", "No se pudo reasignar el VOD. Inténtalo de nuevo."),
            type: "danger",
          });
        }
      } finally {
        this.assigningUserInfoId = null;
      }
    },
    requestReleaseStuckCollection(collection) {
      if (!this.canReleaseStuckCollection(collection)) return;
      if (this.releasingStuckCollectionJobId != null) return;
      this.closeCollectionActionsMenu();
      this.releaseStuckCollectionTarget = collection;
      this.showReleaseStuckCollectionModal = true;
    },
    closeReleaseStuckCollectionModal(force = false) {
      if (this.releasingStuckCollectionJobId != null && !force) return;
      this.showReleaseStuckCollectionModal = false;
      this.releaseStuckCollectionTarget = null;
    },
    async confirmReleaseStuckCollection() {
      if (!this.releaseStuckCollectionTarget || !this.releaseStuckCollectionTarget.jobId) return;
      await this.releaseStuckCollection(this.releaseStuckCollectionTarget);
    },
    async releaseStuckCollection(collection) {
      if (!this.canReleaseStuckCollection(collection)) return;
      if (this.releasingStuckCollectionJobId != null) return;

      const jobId = String(collection?.jobId || "");
      if (!jobId) return;
      this.releasingStuckCollectionJobId = jobId;
      this.closeCollectionActionsMenu();

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw sessionError || new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }

        const response = await fetch(
          `${this.getApiUrl()}/momo/twitch/jobs/${encodeURIComponent(jobId)}/release-stuck`,
          {
            method: "POST",
            headers: buildAuthHeaders(session),
          }
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.detail
            || this.tt("vodClips.releaseStuckError", "No se pudo enviar el VOD a error. Inténtalo de nuevo.")
          );
        }

        this.closeReleaseStuckCollectionModal(true);
        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });

        if (this.$notify) {
          const finalStatus = String(payload?.status || "").toLowerCase();
          const message = finalStatus === "6_rendered"
            ? this.tt("vodClips.releaseStuckRenderedSuccess", "VOD liberado. Se conservaron los clips renderizados.")
            : this.tt("vodClips.releaseStuckFailedSuccess", "VOD enviado a error correctamente.");
          this.$notify({
            message,
            type: "success",
          });
        }
      } catch (error) {
        console.error("Error releasing stuck VOD from schedule:", error);
        if (this.$notify) {
          this.$notify({
            message:
              error?.message
              || this.tt("vodClips.releaseStuckError", "No se pudo enviar el VOD a error. Inténtalo de nuevo."),
            type: "danger",
          });
        }
      } finally {
        this.releasingStuckCollectionJobId = null;
      }
    },
    requestFullRerenderCollection(collection) {
      this.requestRerenderCollection(collection, "full");
    },
    requestRerenderFailedClipsCollection(collection) {
      this.requestRerenderCollection(collection, "failed_clips");
    },
    requestRerenderCollection(collection, actionType = "full") {
      const resolvedAction = actionType === "failed_clips" ? "failed_clips" : "full";
      const canProceed = resolvedAction === "failed_clips"
        ? this.canRerenderFailedClipsCollection(collection)
        : this.canFullRerenderCollection(collection);
      if (!canProceed) return;
      if (this.rerenderingCollectionJobId != null) return;
      this.closeCollectionActionsMenu();
      this.rerenderCollectionTarget = collection;
      this.rerenderCollectionActionType = resolvedAction;
      this.showRerenderCollectionModal = true;
    },
    closeRerenderCollectionModal(force = false) {
      if (this.rerenderingCollectionJobId != null && !force) return;
      this.showRerenderCollectionModal = false;
      this.rerenderCollectionTarget = null;
      this.rerenderCollectionActionType = "full";
    },
    async confirmRerenderCollection() {
      if (!this.rerenderCollectionTarget || !this.rerenderCollectionTarget.jobId) return;
      await this.rerenderCollection(this.rerenderCollectionTarget);
    },
    async rerenderCollection(collection) {
      const rerenderActionType = this.rerenderCollectionActionType === "failed_clips"
        ? "failed_clips"
        : "full";
      if (rerenderActionType === "failed_clips") {
        if (!this.canRerenderFailedClipsCollection(collection)) return;
      } else if (!this.canFullRerenderCollection(collection)) {
        return;
      }
      if (this.rerenderingCollectionJobId != null) return;

      const jobId = String(collection?.jobId || "");
      if (!jobId) return;
      this.rerenderingCollectionJobId = jobId;
      this.closeCollectionActionsMenu();

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw sessionError || new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }

        const endpointPath = rerenderActionType === "failed_clips"
          ? "rerender-failed-clips"
          : "rerender";
        const response = await fetch(
          `${this.getApiUrl()}/momo/twitch/jobs/${encodeURIComponent(jobId)}/${endpointPath}`,
          {
            method: "POST",
            headers: buildAuthHeaders(session),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(
            errData?.detail
            || (
              rerenderActionType === "failed_clips"
                ? this.tt("vodClips.rerenderFailedClipsError", "No se pudieron re-renderizar los clips fallidos. Inténtalo de nuevo.")
                : this.tt("vodClips.rerenderVodError", "No se pudo volver a descargar y renderear el VOD. Inténtalo de nuevo.")
            )
          );
        }

        this.closeRerenderCollectionModal(true);
        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });

        if (this.$notify) {
          this.$notify({
            message:
              rerenderActionType === "failed_clips"
                ? this.tt("vodClips.rerenderFailedClipsSuccess", "Clips fallidos reencolados correctamente.")
                : this.tt("vodClips.rerenderVodSuccess", "El VOD se volvió a encolar correctamente."),
            type: "success",
          });
        }
      } catch (error) {
        console.error("Error rerendering VOD from schedule:", error);
        if (this.$notify) {
          this.$notify({
            message:
              error?.message
              || (
                rerenderActionType === "failed_clips"
                  ? this.tt("vodClips.rerenderFailedClipsError", "No se pudieron re-renderizar los clips fallidos. Inténtalo de nuevo.")
                  : this.tt("vodClips.rerenderVodError", "No se pudo volver a descargar y renderear el VOD. Inténtalo de nuevo.")
              ),
            type: "danger",
          });
        }
      } finally {
        this.rerenderingCollectionJobId = null;
      }
    },
    reloadCurrentPage() {
      if (typeof window !== "undefined" && window.location) {
        window.location.reload();
      }
    },
    getApiUrl() {
      return resolveApiUrl();
    },
    async fetchAdminPostTimelineForSchedule(existingSession = null) {
      const session = existingSession || (await supabase.auth.getSession())?.data?.session;
      console.log("[ScheduleAutoSuggest] fetchAdminPostTimelineForSchedule:start", {
        hasSession: Boolean(session),
        sessionUserId: session?.user?.id || null,
        effectiveUserId: this.userId || null,
        isAdminUser: this.isAdminUser,
        isImpersonating: isImpersonating(),
      });
      if (!session) {
        throw new Error(
          this.tt("schedulePost.errorLoginToLoadTimeline", "Debes iniciar sesión para consultar la timeline.")
        );
      }
      if (!session?.access_token) {
        throw new Error(
          this.tt("schedulePost.errorLoginToLoadTimeline", "Debes iniciar sesión para consultar la timeline.")
        );
      }

      // Este endpoint es admin-only; en impersonación debemos usar el token real de sesión.
      const adminTimelineHeaders = {
        Authorization: `Bearer ${session.access_token}`,
      };

      const response = await fetch(`${this.getApiUrl()}/momo/twitch/post-admin-timeline`, {
        method: "GET",
        headers: adminTimelineHeaders,
      });
      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        console.warn("[ScheduleAutoSuggest] fetchAdminPostTimelineForSchedule:error", {
          status: response.status,
          statusText: response.statusText,
          detail: detail || null,
        });
        throw new Error(
          detail || this.tt("schedulePost.errorLoadAdminTimeline", "No fue posible cargar la timeline para autoprogramar.")
        );
      }

      const payload = await response.json();
      const users = Array.isArray(payload?.users) ? payload.users : [];
      console.log("[ScheduleAutoSuggest] fetchAdminPostTimelineForSchedule:success", {
        usersCount: users.length,
        firstUserIds: users
          .slice(0, 8)
          .map((user) => String(user?.user_id || "").trim())
          .filter(Boolean),
      });
      return payload;
    },
    getEmptyScheduleConnectionsState() {
      return {
        instagram: false,
        tiktok: false,
        youtube: false,
        facebook: false,
        x: false,
      };
    },
    isSchedulePlatformConnected(platform) {
      const key = String(platform || "").trim().toLowerCase();
      return Boolean(this.scheduleConnectedPlatforms?.[key]);
    },
    getConnectedSchedulePlatforms() {
      return this.connectedSchedulePlatforms;
    },
    normalizeSocialPlatformKey(platformKey) {
      const normalized = String(platformKey || "").trim().toLowerCase();
      return normalized === "twitter" ? "x" : normalized;
    },
    syncDraftPlatformsWithConnections() {
      if (!this.clipMetadataDraft) return;
      const draftPlatforms = Array.isArray(this.clipMetadataDraft.platforms)
        ? this.clipMetadataDraft.platforms
        : [];
      const connectedSet = new Set(this.getConnectedSchedulePlatforms());
      const nextPlatforms = this.metadataPlatforms.filter(
        (platform) => connectedSet.has(platform) && draftPlatforms.includes(platform)
      );
      const changed =
        nextPlatforms.length !== draftPlatforms.length
        || nextPlatforms.some((platform, index) => platform !== draftPlatforms[index]);
      if (changed) {
        this.clipMetadataDraft.platforms = nextPlatforms;
      }
      if (!nextPlatforms.includes(this.activePlatformTab)) {
        this.activePlatformTab = nextPlatforms[0] || this.metadataPlatforms[0];
      }
    },
    async fetchSchedulePlatformConnections(existingSession = null) {
      const session = existingSession || (await supabase.auth.getSession())?.data?.session;
      if (!session) {
        this.scheduleConnectedPlatforms = this.getEmptyScheduleConnectionsState();
        return;
      }

      this.scheduleConnectionsLoading = true;
      try {
        const socialProfileStore = useSocialProfileStore();
        await socialProfileStore.fetchProfile(session);
        const usernamesByPlatform = socialProfileStore.usernamesByPlatform || {};

        const nextConnections = this.getEmptyScheduleConnectionsState();
        this.metadataPlatforms.forEach((platform) => {
          nextConnections[platform] = Boolean(
            String(usernamesByPlatform[platform] || "").trim()
          );
        });
        this.scheduleConnectedPlatforms = nextConnections;
        if (this.clipMetadataDraft) {
          this.syncDraftPlatformsWithConnections();
        }
      } catch (error) {
        console.error("Error fetching schedule platform connections:", error);
        this.scheduleConnectedPlatforms = this.getEmptyScheduleConnectionsState();
      } finally {
        this.scheduleConnectionsLoading = false;
      }
    },
    async syncCurrentUserContext(session = null) {
      const currentUserStore = useCurrentUserStore();
      await currentUserStore.ensureSessionReady(session);
      this.userRole = currentUserStore.useRole;
      this.canViewAllUsers = currentUserStore.hasAdminAccess;
      const resolvedRole = String(currentUserStore.useRole || "").trim().toLowerCase();
      this.scheduleSubscriptionHasActivePlan =
        isImpersonating()
        || resolvedRole === "admin"
        || currentUserStore.canSchedulePosts === true;
      return currentUserStore;
    },
    applyRouteSelectedCollection() {
      const routeJobId = this.getCollectionJobIdFromRoute();
      if (!routeJobId) {
        if (this.selectedCollectionJobId) {
          this.selectedCollectionJobId = null;
          this.selectedClipId = null;
          this.selectedClipDetails = null;
        }
        return;
      }
      if (!this.visibleClipCollections.some((collection) => String(collection.jobId) === routeJobId)) {
        return;
      }
      const routeCollection = this.visibleClipCollections.find(
        (collection) => String(collection.jobId) === routeJobId
      ) || null;
      const routeJobStatus = this.normalizeJobStatus(routeCollection?.jobStatus);
      if (
        (
          this.isCollectionReadyToOpen(routeJobStatus)
          || this.collectionHasLocalOutputsReady(routeCollection)
        )
        && !this.loadingClips
        && !this.realtimeCollectionsRefreshInFlight
      ) {
        void this.loadClips({ silent: true, priorityJobId: routeJobId });
      }
      this.selectedCollectionJobId = routeJobId;
      const routeClipId = this.getClipEditorRouteClipId();
      if (routeClipId) {
        this.selectedClipId = routeClipId;
        this.selectedClipDetails = null;
        return;
      }
      this.selectedClipId = null;
      this.selectedClipDetails = null;
    },
    normalizeFacebookPageId(value) {
      return String(value || "").trim();
    },
    getDraftFacebookPageId() {
      if (!this.clipMetadataDraft?.platform_metadata?.facebook) return "";
      return this.normalizeFacebookPageId(
        this.clipMetadataDraft.platform_metadata.facebook.facebook_page_id
      );
    },
    normalizeFacebookPageOption(rawPage) {
      if (!rawPage || typeof rawPage !== "object") return null;
      const id = this.normalizeFacebookPageId(rawPage.id);
      if (!id) return null;
      const name = String(rawPage.name || "").trim() || id;
      return { id, name };
    },
    async fetchScheduleFacebookPages(session, { force = false } = {}) {
      if (this.scheduleFacebookPagesLoading) return;
      if (this.scheduleFacebookPagesRequestedForCurrentView && !force) return;

      this.scheduleFacebookPagesRequestedForCurrentView = true;
      this.scheduleFacebookPagesLoading = true;
      this.scheduleFacebookPagesLoadError = "";

      try {
        const payload = await fetchFacebookPages(session);
        const rawPages = Array.isArray(payload?.pages) ? payload.pages : [];
        this.scheduleFacebookPages = rawPages
          .map((page) => this.normalizeFacebookPageOption(page))
          .filter((page) => !!page);
      } catch (error) {
        console.error("Error fetching facebook pages for schedule:", error);
        this.scheduleFacebookPages = [];
        this.scheduleFacebookPagesLoadError = String(
          error?.message || this.tt("schedulePost.facebookPagesLoadError", "No se pudieron cargar tus páginas de Facebook.")
        );
      } finally {
        this.scheduleFacebookPagesLoading = false;
      }
    },
    async ensureScheduleFacebookPagesLoaded(existingSession = null) {
      if (!this.clipMetadataDraft?.platforms?.includes("facebook")) return;
      if (this.scheduleFacebookPagesRequestedForCurrentView || this.scheduleFacebookPagesLoading) return;

      const session = existingSession || (await supabase.auth.getSession())?.data?.session;
      if (!session) return;
      await this.fetchScheduleFacebookPages(session);
    },
    async applyDefaultFacebookPageIdToDraft(existingSession = null) {
      if (!this.clipMetadataDraft?.platform_metadata?.facebook) return;
      if (!this.isSchedulePlatformConnected("facebook")) return;

      const currentValue = String(
        this.clipMetadataDraft.platform_metadata.facebook.facebook_page_id || ""
      ).trim();
      if (currentValue) return;

      const session = existingSession || (await supabase.auth.getSession())?.data?.session;
      if (!session) return;

      const facebookPageIdStore = useFacebookPageIdStore();
      const pageId = await facebookPageIdStore.fetchPageId(session);
      if (!this.clipMetadataDraft?.platform_metadata?.facebook) return;
      const latestDraftValue = String(
        this.clipMetadataDraft.platform_metadata.facebook.facebook_page_id || ""
      ).trim();
      if (latestDraftValue) return;
      this.clipMetadataDraft.platform_metadata.facebook.facebook_page_id = pageId;
    },
    async hydrateScheduleFacebookContext(existingSession = null, { loadPages = false } = {}) {
      try {
        await this.applyDefaultFacebookPageIdToDraft(existingSession);
      } catch (error) {
        console.warn("No se pudo resolver el Facebook Page ID para el modal de schedule:", error);
      } finally {
        this.scheduleFacebookPageIdBaseline = this.getDraftFacebookPageId();
      }
      if (!loadPages) return;
      await this.ensureScheduleFacebookPagesLoaded(existingSession);
    },
    isConverterJob(job) {
      if (!job) return false;
      const jobType = String(job?.job_type || job?.jobType || "")
        .trim()
        .toLowerCase();
      return jobType === "converter";
    },
    isConverterClip(clip) {
      if (!clip) return false;
      const relatedJob = this.jobsById?.[clip.job_id] || clip.job || null;
      const jobType = String(
        relatedJob?.job_type
        || relatedJob?.jobType
        || clip?.job_type
        || clip?.jobType
        || ""
      )
        .trim()
        .toLowerCase();
      if (jobType === "converter") return true;
      const reason = String(clip?.reason || "").trim().toLowerCase();
      return reason === "converter";
    },
    isDemoCollection(collection) {
      if (!collection) return false;
      if (collection.isDemo || collection.isReadOnly) return true;
      return String(collection.jobId || "") === CLIPR_DEMO_COLLECTION_JOB_ID;
    },
    isDemoClip(clip) {
      if (!clip) return false;
      if (clip.isDemo || clip.isReadOnly) return true;
      return String(clip.job_id || "") === CLIPR_DEMO_COLLECTION_JOB_ID;
    },
    showDemoReadOnlySnackbar() {
      this.$notify({
        message: this.tt(
          "schedulePost.demoVodActionBlocked",
          "Este es un VOD de prueba, no se puede realizar esta accion"
        ),
        type: "info",
        icon: "ti-info-alt",
      });
    },
    isClipDownloading(clip) {
      const clipKey = clip && clip.id ? String(clip.id) : "";
      return Boolean(clipKey && this.downloadingClipIds[clipKey]);
    },
    isCollectionZipDownloading(collection) {
      const jobId = String(collection?.jobId || "");
      return Boolean(jobId && this.downloadingCollectionZips[jobId]);
    },
    isClipDownloadable(clip) {
      return Boolean(clip?.id && this.isClipPlaybackReadyStatus(clip.status));
    },
    getDownloadableCollectionClips(collection) {
      const jobId = String(collection?.jobId || "");
      if (!jobId) return [];
      return this.getScopedCollectionClips(jobId).filter(
        (clip) => this.isClipDownloadable(clip)
      );
    },
    isClipSelectedForZip(clip) {
      const clipId = String(clip?.id || "");
      return Boolean(clipId && this.selectedCollectionZipClipIds[clipId]);
    },
    toggleClipZipSelection(clip) {
      const clipId = String(clip?.id || "");
      if (!clipId || !this.isClipDownloadable(clip)) return;
      if (this.isClipSelectedForZip(clip)) {
        this.$delete(this.selectedCollectionZipClipIds, clipId);
      } else {
        this.$set(this.selectedCollectionZipClipIds, clipId, true);
      }
    },
    areAllCollectionZipClipsSelected(collection) {
      const clips = this.getDownloadableCollectionClips(collection);
      return clips.length > 0 && clips.every((clip) => this.isClipSelectedForZip(clip));
    },
    toggleAllCollectionZipClips(collection) {
      const clips = this.getDownloadableCollectionClips(collection);
      const shouldSelect = !this.areAllCollectionZipClipsSelected(collection);
      clips.forEach((clip) => {
        const clipId = String(clip.id);
        if (shouldSelect) {
          this.$set(this.selectedCollectionZipClipIds, clipId, true);
        } else {
          this.$delete(this.selectedCollectionZipClipIds, clipId);
        }
      });
    },
    collectionZipDownloadLabel(collection, clips) {
      const progress = this.downloadingCollectionZips[String(collection?.jobId || "")];
      if (progress) {
        return `${this.tt("schedulePost.preparingZip", "Preparando ZIP")} ${progress.completed}/${progress.total}`;
      }
      if (!clips.length) return this.tt("schedulePost.downloadZip", "Descargar ZIP");
      return `${this.tt("schedulePost.download", "Descargar")} (${clips.length})`;
    },
    isClipDeleting(clip) {
      const clipKey = clip && clip.id ? String(clip.id) : "";
      return Boolean(clipKey && this.deletingClipIds[clipKey]);
    },
    normalizeClipStatus(status) {
      return String(status || "").trim().toLowerCase();
    },
    isClipRenderedStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return normalized === "4_rendered" || normalized === "4.3_rendered";
    },
    isClipLocalRenderReady(clip) {
      return Boolean(
        clip?.local_render_ready === true
        && (
          clip?.local_rendered_video_url
          || clip?.localRenderedVideoUrl
          || clip?.playbackUrl
        )
      );
    },
    isClipPlaybackReadyStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return (
        normalized === "4_rendered"
        || normalized === "4.3_rendered"
        || normalized === "5_scheduled_to_post"
        || normalized === "posted"
        || normalized === "6_posted"
        || normalized === "7_posted"
      );
    },
    shouldClipRetainRenderingOverlay(clipId, normalizedClipStatus, previousClip = null) {
      if (this.isClipRerenderFailedStatus(normalizedClipStatus)) return false;
      if (this.isClipIntermediateRenderingStatus(normalizedClipStatus)) return true;
      if (this.isClipMarkedAsRerendering(clipId)) return true;
      if (this.isClipPlaybackReadyStatus(normalizedClipStatus)) return false;
      if (Boolean(previousClip?.isRerendering)) return true;
      return false;
    },
    isClipRendered(clip) {
      return this.isClipRenderedStatus(clip?.status);
    },
    isClipRerenderFailedStatus(status) {
      return this.normalizeClipStatus(status) === "4.4_rerender_failed";
    },
    isClipIntermediateRenderingStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return (
        normalized === "2_accepted"
        || normalized === "3_rendering"
        || normalized === "4.1_rerender_requested"
        || normalized === "4.2_rerendering"
      );
    },
    isClipMarkedAsRerendering(clipId) {
      const clipKey = String(clipId || "").trim();
      return Boolean(clipKey && this.rerenderingClipIds[clipKey]);
    },
    isClipRerendering(clip) {
      if (!clip) return false;
      if (this.isClipReadyForPlaybackDespiteStatusLag(clip)) return false;
      if (clip.isRerendering) return true;
      if (this.isClipMarkedAsRerendering(clip.id)) return true;
      const normalizedStatus = this.normalizeClipStatus(clip.status);
      if (this.isClipRerenderFailedStatus(normalizedStatus)) return false;
      if (this.isClipIntermediateRenderingStatus(normalizedStatus)) return true;
      return false;
    },
    resolveClipJobForPlayback(clip) {
      const jobId = String(clip?.job_id || clip?.jobId || "").trim();
      if (!jobId) return null;
      return clip?.job || this.jobsById?.[jobId] || null;
    },
    isClipJobReadyForPlayback(clip) {
      if (this.isClipLocalRenderReady(clip)) return true;
      const job = this.resolveClipJobForPlayback(clip);
      return this.isCollectionReadyToOpen(this.normalizeJobStatus(job?.status));
    },
    isClipHasPlayableAsset(clip) {
      return Boolean(
        clip?.playbackUrl
        || clip?.rendered_video_url
        || clip?.renderedVideoUrl
        || clip?.local_rendered_video_url
        || clip?.localRenderedVideoUrl
      );
    },
    isClipReadyForPlaybackDespiteStatusLag(clip) {
      if (!clip) return false;
      const normalizedStatus = this.normalizeClipStatus(clip?.status);
      if (
        normalizedStatus === "4.1_rerender_requested"
        || (
          normalizedStatus === "4.2_rerendering"
          && !this.isClipLocalRenderReady(clip)
        )
      ) {
        return false;
      }
      if (!this.isClipJobReadyForPlayback(clip)) return false;
      if (this.isClipPlaybackReadyStatus(clip?.status)) return true;
      return this.isClipHasPlayableAsset(clip);
    },
    isClipRendering(clip) {
      return this.isClipRerendering(clip);
    },
    isClipInteractionLocked(clip) {
      return this.isClipRerendering(clip);
    },
    showClipRerenderingLockedSnackbar() {
      this.showClipActionSnackbar(
        this.tt("vodClips.renderingModalMessage", "Este video se está renderizando."),
        "info"
      );
    },
    syncRerenderingClipIdsWithClips() {
      const activeRerenderingClipIds = new Set();
      (this.clips || []).forEach((clip) => {
        const clipId = String(clip?.id || "").trim();
        if (!clipId) return;
        const normalizedStatus = this.normalizeClipStatus(clip?.status);
        if (
          this.isClipReadyForPlaybackDespiteStatusLag(clip)
          || this.isClipPlaybackReadyStatus(normalizedStatus)
          || this.isClipRerenderFailedStatus(normalizedStatus)
        ) {
          return;
        }
        if (
          this.isClipMarkedAsRerendering(clipId)
          || clip?.isRerendering
          || this.isClipIntermediateRenderingStatus(normalizedStatus)
        ) {
          activeRerenderingClipIds.add(clipId);
        }
      });

      Object.keys(this.rerenderingClipIds || {}).forEach((clipId) => {
        if (!activeRerenderingClipIds.has(clipId)) {
          this.$delete(this.rerenderingClipIds, clipId);
        }
      });

      activeRerenderingClipIds.forEach((clipId) => {
        if (!this.rerenderingClipIds[clipId]) {
          this.$set(this.rerenderingClipIds, clipId, true);
        }
      });
    },
    markClipAsRerendering(clip, { clipStatus = "4.1_rerender_requested" } = {}) {
      if (!clip || !clip.id) return;
      const clipId = String(clip.id);
      const normalizedClipStatus = this.normalizeClipStatus(clipStatus);

      this.$set(this.rerenderingClipIds, clipId, true);
      this.pauseAllVideos();
      this.$delete(this.clipPlayback, clipId);
      this.$delete(this.videoElements, `clip-${clipId}`);
      if (String(this.fullscreenClipId || "") === clipId) {
        this.exitClipFullscreen();
        this.fullscreenClipId = null;
      }

      const clipIndex = (this.clips || []).findIndex((item) => String(item?.id || "") === clipId);
      const preservedPlaybackUrl = clipIndex >= 0
        ? (this.clips[clipIndex]?.playbackUrl || clip?.playbackUrl || null)
        : (clip?.playbackUrl || null);
      const preservedThumbnailUrl = clipIndex >= 0
        ? (this.clips[clipIndex]?.thumbnailUrl || clip?.thumbnailUrl || null)
        : (clip?.thumbnailUrl || null);

      if (clipIndex >= 0) {
        const currentClip = this.clips[clipIndex] || {};
        this.$set(this.clips, clipIndex, {
          ...currentClip,
          ...clip,
          status: normalizedClipStatus,
          playbackUrl: preservedPlaybackUrl,
          thumbnailUrl: preservedThumbnailUrl,
          isRerendering: true,
        });
      } else {
        this.clips = [
          ...(this.clips || []),
          {
            ...clip,
            status: normalizedClipStatus,
            playbackUrl: preservedPlaybackUrl,
            thumbnailUrl: preservedThumbnailUrl,
            isRerendering: true,
          },
        ];
      }

      if (String(this.selectedClipId || "") === clipId) {
        this.selectedClipDetails = null;
      }

      this.syncRerenderingClipIdsWithClips();
    },
    handleClipCardClick(clip) {
      if (!clip || !clip.id) return;
      if (this.mobileWorkspacePanel === "clips") {
        this.closeMobileWorkspacePanel();
      }
      this.selectClip(clip.id);
    },
    selectNextWorkspaceClip() {
      const clip = this.nextWorkspaceClip;
      if (!clip) return;
      this.selectClip(clip.id);
    },
    selectPreviousWorkspaceClip() {
      const clip = this.previousWorkspaceClip;
      if (!clip) return;
      this.selectClip(clip.id);
    },
    isMobileWorkspaceViewport() {
      return window.matchMedia("(max-width: 991px)").matches;
    },
    isMobileClipTimeEditorControlsViewport() {
      return window.matchMedia("(max-width: 767.98px)").matches;
    },
    openMobileWorkspacePanel(panel, event = null) {
      if (!this.isMobileWorkspaceViewport()) return;
      if (event?.currentTarget) {
        this.mobileWorkspacePanelTrigger = event.currentTarget;
      }
      this.mobileWorkspacePanel = panel;
      this.$nextTick(() => {
        const target = panel === "clips"
          ? this.$refs.mobileClipsClose
          : this.$refs.mobileActionsClose;
        if (target) target.focus();
      });
    },
    closeMobileWorkspacePanel(restoreFocus = true) {
      if (this.mobileWorkspacePanel === "schedule" && this.showScheduleModal) {
        this.closeScheduleModal();
        return;
      }
      const trigger = this.mobileWorkspacePanelTrigger;
      this.mobileWorkspacePanel = null;
      this.mobileScheduleOpening = false;
      this.mobileWorkspacePanelTrigger = null;
      if (!restoreFocus || !trigger) return;
      this.$nextTick(() => trigger.focus());
    },
    closeMobileWorkspacePanelOnDesktop() {
      if (window.innerWidth <= 991 || !this.mobileWorkspacePanel) return;
      this.mobileWorkspacePanel = null;
      this.mobileScheduleOpening = false;
      this.mobileWorkspacePanelTrigger = null;
    },
    trapMobileWorkspaceFocus(event) {
      if (!this.mobileWorkspacePanel) return;
      const panel = this.mobileWorkspacePanel === "clips"
        ? this.$refs.workspaceClipsPanel
        : this.$refs.workspaceActionsPanel;
      const focusableElements = Array.from(
        panel.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.getClientRects().length > 0);
      if (focusableElements.length === 0) return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    async openPrimaryWorkspaceAction(clip, event = null) {
      if (!this.isMobileWorkspaceViewport()) {
        await this.handleClipPrimaryAction(clip);
        return;
      }
      if (this.isClipManageable(clip)) {
        this.closeMobileWorkspacePanel(false);
        await this.handleClipPrimaryAction(clip);
        return;
      }
      this.openMobileWorkspacePanel("schedule", event);
      this.mobileScheduleOpening = true;
      try {
        await this.handleClipPrimaryAction(clip);
      } finally {
        this.mobileScheduleOpening = false;
        if (!this.showScheduleModal && this.mobileWorkspacePanel === "schedule") {
          this.closeMobileWorkspacePanel(false);
        }
      }
    },
    showClipActionSnackbar(message, type = "download") {
      if (this.clipActionSnackbarTimeout) clearTimeout(this.clipActionSnackbarTimeout);
      this.clipActionSnackbarMessage = message;
      this.clipActionSnackbarType = type;
      this.clipActionSnackbarVisible = true;
      this.clipActionSnackbarTimeout = setTimeout(() => {
        this.clipActionSnackbarVisible = false;
        this.clipActionSnackbarTimeout = null;
      }, 3500);
    },
    buildClipDownloadFilename(clip, fallback = "clip.mp4") {
      const clipIdPart = clip && clip.id ? String(clip.id).slice(0, 8) : "clip";
      const titleRaw = clip && (clip.clipTitle || clip.title)
        ? String(clip.clipTitle || clip.title).toLowerCase()
        : "clip";
      const normalizedTitle = titleRaw
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9._-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^[-._]+|[-._]+$/g, "");
      const safeTitle = normalizedTitle || "clip";
      const extensionMatch = String(fallback || "").match(/\.[a-z0-9]+$/i);
      const extension = extensionMatch ? extensionMatch[0].toLowerCase() : ".mp4";
      return `${safeTitle}-${clipIdPart}${extension}`;
    },
    async downloadClipAsset(clip) {
      if (this.isDemoClip(clip)) {
        const link = document.createElement("a");
        link.href = clip.rendered_video_url;
        link.download = this.buildClipDownloadFilename(clip, "clip.mp4");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.showClipActionSnackbar(this.tt("publicResults.downloadSuccess", "Clip descargado correctamente"), "download");
        return;
      }
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      const clipId = clip && clip.id ? String(clip.id) : "";
      const jobId = clip && clip.job_id ? String(clip.job_id) : "";
      if (!clipId || !jobId || this.isClipDownloading(clip) || this.isClipDeleting(clip)) return;

      this.$set(this.downloadingClipIds, clipId, true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        }
        const payload = await requestTwitchClipDownload(
          session,
          clipId,
          window.crypto.randomUUID()
        );
        if (!payload.url) {
          throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
        }
        const response = await fetch(payload.url);
        if (!response.ok) {
          throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
        }
        const blobUrl = window.URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = this.buildClipDownloadFilename(clip, "clip.mp4");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.showClipActionSnackbar(this.tt("publicResults.downloadSuccess", "Clip descargado correctamente"), "download");
      } catch (err) {
        this.$notify({
          message: err?.message || this.tt("publicResults.downloadRetryError", "Error al descargar el clip."),
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.$delete(this.downloadingClipIds, clipId);
      }
    },
    async downloadCollectionZip(collection, clips) {
      const jobId = String(collection?.jobId || "");
      if (!jobId || !clips.length || this.isCollectionZipDownloading(collection)) return;

      this.$set(this.downloadingCollectionZips, jobId, {
        completed: 0,
        total: clips.length,
      });
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session && !this.isDemoCollection(collection)) {
          throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        }

        const zip = new JSZip();
        for (let index = 0; index < clips.length; index += 1) {
          const clip = clips[index];
          let url = clip.rendered_video_url;
          if (!this.isDemoClip(clip)) {
            const payload = await requestTwitchClipDownload(
              session,
              String(clip.id),
              window.crypto.randomUUID()
            );
            url = payload.url;
          }
          if (!url) {
            throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
          }
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
          }
          zip.file(this.buildClipDownloadFilename(clip, "clip.mp4"), await response.blob());
          this.$set(this.downloadingCollectionZips, jobId, {
            completed: index + 1,
            total: clips.length,
          });
        }

        const blobUrl = window.URL.createObjectURL(
          await zip.generateAsync({ type: "blob" })
        );
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${String(collection.displayTitle || collection.jobId)
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9._-]+/gi, "-")
          .replace(/-+/g, "-")
          .replace(/^[-._]+|[-._]+$/g, "") || jobId}-clips.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.showClipActionSnackbar(
          this.tt("schedulePost.zipDownloadSuccess", "ZIP descargado correctamente"),
          "download"
        );
      } catch (err) {
        this.$notify({
          message: err?.message || this.tt("publicResults.downloadRetryError", "Error al descargar los clips."),
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.$delete(this.downloadingCollectionZips, jobId);
      }
    },
    openClipDeleteModal(clip) {
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      const clipId = clip && clip.id ? String(clip.id) : "";
      if (!clipId || this.isClipDownloading(clip) || this.isClipDeleting(clip)) return;
      this.stopVideoCardMediaPlayback();
      this.pendingDeleteClip = clip;
      this.deleteClipConfirmStep = 1;
      this.deleteClipModalError = "";
      this.showClipDeleteModal = true;
    },
    closeClipDeleteModal(force = false) {
      if (this.isDeletingPendingScheduleClip && !force) return;
      this.showClipDeleteModal = false;
      this.pendingDeleteClip = null;
      this.deleteClipConfirmStep = 1;
      this.deleteClipModalError = "";
    },
    async confirmClipDeleteStep() {
      if (!this.pendingDeleteClip || this.isDeletingPendingScheduleClip) return;
      if (this.isClipInteractionLocked(this.pendingDeleteClip)) {
        this.deleteClipModalError = this.tt("vodClips.renderingModalMessage", "Este video se está renderizando.");
        return;
      }

      if (this.deleteClipConfirmStep === 1) {
        this.deleteClipConfirmStep = 2;
        this.deleteClipModalError = "";
        return;
      }

      const clipId = String(this.pendingDeleteClip.id || "");
      if (!clipId) return;

      this.$set(this.deletingClipIds, clipId, true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        }
        await deleteTwitchClip(session, clipId);

        this.clips = (this.clips || []).filter((item) => String(item && item.id) !== clipId);
        this.$delete(this.clipPlayback, clipId);
        this.$delete(this.videoElements, `clip-${clipId}`);
        this.$delete(this.clipMutedState, clipId);
        if (String(this.fullscreenClipId || "") === clipId) {
          this.exitClipFullscreen();
          this.fullscreenClipId = null;
        }
        if (String(this.selectedClipId || "") === clipId) {
          this.selectedClipId = null;
          this.selectedClipDetails = null;
        }
        this.closeClipDeleteModal(true);
        await this.loadCollections();
        await this.loadPostingTimeline({ silent: true });
        this.showClipActionSnackbar(this.tt("publicResults.deleteSuccess", "Clip eliminado"), "delete");
      } catch (err) {
        this.deleteClipModalError = err?.message || this.tt("publicResults.deleteRetryError", "Error al eliminar el clip.");
      } finally {
        this.$delete(this.deletingClipIds, clipId);
      }
    },
    async loadUserSession() {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && session.user) {
        const effectiveUserId = getEffectiveUserId(session.user.id);
        this.userId = effectiveUserId;
        this.scheduleFilterGateVodCount = 0;
        this.userHasEverRenderedStream = null;
        this.pendingScheduleFilterTransitionReload = false;
        try {
          await this.syncCurrentUserContext(session);
        } catch (roleErr) {
          console.warn("Failed to resolve user role:", roleErr);
          this.userRole = null;
          this.canViewAllUsers = false;
          this.scheduleSubscriptionHasActivePlan = false;
        }
        this.resolveUserHasEverRenderedStream(session);
      } else {
        this.userId = null;
        this.userRole = null;
        this.canViewAllUsers = false;
        this.scheduleSubscriptionHasActivePlan = false;
        this.scheduleFilterGateVodCount = 0;
        this.userHasEverRenderedStream = null;
        this.pendingScheduleFilterTransitionReload = false;
      }
      await this.setupRealtimeCollectionsSubscription();
    },
    async resolveUserHasEverRenderedStream(session) {
      if (!session || this.isScheduleCollectionsDestroyed) return;
      if (this.isAdminUser) {
        this.userHasEverRenderedStream = true;
        return;
      }
      try {
        const payload = await fetchTwitchJobs(session, { days: null });
        if (this.isScheduleCollectionsDestroyed) return;
        const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
        this.userHasEverRenderedStream = jobs.length > 0;
      } catch (error) {
        console.warn("No se pudo resolver historial de renders del usuario:", error);
        if (!this.isScheduleCollectionsDestroyed) {
          this.userHasEverRenderedStream = true;
        }
      }
    },
    shouldEnableCollectionsRealtime() {
      if (isImpersonating()) return false;
      return !!this.userId;
    },
    buildCollectionsRealtimeFilter() {
      if (!this.userId) return "";
      const shouldLimitToCurrentUser = !this.canViewAllUsers || this.isScheduleOwnClipsScope;
      if (!shouldLimitToCurrentUser) return "";
      return `user_id=eq.${String(this.userId)}`;
    },
    buildCollectionsRealtimeSubscriptionKey() {
      if (!this.shouldEnableCollectionsRealtime()) return "disabled";
      const filter = this.buildCollectionsRealtimeFilter();
      return filter || "all-users";
    },
    shouldRefreshFromJobRealtimePayload(payload) {
      const eventType = String(payload?.eventType || "").toUpperCase();
      if (eventType === "DELETE") return true;
      if (eventType !== "INSERT" && eventType !== "UPDATE") return false;

      const nextStatus = this.normalizeJobStatus(payload?.new?.status);
      if (!nextStatus) return false;
      if (eventType === "INSERT") {
        this.userHasEverRenderedStream = true;
        return true;
      }

      const previousStatus = this.normalizeJobStatus(payload?.old?.status);
      if (nextStatus !== previousStatus) return true;
      return (
        ["5_rendering", "6_rendered", "7_scheduling"].includes(nextStatus)
        && String(payload?.new?.updated_at || "") !== String(payload?.old?.updated_at || "")
      );
    },
    scheduleRealtimeCollectionsRefresh(payload = null) {
      if (!this.shouldEnableCollectionsRealtime()) return;
      if (payload && typeof payload === "object") {
        this.realtimeCollectionsLastPayload = payload;
      }
      if (this.realtimeCollectionsRefreshDebounceTimer) {
        clearTimeout(this.realtimeCollectionsRefreshDebounceTimer);
      }
      this.realtimeCollectionsRefreshDebounceTimer = setTimeout(() => {
        this.realtimeCollectionsRefreshDebounceTimer = null;
        const pendingPayload = this.realtimeCollectionsLastPayload;
        this.realtimeCollectionsLastPayload = null;
        this.runRealtimeCollectionsRefresh(pendingPayload);
      }, 600);
    },
    shouldRunSilentCollectionsReloadFromRealtime(payload) {
      const eventType = String(payload?.eventType || "").toUpperCase();
      if (eventType === "DELETE") return true;
      if (eventType !== "INSERT" && eventType !== "UPDATE") return false;

      const nextStatus = this.normalizeJobStatus(payload?.new?.status);
      if (!nextStatus) return false;

      if (eventType === "INSERT") {
        return ["6_rendered", "7_scheduling", "8_published"].includes(nextStatus);
      }

      const previousStatus = this.normalizeJobStatus(payload?.old?.status);
      if (nextStatus === previousStatus) {
        return (
          ["5_rendering", "6_rendered", "7_scheduling"].includes(nextStatus)
          && String(payload?.new?.updated_at || "") !== String(payload?.old?.updated_at || "")
        );
      }

      return ["6_rendered", "7_scheduling", "8_published"].includes(nextStatus) || this.isJobFailed(nextStatus);
    },
    applyRealtimeJobPayloadToState(payload) {
      const eventType = String(payload?.eventType || "").toUpperCase();
      if (!["INSERT", "UPDATE", "DELETE"].includes(eventType)) return;

      const activeRow = eventType === "DELETE" ? payload?.old : payload?.new;
      const jobId = String(activeRow?.id || "").trim();
      if (!jobId) return;

      if (eventType === "DELETE") {
        this.$delete(this.mediaStatusByJobId, jobId);
        this.$delete(this.mediaExpiryByJobId, jobId);
        invalidateScheduleClipLibraryCache(this.getClipLibraryCacheParams());
        if (this.jobsById && this.jobsById[jobId]) {
          this.$delete(this.jobsById, jobId);
        }
        if (Array.isArray(this.jobs) && this.jobs.length > 0) {
          this.jobs = this.jobs.filter((job) => String(job?.id || "") !== jobId);
        }
        if (Array.isArray(this.clips) && this.clips.length > 0) {
          this.clips = this.clips.filter((clip) => String(clip?.job_id || "") !== jobId);
        }
        this.loadCollections();
        return;
      }

      const previousJob = this.jobsById?.[jobId] || null;
      const mergedJob = {
        ...(previousJob || {}),
        ...(payload?.new || {}),
        id: jobId,
      };
      if (didScheduleClipLibraryMediaRevisionChange(previousJob, mergedJob)) {
        invalidateScheduleClipLibraryCache(this.getClipLibraryCacheParams());
      }

      if (!this.jobsById || typeof this.jobsById !== "object") {
        this.jobsById = {};
      }
      this.$set(this.jobsById, jobId, mergedJob);

      const jobsList = Array.isArray(this.jobs) ? this.jobs.slice() : [];
      const existingJobIndex = jobsList.findIndex((job) => String(job?.id || "") === jobId);
      if (existingJobIndex >= 0) {
        jobsList.splice(existingJobIndex, 1, {
          ...(jobsList[existingJobIndex] || {}),
          ...mergedJob,
        });
      } else if (this.shouldShowEarlyJobStatuses() || this.isJobProcessing(mergedJob.status)) {
        jobsList.unshift(mergedJob);
      }
      this.jobs = jobsList;

      const nextStatus = this.normalizeJobStatus(mergedJob?.status);
      if (nextStatus) {
        this.updateCollectionStatusInState(
          jobId,
          nextStatus,
          mergedJob.error_code,
          mergedJob.error_message
        );
      }

      if (Array.isArray(this.clips) && this.clips.length > 0) {
        const thumbnailRaw = this.normalizeBatchThumbnailUrl(mergedJob.job_thumbnail_url);
        const thumbnailVersion = `${mergedJob.media_revision ?? ""}:${mergedJob.media_storage_state ?? ""}`;
        const thumbnailUrl = this.withCacheBusterIfSafe(thumbnailRaw, thumbnailVersion);
        let clipListChanged = false;
        const nextClips = this.clips.map((clip) => {
          if (String(clip?.job_id || "") !== jobId) return clip;
          clipListChanged = true;
          return {
            ...clip,
            job: {
              ...(clip?.job || {}),
              ...mergedJob,
            },
            source_username: mergedJob.source_username || clip.source_username || null,
            jobThumbnailUrl: thumbnailUrl,
          };
        });
        if (clipListChanged) {
          this.clips = nextClips;
        }
      }

      if (this.isCollectionReadyToOpen(nextStatus)) {
        this.clearStaleRenderingStateForJobClips(jobId);
      }

      this.loadCollections();
    },
    clearStaleRenderingStateForJobClips(jobId) {
      const normalizedJobId = String(jobId || "").trim();
      if (!normalizedJobId) return;

      let clipListChanged = false;
      const nextClips = (this.clips || []).map((clip) => {
        if (String(clip?.job_id || "") !== normalizedJobId) return clip;
        const normalizedStatus = this.normalizeClipStatus(clip?.status);
        if (!this.isClipPlaybackReadyStatus(normalizedStatus)) return clip;
        if (!clip?.isRerendering && !this.isClipMarkedAsRerendering(clip.id)) return clip;

        clipListChanged = true;
        const clipId = String(clip?.id || "").trim();
        if (clipId) {
          this.$delete(this.rerenderingClipIds, clipId);
        }
        return {
          ...clip,
          isRerendering: false,
        };
      });

      if (!clipListChanged) return;
      this.clips = nextClips;
      this.syncRerenderingClipIdsWithClips();
    },
    async runRealtimeCollectionsRefresh(payload = null) {
      if (!this.shouldEnableCollectionsRealtime()) return;
      if (this.loadingClips || this.realtimeCollectionsRefreshInFlight) {
        this.realtimeCollectionsRefreshQueued = true;
        if (payload && typeof payload === "object") {
          this.realtimeCollectionsLastPayload = payload;
        }
        return;
      }
      this.realtimeCollectionsRefreshQueued = false;
      if (!this.shouldRunSilentCollectionsReloadFromRealtime(payload)) return;

      this.realtimeCollectionsRefreshInFlight = true;
      try {
        const refreshedJobId = String(payload?.new?.id || payload?.old?.id || "").trim() || null;
        await this.loadClips({ silent: true, priorityJobId: refreshedJobId });
        await this.loadPostingTimeline({ silent: true });
      } catch (error) {
        console.warn("No se pudo refrescar clips desde realtime:", error);
      } finally {
        this.realtimeCollectionsRefreshInFlight = false;
        if (this.realtimeCollectionsRefreshQueued && !this.loadingClips && !this.realtimeCollectionsRefreshInFlight) {
          this.realtimeCollectionsRefreshQueued = false;
          this.scheduleRealtimeCollectionsRefresh();
        }
      }
    },
    async setupRealtimeCollectionsSubscription() {
      const subscriptionKey = this.buildCollectionsRealtimeSubscriptionKey();
      if (
        this.realtimeCollectionsChannel
        && this.realtimeCollectionsSubscriptionKey === subscriptionKey
      ) {
        return;
      }

      this.teardownRealtimeCollectionsSubscription();
      if (!this.shouldEnableCollectionsRealtime()) return;

      const filter = this.buildCollectionsRealtimeFilter();
      const channelName = `schedule-collections-${this.embedded ? "embedded" : "standalone"}-${Date.now()}`;
      const jobsChangeConfig = {
        event: "*",
        schema: "public",
        table: "twitch_jobs",
      };
      if (filter) {
        jobsChangeConfig.filter = filter;
      }

      const channel = supabase
        .channel(channelName)
        .on("postgres_changes", jobsChangeConfig, (payload) => {
          if (!this.shouldRefreshFromJobRealtimePayload(payload)) return;
          this.applyRealtimeJobPayloadToState(payload);
          this.scheduleRealtimeCollectionsRefresh(payload);
        });

      const nextChannel = channel.subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.warn("Error al suscribirse a realtime de colecciones.");
        }
      });

      this.realtimeCollectionsChannel = nextChannel;
      this.realtimeCollectionsSubscriptionKey = subscriptionKey;
    },
    teardownRealtimeCollectionsSubscription() {
      if (this.realtimeCollectionsRefreshDebounceTimer) {
        clearTimeout(this.realtimeCollectionsRefreshDebounceTimer);
        this.realtimeCollectionsRefreshDebounceTimer = null;
      }
      this.realtimeCollectionsRefreshQueued = false;
      this.realtimeCollectionsRefreshInFlight = false;
      this.realtimeCollectionsLastPayload = null;
      this.realtimeCollectionsSubscriptionKey = "";

      const channel = this.realtimeCollectionsChannel;
      this.realtimeCollectionsChannel = null;
      if (!channel) return;
      const removeResult = supabase.removeChannel(channel);
      if (removeResult && typeof removeResult.catch === "function") {
        removeResult.catch(() => {});
      }
    },
    resolveAuthoritativeJob(jobId, fallbackJob = null) {
      const normalizedJobId = String(jobId || "").trim();
      if (!normalizedJobId) return fallbackJob || null;
      return this.jobsById?.[normalizedJobId] || fallbackJob || null;
    },
    applyAuthoritativeJobFieldsToCollection(collection, fallbackJob = null) {
      if (!collection) return collection;
      const authoritativeJob = this.resolveAuthoritativeJob(collection.jobId, fallbackJob);
      if (!authoritativeJob) return collection;

      const localOutputsReady = authoritativeJob.local_outputs_ready === true;
      if (authoritativeJob.status) {
        collection.jobStatus = (
          localOutputsReady
          && this.normalizeJobStatus(authoritativeJob.status) === "5_rendering"
        )
          ? "6_rendered"
          : authoritativeJob.status;
      }
      if (authoritativeJob.error_code) {
        collection.jobErrorCode = authoritativeJob.error_code;
      }
      if (authoritativeJob.error_message) {
        collection.jobErrorMessage = authoritativeJob.error_message;
      }
      collection.sourceType = authoritativeJob.source_type || collection.sourceType || null;
      collection.refundStatus = authoritativeJob.refund_status || null;
      collection.refundAvailableAt = authoritativeJob.refund_available_at || null;
      collection.refundCompletedAt = authoritativeJob.refund_completed_at || null;
      collection.refundTokens = Number(authoritativeJob.refund_tokens || 0);
      if (authoritativeJob.updated_at) {
        collection.jobUpdatedAt = authoritativeJob.updated_at;
      }
      if (authoritativeJob.created_at && !collection.jobCreatedAt) {
        collection.jobCreatedAt = authoritativeJob.created_at;
      }
      collection.localOutputsReady = localOutputsReady;
      if (authoritativeJob.download_path && !collection.downloadPath) {
        collection.downloadPath = authoritativeJob.download_path;
      }
      if (authoritativeJob.download_url && !collection.downloadUrl) {
        collection.downloadUrl = authoritativeJob.download_url;
      }
      const thumbnailVersion = `${authoritativeJob.media_revision ?? ""}:${authoritativeJob.media_storage_state ?? ""}`;
      const jobThumbnailUrl = this.withCacheBusterIfSafe(
        this.normalizeBatchThumbnailUrl(authoritativeJob.job_thumbnail_url),
        thumbnailVersion
      );
      collection.jobThumbnailUrl = jobThumbnailUrl;
      collection.thumbnailUrl = jobThumbnailUrl;
      return collection;
    },
    shouldShowEarlyJobStatuses() {
      return this.embedded && this.showEarlyJobStatuses;
    },
    getClipLibraryCacheParams() {
      return {
        userId: this.userId,
        days: this.getClipLibraryDaysFilter(),
        ownClipsOnly: this.canViewAllUsers && this.isScheduleOwnClipsScope,
        assignedAdminOnly: this.canViewAllUsers && this.isScheduleAssignedUsersScope,
        showEarlyJobStatuses: this.shouldShowEarlyJobStatuses(),
      };
    },
    hydrateClipLibraryFromCache() {
      if (!this.userId) return false;

      const cached = readScheduleClipLibraryCache(this.getClipLibraryCacheParams());
      if (!cached) return false;

      const cachedCollections = Array.isArray(cached.clipCollections) ? cached.clipCollections : [];
      if (cachedCollections.length === 0) return false;

      this.jobs = Array.isArray(cached.jobs) ? cached.jobs : [];
      this.jobsById = cached.jobsById && typeof cached.jobsById === "object" ? cached.jobsById : {};
      this.clips = Array.isArray(cached.clips) ? cached.clips : [];
      this.clipCollections = cachedCollections;
      this.loadedClipLibraryDaysFilter = cached.loadedClipLibraryDaysFilter ?? null;
      this.hasLoadedClipLibrary = true;
      this.loadingClips = false;
      this.loadClipsError = null;
      this.pruneScheduleCollectionRuntimeCaches();
      return true;
    },
    persistClipLibraryCache() {
      if (!this.userId || !this.hasLoadedClipLibrary) return;

      writeScheduleClipLibraryCache(this.getClipLibraryCacheParams(), {
        jobs: this.jobs,
        jobsById: this.jobsById,
        clips: this.clips,
        clipCollections: this.clipCollections,
        loadedClipLibraryDaysFilter: this.loadedClipLibraryDaysFilter,
      });
    },
    async refreshEmbeddedScheduleData() {
      await this.loadClips({ silent: true });
      await this.loadPostingTimeline({ silent: true });
    },
    updateCollectionStatusInState(jobId, status, errorCode = null, errorMessage = null) {
      const index = (this.clipCollections || []).findIndex(
        (collection) => String(collection?.jobId || "") === String(jobId || "")
      );
      if (index < 0) return;
      const current = this.clipCollections[index];
      this.$set(this.clipCollections, index, {
        ...current,
        jobStatus: status,
        jobErrorCode: errorCode != null ? errorCode : current.jobErrorCode,
        jobErrorMessage: errorMessage != null ? errorMessage : current.jobErrorMessage,
      });
    },
    async refreshCollectionMediaStatus(collection) {
      if (!this.isAdminUser) return;
      const jobId = String(collection?.jobId || "").trim();
      if (!jobId || this.isDemoCollection(collection)) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const statusPayload = await fetchTwitchJobMediaSyncStatus(session, jobId);
      const previousStatus = this.mediaStatusByJobId[jobId];
      this.$set(this.mediaStatusByJobId, jobId, statusPayload);
      const previousBatch = previousStatus?.batches?.[0] || null;
      const currentBatch = statusPayload?.batches?.[0] || null;
      const previousRevision = previousBatch
        ? `${previousBatch.id}:${previousBatch.generation}:${previousBatch.revision}:${previousBatch.status}`
        : "";
      const currentRevision = currentBatch
        ? `${currentBatch.id}:${currentBatch.generation}:${currentBatch.revision}:${currentBatch.status}`
        : "";
      if (previousStatus && previousRevision !== currentRevision) {
        invalidateScheduleClipLibraryCache(this.getClipLibraryCacheParams());
      }
      return statusPayload;
    },
    async refreshAdminMediaExpiryStatus() {
      if (!this.isAdminUser) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const payload = await fetchTwitchMediaExpiryStatus(session);
      const expiryByJobId = {};
      (payload.jobs || []).forEach((job) => {
        const jobId = String(job?.job_id || "").trim();
        if (!jobId) return;
        expiryByJobId[jobId] = job;
        if (job.media_tombstoned_at) {
          this.updateCollectionStatusInState(
            jobId,
            "expired",
            "media_expired",
            "Assets no longer available"
          );
        }
      });
      this.mediaExpiryByJobId = expiryByJobId;
    },
    collectionMediaStatusLabel(collection) {
      const jobId = String(collection?.jobId || "");
      const expiry = this.mediaExpiryByJobId[jobId];
      if (expiry?.media_tombstoned_at) {
        return this.tt("vodClips.statusExpired", "Expirado");
      }
      if (expiry?.media_expiry_deferred_at) {
        return this.tt("schedulePost.mediaExpiryDeferred", "Expiración diferida");
      }
      const latestBatch = this.mediaStatusByJobId[jobId]?.batches?.[0];
      if (!latestBatch?.status) return "";
      const totalBytes = Number(latestBatch.total_bytes);
      const verifiedBytes = Number(latestBatch.verified_bytes);
      const progress = totalBytes > 0
        ? Math.round((verifiedBytes / totalBytes) * 100)
        : 0;
      return `${latestBatch.status} · ${progress}%`;
    },
    async injectDemoCollectionIfNeeded(session) {
      this.demoAssetsError = null;
      this.demoCollection = null;

      if (this.isAdminUser) return;
      if (this.realClipCollections.length > 0) return;

      this.demoAssetsLoading = true;
      try {
        const language = this.$i18n.locale;
        const payload = await fetchCliprDemoAssets(session, language);
        const { collection, clips } = mapDemoAssetsToScheduleCollection(payload, {
          translate: (key, fallback) => this.tt(key, fallback),
          jobId: CLIPR_DEMO_COLLECTION_JOB_ID,
          language,
        });
        if (!clips.length) {
          this.demoAssetsError = this.tt(
            "schedulePost.demoAssetsError",
            "No se pudo cargar el VOD de prueba."
          );
          this.demoCollection = null;
          return;
        }

        const nonDemoClips = (this.clips || []).filter((clip) => !this.isDemoClip(clip));
        this.clips = [...nonDemoClips, ...clips];
        this.demoCollection = collection;
      } catch (error) {
        console.warn("No se pudieron cargar los assets demo de Clipr:", error);
        this.demoAssetsError = error?.message
          || this.tt("schedulePost.demoAssetsError", "No se pudo cargar el VOD de prueba.");
        this.demoCollection = null;
      } finally {
        this.demoAssetsLoading = false;
      }
    },
    async reloadDemoCollectionForInterfaceLanguage() {
      this.clipPlaybackSessionId += 1;
      this.pauseAllVideos();
      this.clipPlayback = {};
      this.videoElements = {};
      this.fullscreenClipId = null;
      const { data: { session } } = await supabase.auth.getSession();
      await this.injectDemoCollectionIfNeeded(session);
    },
    async mergeFreshJobClipsIntoLibrary(session, jobId, libraryClips, jobsById) {
      const normalizedJobId = String(jobId || "").trim();
      if (!normalizedJobId || !session) {
        return Array.isArray(libraryClips) ? libraryClips : [];
      }

      try {
        const jobPayload = await fetchTwitchJobClips(session, normalizedJobId);
        const jobClips = Array.isArray(jobPayload?.clips) ? jobPayload.clips : [];
        if (jobClips.length === 0) {
          return Array.isArray(libraryClips) ? libraryClips : [];
        }

        const jobVideoType = String(jobsById?.[normalizedJobId]?.video_type || "").trim();
        const freshJobClips = jobClips.map((row) => ({
          ...row,
          effective_video_type:
            String(row?.effective_video_type || "").trim()
            || String(row?.video_type || "").trim()
            || jobVideoType
            || null,
        }));
        const libraryWithoutJob = (Array.isArray(libraryClips) ? libraryClips : [])
          .filter((row) => String(row?.job_id || "").trim() !== normalizedJobId);
        return libraryWithoutJob.concat(freshJobClips);
      } catch (error) {
        console.warn("No se pudieron cargar los clips del VOD seleccionado:", error);
        return Array.isArray(libraryClips) ? libraryClips : [];
      }
    },
    async loadClips({ throwOnError = false, silent = false, priorityJobId = null } = {}) {
      if (!this.userId) {
        await this.loadUserSession();
        if (this.isScheduleCollectionsDestroyed) return;
        if (!this.userId) return;
      }

      this.demoAssetsLoading = false;
      this.demoAssetsError = null;
      this.demoCollection = null;

      if (!silent) {
        this.loadingClips = true;
        this.loadClipsError = null;
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error("No session found");
          return;
        }
        const effectiveDays = this.getClipLibraryDaysFilter();
        const routeJobId = this.getCollectionJobIdFromRoute();
        const ensuredJobId = String(priorityJobId || routeJobId || "").trim() || null;
        const shouldLimitUnscopedLibrary = effectiveDays == null;
        const previousClips = Array.isArray(this.clips) ? this.clips.slice() : [];
        const previousDemoClips = previousClips.filter((clip) => this.isDemoClip(clip));
        const previousClipsById = previousClips.reduce((acc, clip) => {
          const clipId = String(clip?.id || "").trim();
          if (clipId) {
            acc[clipId] = clip;
          }
          return acc;
        }, {});
        const libraryPayload = await fetchTwitchClipLibrary(session, {
          days: effectiveDays,
          limit: shouldLimitUnscopedLibrary ? CLIP_LIBRARY_UNSCOPED_CLIP_LIMIT : null,
          ownClipsOnly: this.canViewAllUsers && this.isScheduleOwnClipsScope,
          assignedAdminOnly: this.canViewAllUsers && this.isScheduleAssignedUsersScope,
          includeJobs: true,
          includeConverter: false,
        });
        if (this.isScheduleCollectionsDestroyed) return;
        const jobs = Array.isArray(libraryPayload?.jobs) ? libraryPayload.jobs : [];
        const cachedJobsById = this.jobsById || {};
        if (
          jobs.some((job) =>
            job?.id
            && cachedJobsById[job.id]
            && didScheduleClipLibraryMediaRevisionChange(cachedJobsById[job.id], job)
          )
        ) {
          invalidateScheduleClipLibraryCache(this.getClipLibraryCacheParams());
        }
        const jobsById = jobs.reduce((acc, job) => {
          if (job && job.id) acc[job.id] = job;
          return acc;
        }, {});
        this.jobsById = jobsById;
        const visibleScheduleJobs = this.shouldShowEarlyJobStatuses()
          ? (jobs || []).map((job) => jobsById[job.id] || job).filter((job) => !!job && !!job.id)
          : Object.values(jobsById);
        this.jobs = visibleScheduleJobs.filter((job) => !this.isConverterJob(job));

        let libraryClips = Array.isArray(libraryPayload?.clips) ? libraryPayload.clips : [];
        if (ensuredJobId) {
          libraryClips = await this.mergeFreshJobClipsIntoLibrary(
            session,
            ensuredJobId,
            libraryClips,
            jobsById
          );
          if (this.isScheduleCollectionsDestroyed) return;
        }

        const clipsRaw = libraryClips.filter((row) => {
          if (!this.isClipActive(row)) return false;
          if (this.isConverterClip(row)) return false;
          return this.isClipStatusVisibleInSchedule(row);
        });
        if (
          clipsRaw.some((clip) =>
            clip?.id
            && previousClipsById[clip.id]
            && didScheduleClipMediaRevisionChange(previousClipsById[clip.id], clip)
          )
        ) {
          invalidateScheduleClipLibraryCache(this.getClipLibraryCacheParams());
        }
        const missingRendered = clipsRaw.filter((row) => {
          const normalizedStatus = this.normalizeClipStatus(row?.status);
          const shouldHaveRenderedAsset =
            normalizedStatus === "4_rendered"
            || normalizedStatus === "4.3_rendered"
            || normalizedStatus === "5_scheduled_to_post"
            || normalizedStatus === "posted"
            || normalizedStatus === "6_posted"
            || normalizedStatus === "7_posted";
          const hasRenderedAsset = Boolean(
            row.rendered_video_url
            || (
              row.local_render_ready === true
              && row.local_rendered_video_url
            )
          );
          return shouldHaveRenderedAsset && !hasRenderedAsset;
        });
        if (!this.shouldShowEarlyJobStatuses() && missingRendered.length > 0) {
          this.loadClipsError = `${this.tt("schedulePost.missingRenderedVideosFor", "Faltan videos renderizados para")} ${missingRendered.length} ${this.tt("schedulePost.clip", "clip")}${missingRendered.length === 1 ? "" : "s"}.`;
          throw new Error(this.loadClipsError);
        }

        const mapped = clipsRaw.map((row) => {
          const clipId = String(row?.id || "").trim();
          const previousClip = previousClipsById[clipId] || null;
          const start = Number(row.start_seconds ?? 0);
          const end = Number(row.end_seconds ?? 0);
          const durationSec =
            end > start ? Math.max(end - start, 0) : Number(row.duration || 0);

          const formatSeconds = (seconds) => {
            if (!seconds || isNaN(seconds)) return null;
            const s = Math.floor(seconds % 60)
              .toString()
              .padStart(2, "0");
            const m = Math.floor((seconds / 60) % 60)
              .toString()
              .padStart(2, "0");
            const h = Math.floor(seconds / 3600);
            return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
          };

          const durationLabel = formatSeconds(durationSec);
          const startLabel = formatSeconds(start);
          const endLabel = formatSeconds(end);

          let timeRangeLabel = null;
          if (startLabel && endLabel) {
            timeRangeLabel = `${startLabel} - ${endLabel}`;
          }

          const job = jobsById[row.job_id] || null;
          const playbackVersion = row.updated_at || row.rendered_at || row.created_at || null;
          const normalizedClipStatus = this.normalizeClipStatus(row.status);
          const shouldKeepRerendering = this.isClipReadyForPlaybackDespiteStatusLag({
            ...row,
            job,
          })
            ? false
            : this.shouldClipRetainRenderingOverlay(
              clipId,
              normalizedClipStatus,
              previousClip
            );
          const resolvedRenderedUrl = this.withCacheBusterIfSafe(
            row.local_render_ready === true
              ? (row.local_rendered_video_url || null)
              : (row.rendered_video_url || null),
            playbackVersion
          );
          const playbackUrl = shouldKeepRerendering
            ? (previousClip?.playbackUrl || resolvedRenderedUrl || null)
            : resolvedRenderedUrl;
          let renderMeta = null;
          if (row && typeof row.render_meta === "object") {
            renderMeta = row.render_meta;
          } else if (row && typeof row.render_meta === "string") {
            try {
              renderMeta = JSON.parse(row.render_meta);
            } catch (e) {
              renderMeta = null;
            }
          }
          const rawThumbnailUrl =
            row.thumbnail_url ||
            row.thumbnail ||
            (renderMeta && renderMeta.thumbnail_url) ||
            null;
          const thumbnailUrl = this.withCacheBusterIfSafe(rawThumbnailUrl, playbackVersion);
          const rawJobThumbnailUrl = this.normalizeBatchThumbnailUrl(
            job && job.job_thumbnail_url
          );
          const jobThumbnailVersion = job
            ? `${job.media_revision ?? ""}:${job.media_storage_state ?? ""}`
            : "";
          const jobThumbnailUrl = this.withCacheBusterIfSafe(rawJobThumbnailUrl, jobThumbnailVersion);
          const clipTitle = this.parseClipTitle(row);
          const editWindowStart = Number(row.edit_window_start_seconds);
          const editWindowEnd = Number(row.edit_window_end_seconds);
          const normalizedVideoType = this.normalizeClipVideoType(
            row.effective_video_type ?? row.effectiveVideoType ?? row.video_type ?? row.videoType ?? null
          );
          const normalizedContextPosition = this.normalizeClipTimeEditorContextPosition(
            row.context_position ?? row.contextPosition ?? null
          );
          const normalizedPreviewLayout = this.cloneClipTimeEditorPreviewLayout(
            row.preview_layout ?? row.previewLayout ?? null
          );

          return {
            ...row,
            video_type: normalizedVideoType,
            videoType: normalizedVideoType,
            effective_video_type: normalizedVideoType,
            effectiveVideoType: normalizedVideoType,
            context_position: normalizedContextPosition,
            contextPosition: normalizedContextPosition,
            preview_layout: normalizedPreviewLayout,
            previewLayout: normalizedPreviewLayout,
            fallbackKey: `${row.job_id || "job"}-${start}-${end}`,
            durationLabel,
            timeRangeLabel,
            startSeconds: isNaN(start) ? 0 : start,
            endSeconds: end > start ? end : null,
            editWindowStartSeconds: Number.isFinite(editWindowStart) ? editWindowStart : null,
            editWindowEndSeconds: Number.isFinite(editWindowEnd) ? editWindowEnd : null,
            thumbnailUrl,
            jobThumbnailUrl,
            playbackUrl,
            clipTitle,
            job,
            source_username: (job && job.source_username) || null,
            isUpdatingStatus: false,
            originalStatus: row.status || "4_rendered",
            isRerendering: shouldKeepRerendering,
            job_id_short:
              row.job_id && typeof row.job_id === "string"
                ? row.job_id.slice(0, 8)
                : "N/A",
          };
        });
        const mappedClipIds = new Set(
          mapped
            .map((clip) => String(clip?.id || "").trim())
            .filter((clipId) => clipId !== "")
        );
        const preservedRerenderingClips = previousClips
          .filter((clip) => {
            const clipId = String(clip?.id || "").trim();
            if (!clipId || mappedClipIds.has(clipId)) return false;
            if (!this.isClipMarkedAsRerendering(clipId)) return false;
            return !this.isClipRerenderFailedStatus(clip?.status);
          })
          .map((clip) => ({
            ...clip,
            status: this.isClipIntermediateRenderingStatus(clip?.status)
              ? this.normalizeClipStatus(clip?.status)
              : "4.1_rerender_requested",
            isRerendering: true,
          }));
        const nextClips = mapped.concat(preservedRerenderingClips);

        // Evita referencias stale de video después de recargar datos.
        if (!silent) {
          this.clipPlaybackSessionId += 1;
          this.clipPlayback = {};
          this.videoElements = {};
          this.fullscreenClipId = null;
        }
        this.clips = [...nextClips, ...previousDemoClips];
        this.syncRerenderingClipIdsWithClips();
        
        // Load collections from clips
        await this.loadCollections();
        if (this.isScheduleCollectionsDestroyed) return;
        if (!this.isAdminUser && effectiveDays == null) {
          this.scheduleFilterGateVodCount = this.clipCollections.length;
        }
        await this.injectDemoCollectionIfNeeded(session);
        if (this.isScheduleCollectionsDestroyed) return;
        this.pruneScheduleCollectionRuntimeCaches();
        
        this.$nextTick(() => {
          this.ensureSelectedCollectionStillVisible();
          this.syncSelectedClipPlaybackSession();
        });
        this.loadedClipLibraryDaysFilter = effectiveDays;
        this.hasLoadedClipLibrary = true;
        this.persistClipLibraryCache();

      } catch (err) {
        console.error("Error loading clips:", err);
        if (!silent) {
          this.jobs = [];
          this.jobsById = {};
          this.clips = [];
          this.hasLoadedClipLibrary = false;
          this.loadedClipLibraryDaysFilter = null;
        }
        if (throwOnError && !this.loadClipsError) {
          throw err;
        }
      } finally {
        if (this.isScheduleCollectionsDestroyed) return;
        if (!silent) {
          this.loadingClips = false;
        }
        this.scheduleTimelineLayoutRefresh();
        if (this.pendingScheduleFilterTransitionReload) {
          this.pendingScheduleFilterTransitionReload = false;
          this.loadClips();
        }
        if (this.realtimeCollectionsRefreshQueued && !this.loadingClips && !this.realtimeCollectionsRefreshInFlight) {
          this.realtimeCollectionsRefreshQueued = false;
          this.scheduleRealtimeCollectionsRefresh();
        }
      }
    },
    async loadCollections() {
      try {
        const collectionsMap = {};

        if (this.shouldShowEarlyJobStatuses()) {
          (this.jobs || []).forEach((job) => {
            if (!job || !job.id || this.isConverterJob(job)) return;
            const authoritativeJob = this.resolveAuthoritativeJob(job.id, job);
            const jobTitle = this.resolveJobTitle(authoritativeJob) || "N/A";
            collectionsMap[job.id] = {
              jobId: job.id,
              sourceUsername: authoritativeJob.source_username || null,
              ownerName: this.resolveJobOwnerName(authoritativeJob) || null,
              clips: [],
              thumbnailUrl: this.resolveJobThumbnailUrl(authoritativeJob) || null,
              jobThumbnailUrl: this.resolveJobThumbnailUrl(authoritativeJob) || null,
              readyCount: 0,
              failedClipCount: 0,
              usableClipCount: 0,
              totalClips: 0,
              displayTitle: jobTitle,
              jobStatus: authoritativeJob.status || null,
              jobErrorCode: authoritativeJob.error_code || null,
              jobErrorMessage: authoritativeJob.error_message || null,
              sourceType: authoritativeJob.source_type || null,
              refundStatus: authoritativeJob.refund_status || null,
              refundAvailableAt: authoritativeJob.refund_available_at || null,
              refundCompletedAt: authoritativeJob.refund_completed_at || null,
              refundTokens: Number(authoritativeJob.refund_tokens || 0),
              jobCreatedAt: authoritativeJob.created_at || null,
              jobUpdatedAt: authoritativeJob.updated_at || null,
              downloadPath: authoritativeJob.download_path || null,
              downloadUrl: authoritativeJob.download_url || null,
            };
          });
        }

        this.clips.forEach((clip) => {
          if (this.isConverterClip(clip)) return;
          const jobId = clip.job_id;
          if (!jobId) return;
          const latestJob = this.jobsById?.[jobId] || clip.job || null;

          if (!collectionsMap[jobId]) {
            collectionsMap[jobId] = {
              jobId: jobId,
              sourceUsername: clip.source_username || null,
              ownerName:
                this.resolveJobOwnerName(latestJob) ||
                this.resolveJobOwnerName(clip?.job) ||
                this.resolveJobOwnerName(clip) ||
                null,
              clips: [],
              thumbnailUrl: null,
              jobThumbnailUrl: null,
              readyCount: 0,
              failedClipCount: 0,
              usableClipCount: 0,
              totalClips: 0,
              displayTitle:
                this.resolveJobTitle(latestJob) ||
                this.resolveJobTitle(clip?.job) ||
                "N/A",
              jobStatus: latestJob?.status || null,
              jobErrorCode: latestJob?.error_code || null,
              jobErrorMessage: latestJob?.error_message || null,
              sourceType: latestJob?.source_type || null,
              refundStatus: latestJob?.refund_status || null,
              refundAvailableAt: latestJob?.refund_available_at || null,
              refundCompletedAt: latestJob?.refund_completed_at || null,
              refundTokens: Number(latestJob?.refund_tokens || 0),
              jobCreatedAt: latestJob?.created_at || null,
              jobUpdatedAt: latestJob?.updated_at || clip.updated_at || null,
              downloadPath: latestJob?.download_path || null,
              downloadUrl: latestJob?.download_url || null,
            };
          }
          if (!collectionsMap[jobId].sourceUsername && latestJob?.source_username) {
            collectionsMap[jobId].sourceUsername = latestJob.source_username;
          }
          if (!collectionsMap[jobId].sourceUsername && clip.source_username) {
            collectionsMap[jobId].sourceUsername = clip.source_username;
          }
          if (!collectionsMap[jobId].ownerName) {
            collectionsMap[jobId].ownerName =
              this.resolveJobOwnerName(latestJob) ||
              this.resolveJobOwnerName(clip?.job) ||
              this.resolveJobOwnerName(clip) ||
              null;
          }
          if (!collectionsMap[jobId].displayTitle || collectionsMap[jobId].displayTitle === "N/A") {
            collectionsMap[jobId].displayTitle =
              this.resolveJobTitle(latestJob) ||
              this.resolveJobTitle(clip?.job) ||
              collectionsMap[jobId].displayTitle;
          }

          collectionsMap[jobId].clips.push(clip);

          const normalizedClipStatus = this.normalizeClipStatus(clip?.status);
          if (this.isCollectionClipFailedStatus(normalizedClipStatus)) {
            collectionsMap[jobId].failedClipCount++;
          }
          if (this.isCollectionClipUsableStatus(normalizedClipStatus)) {
            collectionsMap[jobId].usableClipCount++;
          }

          // Count clips visible in schedule page
          if (this.isClipVisibleInSchedule(clip) && !this.isCollectionClipFailedStatus(normalizedClipStatus)) {
            collectionsMap[jobId].readyCount++;
          }

          // Usar únicamente job_thumbnail_url entregado por twitch_jobs.
          if (clip.jobThumbnailUrl) {
            collectionsMap[jobId].jobThumbnailUrl = clip.jobThumbnailUrl;
            collectionsMap[jobId].thumbnailUrl = clip.jobThumbnailUrl;
          }
          this.applyAuthoritativeJobFieldsToCollection(collectionsMap[jobId], latestJob);
          if (!collectionsMap[jobId].jobUpdatedAt && clip.updated_at) {
            collectionsMap[jobId].jobUpdatedAt = clip.updated_at;
          }
          if (!collectionsMap[jobId].downloadPath && latestJob?.download_path) {
            collectionsMap[jobId].downloadPath = latestJob.download_path;
          }
          if (!collectionsMap[jobId].downloadUrl && latestJob?.download_url) {
            collectionsMap[jobId].downloadUrl = latestJob.download_url;
          }
        });

        // Convert to array and compute properties
        const allCollections = Object.values(collectionsMap).map((collection) => {
          this.applyAuthoritativeJobFieldsToCollection(collection);
          collection.totalClips = collection.clips.length;
          collection.jobIdShort = collection.jobId && typeof collection.jobId === "string"
            ? collection.jobId.slice(0, 8)
            : "N/A";
          if (!collection.displayTitle || collection.displayTitle === "N/A") {
            collection.displayTitle = `Job ${collection.jobIdShort}`;
          }
          if (!collection.jobCreatedAt && collection.clips.length > 0) {
            collection.jobCreatedAt = collection.clips[0].created_at || null;
          }
          if (this.shouldForceExpiredStatus(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage)) {
            collection.jobStatus = "expired";
          }
          return collection;
        });

        // Filter: show only ready collections in schedule-page normal mode;
        // in MakeClips embedded mode show early job statuses too.
        const filteredCollections = allCollections.filter((collection) => {
          if (collection.readyCount > 0) return true;
          if (this.isAdminUser && Number(collection.failedClipCount || 0) > 0) return true;
          if (this.shouldShowEarlyJobStatuses()) {
            if (this.isJobVisibleInEarlyCollections(collection.jobStatus, collection.jobErrorCode, collection.jobErrorMessage)) {
              return true;
            }
            return collection.totalClips > 0;
          }
          return false;
        }).sort((a, b) => {
          const aLatest = new Date(a.jobCreatedAt || a.jobUpdatedAt || a.clips[0]?.created_at || 0).getTime();
          const bLatest = new Date(b.jobCreatedAt || b.jobUpdatedAt || b.clips[0]?.created_at || 0).getTime();
          return bLatest - aLatest;
        });

        this.clipCollections = filteredCollections;

        // If selected collection no longer has ready clips, deselect it
        if (
          this.selectedCollectionJobId &&
          String(this.selectedCollectionJobId || "") !== CLIPR_DEMO_COLLECTION_JOB_ID &&
          this.getCollectionJobIdFromRoute() !== String(this.selectedCollectionJobId) &&
          !this.clipCollections.some(
            (c) => c.jobId === this.selectedCollectionJobId
          )
        ) {
          this.selectedCollectionJobId = null;
          this.selectedClipId = null;
          this.selectedClipDetails = null;
          this.syncCollectionSelectionToRoute(null);
        }
      } catch (err) {
        console.error("Error loading collections:", err);
      }
    },
    selectCollection(jobId) {
      const nextJobId = this.selectedCollectionJobId === jobId ? null : jobId;
      this.selectedCollectionJobId = nextJobId;
      this.selectedClipId = null;
      this.selectedClipDetails = null;
      if (nextJobId) {
        const firstClip = this.clips.find((clip) =>
          String(clip?.job_id || "") === String(nextJobId)
          && this.isClipActive(clip)
          && this.isClipVisibleInSchedule(clip)
        );
        if (firstClip) {
          this.selectClip(firstClip.id);
        }
      }
      this.syncCollectionSelectionToRoute(nextJobId);
    },
    async handleCollectionItemClick(collection) {
      this.closeCollectionActionsMenu();
      const jobId = collection?.jobId || null;
      if (!jobId) return;
      if (this.isAdminUser) {
        try {
          await this.refreshCollectionMediaStatus(collection);
          if (this.mediaExpiryByJobId[jobId]?.media_tombstoned_at) {
            this.showClipActionSnackbar(
              this.tt(
                "vodClips.vodExpiredOpenSnackbar",
                "El VOD al que desea ingresar ha expirado, por favor renderea uno nuevo."
              ),
              "info"
            );
            return;
          }
        } catch (error) {
          console.warn("No se pudo cargar estado media del VOD:", error);
        }
      }
      const normalizedStatus = this.normalizeJobStatus(collection?.jobStatus);
      if (normalizedStatus === "expired") {
        this.showClipActionSnackbar(
          this.tt(
            "vodClips.vodExpiredOpenSnackbar",
            "El VOD al que desea ingresar ha expirado, por favor renderea uno nuevo."
          ),
          "info"
        );
        return;
      }
      const isInsufficientTokensFailure = this.isJobInsufficientTokens(
        collection?.jobStatus,
        collection?.jobErrorCode,
        collection?.jobErrorMessage
      );
      if (isInsufficientTokensFailure) {
        this.$emit("collection-insufficient-tokens-click", {
          jobId: collection.jobId || null,
          displayTitle: collection.displayTitle || "",
          sourceUsername: collection.sourceUsername || "",
          jobStatus: collection.jobStatus || "",
          jobErrorCode: collection.jobErrorCode || "",
          jobErrorMessage: collection.jobErrorMessage || "",
        });
        return;
      }
      if (this.shouldBlockCollectionDetailOpen(collection)) {
        this.openCollectionProcessingModal(collection?.jobStatus || null);
        return;
      }
      // Muestra workspace primero; recarga VOD después en segundo plano.
      this.selectCollection(jobId);
      if (
        (
          this.isCollectionReadyToOpen(normalizedStatus)
          || this.collectionHasLocalOutputsReady(collection)
        )
        && !this.loadingClips
        && !this.realtimeCollectionsRefreshInFlight
      ) {
        void this.loadClips({ silent: true, priorityJobId: jobId }).catch((error) => {
          console.warn("No se pudieron refrescar clips al abrir VOD:", error);
        });
      }
    },
    shouldBlockCollectionDetailOpen(collection) {
      if (!this.blockOpenUntilRendered) return false;
      const normalizedStatus = String(collection?.jobStatus || "").toLowerCase();
      return (
        !this.isCollectionReadyToOpen(normalizedStatus)
        && !this.collectionHasLocalOutputsReady(collection)
      );
    },
    isCollectionReadyToOpen(status) {
      const normalized = String(status || "").toLowerCase();
      return normalized === "6_rendered" || normalized === "7_scheduling" || normalized === "8_published";
    },
    openCollectionProcessingModal(status) {
      this.collectionProcessingModalStatus = String(status || "").toLowerCase() || null;
      this.showCollectionProcessingModal = true;
    },
    closeCollectionProcessingModal() {
      this.showCollectionProcessingModal = false;
      this.collectionProcessingModalStatus = null;
    },
    isCollectionProcessingModalFailed(status) {
      return this.isJobFailed(status);
    },
    getCollectionProcessingModalIndicatorPhase(status) {
      if (this.isJobFailed(status)) return "failed";
      return this.getJobProcessingPhase(status) || "queued";
    },
    getCollectionProcessingModalStatusLabel(status) {
      if (this.isJobFailed(status)) {
        return this.tt("vodClips.statusFailed", "Fallido");
      }
      if (this.isJobRunning(status)) {
        return this.tt("vodClips.downloading", "Descargando");
      }
      if (this.isJobDownloaded(status)) {
        return this.tt("vodClips.preparingClips", "Preparando clips");
      }
      if (this.isJobRendering(status)) {
        return this.tt("vodClips.creatingClips", "Creando tus clips");
      }
      if (this.isJobDecided(status)) {
        return this.tt("vodClips.inQueue", "En cola");
      }
      if (this.isJobQueued(status)) {
        return this.tt("vodClips.validating", "Validando");
      }
      return this.jobStatusLabel(status);
    },
    getCollectionProcessingModalTitle(status) {
      if (this.isJobFailed(status)) {
        return this.tt("schedulePost.collectionProcessingModalTitleFailed", "Procesamiento fallido");
      }
      const normalized = this.normalizeJobStatus(status);
      const titleMap = {
        "1_queued": this.tt("schedulePost.collectionProcessingModalTitleQueued", "En cola de procesamiento"),
        "2_running": this.tt("schedulePost.collectionProcessingModalTitleDownloading", "Descargando stream"),
        "3_downloaded": this.tt("schedulePost.collectionProcessingModalTitleDownloaded", "Procesando stream"),
        "4_decided": this.tt("schedulePost.collectionProcessingModalTitleDecided", "En cola de renderizado"),
        "5_rendering": this.tt("schedulePost.collectionProcessingModalTitleRendering", "Creando tus clips"),
      };
      return titleMap[normalized] || this.tt("schedulePost.collectionProcessingModalTitle", "Stream en procesamiento");
    },
    getCollectionProcessingModalMessage(status) {
      if (this.isJobFailed(status)) {
        return this.tt(
          "schedulePost.collectionProcessingModalMessageFailed",
          "Lamentamos los inconvenientes. Verificaremos el error y nos contactaremos contigo por correo para compensarte."
        );
      }
      const normalized = this.normalizeJobStatus(status);
      const messageMap = {
        "1_queued": this.tt(
          "schedulePost.collectionProcessingModalMessageQueued",
          "Tu stream está en cola. Sé paciente, pronto comenzará el procesamiento."
        ),
        "2_running": this.tt(
          "schedulePost.collectionProcessingModalMessageDownloading",
          "Estamos descargando tu stream. Sé paciente, esto terminará pronto."
        ),
        "3_downloaded": this.tt(
          "schedulePost.collectionProcessingModalMessageDownloaded",
          "El stream ya se descargó y estamos preparando tus clips. Pronto terminará."
        ),
        "4_decided": this.tt(
          "schedulePost.collectionProcessingModalMessageDecided",
          "Tu stream está en cola para renderizarse. Sé paciente, falta poco."
        ),
        "5_rendering": this.tt(
          "schedulePost.collectionProcessingModalMessageRendering",
          "Estamos renderizando tus clips. Sé paciente, ya casi terminamos."
        ),
      };
      return messageMap[normalized] || this.tt(
        "schedulePost.collectionProcessingModalMessage",
        "Este stream aún se está procesando. Sé paciente, pronto podrás abrir la colección."
      );
    },
    scrollScheduleJobsRow(direction) {
      const el = this.$refs.scheduleJobsScroll;
      if (!el || typeof el.scrollBy !== "function") return;
      const amount = Math.min(400, el.clientWidth * 0.85) || 320;
      el.scrollBy({ left: direction * amount, behavior: "smooth" });
    },
    scrollScheduleClipsRow(direction) {
      const el = this.$refs.scheduleClipsScroll;
      if (!el || typeof el.scrollBy !== "function") return;
      const amount = Math.min(400, el.clientWidth * 0.85) || 320;
      el.scrollBy({ left: direction * amount, behavior: "smooth" });
    },
    ensureSelectedCollectionStillVisible() {
      if (!this.selectedCollectionJobId) return;
      const ok = this.filteredClipCollections.some(
        (c) => c.jobId === this.selectedCollectionJobId
      );
      if (!ok && this.getCollectionJobIdFromRoute() !== String(this.selectedCollectionJobId)) {
        this.selectedCollectionJobId = null;
        this.selectedClipId = null;
        this.selectedClipDetails = null;
        this.syncCollectionSelectionToRoute(null);
      }
    },
    getSelectedCollectionJobIdShort() {
      if (!this.selectedCollectionJobId) return '';
      return this.selectedCollectionJobId && typeof this.selectedCollectionJobId === 'string'
        ? this.selectedCollectionJobId.slice(0, 8)
        : 'N/A';
    },
    getSelectedCollectionSourceUsername() {
      if (!this.selectedCollectionJobId) return "N/A";
      const selected = this.visibleClipCollections.find(
        (collection) => collection.jobId === this.selectedCollectionJobId
      );
      return (selected && selected.sourceUsername) || "N/A";
    },
    isClipMuted(clip) {
      const clipId = String(clip?.id || "");
      if (!clipId) return false;
      return Boolean(this.clipMutedState[clipId]);
    },
    toggleClipMute(clip) {
      if (!clip || !clip.id) return;
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      const clipId = String(clip.id);
      const nextMuted = !this.isClipMuted(clip);
      this.$set(this.clipMutedState, clipId, nextMuted);
      this.applyClipMutedStateToVideo(clipId);
    },
    getClipVideoElement(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId) return null;
      const clipKey = `clip-${normalizedClipId}`;
      const playbackContainer = this.getClipPlaybackContainer(normalizedClipId);
      const containerVideo = playbackContainer?.querySelector?.("video");
      if (containerVideo && containerVideo.tagName === "VIDEO") {
        this.videoElements[clipKey] = containerVideo;
        return containerVideo;
      }

      this.$delete(this.videoElements, clipKey);
      return null;
    },
    applyClipMutedStateToVideo(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId) return;
      const video = this.getClipVideoElement(normalizedClipId);
      if (!video) return;
      video.muted = Boolean(this.clipMutedState[normalizedClipId]);
    },
    setActiveClipPlayback(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId) return;
      this.clipPlayback = { [normalizedClipId]: true };
    },
    getActiveClipPlaybackId() {
      return Object.keys(this.clipPlayback).find((clipId) => this.clipPlayback[clipId]) || "";
    },
    startClipPlaybackSession(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId) return 0;
      this.clipPlaybackSessionId += 1;
      this.pauseAllVideos();
      this.videoElements = {};
      this.setActiveClipPlayback(normalizedClipId);
      return this.clipPlaybackSessionId;
    },
    isCurrentClipPlaybackSession(clipId, sessionId) {
      const normalizedClipId = String(clipId || "");
      return Boolean(sessionId)
        && sessionId === this.clipPlaybackSessionId
        && normalizedClipId !== ""
        && normalizedClipId === this.getActiveClipPlaybackId();
    },
    pauseStaleClipVideo(clipId, video, sessionId) {
      if (!video) return;
      if (
        this.isCurrentClipPlaybackSession(clipId, sessionId)
        && this.isCurrentClipVideoElement(clipId, video)
      ) {
        return;
      }
      try {
        video.pause();
      } catch (_err) {
        // noop
      }
    },
    isCurrentClipVideoElement(clipId, video) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId || !video || video.tagName !== "VIDEO") return false;
      const playbackContainer = this.getClipPlaybackContainer(normalizedClipId);
      return Boolean(playbackContainer && playbackContainer.contains(video));
    },
    isClipInFullscreen(clipId) {
      const normalizedClipId = String(clipId || "");
      return normalizedClipId !== "" && normalizedClipId === String(this.fullscreenClipId || "");
    },
    getClipPlaybackContainer(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId || !this.$el || typeof this.$el.querySelector !== "function") return null;
      return this.$el.querySelector(`.video-card-playback[data-playback-clip-id="${normalizedClipId}"]`);
    },
    pauseClipVideoById(clipId) {
      const normalizedClipId = String(clipId || "");
      if (!normalizedClipId) return;
      const videos = [];
      const mappedVideo = this.getClipVideoElement(normalizedClipId);
      if (mappedVideo && mappedVideo.tagName === "VIDEO") {
        videos.push(mappedVideo);
      }
      const playbackContainer = this.getClipPlaybackContainer(normalizedClipId);
      const containerVideo = playbackContainer?.querySelector?.("video");
      if (
        containerVideo
        && containerVideo.tagName === "VIDEO"
        && !videos.includes(containerVideo)
      ) {
        videos.push(containerVideo);
      }
      videos.forEach((video) => {
        if (!video || video.paused) return;
        try {
          video.pause();
        } catch (_err) {
          // noop
        }
      });
    },
    async playClipVideoElement(clip, sessionId = this.clipPlaybackSessionId) {
      if (!clip || !clip.id) return false;
      const clipId = String(clip.id);
      if (!this.isCurrentClipPlaybackSession(clipId, sessionId)) return false;
      const video = this.getClipVideoElement(clipId);
      if (!video) return false;
      try {
        this.pauseAllVideos(video);
        if (!this.isCurrentClipPlaybackSession(clipId, sessionId)) return false;
        if (video.ended) {
          video.currentTime = 0;
        }
        await video.play();
        this.pauseStaleClipVideo(clipId, video, sessionId);
        if (!this.isCurrentClipPlaybackSession(clipId, sessionId)) return false;
        return true;
      } catch (_err) {
        return false;
      }
    },
    onClipVideoClick(clip) {
      if (!clip || !clip.id) return;
      if (this.isClipInFullscreen(clip.id)) return;
      this.toggleClipVideoPlayback(clip);
    },
    async toggleClipVideoPlayback(clip) {
      if (!clip || !clip.id) return;
      const video = this.getClipVideoElement(clip.id);
      if (!video) return;
      if (video.paused || video.ended) {
        await this.playClipVideoElement(clip);
        return;
      }
      try {
        video.pause();
      } catch (_err) {
        // noop
      }
    },
    async handleClipFullscreenAction(clip) {
      if (!clip || !clip.id) return;
      if (this.isClipInFullscreen(clip.id)) {
        await this.exitClipFullscreen();
        return;
      }
      await this.enterClipFullscreen(clip);
    },
    async enterClipFullscreen(clip) {
      if (!clip || !clip.id || !clip.playbackUrl) return;
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      const targetClipId = String(clip.id);
      const previousFullscreenClipId = String(this.fullscreenClipId || "");
      if (previousFullscreenClipId && previousFullscreenClipId !== targetClipId) {
        this.pauseClipVideoById(previousFullscreenClipId);
        if (this.clipPlayback[previousFullscreenClipId]) {
          this.$delete(this.clipPlayback, previousFullscreenClipId);
        }
      }

      const sessionId = this.startClipPlayback(clip, false);
      await this.$nextTick();
      if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;
      this.applyClipMutedStateToVideo(targetClipId);
      await this.playClipVideoElement(clip, sessionId);
      if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;

      const playbackContainer = this.getClipPlaybackContainer(targetClipId);
      if (!playbackContainer || typeof playbackContainer.requestFullscreen !== "function") return;
      try {
        await playbackContainer.requestFullscreen();
        if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;
        this.fullscreenClipId = targetClipId;
      } catch (_err) {
        // noop
      }
    },
    async exitClipFullscreen() {
      if (typeof document === "undefined") return;
      if (!document.fullscreenElement || typeof document.exitFullscreen !== "function") {
        this.fullscreenClipId = null;
        return;
      }
      try {
        await document.exitFullscreen();
      } catch (_err) {
        // noop
      }
    },
    onVideoCardFullscreenChange() {
      if (typeof document === "undefined") return;
      const previousFullscreenClipId = String(this.fullscreenClipId || "");
      const fullscreenElement = document.fullscreenElement;
      if (!fullscreenElement) {
        this.fullscreenClipId = null;
        if (previousFullscreenClipId) {
          this.$nextTick(() => {
            const video = this.getClipVideoElement(previousFullscreenClipId);
            if (!video || !video.paused) return;
            this.$delete(this.clipPlayback, previousFullscreenClipId);
          });
        }
        return;
      }

      const playbackElement = fullscreenElement.classList?.contains("video-card-playback")
        ? fullscreenElement
        : fullscreenElement.closest?.(".video-card-playback");
      const clipId = playbackElement?.getAttribute?.("data-playback-clip-id") || "";
      if (!clipId || String(clipId) !== this.getActiveClipPlaybackId()) {
        this.fullscreenClipId = null;
        return;
      }
      this.fullscreenClipId = String(clipId);
    },
    onVideoCardKeydown(event) {
      if (this.handleClipTimeEditorUndoKeydown(event)) return;
      if (!this.fullscreenClipId || !event) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.navigateFullscreenClip(-1);
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.navigateFullscreenClip(1);
      }
    },
    getFullscreenNavigableClips() {
      return (this.filteredCollectionClips || []).filter(
        (clip) => clip && clip.id && clip.playbackUrl && !this.isClipInteractionLocked(clip)
      );
    },
    getAdjacentFullscreenClip(direction) {
      const step = direction >= 0 ? 1 : -1;
      const clips = this.getFullscreenNavigableClips();
      if (!clips.length) return null;

      const currentClipId = String(this.fullscreenClipId || "");
      const currentIndex = clips.findIndex((clip) => String(clip.id) === currentClipId);
      if (currentIndex < 0) {
        return step > 0 ? clips[0] : clips[clips.length - 1];
      }

      const nextIndex = currentIndex + step;
      if (nextIndex < 0 || nextIndex >= clips.length) return null;
      return clips[nextIndex];
    },
    hasAdjacentFullscreenClip(direction) {
      return Boolean(this.getAdjacentFullscreenClip(direction));
    },
    async navigateFullscreenClip(direction) {
      if (!this.fullscreenClipId) return;
      const targetClip = this.getAdjacentFullscreenClip(direction);
      if (!targetClip) return;
      const previousFullscreenClipId = String(this.fullscreenClipId || "");
      const targetClipId = String(targetClip.id);
      if (previousFullscreenClipId && previousFullscreenClipId !== targetClipId) {
        this.pauseClipVideoById(previousFullscreenClipId);
        if (this.clipPlayback[previousFullscreenClipId]) {
          this.$delete(this.clipPlayback, previousFullscreenClipId);
        }
      }

      const sessionId = this.startClipPlayback(targetClip, false);
      await this.$nextTick();
      if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;
      this.applyClipMutedStateToVideo(targetClipId);
      await this.playClipVideoElement(targetClip, sessionId);
      if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;

      const playbackContainer = this.getClipPlaybackContainer(targetClipId);
      if (!playbackContainer || typeof playbackContainer.requestFullscreen !== "function") return;
      try {
        await playbackContainer.requestFullscreen();
        if (!this.isCurrentClipPlaybackSession(targetClipId, sessionId)) return;
        this.fullscreenClipId = targetClipId;
      } catch (_err) {
        // noop
      }
    },
    syncSelectedClipPlaybackSession() {
      const clip = this.selectedWorkspaceClip;
      if (!clip?.id || !clip?.playbackUrl || this.isClipRendering(clip)) return;
      const clipId = String(clip.id);
      if (this.getActiveClipPlaybackId() === clipId) {
        this.applyClipMutedStateToVideo(clipId);
        return;
      }
      this.startClipPlayback(clip, false);
    },
    startClipPlayback(clip, playAfterRender = true) {
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      if (!clip || !clip.id || !clip.playbackUrl) return;
      const clipId = String(clip.id);
      const sessionId = this.startClipPlaybackSession(clipId);
      if (!playAfterRender) return sessionId;
      
      // Esperar a que el DOM se actualice y luego pausar otros videos cuando se reproduzca
      this.$nextTick(() => {
        if (!this.isCurrentClipPlaybackSession(clipId, sessionId)) return;
        const video = this.getClipVideoElement(clipId);
        if (video) {
          // Asegura que solo el clip solicitado quede reproduciéndose.
          this.pauseAllVideos(video);
        }
        this.applyClipMutedStateToVideo(clipId);
        this.playClipVideoElement(clip, sessionId);
      });
      return sessionId;
    },
    isClipPlaybackActive(clipId) {
      return Boolean(this.clipPlayback[clipId]);
    },
    pauseAllVideos(excludeVideo = null) {
      const pauseVideoElement = (video, { resetTime = false } = {}) => {
        if (!video || video === excludeVideo || video.tagName !== "VIDEO") return;
        try {
          if (!video.paused) {
            video.pause();
          }
          if (resetTime) {
            video.currentTime = 0;
          }
        } catch (_err) {
          // noop
        }
      };

      Object.keys(this.videoElements).forEach((key) => {
        pauseVideoElement(this.videoElements[key]);
      });

      const cardVideos = this.$el?.querySelectorAll?.(".video-card-playback video");
      if (cardVideos && typeof cardVideos.forEach === "function") {
        cardVideos.forEach((video) => {
          pauseVideoElement(video);
        });
      }

      const previewVideos = this.$el?.querySelectorAll?.(".batch-preview-video video");
      if (previewVideos && typeof previewVideos.forEach === "function") {
        previewVideos.forEach((video) => {
          pauseVideoElement(video, { resetTime: true });
        });
      }
    },
    stopVideoCardMediaPlayback() {
      this.clipPlaybackSessionId += 1;
      if (this.fullscreenClipId) {
        this.exitClipFullscreen();
        this.fullscreenClipId = null;
      }
      this.pauseAllVideos();
      this.clipPlayback = {};
      this.$nextTick(() => {
        const cardVideos = this.$el?.querySelectorAll?.(".video-card-playback video");
        if (!cardVideos) return;
        cardVideos.forEach((video) => {
          if (!video || video.tagName !== "VIDEO") return;
          try {
            video.pause();
            video.currentTime = 0;
          } catch (_err) {
            // noop
          }
        });
      });
    },
    onClipPlay(clip, event) {
      const video = event && event.target;
      if (!video) return;
      const clipId = String(clip?.id || "");
      if (!clipId) return;

      if (this.isClipInteractionLocked(clip)) {
        try {
          video.pause();
        } catch (_err) {
          // noop
        }
        return;
      }

      if (!this.isCurrentClipVideoElement(clipId, video)) {
        this.pauseStaleClipVideo(clipId, video, 0);
        return;
      }

      this.setActiveClipPlayback(clipId);
      const clipKey = `clip-${clipId}`;
      this.videoElements[clipKey] = video;
      this.pauseAllVideos(video);
    },
    onClipPause(clip) {
      const clipId = String(clip?.id || "");
      if (!clipId) return;
      if (!this.clipPlayback[clipId]) return;

      // Mantener playback activo al pausar para permitir reanudar con click.
      this.setActiveClipPlayback(clipId);
    },
    onClipEnded(clip, event) {
      const clipId = String(clip?.id || "");
      if (!clipId) return;
      if (!this.clipPlayback[clipId]) return;

      const video = event && event.target;
      if (video && video.tagName === "VIDEO") {
        video.currentTime = 0;
      }
      this.setActiveClipPlayback(clipId);
    },
    onClipLoadedMetadata(clip, event) {
      const video = event && event.target;
      if (!video) return;
      const clipId = String(clip?.id || "");
      if (!clipId) return;

      const playbackContainer = this.getClipPlaybackContainer(clipId);
      if (!playbackContainer || !playbackContainer.contains(video)) {
        this.pauseStaleClipVideo(clipId, video, 0);
        return;
      }

      const selectedId = String(this.selectedWorkspaceClip?.id || "");
      if (
        selectedId === clipId
        && clip?.playbackUrl
        && !this.isClipRendering(clip)
      ) {
        if (this.getActiveClipPlaybackId() !== clipId) {
          this.setActiveClipPlayback(clipId);
        }
      } else if (this.getActiveClipPlaybackId() !== clipId) {
        this.pauseStaleClipVideo(clipId, video, 0);
        return;
      }

      const clipKey = `clip-${clipId}`;
      this.videoElements[clipKey] = video;
      this.applyClipMutedStateToVideo(clipId);
    },
    rebuildPostingTimelineEvents() {
      const timelineTimezone = this.postingUserTimezone || this.postingTimelineUserTimezone || "UTC";
      const currentUserName = String(this.postingTimelineUserName || "").trim();
      const rawEvents = Array.isArray(this.postingTimelineRawEvents) ? this.postingTimelineRawEvents : [];
      const calendarEvents = rawEvents
        .filter((event) => {
          if (!event?.clip_id || !event?.posting_at) return false;
          const status = this.normalizeClipStatus(event?.status);
          return (
            status === "5_scheduled_to_post"
            || status === "posted"
            || status === "6_posted"
            || status === "7_posted"
          );
        })
        .map((event) => {
          const postingDateParts = this.getPostingDatePartsByTimezone(event?.posting_at, timelineTimezone);
          const dateStr = postingDateParts.dateStr || String(event?.posting_date || "").slice(0, 10);
          if (!dateStr) return null;

          const timeStr = postingDateParts.timeStr || String(event?.posting_time || "").trim();
          const clipId = String(event?.clip_id || "");
          const sourceUsername = String(event?.source_username || "").trim();
          const rawTitle = String(event?.title || "").trim();
          const fallbackTitle = sourceUsername ? `@${sourceUsername}` : `Clip #${clipId.slice(0, 8)}`;
          const status = this.normalizeClipStatus(event?.status);

          return {
            id: `timeline-${clipId}-${String(event?.posting_at || dateStr)}`,
            title: rawTitle || fallbackTitle,
            start: dateStr,
            allDay: true,
            className: ["cycle-calendar-event"],
            extendedProps: {
              clipId,
              jobId: event?.job_id ? String(event.job_id) : null,
              postingTime: timeStr || null,
              clipStatus: status,
              isPosted: status === "posted" || status === "6_posted" || status === "7_posted",
              postingAt: event?.posting_at || null,
              sourceUsername: sourceUsername || null,
              userName: currentUserName || null,
              outputUrls: event?.output_urls || null,
            },
          };
        })
        .filter((event) => Boolean(event));

      calendarEvents.sort((a, b) => {
        const isPostedA = a.extendedProps?.isPosted ? 1 : 0;
        const isPostedB = b.extendedProps?.isPosted ? 1 : 0;
        if (isPostedA !== isPostedB) {
          return isPostedA - isPostedB;
        }
        const clipIdA = a.extendedProps?.clipId || "";
        const clipIdB = b.extendedProps?.clipId || "";
        if (clipIdA !== clipIdB) {
          return String(clipIdA).localeCompare(String(clipIdB));
        }
        const timeA = a.extendedProps?.postingTime || "";
        const timeB = b.extendedProps?.postingTime || "";
        return timeA.localeCompare(timeB);
      });

      this.events = calendarEvents;
    },
    async loadPostingTimeline({ silent = false } = {}) {
      if (!this.userId) {
        this.postingTimelineRawEvents = [];
        this.postingTimelineUserName = "";
        this.postingTimelineUserTimezone = "";
        this.events = [];
        this.postingTimelineError = "";
        this.loadingPostingTimeline = false;
        return;
      }
      if (!silent) {
        this.loadingPostingTimeline = true;
      }
      this.postingTimelineError = "";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("schedulePost.errorLoginToLoadTimeline", "Debes iniciar sesión para consultar la timeline."));
        }
        const payload = await fetchTwitchPostingTimeline(session);
        this.postingTimelineRawEvents = Array.isArray(payload?.events) ? payload.events : [];
        this.postingTimelineUserName = String(payload?.use_name || "").trim();
        this.postingTimelineUserTimezone = String(payload?.use_timezone || "").trim();
        this.rebuildPostingTimelineEvents();
      } catch (error) {
        this.postingTimelineRawEvents = [];
        this.postingTimelineUserName = "";
        this.events = [];
        this.postingTimelineError = error?.message || this.tt("schedulePost.errorLoadAdminTimeline", "No fue posible cargar la timeline para autoprogramar.");
      } finally {
        if (!silent) {
          this.loadingPostingTimeline = false;
        }
      }
    },
    async selectClip(clipId) {
      const selectedClip = this.clips.find(c => c.id === clipId);
      this.selectedClipId = clipId;
      this.loadingClipDetail = true;
      this.selectedClipDetails = null;
      
      try {
        const clip = selectedClip || this.clips.find(c => c.id === clipId);
        if (clip) {
          if (clip.job_id && this.selectedCollectionJobId !== clip.job_id) {
            this.selectedCollectionJobId = clip.job_id;
          }
          
          this.selectedClipDetails = clip;
          this.$nextTick(() => {
            this.syncSelectedClipPlaybackSession();
          });
        } else {
          throw new Error(this.tt("schedulePost.clipNotFound", "Clip no encontrado"));
        }
      } catch (err) {
        console.error('Error loading clip detail:', err);
        this.$notify({
          message: err.message || this.tt("schedulePost.loadClipDetailsError", "Error al cargar los detalles del clip"),
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        this.selectedClipId = null;
        this.selectedClipDetails = null;
      } finally {
        this.loadingClipDetail = false;
      }
    },
    goBackToClips() {
      this.selectedClipId = null;
      this.selectedClipDetails = null;
    },
    goBackToCollectionList() {
      this.selectedCollectionJobId = null;
      this.selectedClipId = null;
      this.selectedClipDetails = null;
      this.syncCollectionSelectionToRoute(null);
      if (!this.embedded && typeof window !== "undefined") {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        // Evitar problemas de zona horaria: tomar siempre solo la parte de fecha (YYYY-MM-DD)
        const match = /(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
        if (match) {
          const [, year, month, day] = match;
          const date = new Date(Number(year), Number(month) - 1, Number(day));
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
          });
        }

        // Fallback para otros formatos de fecha
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString;
        }
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        });
      } catch (e) {
        return dateString;
      }
    },
    getPostingIanaTimezone(tz) {
      const map = {
        // Use fixed offsets to match scheduling math (no DST auto-shift).
        PST: "Etc/GMT+8",
        MST: "Etc/GMT+7",
        CST: "Etc/GMT+6",
        EST: "Etc/GMT+5",
        UTC: "UTC",
      };
      return map[tz] || "UTC";
    },
    getPostingTodayInTimezoneValue(timezoneValue) {
      const tzIana = this.getPostingIanaTimezone(timezoneValue);
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: tzIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    },
    getPostingTodayInTimezone() {
      return this.getPostingTodayInTimezoneValue(this.postingUserTimezone);
    },
    getStoredPostingTimezone() {
      try {
        return window.localStorage.getItem(LOCAL_STORAGE_TWITCH_TIMEZONE_KEY) || "";
      } catch (e) {
        return "";
      }
    },
    setStoredPostingTimezone(tz) {
      try {
        if (tz) {
          window.localStorage.setItem(LOCAL_STORAGE_TWITCH_TIMEZONE_KEY, tz);
        } else {
          window.localStorage.removeItem(LOCAL_STORAGE_TWITCH_TIMEZONE_KEY);
        }
      } catch (e) {
        // ignore storage errors
      }
    },
    refreshPostingTimezonePreviewTimes() {
      const now = new Date();
      const nextPreviewTimes = {};
      ["EST", "PST", "CST", "MST", "UTC"].forEach((timezoneAbbreviation) => {
        nextPreviewTimes[timezoneAbbreviation] = new Intl.DateTimeFormat("en-US", {
          timeZone: this.getPostingIanaTimezone(timezoneAbbreviation),
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(now);
      });
      this.postingTimezonePreviewTimes = nextPreviewTimes;
    },
    onPostingTimezoneChange() {
      if (!this.postingSelectedTimezone) return;
      this.postingUserTimezone = this.postingSelectedTimezone;
      this.postingTimelineStartDate = this.getTimelineWindowStartDate();
      {
        const { minIso, todayIso } = this.getMainTimelineAllowedRange();
        this.timelineStartDate = minIso;
        this.timelineVisibleMonthIso = todayIso;
        this.mainTimelineInitialScrollPending = true;
      }
      this.setStoredPostingTimezone(this.postingSelectedTimezone);
      this.rebuildPostingTimelineEvents();
      this.scheduleTimelineLayoutRefresh();
    },
    parsePostingAtDate(postingAt) {
      if (!postingAt) return null;
      const raw = String(postingAt).trim();
      if (!raw) return null;
      const normalized = raw
        .replace(" ", "T")
        .replace(/([+-]\d{2})$/, "$1:00");
      const date = new Date(normalized);
      if (!isNaN(date.getTime())) return date;
      const fallback = new Date(raw);
      return !isNaN(fallback.getTime()) ? fallback : null;
    },
    getPostingDatePartsByTimezone(postingAt, timezoneValue) {
      const dateObj = this.parsePostingAtDate(postingAt);
      if (!dateObj) return { dateStr: "", timeStr: "" };
      const tzIana = this.getPostingIanaTimezone(timezoneValue);
      const dateStr = new Intl.DateTimeFormat("en-CA", {
        timeZone: tzIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(dateObj);
      const timeStr = new Intl.DateTimeFormat("en-GB", {
        timeZone: tzIana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(dateObj);
      return { dateStr, timeStr };
    },
    getPostingDateParts(postingAt) {
      return this.getPostingDatePartsByTimezone(postingAt, this.postingUserTimezone);
    },
    async loadPostingTimezone() {
      const storedTimezone = this.getStoredPostingTimezone();
      if (storedTimezone) {
        this.postingUserTimezone = storedTimezone;
        this.postingTimelineStartDate = this.getTimelineWindowStartDate();
        return;
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          this.postingUserTimezone = "";
          this.postingTimelineStartDate = this.getTimelineWindowStartDate();
          return;
        }
        const currentUserStore = await this.syncCurrentUserContext(session);
        this.postingUserTimezone = currentUserStore.timezone || "";
        this.postingTimelineStartDate = this.getTimelineWindowStartDate();
      } catch (e) {
        this.postingUserTimezone = "";
        this.postingTimelineStartDate = this.getTimelineWindowStartDate();
      }
    },
    getPlatformLabel(platform) {
      const labels = {
        instagram: "Instagram",
        tiktok: "TikTok",
        youtube: "YouTube",
        facebook: "Facebook",
        x: "X (Twitter)",
      };
      return labels[platform] || platform;
    },
    formatRelativeTime(value) {
      if (!value) return "";
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const now = new Date();
      const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
      const absSeconds = Math.abs(diffSeconds);
      let unit = "second";
      let amount = diffSeconds;

      if (absSeconds >= 60 && absSeconds < 3600) {
        unit = "minute";
        amount = Math.round(diffSeconds / 60);
      } else if (absSeconds >= 3600 && absSeconds < 86400) {
        unit = "hour";
        amount = Math.round(diffSeconds / 3600);
      } else if (absSeconds >= 86400 && absSeconds < 604800) {
        unit = "day";
        amount = Math.round(diffSeconds / 86400);
      } else if (absSeconds >= 604800 && absSeconds < 2592000) {
        unit = "week";
        amount = Math.round(diffSeconds / 604800);
      } else if (absSeconds >= 2592000) {
        unit = "month";
        amount = Math.round(diffSeconds / 2592000);
      }

      const displayAmount = Math.abs(amount);
      const suffix = amount < 0 ? "hace" : "en";
      if (unit === "second") return `${displayAmount}s ${suffix}`;
      if (unit === "minute") return `${displayAmount}m ${suffix}`;
      if (unit === "hour") return `${displayAmount}h ${suffix}`;
      if (unit === "day") return `${displayAmount}d ${suffix}`;
      if (unit === "week") return `${displayAmount}sem ${suffix}`;
      return `${displayAmount}mes ${suffix}`;
    },
    getCollectionRelativeTime(collection) {
      if (!collection) return "";
      const raw = collection.jobCreatedAt || collection.jobUpdatedAt;
      return this.formatRelativeTime(raw);
    },
    getClipDurationShort(clip) {
      const start = Number(clip?.startSeconds ?? clip?.start_seconds ?? NaN);
      const end = Number(clip?.endSeconds ?? clip?.end_seconds ?? NaN);
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        return `${Math.round(end - start)}s`;
      }
      const label = String(clip?.durationLabel || "").trim();
      if (!label) return "";
      const parts = label.split(":").map((part) => Number(part));
      if (parts.some((part) => Number.isNaN(part))) return label;
      if (parts.length === 3) {
        return `${parts[0] * 3600 + parts[1] * 60 + parts[2]}s`;
      }
      if (parts.length === 2) {
        return `${parts[0] * 60 + parts[1]}s`;
      }
      return label;
    },
    /** Iconos locales del proyecto (SVG en assets/img/platform-icons). */
    getPlatformLogoUrl(platform) {
      const key = String(platform || "").toLowerCase();
      return WORKSPACE_PLATFORM_ICON_URLS[key] || "";
    },
    isWorkspaceMetadataEditable(clip) {
      return Boolean(clip) && !this.isDemoClip(clip) && !this.isClipInteractionLocked(clip);
    },
    hydrateWorkspaceMetadataDraft(clip) {
      if (!clip) {
        this.workspaceMetadataDraft = {
          clipId: null,
          title: "",
          description: "",
          hashtags: "",
        };
        this.workspaceHashtagTags = [];
        this.workspaceHashtagInput = "";
        this.workspaceMetadataBaseline = "";
        return;
      }
      const draft = this.buildClipMetadataDraft(clip);
      this.workspaceMetadataDraft = {
        clipId: String(clip.id),
        title: draft.title || "",
        description: draft.description || "",
        hashtags: this.formatHashtagsForInput(draft.hashtags || ""),
      };
      this.setWorkspaceHashtagEditor(this.workspaceMetadataDraft.hashtags);
      this.syncWorkspaceMetadataBaseline();
    },
    setWorkspaceHashtagEditor(text) {
      this.workspaceHashtagTags = this.parseHashtagList(text);
      this.workspaceHashtagInput = "";
      this.syncWorkspaceHashtagsDraft();
    },
    syncWorkspaceHashtagsDraft() {
      this.workspaceMetadataDraft.hashtags = [
        ...this.workspaceHashtagTags.map((tag) => `#${tag}`),
        this.workspaceHashtagInput,
      ]
        .filter(Boolean)
        .join(" ");
    },
    getWorkspaceHashtagInputMaxLength() {
      const committedTagsLength = this.workspaceHashtagTags
        .map((tag) => `#${tag}`)
        .join(" ").length;
      return Math.max(0, 200 - committedTagsLength - (committedTagsLength ? 1 : 0));
    },
    focusWorkspaceHashtagInput() {
      this.$refs.workspaceHashtagInput.focus();
    },
    commitWorkspaceHashtagInput() {
      const tags = this.parseHashtagList(this.workspaceHashtagInput);
      const existingTags = new Set(
        this.workspaceHashtagTags.map((tag) => tag.toLowerCase())
      );
      const duplicateTags = tags.filter((tag) => existingTags.has(tag.toLowerCase()));
      if (tags.length) {
        this.workspaceHashtagTags.push(...tags);
      }
      this.workspaceHashtagInput = "";
      this.syncWorkspaceHashtagsDraft();
      if (!duplicateTags.length) return;

      setTimeout(() => {
        const duplicateTagKeys = new Set(
          duplicateTags.map((tag) => tag.toLowerCase())
        );
        const seenDuplicateTags = new Set();
        this.workspaceHashtagTags = this.workspaceHashtagTags.filter((tag) => {
          const tagKey = tag.toLowerCase();
          if (!duplicateTagKeys.has(tagKey)) return true;
          if (seenDuplicateTags.has(tagKey)) return false;
          seenDuplicateTags.add(tagKey);
          return true;
        });
        this.syncWorkspaceHashtagsDraft();
        this.showClipActionSnackbar(
          this.tt("schedulePost.tagsDuplicate", "El hashtag ya existe."),
          "info"
        );
      }, 300);
    },
    onWorkspaceHashtagKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.commitWorkspaceHashtagInput();
        return;
      }
      if (event.key !== "Backspace" || this.workspaceHashtagInput || !this.workspaceHashtagTags.length) {
        return;
      }
      event.preventDefault();
      const previousTag = this.workspaceHashtagTags.pop();
      this.workspaceHashtagInput = previousTag.slice(1);
      this.syncWorkspaceHashtagsDraft();
    },
    onWorkspaceHashtagEditorBlur() {
      this.commitWorkspaceHashtagInput();
      this.onWorkspaceMetadataBlur("hashtags");
    },
    getWorkspaceMetadataSnapshot() {
      return {
        clipId: String(this.workspaceMetadataDraft.clipId || ""),
        title: String(this.workspaceMetadataDraft.title || ""),
        description: String(this.workspaceMetadataDraft.description || ""),
        hashtags: String(this.workspaceMetadataDraft.hashtags || ""),
      };
    },
    isWorkspaceMetadataDirty() {
      return this.workspaceMetadataBaseline !== JSON.stringify(this.getWorkspaceMetadataSnapshot());
    },
    syncWorkspaceMetadataBaseline() {
      this.workspaceMetadataBaseline = JSON.stringify(this.getWorkspaceMetadataSnapshot());
    },
    patchClipColumnsInLocalState(clipId, patch = {}) {
      const normalizedId = String(clipId || "");
      if (!normalizedId || !Array.isArray(this.clips)) return;
      const index = this.clips.findIndex((clip) => String(clip?.id) === normalizedId);
      if (index < 0) return;
      const current = this.clips[index];
      this.$set(this.clips, index, {
        ...current,
        title: patch.title ?? current.title,
        clipTitle: patch.title ?? current.clipTitle,
        clip_description: patch.description ?? current.clip_description,
        clip_tags: patch.clip_tags ?? current.clip_tags,
      });
    },
    async saveWorkspaceClipColumns({ clip = null, silent = false } = {}) {
      const targetClip = clip || this.selectedWorkspaceClip;
      if (!targetClip || !this.isWorkspaceMetadataEditable(targetClip)) return;
      if (String(this.workspaceMetadataDraft.clipId || "") !== String(targetClip.id || "")) return;
      if (!this.isWorkspaceMetadataDirty()) return;
      if (this.workspaceMetadataSaving) return;

      try {
        this.workspaceMetadataSaving = true;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("schedulePost.errorLoginToSaveMetadata", "Debes iniciar sesión para guardar los metadatos."));
        }

        const title = String(this.workspaceMetadataDraft.title || "").trim();
        const description = String(this.workspaceMetadataDraft.description || "").trim();
        const hashtags = this.normalizeHashtagsInput(this.workspaceMetadataDraft.hashtags);
        this.workspaceMetadataDraft.hashtags = hashtags;
        this.setWorkspaceHashtagEditor(hashtags);
        const clipTags = this.parseHashtagList(hashtags);

        await updateTwitchClip(session, targetClip.id, {
          title,
          clip_description: description,
          clip_tags: clipTags,
        });

        this.patchClipColumnsInLocalState(targetClip.id, {
          title,
          description,
          clip_tags: clipTags,
        });
        this.syncWorkspaceMetadataBaseline();
      } catch (error) {
        if (!silent) {
          this.showClipActionSnackbar(
            error?.message || this.tt("schedulePost.errorSavingMetadata", "Error al guardar los metadatos."),
            "info"
          );
        } else {
          console.warn("No se pudo guardar columnas del workspace:", error);
        }
      } finally {
        this.workspaceMetadataSaving = false;
      }
    },
    applyWorkspaceMetadataToClipDraft() {
      if (!this.clipMetadataDraft) return;
      if (String(this.workspaceMetadataDraft.clipId) !== String(this.selectedClipForSchedule?.id || "")) return;
      this.clipMetadataDraft.title = this.workspaceMetadataDraft.title;
      this.clipMetadataDraft.description = this.workspaceMetadataDraft.description;
      this.clipMetadataDraft.hashtags = this.normalizeHashtagsInput(this.workspaceMetadataDraft.hashtags);
    },
    syncWorkspaceMetadataForSchedule() {
      this.workspaceMetadataDraft.hashtags = this.normalizeHashtagsInput(this.workspaceMetadataDraft.hashtags);
      this.setWorkspaceHashtagEditor(this.workspaceMetadataDraft.hashtags);
      this.applyWorkspaceMetadataToClipDraft();
    },
    onWorkspaceMetadataBlur(field) {
      if (field === "hashtags") {
        this.workspaceMetadataDraft.hashtags = this.normalizeHashtagsInput(
          this.workspaceMetadataDraft.hashtags
        );
        this.setWorkspaceHashtagEditor(this.workspaceMetadataDraft.hashtags);
      }
      this.saveWorkspaceClipColumns({ silent: true });
    },
    parseCommaList(text) {
      if (!text || typeof text !== "string") return [];
      return text
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },
    parseHashtagList(text) {
      if (Array.isArray(text)) {
        const tags = [];
        const seen = new Set();
        text.forEach((item) => {
          const normalized = String(item || "").trim().replace(/^#+/, "");
          if (!normalized) return;
          const key = normalized.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          tags.push(normalized);
        });
        return tags;
      }
      if (!text || typeof text !== "string") return [];
      const raw = text.trim();
      if (!raw) return [];

      const segments = raw.includes("#")
        ? raw.split("#")
        : raw.includes(",")
          ? this.parseCommaList(raw)
          : [raw];
      const tags = [];
      const seen = new Set();
      segments.forEach((segment) => {
        const part = String(segment || "").replace(/,/g, " ").trim();
        if (!part) return;
        const normalized = part.replace(/^#+/, "").replace(/\s+/g, " ").trim();
        if (!normalized) return;
        const key = normalized.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        tags.push(normalized);
      });
      return tags;
    },
    formatHashtagsForInput(value) {
      return this.parseHashtagList(value)
        .map((tag) => `#${tag}`)
        .join(" ");
    },
    normalizeHashtagsInput(text) {
      return this.formatHashtagsForInput(text);
    },
    normalizeClipTagsText(clipTags) {
      if (Array.isArray(clipTags)) {
        return this.formatHashtagsForInput(clipTags);
      }
      if (typeof clipTags !== "string") return "";
      const trimmed = clipTags.trim();
      if (!trimmed) return "";
      const withoutBraces = trimmed.replace(/^\{/, "").replace(/\}$/, "");
      return this.formatHashtagsForInput(withoutBraces);
    },
    composeDescriptionWithHashtags(descriptionText, hashtagsText) {
      const description = String(descriptionText || "").trim();
      const hashtags = this.parseHashtagList(hashtagsText)
        .map((tag) => `#${tag}`)
        .join(" ")
        .trim();
      if (description && hashtags) return `${description}\n\n${hashtags}`;
      if (hashtags) return hashtags;
      return description;
    },
    resolveYoutubeCategoryIdFromLayoutAnal(layoutAnal) {
      let parsed = layoutAnal;
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = null;
        }
      }
      if (!parsed || typeof parsed !== "object") return "";
      if (parsed.isGameplay === true) return "20";
      if (parsed.isGameplay === false) return "22";
      return "";
    },
    async syncYoutubeCategoryIdFromCurrentClip(clipId) {
      if (!clipId || !this.clipMetadataDraft) return;
      try {
        const clip = this.clips.find((item) => String(item?.id) === String(clipId));
        const categoryId = this.resolveYoutubeCategoryIdFromLayoutAnal(
          clip && clip.layout_anal
        );
        if (categoryId) {
          this.clipMetadataDraft.platform_metadata.youtube.categoryId = categoryId;
        }
      } catch (e) {
        console.warn("Unexpected error resolving youtube categoryId:", e);
      }
    },
    buildClipMetadataDraft(clip) {
      let raw = (clip && clip.clip_metadata) || {};
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          raw = {};
        }
      }
      const platforms =
        Array.isArray(raw.platforms) && raw.platforms.length > 0
          ? raw.platforms
          : [...this.metadataPlatforms];
      const platformMetadata = raw.platform_metadata || {};
      const igMeta = platformMetadata.instagram || {};
      const ttMeta = platformMetadata.tiktok || {};
      const ytMeta = platformMetadata.youtube || {};
      const fbMeta = platformMetadata.facebook || {};
      const xMeta = platformMetadata.x || platformMetadata.twitter || {};
      const clipTagsText = this.normalizeClipTagsText(clip && clip.clip_tags);
      const resolvedTitle =
        (clip &&
          (clip.title || clip.clip_title || clip.name || clip.clip_name)) ||
        raw.title ||
        (clip ? `twitch-clip-${clip.id}` : "");
      const resolvedDescription =
        (clip &&
          (clip.clip_description ||
            clip.clip_desc ||
            clip.description ||
            clip.reason)) ||
        raw.description ||
        "";
      const clipLanguage = String((clip && clip.clip_lenguage) || "").trim();

      return {
        title: resolvedTitle,
        description: resolvedDescription,
        hashtags: this.formatHashtagsForInput(clipTagsText || ""),
        first_comment: raw.first_comment || "",
        scheduled_date: raw.scheduled_date || "",
        timezone: raw.timezone || "",
        async_upload: Boolean(raw.async_upload),
        platforms,
        platform_metadata: {
          instagram: {
            instagram_title: igMeta.instagram_title || "",
            media_type: "REELS",
            share_to_feed: true,
            collaboratorsText: Array.isArray(igMeta.collaborators)
              ? igMeta.collaborators.join(", ")
              : (igMeta.collaborators || ""),
            cover_url: igMeta.cover_url || "",
            audio_name: igMeta.audio_name || "",
            user_tagsText: "",
            location_id: igMeta.location_id || "",
            thumb_offset:
              (igMeta.thumb_offset ?? "").toString().trim() || "2000",
          },
          tiktok: {
            tiktok_title: ttMeta.tiktok_title || "",
            privacy_level: ttMeta.privacy_level || "",
            disable_duet: Boolean(ttMeta.disable_duet),
            disable_comment: Boolean(ttMeta.disable_comment),
            disable_stitch: Boolean(ttMeta.disable_stitch),
            post_mode: ttMeta.post_mode || "DIRECT_POST",
            cover_timestamp:
              (ttMeta.cover_timestamp ?? "").toString().trim() || "2000",
            brand_content_toggle: Boolean(ttMeta.brand_content_toggle),
            brand_organic_toggle: Boolean(ttMeta.brand_organic_toggle),
            is_aigc: Boolean(ttMeta.is_aigc),
          },
          youtube: {
            tagsText: Array.isArray(ytMeta.tags)
              ? ytMeta.tags.join(", ")
              : (ytMeta.tags || clipTagsText),
            categoryId:
              this.resolveYoutubeCategoryIdFromLayoutAnal(
                clip && clip.layout_anal
              ) ||
              ytMeta.categoryId ||
              "",
            privacyStatus: ytMeta.privacyStatus || "",
            thumbnail_url: ytMeta.thumbnail_url || "",
            embeddable: Boolean(ytMeta.embeddable),
            license: ytMeta.license || "",
            publicStatsViewable: Boolean(ytMeta.publicStatsViewable),
            selfDeclaredMadeForKids: Boolean(ytMeta.selfDeclaredMadeForKids),
            containsSyntheticMedia: Boolean(ytMeta.containsSyntheticMedia),
            defaultLanguage: ytMeta.defaultLanguage || clipLanguage || "",
            defaultAudioLanguage: ytMeta.defaultAudioLanguage || "",
            allowedCountriesText: Array.isArray(ytMeta.allowedCountries)
              ? ytMeta.allowedCountries.join(", ")
              : (ytMeta.allowedCountries || ""),
            blockedCountriesText: Array.isArray(ytMeta.blockedCountries)
              ? ytMeta.blockedCountries.join(", ")
              : (ytMeta.blockedCountries || ""),
            hasPaidProductPlacement: Boolean(ytMeta.hasPaidProductPlacement),
            recordingDate: ytMeta.recordingDate || "",
          },
          facebook: {
            facebook_page_id: fbMeta.facebook_page_id || "",
            call_to_action: fbMeta.call_to_action || "",
            link: fbMeta.link || "",
            facebook_description: fbMeta.facebook_description || "",
            thumbnail_url: fbMeta.thumbnail_url || "",
            audience_restrictions: fbMeta.audience_restrictions || "",
            publish_to_story: Boolean(fbMeta.publish_to_story),
          },
          x: {
            x_title: xMeta.x_title || "",
            reply_settings: xMeta.reply_settings || "",
            thread_mode: xMeta.thread_mode || "",
            quote_url: xMeta.quote_url || "",
            poll_optionsText: Array.isArray(xMeta.poll_options)
              ? xMeta.poll_options.join(", ")
              : (xMeta.poll_options || ""),
            media_tagged_usersText: Array.isArray(xMeta.media_tagged_users)
              ? xMeta.media_tagged_users.join(", ")
              : (xMeta.media_tagged_users || ""),
            sensitive_content: Boolean(xMeta.sensitive_content),
          },
        },
      };
    },
    handleScheduleNetworkRowClick(platform) {
      if (!platform || !this.showScheduleModal) return;
      if (this.scheduleConnectionsLoading || this.scheduleConnectOpening) return;
      if (!this.isSchedulePlatformConnected(platform)) {
        this.openScheduleConnectionPage(platform);
        return;
      }
      this.toggleSchedulePlatform(platform);
    },
    openScheduleConnectPromptModal() {
      if (!this.showScheduleModal) return;
      this.showScheduleConnectPromptModal = true;
    },
    closeScheduleConnectPromptModal() {
      this.showScheduleConnectPromptModal = false;
    },
    async goToSocialConnectionsSettings() {
      this.showScheduleConnectPromptModal = false;
      if (!this.$router) return;
      try {
        await this.$router.push({ path: "/user-profile" });
      } catch (error) {
        const message = String(error?.message || "");
        if (!message.includes("NavigationDuplicated")) {
          this.$notify({
            message: `${this.tt("common.error", "Error")}: ${message || this.tt("schedulePost.errorOpenSettings", "No se pudo abrir configuración.")}`,
            type: "danger",
          });
        }
      }
    },
    async openScheduleConnectionPage(platform) {
      const normalizedPlatform = this.normalizeSocialPlatformKey(platform);
      if (!this.metadataPlatforms.includes(normalizedPlatform)) return;
      if (this.scheduleConnectOpening) return;
      const canOpenSocialConnect = await this.resolveScheduleSubscriptionGate();
      if (!canOpenSocialConnect) {
        this.openScheduleSubscriptionPromptModal();
        return;
      }

      this.scheduleConnectingPlatform = normalizedPlatform;
      this.scheduleConnectOpening = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const routePath = this.$route?.fullPath || "/schedule-collections";
        const redirectUrl = `${origin}${routePath}`;

        const response = await fetch(`${this.getApiUrl()}/momo/social/connect`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            redirect_url: redirectUrl,
            redirect_button_text: this.tt("schedulePost.returnToSchedule", "Volver a programación"),
            platforms: [normalizedPlatform],
            show_calendar: true,
          }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(
            errData?.detail
            || response.statusText
            || this.tt("schedulePost.errorGenerateConnectionLink", "No se pudo generar el enlace de conexión")
          );
        }

        const data = await response.json();
        if (data?.url) {
          window.open(data.url, "_blank");
        }

        this.$notify({
          message: this.tt("userProfile.connectionPageOpened", "Se abrió la página de conexión"),
          type: "info",
        });

        setTimeout(() => {
          this.fetchSchedulePlatformConnections().catch((error) => {
            console.warn("No se pudo refrescar conexiones luego de abrir social connect:", error);
          });
        }, 2000);
      } catch (error) {
        console.error(error);
        this.$notify({
          message: `${this.tt("common.error", "Error")}: ${error?.message || this.tt("schedulePost.errorOpenSocialConnection", "No se pudo abrir la conexión social.")}`,
          type: "danger",
        });
      } finally {
        this.scheduleConnectOpening = false;
        this.scheduleConnectingPlatform = null;
      }
    },
    toggleSchedulePlatform(platform) {
      if (!this.clipMetadataDraft || !platform) return;
      if (!this.isSchedulePlatformConnected(platform)) {
        this.openScheduleConnectionPage(platform);
        return;
      }
      const d = this.clipMetadataDraft;
      if (d.platforms.includes(platform)) {
        d.platforms = d.platforms.filter((p) => p !== platform);
        return;
      }
      const selected = new Set(d.platforms);
      selected.add(platform);
      d.platforms = this.metadataPlatforms.filter((p) => selected.has(p));
      this.activePlatformTab = platform;
    },
    buildClipMetadataPayload(draftOverride = null, clipOverride = null) {
      const draft = draftOverride || this.clipMetadataDraft;
      if (!draft) return null;
      const clipRef = clipOverride || this.selectedClipForSchedule || this.selectedWorkspaceClip;
      const selectedPlatforms = Array.isArray(draft.platforms) ? draft.platforms : [];
      const descriptionWithHashtags = this.composeDescriptionWithHashtags(
        draft.description,
        draft.hashtags
      );
      const syncGeneralMetadata = this.scheduleEditingExistingClip === true;
      const globalDescription = draft.description || "";
      const instagramTitleRaw = draft.platform_metadata.instagram.instagram_title || "";
      const instagramTitle =
        selectedPlatforms.includes("instagram") && (syncGeneralMetadata || !instagramTitleRaw.trim())
          ? descriptionWithHashtags
          : instagramTitleRaw;
      const tiktokTitleRaw = draft.platform_metadata.tiktok.tiktok_title || "";
      const tiktokTitle =
        selectedPlatforms.includes("tiktok") && (syncGeneralMetadata || !tiktokTitleRaw.trim())
          ? descriptionWithHashtags
          : tiktokTitleRaw;
      const facebookDescriptionRaw =
        draft.platform_metadata.facebook.facebook_description || "";
      const facebookDescription =
        selectedPlatforms.includes("facebook") && (syncGeneralMetadata || !facebookDescriptionRaw.trim())
          ? descriptionWithHashtags
          : facebookDescriptionRaw;
      const xTitleRaw = draft.platform_metadata.x.x_title || "";
      const xTitle =
        selectedPlatforms.includes("x") && (syncGeneralMetadata || !xTitleRaw.trim())
          ? descriptionWithHashtags
          : xTitleRaw;
      const inferredYoutubeCategoryId = this.resolveYoutubeCategoryIdFromLayoutAnal(
        clipRef && clipRef.layout_anal
      );
      return {
        title: draft.title || "",
        description: globalDescription,
        hashtags: this.formatHashtagsForInput(draft.hashtags || ""),
        first_comment: draft.first_comment || "",
        scheduled_date: draft.scheduled_date || "",
        timezone: draft.timezone || "",
        async_upload: Boolean(draft.async_upload),
        platforms: selectedPlatforms,
        platform_metadata: {
          instagram: {
            instagram_title: instagramTitle,
            media_type: "REELS",
            share_to_feed: true,
            collaborators: this.parseCommaList(
              draft.platform_metadata.instagram.collaboratorsText
            ),
            cover_url: draft.platform_metadata.instagram.cover_url || "",
            audio_name: draft.platform_metadata.instagram.audio_name || "",
            user_tags: [],
            location_id: draft.platform_metadata.instagram.location_id || "",
            thumb_offset:
              (draft.platform_metadata.instagram.thumb_offset ?? "")
                .toString()
                .trim() || "2000",
          },
          tiktok: {
            tiktok_title: tiktokTitle,
            privacy_level: draft.platform_metadata.tiktok.privacy_level || "",
            disable_duet: Boolean(draft.platform_metadata.tiktok.disable_duet),
            disable_comment: Boolean(draft.platform_metadata.tiktok.disable_comment),
            disable_stitch: Boolean(draft.platform_metadata.tiktok.disable_stitch),
            post_mode: draft.platform_metadata.tiktok.post_mode || "DIRECT_POST",
            cover_timestamp:
              (draft.platform_metadata.tiktok.cover_timestamp ?? "")
                .toString()
                .trim() || "2000",
            brand_content_toggle: Boolean(
              draft.platform_metadata.tiktok.brand_content_toggle
            ),
            brand_organic_toggle: Boolean(
              draft.platform_metadata.tiktok.brand_organic_toggle
            ),
            is_aigc: Boolean(draft.platform_metadata.tiktok.is_aigc),
          },
          youtube: {
            tags: syncGeneralMetadata
              ? this.parseHashtagList(draft.hashtags)
              : this.parseHashtagList(draft.platform_metadata.youtube.tagsText),
            categoryId:
              draft.platform_metadata.youtube.categoryId ||
              inferredYoutubeCategoryId ||
              "",
            privacyStatus: draft.platform_metadata.youtube.privacyStatus || "",
            thumbnail_url: draft.platform_metadata.youtube.thumbnail_url || "",
            embeddable: Boolean(draft.platform_metadata.youtube.embeddable),
            license: draft.platform_metadata.youtube.license || "",
            publicStatsViewable: Boolean(
              draft.platform_metadata.youtube.publicStatsViewable
            ),
            selfDeclaredMadeForKids: Boolean(
              draft.platform_metadata.youtube.selfDeclaredMadeForKids
            ),
            containsSyntheticMedia: Boolean(
              draft.platform_metadata.youtube.containsSyntheticMedia
            ),
            defaultLanguage: draft.platform_metadata.youtube.defaultLanguage || "",
            defaultAudioLanguage:
              draft.platform_metadata.youtube.defaultAudioLanguage || "",
            allowedCountries: this.parseCommaList(
              draft.platform_metadata.youtube.allowedCountriesText
            ),
            blockedCountries: this.parseCommaList(
              draft.platform_metadata.youtube.blockedCountriesText
            ),
            hasPaidProductPlacement: Boolean(
              draft.platform_metadata.youtube.hasPaidProductPlacement
            ),
            recordingDate: draft.platform_metadata.youtube.recordingDate || "",
          },
          facebook: {
            facebook_page_id: draft.platform_metadata.facebook.facebook_page_id || "",
            call_to_action: draft.platform_metadata.facebook.call_to_action || "",
            link: draft.platform_metadata.facebook.link || "",
            facebook_description: facebookDescription,
            thumbnail_url: draft.platform_metadata.facebook.thumbnail_url || "",
            audience_restrictions:
              draft.platform_metadata.facebook.audience_restrictions || "",
            publish_to_story: Boolean(draft.platform_metadata.facebook.publish_to_story),
          },
          x: {
            x_title: xTitle,
            reply_settings: draft.platform_metadata.x.reply_settings || "",
            thread_mode: draft.platform_metadata.x.thread_mode || "",
            quote_url: draft.platform_metadata.x.quote_url || "",
            poll_options: this.parseCommaList(
              draft.platform_metadata.x.poll_optionsText
            ),
            media_tagged_users: this.parseCommaList(
              draft.platform_metadata.x.media_tagged_usersText
            ),
            sensitive_content: Boolean(draft.platform_metadata.x.sensitive_content),
          },
        },
      };
    },
    normalizeJobStatus(value) {
      return String(value || "").toLowerCase().trim();
    },
    shouldForceExpiredStatus(status, errorCode = null, errorMessage = null) {
      const normalizedStatus = this.normalizeJobStatus(status);
      if (normalizedStatus === "expired") return true;

      const normalizedErrorCode = this.normalizeJobErrorIdentifier(errorCode);
      const normalizedErrorMessage = this.normalizeJobErrorIdentifier(errorMessage);
      const exactCodes = [
        "vod_not_found",
        "twitch_vod_not_found",
        "job_not_found",
        "stream_not_found",
        "vod_expired",
      ];

      if (exactCodes.includes(normalizedErrorCode) || exactCodes.includes(normalizedErrorMessage)) {
        return true;
      }

      if (
        normalizedErrorCode.includes("vod_not_found") ||
        normalizedErrorMessage.includes("vod_not_found") ||
        normalizedErrorCode.includes("job_not_found") ||
        normalizedErrorMessage.includes("job_not_found")
      ) {
        return true;
      }

      const rawErrorMessage = String(errorMessage || "").toLowerCase();
      const hasNotFoundPhrase =
        rawErrorMessage.includes("not found") ||
        rawErrorMessage.includes("no se encontro") ||
        rawErrorMessage.includes("no se encontró");
      const hasVodContext =
        rawErrorMessage.includes("vod") ||
        rawErrorMessage.includes("stream") ||
        rawErrorMessage.includes("job");

      if (hasNotFoundPhrase && hasVodContext) return true;
      if (rawErrorMessage.includes("404") && hasVodContext) return true;

      return false;
    },
    matchesScheduleVodStatusFilter(collection, filterValue = null) {
      const normalizedFilter = String(filterValue || "").trim().toLowerCase();
      if (!normalizedFilter) return true;
      if (!collection) return false;

      const normalizedStatus = this.normalizeJobStatus(collection.jobStatus);
      const errorCode = collection.jobErrorCode;
      const errorMessage = collection.jobErrorMessage;

      if (normalizedFilter === "insufficient_tokens") {
        return this.isJobInsufficientTokens(normalizedStatus, errorCode, errorMessage);
      }
      if (normalizedFilter === "failed") {
        return this.isJobFailed(normalizedStatus)
          || normalizedStatus === "expired"
          || this.isJobInsufficientTokens(normalizedStatus, errorCode, errorMessage);
      }
      return normalizedStatus === normalizedFilter;
    },
    isJobInsufficientTokens(status, errorCode, errorMessage = null) {
      const normalizedErrorCode = this.normalizeJobErrorIdentifier(errorCode);
      const normalizedErrorMessage = this.normalizeJobErrorIdentifier(errorMessage);
      const hasInsufficientTokensError =
        normalizedErrorCode === "insufficient_tokens" ||
        normalizedErrorMessage === "insufficient_tokens";
      if (!hasInsufficientTokensError) return false;

      const normalizedStatus = this.normalizeJobStatus(status);
      if (!normalizedStatus) return true;
      return this.isJobFailed(normalizedStatus);
    },
    isJobFailed(status) {
      const normalizedStatus = this.normalizeJobStatus(status);
      return (
        normalizedStatus === "2.1_failed" ||
        normalizedStatus === "2_failed" ||
        normalizedStatus === "failed" ||
        normalizedStatus.endsWith("_failed")
      );
    },
    normalizeJobErrorIdentifier(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_")
        .replace(/_+/g, "_");
    },
    jobFailureLegend(errorMessage, errorCode = null) {
      const normalizedErrorMessage = this.normalizeJobErrorIdentifier(errorMessage);
      const normalizedErrorCode = this.normalizeJobErrorIdentifier(errorCode);
      const normalizedIdentifier = normalizedErrorCode || normalizedErrorMessage;
      if (!normalizedIdentifier) return "";
      if (
        normalizedErrorCode === "insufficient_tokens" ||
        normalizedErrorMessage === "insufficient_tokens"
      ) {
        return this.tt("vodClips.statusInsufficientTokens", "Tokens insuficientes");
      }
      if (
        normalizedErrorCode === "no_speech_intro_scan" ||
        normalizedErrorMessage === "no_speech_intro_scan"
      ) {
        return this.tt("vodClips.failureNoSpeechIntroScan", "No se detecto voz");
      }
      if (
        normalizedErrorCode === "source_video_too_short" ||
        normalizedErrorMessage === "source_video_too_short"
      ) {
        return this.tt(
          "vodClips.failureSourceVideoTooShort",
          "El stream/video es demasiado corto, intenta con uno más largo"
        );
      }
      if (
        normalizedErrorCode === "source_subscriber_only" ||
        normalizedErrorMessage === "source_subscriber_only"
      ) {
        return this.tt("vodClips.failureSourceSubscriberOnly");
      }
      return String(errorMessage || errorCode || "");
    },
    collectionFailureLegend(collection) {
      if (!collection) return "";
      if (
        this.normalizeJobErrorIdentifier(collection.jobErrorCode) ===
        "transient_antibot_block"
      ) {
        const platform = String(collection.sourceType || "").toLowerCase() === "youtube"
          ? "YouTube"
          : String(collection.sourceType || "la plataforma");
        if (collection.refundStatus === "pending") {
          const availableAt = new Date(collection.refundAvailableAt || "").getTime();
          const remainingSeconds = Number.isFinite(availableAt)
            ? Math.max(0, Math.ceil((availableAt - this.refundClockNow) / 1000))
            : 0;
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = String(remainingSeconds % 60).padStart(2, "0");
          const tokens = Number(collection.refundTokens || 0);
          return this.tt(
            "vodClips.antibotRefundPending",
            "{platform} bloqueó temporalmente la descarga. Reembolsaremos {tokens} tokens en {time}.",
            { platform, tokens, time: `${minutes}:${seconds}` }
          );
        }
        if (collection.refundStatus === "completed") {
          return this.tt(
            "vodClips.antibotRefundCompleted",
            "{platform} bloqueó temporalmente la descarga. Tus tokens ya fueron reembolsados.",
            { platform }
          );
        }
        return this.tt(
          "vodClips.antibotBlocked",
          "{platform} bloqueó temporalmente la descarga.",
          { platform }
        );
      }
      if (
        this.isJobInsufficientTokens(
          collection.jobStatus,
          collection.jobErrorCode,
          collection.jobErrorMessage
        )
      ) {
        return this.tt("vodClips.statusInsufficientTokens", "Tokens insuficientes");
      }
      if (!this.isJobFailed(collection.jobStatus)) return "";
      const legend = this.jobFailureLegend(collection.jobErrorMessage, collection.jobErrorCode);
      return legend || this.tt("vodClips.statusFailed", "Fallido");
    },
    isJobVisibleInEarlyCollections(status, errorCode = null, errorMessage = null) {
      if (!status) return false;
      if (this.isJobInsufficientTokens(status, errorCode, errorMessage)) return true;
      const normalized = this.normalizeJobStatus(status);
      return [
        "1_queued",
        "2_running",
        "3_downloaded",
        "4_decided",
        "5_rendering",
        "6_rendered",
        "7_scheduling",
        "8_published",
        "expired",
        "2.1_failed",
        "2_failed",
        "failed",
      ].includes(normalized);
    },
    jobStatusLabel(status, errorCode = null, errorMessage = null, collection = null) {
      if (this.isDemoCollection(collection)) return this.tt("schedulePost.demoBadge", "DEMO");
      if (!status) return this.tt("vodClips.unknown", "Desconocido");
      if (this.isJobInsufficientTokens(status, errorCode, errorMessage)) {
        return this.tt("vodClips.statusInsufficientTokens", "Tokens insuficientes");
      }
      const normalized = this.normalizeJobStatus(status);
      const map = {
        "1_queued": this.tt("vodClips.statusQueued", "En cola"),
        "2_running": this.tt("vodClips.statusDownloading", "Descargando"),
        "5_rendering": this.tt("vodClips.statusRendering", "Renderizando"),
        "3_downloaded": this.tt("vodClips.statusDownloaded", "Descargado"),
        "4_decided": this.tt("vodClips.statusDecided", "Decidido"),
        "6_rendered": this.tt("vodClips.statusRendered", "Renderizado"),
        "7_scheduling": this.tt("vodClips.statusScheduling", "Programando"),
        "8_published": this.tt("vodClips.statusPublished", "Publicado"),
        "expired": this.tt("vodClips.statusExpired", "Expirado"),
        "2.1_failed": this.tt("vodClips.statusFailed", "Fallido"),
        "2_failed": this.tt("vodClips.statusFailed", "Fallido"),
        "failed": this.tt("vodClips.statusFailed", "Fallido"),
      };
      return map[normalized] || status;
    },
    jobStatusClass(status, errorCode = null, errorMessage = null, collection = null) {
      if (this.isDemoCollection(collection)) return "job-status-demo";
      if (!status) return "job-status-unknown";
      if (this.isJobInsufficientTokens(status, errorCode, errorMessage)) {
        return "job-status-insufficient-tokens";
      }
      const normalized = this.normalizeJobStatus(status);
      const map = {
        "1_queued": "queued",
        "2_running": "running",
        "expired": "failed",
        "2.1_failed": "failed",
        "2_failed": "failed",
        "failed": "failed",
        "3_downloaded": "downloaded",
        "4_decided": "decided",
        "5_rendering": "rendering",
        "6_rendered": "rendered",
        "7_scheduling": "scheduling",
        "8_published": "rendered",
      };
      return `job-status-${map[normalized] || "unknown"}`;
    },
    isJobRunning(status) {
      return this.normalizeJobStatus(status) === "2_running";
    },
    isJobDownloaded(status) {
      return this.normalizeJobStatus(status) === "3_downloaded";
    },
    isJobRendering(status) {
      return this.normalizeJobStatus(status) === "5_rendering";
    },
    isJobDecided(status) {
      return this.normalizeJobStatus(status) === "4_decided";
    },
    isJobQueued(status) {
      return this.normalizeJobStatus(status) === "1_queued";
    },
    getJobProcessingPhase(status) {
      const normalized = this.normalizeJobStatus(status);
      const phaseMap = {
        "1_queued": "queued",
        "2_running": "downloading",
        "3_downloaded": "downloaded",
        "4_decided": "decided",
        "5_rendering": "rendering",
      };
      return phaseMap[normalized] || null;
    },
    isJobProcessing(status) {
      return !!this.getJobProcessingPhase(status);
    },
    getClipStatusLabel(status, clip = null) {
      if (this.isDemoClip(clip)) return this.tt("schedulePost.demoBadge", "DEMO");
      if (this.isClipRerendering(clip)) {
        return this.tt("vodClips.statusRendering", "Renderizando");
      }
      if (!status) return this.tt("schedulePost.statusPillReadyToPost", "ready to post");
      const s = String(status).toLowerCase();
      if (this.isClipRenderedStatus(s)) {
        return this.tt("schedulePost.statusPillReadyToPost", "ready to post");
      }
      if (s === "4.4_rerender_failed") {
        return this.tt("vodClips.statusRerenderFailed", "Re-render fallido");
      }
      if (s === "3.1_failed") {
        return this.tt("vodClips.statusFailed", "Fallido");
      }
      if (s === "5_scheduled_to_post") {
        return this.tt("schedulePost.statusPillScheduled", "scheduled");
      }
      if (s === "posted" || s === "6_posted" || s === "7_posted") {
        return this.tt("schedulePost.statusPillPosted", "posted");
      }
      if (s === "6.1_post_failed" || s === "6_1_post_failed" || s === "post_failed") {
        return this.tt("schedulePost.statusPillPostFailed", "post failed");
      }
      return status;
    },
    getClipStatusPillLabel(status, clip = null) {
      if (this.isDemoClip(clip)) return this.tt("schedulePost.demoBadge", "DEMO");
      if (this.isClipRerendering(clip)) {
        return this.tt("vodClips.statusRendering", "Renderizando");
      }
      const normalized = String(status || "").toLowerCase();
      if (this.isClipRenderedStatus(normalized)) {
        return this.tt("schedulePost.statusPillReadyToPost", "ready to post");
      }
      if (normalized === "4.4_rerender_failed") {
        return this.tt("vodClips.statusRerenderFailed", "Re-render fallido");
      }
      if (normalized === "3.1_failed") {
        return this.tt("vodClips.statusFailed", "Fallido");
      }
      if (normalized === "5_scheduled_to_post") {
        return this.tt("schedulePost.statusPillScheduled", "scheduled");
      }
      if (normalized === "posted" || normalized === "6_posted" || normalized === "7_posted") {
        return this.tt("schedulePost.statusPillPosted", "posted");
      }
      if (normalized === "6.1_post_failed" || normalized === "6_1_post_failed" || normalized === "post_failed") {
        return this.tt("schedulePost.statusPillPostFailed", "post failed");
      }
      return this.getClipStatusLabel(status, clip);
    },
    getClipStatusPillClass(status, clip = null) {
      if (this.isDemoClip(clip)) {
        return "badge-status-demo";
      }
      if (this.isClipRerendering(clip)) {
        return "badge-status-rendering";
      }
      const normalized = String(status || "").toLowerCase();
      if (this.isClipRenderedStatus(normalized)) {
        return "badge-status-ready-to-post";
      }
      if (normalized === "4.4_rerender_failed") {
        return "badge-status-post-failed";
      }
      if (normalized === "3.1_failed") {
        return "badge-status-post-failed";
      }
      if (normalized === "5_scheduled_to_post") {
        return "badge-status-scheduled";
      }
      if (normalized === "posted" || normalized === "6_posted" || normalized === "7_posted") {
        return "badge-status-posted";
      }
      if (normalized === "6.1_post_failed" || normalized === "6_1_post_failed" || normalized === "post_failed") {
        return "badge-status-post-failed";
      }
      return "badge-status-default";
    },
    getScoreClass(confidence) {
      const normalizedConfidence = Number(confidence);
      if (!Number.isFinite(normalizedConfidence)) {
        return "score-low";
      }
      const score = normalizedConfidence * 100;
      if (score >= 80) return "score-excellent";
      if (score >= 60) return "score-good";
      return "score-low";
    },
    formatTimelineTime12h(timeStr) {
      const raw = String(timeStr || "").trim();
      if (!raw) return "";
      const match = raw.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return raw;

      const hour24 = Number(match[1]);
      const minute = match[2];
      if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return raw;

      const period = hour24 >= 12 ? "pm" : "am";
      const hour12 = hour24 % 12 || 12;
      return `${hour12}:${minute} ${period}`;
    },
    getTimelineEventContent(event) {
      const rawTitle = String(event?.title || "").trim();
      const postingTime = String(event?.extendedProps?.postingTime || "").trim();
      if (!rawTitle) return "";
      const displayPostingTime = this.formatTimelineTime12h(postingTime);

      // Evita duplicar la hora cuando el título ya la trae al final (" - HH:mm").
      const timeSuffix = postingTime ? ` - ${postingTime}` : "";
      const normalizedTitle =
        postingTime && rawTitle.endsWith(timeSuffix)
          ? rawTitle.slice(0, rawTitle.length - timeSuffix.length)
          : rawTitle;

      return displayPostingTime ? `${displayPostingTime} · ${normalizedTitle}` : normalizedTitle;
    },
    closeTimelinePreviewModal() {
      this.previewModalOpen = false;
      this.previewLoading = false;
      this.previewError = "";
      this.selectedPreviewEvent = null;
      this.previewClip = null;
    },
    async openTimelinePreviewModal(event) {
      const clipId = String(event?.extendedProps?.clipId || "").trim();
      if (!clipId) return;

      this.previewModalOpen = true;
      this.previewLoading = true;
      this.previewError = "";
      this.previewClip = null;
      this.selectedPreviewEvent = {
        clip_id: clipId,
        title: event?.title || "",
        posting_time: event?.extendedProps?.postingTime || "",
        status: event?.extendedProps?.clipStatus || "",
        user_name: event?.extendedProps?.userName || this.postingTimelineUserName || null,
      };

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("schedulePost.errorLoginToLoadTimeline", "Debes iniciar sesión para consultar la timeline."));
        }

        const payload = await fetchTwitchSchedulePreview(session, clipId);
        const clipData = payload?.clip || null;
        this.previewClip = {
          id: clipData?.id || clipId,
          videoUrl: clipData?.rendered_video_url || null,
          status: clipData?.status || event?.extendedProps?.clipStatus || null,
          title: clipData?.title || event?.title || null,
        };

        if (!this.previewClip.videoUrl) {
          this.previewError = this.tt("twitchPostAdmin.previewUrlMissing", "No se encontró URL de preview para este clip programado.");
        }
      } catch (error) {
        this.previewError = error?.message || this.tt("twitchPostAdmin.previewLoadError", "No fue posible cargar la preview del clip.");
      } finally {
        this.previewLoading = false;
      }
    },
    getClipDisplayName(clip) {
      if (clip && clip.id) {
        const clipId = String(clip.id);
        // Tomar solo la parte antes del primer guion
        const firstPart = clipId.split('-')[0];
        // Si hay más partes después del guion, agregar elipsis
        const hasMore = clipId.includes('-');
        return `Clip #${firstPart}${hasMore ? '...' : ''}`;
      }
      return 'Clip';
    },
    normalizeBatchThumbnailUrl(rawValue) {
      const raw = String(rawValue || "").trim();
      if (!raw) return null;
      try {
        const parsed = new URL(raw, window.location.origin);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return null;
        }
        return parsed.toString();
      } catch (_err) {
        return null;
      }
    },
    effectiveBatchThumbnailUrl(collection) {
      if (!collection || !collection.jobId) return null;
      if (this.failedBatchThumbnailJobIds.has(collection.jobId)) return null;
      return this.normalizeBatchThumbnailUrl(collection.jobThumbnailUrl);
    },
    onBatchThumbnailError(jobId) {
      if (!jobId) return;
      this.failedBatchThumbnailJobIds.add(jobId);
      this.$forceUpdate();
    },
    parseClipTitle(clip) {
      if (!clip) return null;
      const directTitle = clip.title || clip.name || clip.clip_name;
      if (directTitle) return directTitle;
      const raw = clip.clip_metadata;
      if (!raw) return null;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return parsed && parsed.title ? parsed.title : null;
        } catch (e) {
          return null;
        }
      }
      if (typeof raw === "object" && raw.title) {
        return raw.title;
      }
      return null;
    },
    resolveJobThumbnailUrl(job) {
      if (!job || typeof job !== "object") return null;
      return this.normalizeBatchThumbnailUrl(job.job_thumbnail_url);
    },
    withCacheBusterIfSafe(url, version) {
      const raw = String(url || "").trim();
      if (!raw) return null;
      try {
        const parsed = new URL(raw, window.location.origin);
        if (
          parsed.pathname.startsWith("/public/jobs/")
          || parsed.pathname.startsWith("/public/media-v2/")
        ) {
          return raw;
        }
      } catch (_err) {
        // Mantener cache-buster para URLs legacy no parseables.
      }
      const lower = raw.toLowerCase();
      const isSigned =
        lower.includes("x-amz-signature=") ||
        lower.includes("x-amz-credential=") ||
        lower.includes("token=") ||
        lower.includes("sig=") ||
        lower.includes("signature=");
      if (isSigned) {
        // No agregar query params a URLs firmadas para no invalidar la firma.
        return raw;
      }
      return this.withCacheBuster(raw, version);
    },
    withCacheBuster(url, version) {
      if (!url || typeof url !== "string") return null;
      const token = version ? String(version) : String(Date.now());
      return url.includes("?")
        ? `${url}&v=${encodeURIComponent(token)}`
        : `${url}?v=${encodeURIComponent(token)}`;
    },
    confirmDeleteClip(clip) {
      console.log('Delete clip:', clip.id);
    },
    // Reproducir / pausar previews de video al hacer hover
    playPreview(event) {
      const video = event && event.target;
      if (!video || typeof video.play !== 'function') return;
      
      // Pausar todos los demás videos antes de reproducir este
      this.pauseAllVideos(video);
      
      if (video.paused) {
        video.currentTime = 1;
        video.play().catch(() => {});
      }
    },
    pausePreview(event) {
      const video = event && event.target;
      if (!video || typeof video.pause !== 'function') return;
      video.pause();
      video.currentTime = 0;
    },
    // Drag & Drop desde el clip
    scheduleTimelineLayoutRefresh() {
      if (this.isClipTimeEditorVisible) {
        this.clearClipTimeEditorPreviewCaches();
      }
      this.$nextTick(() => {
        if (this.mainTimelineInitialScrollPending) {
          this.mainTimelineInitialScrollPending = !this.scrollMainTimelineToToday();
        } else {
          this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
        }
        this.recenterTimelineOnMobile("postingTimelineDays");
        if (
          this.scheduleTimelineLayoutRefreshFrameId != null
          || typeof window === "undefined"
          || typeof window.requestAnimationFrame !== "function"
        ) {
          return;
        }
        this.scheduleTimelineLayoutRefreshFrameId = window.requestAnimationFrame(() => {
          this.scheduleTimelineLayoutRefreshFrameId = null;
          if (this.mainTimelineInitialScrollPending) {
            this.mainTimelineInitialScrollPending = !this.scrollMainTimelineToToday();
          } else {
            this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
          }
          this.recenterTimelineOnMobile("postingTimelineDays");
        });
      });
    },
    parseIsoDateToLocal(isoDate) {
      const parts = String(isoDate || "").split("-").map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
      const [year, month, day] = parts;
      return new Date(year, month - 1, day);
    },
    formatLocalDateToIso(dateObj) {
      if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    addDaysToIsoDate(isoDate, days) {
      const base = this.parseIsoDateToLocal(isoDate);
      if (!base) return "";
      base.setDate(base.getDate() + Number(days || 0));
      return this.formatLocalDateToIso(base);
    },
    getMainTimelineAllowedRange() {
      const todayIso = this.getPostingTodayInTimezone();
      const minIso = this.addDaysToIsoDate(todayIso, -7);
      const maxIso = this.addDaysToIsoDate(todayIso, 15);
      return { minIso, maxIso, todayIso };
    },
    getTimelineAllowedRange() {
      const todayIso = this.getPostingTodayInTimezone();
      const minIso = this.addDaysToIsoDate(todayIso, -7);
      const maxIso = this.addDaysToIsoDate(todayIso, 15);
      return { minIso, maxIso, todayIso };
    },
    getTimelineWindowStartDate() {
      return this.getTimelineAllowedRange().todayIso;
    },
    clampTimelineStartIso(startIso, renderDays) {
      const { minIso, maxIso } = this.getTimelineAllowedRange();
      const safeRenderDays = Math.max(1, Number(renderDays || 1));
      const maxStartIso = this.addDaysToIsoDate(maxIso, -(safeRenderDays - 1));
      if (!startIso) return minIso;
      if (startIso < minIso) return minIso;
      if (startIso > maxStartIso) return maxStartIso;
      return startIso;
    },
    isMobileTimelineMode() {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
      return window.matchMedia("(max-width: 768px)").matches;
    },
    getTimelineRenderDaysCount() {
      return 23;
    },
    getPostingTimelineRenderDaysCount() {
      return 23;
    },
    getTimelineRefElement(refName) {
      const ref = this.$refs[refName];
      if (Array.isArray(ref)) return ref[0] || null;
      return ref || null;
    },
    scrollMainTimelineToToday() {
      const el = this.getTimelineRefElement("mainTimelineDays");
      if (!el) return false;
      const todayCard = el.querySelector(".timeline-day-card.is-today[data-iso-date]");
      if (!todayCard) {
        this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
        return false;
      }
      el.scrollLeft = Math.max(0, todayCard.offsetLeft);
      this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
      return true;
    },
    recenterTimelineOnMobile(refName) {
      if (!this.isMobileTimelineMode()) return;
      const el = this.getTimelineRefElement(refName);
      if (!el) return;
      if (el.scrollWidth <= el.clientWidth + 4) return;
      const firstCard = el.querySelector(".timeline-day-card");
      const dayOffset = 0;
      if (!firstCard) {
        el.scrollLeft = 0;
        return;
      }
      const styles = window.getComputedStyle(el);
      const gapRaw = styles.columnGap || styles.gap || "0";
      const gap = Number.parseFloat(gapRaw) || 0;
      const step = firstCard.getBoundingClientRect().width + gap;
      el.scrollLeft = Math.max(0, step * dayOffset);
      if (refName === "mainTimelineDays") {
        this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
      } else if (refName === "postingTimelineDays") {
        this.updateVisibleTimelineMonthFromScroll("postingTimelineDays", "postingTimelineVisibleMonthIso");
      }
    },
    onMainTimelineScroll() {
      this.updateVisibleTimelineMonthFromScroll("mainTimelineDays", "timelineVisibleMonthIso");
    },
    onPostingTimelineScroll() {
      this.updateVisibleTimelineMonthFromScroll("postingTimelineDays", "postingTimelineVisibleMonthIso");
    },
    updateVisibleTimelineMonthFromScroll(refName, targetField) {
      const el = this.getTimelineRefElement(refName);
      if (!el) return;
      const cards = el.querySelectorAll(".timeline-day-card[data-iso-date]");
      if (!cards || cards.length === 0) return;
      const viewportCenter = el.scrollLeft + el.clientWidth / 2;
      let closestIso = "";
      let closestDistance = Number.POSITIVE_INFINITY;
      cards.forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIso = card.getAttribute("data-iso-date") || "";
        }
      });
      if (closestIso && this[targetField] !== closestIso) {
        this[targetField] = closestIso;
      }
    },
    async handleClipPrimaryAction(clip) {
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      if (this.isClipManageable(clip)) {
        this.openManageScheduledModal(clip);
        return;
      }
      try {
        await this.requestOpenScheduleModal(clip);
      } catch (error) {
        console.error("No se pudo abrir el flujo de programación:", error);
        this.scheduleSubscriptionHasActivePlan = false;
        this.openScheduleSubscriptionPromptModal();
      }
    },
    async requestOpenScheduleModal(clip) {
      if (!clip) return;
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      const hasExternalScheduleListener = !!(
        this.embedded
        && this.$listeners
        && this.$listeners["clip-schedule-click"]
      );
      if (hasExternalScheduleListener) {
        await new Promise((resolve) => {
          this.$emit("clip-schedule-click", {
            clip,
            onComplete: resolve,
          });
        });
        return;
      }
      const canOpenSchedule = await this.resolveScheduleSubscriptionGate();
      if (!canOpenSchedule) {
        this.openScheduleSubscriptionPromptModal();
        return;
      }
      await this.openScheduleModal(clip);
    },
    canUsePremiumEditorForClip(clip) {
      if (!clip) return false;
      if (isImpersonating()) return true;
      const resolvedRole = String(this.userRole || "").trim().toLowerCase();
      if (resolvedRole === "admin") return true;
      return this.scheduleSubscriptionHasActivePlan === true;
    },
    clipHasEditSourceWindow(clip) {
      if (!clip) return false;
      const editWindowStart = Number(
        clip.edit_window_start_seconds ?? clip.editWindowStartSeconds
      );
      const editWindowEnd = Number(
        clip.edit_window_end_seconds ?? clip.editWindowEndSeconds
      );
      return Number.isFinite(editWindowStart) && Number.isFinite(editWindowEnd) && editWindowEnd > editWindowStart;
    },
    clipEditorUnavailableMessage() {
      return this.tt("vodClips.premiumEditorNotEditable", "Este clip no se puede editar.");
    },
    canEditClipFromSchedule(clip) {
      const status = String(clip?.status || "").trim().toLowerCase();
      return (
        (
          this.isClipRenderedStatus(status)
          || this.isClipRerenderFailedStatus(status)
          || (
            this.isClipLocalRenderReady(clip)
            && (status === "3_rendering" || status === "4.2_rerendering")
          )
        )
        && this.clipHasEditSourceWindow(clip)
      );
    },
    normalizeClipVideoType(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (
        normalized === "auto"
        || normalized === "gameplay"
        || normalized === "just_chatting"
        || normalized === "irl"
        || normalized === "podcast"
        || normalized === "center"
      ) {
        return normalized;
      }
      return "auto";
    },
    defaultClipTimeEditorPreviewLayout() {
      return createDefaultClipTimeEditorPreviewLayout();
    },
    normalizeClipTimeEditorPreviewLayoutRect(value) {
      if (!value || typeof value !== "object") return null;
      const x = Number(value.x);
      const y = Number(value.y);
      const width = Number(value.width);
      const height = Number(value.height);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
        return null;
      }
      if (x < 0 || y < 0 || width <= 0 || height <= 0) {
        return null;
      }
      if (x > 1 || y > 1 || x + width > 1 || y + height > 1) {
        return null;
      }
      return {
        x: Number(x.toFixed(6)),
        y: Number(y.toFixed(6)),
        width: Number(width.toFixed(6)),
        height: Number(height.toFixed(6)),
      };
    },
    cloneClipTimeEditorPreviewLayout(value) {
      let source = value;
      if (typeof source === "string") {
        try {
          source = JSON.parse(source);
        } catch (_err) {
          source = null;
        }
      }
      const defaults = this.defaultClipTimeEditorPreviewLayout();
      if (!source || typeof source !== "object") {
        return defaults;
      }
      const normalizeRect = (candidate, fallback) => {
        const normalized = this.normalizeClipTimeEditorPreviewLayoutRect(candidate);
        if (normalized) return normalized;
        return { ...fallback };
      };
      return {
        version: 1,
        irl: {
          background: normalizeRect(source?.irl?.background, defaults.irl.background),
          foreground: normalizeRect(source?.irl?.foreground, defaults.irl.foreground),
        },
        just_chatting: {
          crop: normalizeRect(source?.just_chatting?.crop, defaults.just_chatting.crop),
        },
        gameplay: {
          face: normalizeRect(source?.gameplay?.face, defaults.gameplay.face),
          gameplay: normalizeRect(source?.gameplay?.gameplay, defaults.gameplay.gameplay),
        },
      };
    },
    roundClipTimeEditorLayoutValue(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return 0;
      return Number(Math.max(0, Math.min(1, numericValue)).toFixed(6));
    },
    clampClipTimeEditorLayoutRect(rect) {
      const normalized = this.normalizeClipTimeEditorPreviewLayoutRect(rect);
      if (normalized) return normalized;
      return { x: 0, y: 0, width: 1, height: 1 };
    },
    clipTimeEditorSourceVideoSize() {
      const video = this.$refs.clipTimeEditorVideo;
      const width = Number(video?.videoWidth || 0);
      const height = Number(video?.videoHeight || 0);
      if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
        return { width, height };
      }
      const rect = video?.getBoundingClientRect ? video.getBoundingClientRect() : null;
      const fallbackWidth = Number(rect?.width || video?.clientWidth || 0);
      const fallbackHeight = Number(rect?.height || video?.clientHeight || 0);
      return {
        width: Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : 0,
        height: Number.isFinite(fallbackHeight) && fallbackHeight > 0 ? fallbackHeight : 0,
      };
    },
    clipTimeEditorVerticalSourceRectForVideo(videoWidth, videoHeight, currentRect = null) {
      const width = Number(videoWidth || 0);
      const height = Number(videoHeight || 0);
      if (width <= 0 || height <= 0) {
        return this.clampClipTimeEditorLayoutRect(currentRect || { x: 0, y: 0, width: 1, height: 1 });
      }
      const sourceAspect = width / height;
      let rectWidth = 1;
      let rectHeight = 1;
      if (sourceAspect > CLIP_TIME_EDITOR_VERTICAL_SOURCE_ASPECT) {
        rectWidth = CLIP_TIME_EDITOR_VERTICAL_SOURCE_ASPECT / sourceAspect;
      } else {
        rectHeight = sourceAspect / CLIP_TIME_EDITOR_VERTICAL_SOURCE_ASPECT;
      }
      const source = currentRect && typeof currentRect === "object" ? currentRect : {};
      const fallbackX = (1 - rectWidth) / 2;
      const fallbackY = (1 - rectHeight) / 2;
      const sourceWidth = Number(source.width);
      const sourceHeight = Number(source.height);
      const sourceMatchesTarget = Number.isFinite(sourceWidth)
        && Number.isFinite(sourceHeight)
        && Math.abs(sourceWidth - rectWidth) < 0.0005
        && Math.abs(sourceHeight - rectHeight) < 0.0005;
      const nextX = sourceMatchesTarget ? Number(source.x ?? fallbackX) : fallbackX;
      const nextY = sourceMatchesTarget ? Number(source.y ?? fallbackY) : fallbackY;
      return {
        x: this.roundClipTimeEditorLayoutValue(Math.min(Math.max(0, nextX), Math.max(0, 1 - rectWidth))),
        y: this.roundClipTimeEditorLayoutValue(Math.min(Math.max(0, nextY), Math.max(0, 1 - rectHeight))),
        width: this.roundClipTimeEditorLayoutValue(rectWidth),
        height: this.roundClipTimeEditorLayoutValue(rectHeight),
      };
    },
    clipTimeEditorSquareSourceRectForVideo(videoWidth, videoHeight, currentRect = null) {
      const width = Number(videoWidth || 0);
      const height = Number(videoHeight || 0);
      if (width <= 0 || height <= 0) {
        return this.clampClipTimeEditorLayoutRect(currentRect || { x: 0.05, y: 0.05, width: 0.18, height: 0.18 });
      }
      const sidePx = Math.max(1, Math.min(width, height) * CLIP_TIME_EDITOR_GAMEPLAY_FACE_SIDE_RATIO);
      const rectWidth = Math.min(1, sidePx / width);
      const rectHeight = Math.min(1, sidePx / height);
      const source = currentRect && typeof currentRect === "object" ? currentRect : {};
      const sourceWidth = Number(source.width);
      const sourceHeight = Number(source.height);
      const sourceMatchesTarget = Number.isFinite(sourceWidth)
        && Number.isFinite(sourceHeight)
        && Math.abs(sourceWidth - rectWidth) < 0.0005
        && Math.abs(sourceHeight - rectHeight) < 0.0005;
      const nextX = sourceMatchesTarget ? Number(source.x ?? 0.05) : 0.05;
      const nextY = sourceMatchesTarget ? Number(source.y ?? 0.05) : 0.05;
      return {
        x: this.roundClipTimeEditorLayoutValue(Math.min(Math.max(0, nextX), Math.max(0, 1 - rectWidth))),
        y: this.roundClipTimeEditorLayoutValue(Math.min(Math.max(0, nextY), Math.max(0, 1 - rectHeight))),
        width: this.roundClipTimeEditorLayoutValue(rectWidth),
        height: this.roundClipTimeEditorLayoutValue(rectHeight),
      };
    },
    normalizeClipTimeEditorPreviewLayoutForVideo(layout, videoSize = null) {
      const size = videoSize || this.clipTimeEditorSourceVideoSize();
      const normalized = this.cloneClipTimeEditorPreviewLayout(layout);
      normalized.just_chatting.crop = this.clipTimeEditorVerticalSourceRectForVideo(
        size.width,
        size.height,
        normalized.just_chatting.crop
      );
      normalized.gameplay.gameplay = this.clipTimeEditorVerticalSourceRectForVideo(
        size.width,
        size.height,
        normalized.gameplay.gameplay
      );
      normalized.gameplay.face = this.clipTimeEditorSquareSourceRectForVideo(
        size.width,
        size.height,
        normalized.gameplay.face
      );
      return normalized;
    },
    ensureClipTimeEditorPreviewLayoutForVideo(options = {}) {
      const size = this.clipTimeEditorSourceVideoSize();
      if (size.width <= 0 || size.height <= 0) return;
      const nextLayout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor?.draftPreviewLayout,
        size
      );
      this.clipTimeEditor.draftPreviewLayout = nextLayout;
      if (options.syncBase === true) {
        this.clipTimeEditor.basePreviewLayout = this.cloneClipTimeEditorPreviewLayout(nextLayout);
      }
    },
    clipTimeEditorPreviewLayoutForSave() {
      return this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor?.draftPreviewLayout
      );
    },
    defaultClipTimeEditorContextPosition() {
      return {
        x: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
        y: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
      };
    },
    normalizeClipTimeEditorContextPosition(value) {
      let source = value;
      if (typeof source === "string") {
        try {
          source = JSON.parse(source);
        } catch (_err) {
          source = null;
        }
      }
      if (!source || typeof source !== "object") return null;
      const x = Number(source.x);
      const y = Number(source.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      if (x < 0 || x > 1 || y < 0 || y > 1) return null;
      return {
        x: Number(x.toFixed(6)),
        y: Number(y.toFixed(6)),
      };
    },
    cloneClipTimeEditorContextPosition(value) {
      const normalized = this.normalizeClipTimeEditorContextPosition(value);
      if (!normalized) {
        return this.defaultClipTimeEditorContextPosition();
      }
      return {
        x: normalized.x,
        y: normalized.y,
      };
    },
    clipTimeEditorContextPositionForSave() {
      return this.clipTimeEditorContextPositionWithSubtitleClearance(
        this.clipTimeEditor?.draftContextPosition
      );
    },
    clipTimeEditorContextPositionWithSubtitleClearance(value) {
      const position = this.cloneClipTimeEditorContextPosition(value);
      if (
        !this.clipTimeEditorIsIrlVideoType
        || !this.clipTimeEditor?.draftSubtitlesEnabled
        || !this.clipTimeEditorContextHasText
      ) {
        return position;
      }
      const lines = this.wrapClipTimeEditorContextLines(
        this.clipTimeEditor?.draftContextText,
        CLIP_TIME_EDITOR_IRL_CONTEXT_MAX_LINE_CHARS
      );
      const contextHeight = (lines.length * CLIP_TIME_EDITOR_IRL_CONTEXT_FONT_SIZE)
        + (Math.max(0, lines.length - 1) * CLIP_TIME_EDITOR_IRL_CONTEXT_LINE_SPACING)
        + (2 * CLIP_TIME_EDITOR_IRL_CONTEXT_BOX_BORDER);
      const maxY = Math.max(
        0,
        (
          (CLIP_TIME_EDITOR_IRL_CAPTION_Y * CLIP_TIME_EDITOR_IRL_OUTPUT_HEIGHT)
          - CLIP_TIME_EDITOR_CAPTION_FONT_SIZE
          - CLIP_TIME_EDITOR_CAPTION_OUTLINE
          - contextHeight
        ) / CLIP_TIME_EDITOR_IRL_OUTPUT_HEIGHT
      );
      return {
        ...position,
        y: Math.min(position.y, Number(maxY.toFixed(6))),
      };
    },
    clipTimeEditorContextTextForSave() {
      return String(this.clipTimeEditor?.draftContextText || "").trim();
    },
    wrapClipTimeEditorContextLines(value, maxLineChars = 28) {
      const normalized = String(value || "")
        .split(/\s+/)
        .filter(Boolean)
        .join(" ");
      if (!normalized) return [];
      const limit = Math.max(1, Number(maxLineChars) || 28);
      const words = normalized.split(" ");
      const lines = [];
      let currentLine = "";

      words.forEach((word) => {
        if (!currentLine) {
          currentLine = word;
          return;
        }

        const nextLine = `${currentLine} ${word}`;
        if (nextLine.length <= limit) {
          currentLine = nextLine;
          return;
        }

        lines.push(currentLine);
        currentLine = word;
      });

      if (currentLine) lines.push(currentLine);
      return lines.length > 0 ? lines : [normalized];
    },
    setClipTimeEditorContextEnabled(enabled) {
      if (this.clipTimeEditorSaving || !this.clipTimeEditorAllowsContextEditing) return;
      this.$set(this.clipTimeEditor, "draftContextEnabled", Boolean(enabled));
      if (!enabled) {
        this.stopClipTimeEditorContextDrag();
        return;
      }
      this.$nextTick(() => {
        const textarea = this.$refs.clipTimeEditorContextInput;
        if (textarea?.focus) {
          textarea.focus();
        }
      });
    },
    clipTimeEditorContextEmojiLabel(emoji) {
      const labelKey = String(emoji?.labelKey || "").trim();
      if (!labelKey) return String(emoji?.label || "").trim();
      return this.tt(`vodClips.contextEmojiLabels.${labelKey}`, emoji.label || labelKey);
    },
    insertClipTimeEditorContextEmoji(symbol) {
      const textarea = this.$refs.clipTimeEditorContextInput;
      const currentText = String(this.clipTimeEditor.draftContextText || "");
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      const insertText = String(symbol);
      this.clipTimeEditor.draftContextText = `${currentText.slice(0, selectionStart)}${insertText}${currentText.slice(selectionEnd)}`;
      this.$nextTick(() => {
        const nextPosition = selectionStart + insertText.length;
        textarea.focus();
        textarea.setSelectionRange(nextPosition, nextPosition);
      });
    },
    normalizeClipTranscriptWord(word) {
      return {
        start: Number(word?.start || 0),
        end: Number(word?.end || 0),
        word: String(word?.word || ""),
      };
    },
    normalizeClipTranscript(transcript) {
      return {
        clip_id: String(transcript?.clip_id || ""),
        edit_window_start_seconds: Number(transcript?.edit_window_start_seconds || 0),
        edit_window_end_seconds: Number(transcript?.edit_window_end_seconds || 0),
        language: String(transcript?.language || ""),
        words: (Array.isArray(transcript?.words) ? transcript.words : [])
          .map((word) => this.normalizeClipTranscriptWord(word)),
        blocks: (Array.isArray(transcript?.blocks) ? transcript.blocks : []).map((block) => ({
          id: block.id,
          start: Number(block?.start || 0),
          end: Number(block?.end || 0),
          text: String(block?.text || ""),
          words: (Array.isArray(block?.words) ? block.words : [])
            .map((word) => this.normalizeClipTranscriptWord(word)),
        })),
        has_corrections: Boolean(transcript?.has_corrections),
      };
    },
    cloneClipTranscript(transcript) {
      return this.normalizeClipTranscript(transcript);
    },
    clipTranscriptPayload(transcript) {
      const normalized = this.normalizeClipTranscript(transcript);
      return {
        provider: "user",
        language: normalized.language,
        words: normalized.words,
        blocks: normalized.blocks,
      };
    },
    clipTranscriptHasChanges() {
      if (!this.clipTranscriptBase || !this.clipTranscriptDraft) return false;
      return JSON.stringify(this.clipTranscriptPayload(this.clipTranscriptDraft))
        !== JSON.stringify(this.clipTranscriptPayload(this.clipTranscriptBase));
    },
    clipTranscriptWordsForBlock(block) {
      const tokens = String(block?.text || "").trim().split(/\s+/).filter(Boolean);
      const existingWords = Array.isArray(block?.words) ? block.words : [];
      if (tokens.length === existingWords.length) {
        return tokens.map((word, index) => ({
          ...this.normalizeClipTranscriptWord(existingWords[index]),
          word,
        }));
      }
      const start = Number(block?.start || 0);
      const end = Number(block?.end || start);
      const duration = Math.max(0, end - start);
      return tokens.map((word, index) => ({
        start: Number((start + ((duration * index) / tokens.length)).toFixed(6)),
        end: Number((start + ((duration * (index + 1)) / tokens.length)).toFixed(6)),
        word,
      }));
    },
    async openClipTranscriptEditor() {
      if (!this.clipTimeEditor?.clip?.id || this.clipTimeEditorSaving) return;
      this.showClipTranscriptEditorModal = true;
      this.clipTranscriptError = "";
      if (this.clipTranscriptDraft) {
        this.clipTranscriptModalBlocks = this.cloneClipTranscript(this.clipTranscriptDraft).blocks;
        return;
      }
      await this.loadClipTranscript();
    },
    async loadClipTranscript() {
      const clipId = String(this.clipTimeEditor?.clip?.id || "");
      if (!clipId || this.clipTranscriptLoading) return;
      this.clipTranscriptLoading = true;
      this.clipTranscriptError = "";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }
        const transcript = this.normalizeClipTranscript(
          await fetchTwitchClipTranscript(session, clipId)
        );
        if (String(this.clipTimeEditor?.clip?.id || "") !== clipId) return;
        this.clipTranscriptBase = this.cloneClipTranscript(transcript);
        this.clipTranscriptDraft = this.cloneClipTranscript(transcript);
        this.clipTranscriptModalBlocks = this.cloneClipTranscript(transcript).blocks;
      } catch (error) {
        if (String(this.clipTimeEditor?.clip?.id || "") !== clipId) return;
        this.clipTranscriptError = String(
          error?.message
          || this.tt("vodClips.transcriptLoadError", "No se pudo cargar la transcripción.")
        );
      } finally {
        if (String(this.clipTimeEditor?.clip?.id || "") === clipId) {
          this.clipTranscriptLoading = false;
        }
      }
    },
    closeClipTranscriptEditor() {
      this.showClipTranscriptEditorModal = false;
      this.clipTranscriptModalBlocks = [];
      this.clipTranscriptError = "";
    },
    applyClipTranscriptChanges() {
      if (!this.clipTranscriptDraft || this.isDemoClip(this.clipTimeEditor?.clip)) return;
      const blocks = (this.clipTranscriptModalBlocks || []).map((block) => {
        const words = this.clipTranscriptWordsForBlock(block);
        return {
          id: block.id,
          start: Number(block.start || 0),
          end: Number(block.end || 0),
          text: String(block.text || ""),
          words,
        };
      });
      this.clipTranscriptDraft = {
        ...this.cloneClipTranscript(this.clipTranscriptDraft),
        words: blocks.flatMap((block) => block.words.map((word) => ({ ...word }))),
        blocks,
      };
      this.closeClipTranscriptEditor();
      this.scheduleClipTimeEditorPreviewRender();
    },
    clipTimeEditorActiveTranscriptText() {
      return this.clipTimeEditorActiveTranscriptCaption().words.join(" ");
    },
    clipTimeEditorActiveTranscriptCaption() {
      const current = Number(this.clipTimeEditor?.currentAbsolute || 0);
      const transcriptWords = (this.clipTranscriptDraft?.words || [])
        .map((word) => ({
          start: Number(word?.start),
          end: Number(word?.end),
          text: String(word?.word || word?.text || "").trim(),
        }))
        .filter((word) => (
          Number.isFinite(word.start)
          && Number.isFinite(word.end)
          && word.end >= word.start
          && word.text
        ))
        .sort((left, right) => left.start - right.start);
      if (!transcriptWords.length) {
        return {
          words: [this.tt("vodClips.editorSubtitlePlaceholder", "Tus subtítulos irán aquí")],
          activeIndex: 0,
        };
      }
      if (current >= transcriptWords[transcriptWords.length - 1].end + 0.25) {
        return { words: [], activeIndex: -1 };
      }
      const chunks = [];
      let index = 0;
      while (index < transcriptWords.length) {
        const chunk = [];
        let charCount = 0;
        while (index < transcriptWords.length) {
          const word = transcriptWords[index];
          if (chunk.length && word.start - chunk[chunk.length - 1].end >= 0.12) break;
          const nextCharCount = charCount + word.text.length + (chunk.length ? 1 : 0);
          if (chunk.length && (chunk.length >= 4 || nextCharCount > 20)) break;
          chunk.push(word);
          charCount = nextCharCount;
          index += 1;
        }
        chunks.push(chunk);
      }
      let activeChunkIndex = chunks.findIndex((chunk, chunkIndex) => {
        const nextChunk = chunks[chunkIndex + 1];
        return current < Number(nextChunk?.[0]?.start ?? Number.POSITIVE_INFINITY);
      });
      if (activeChunkIndex < 0) activeChunkIndex = chunks.length - 1;
      const activeChunk = chunks[activeChunkIndex];
      return {
        words: activeChunk.map((word) => word.text),
        activeIndex: activeChunk.findIndex((word) => word.start <= current && word.end > current),
      };
    },
    formatClipEditorSeconds(seconds) {
      const value = Number(seconds || 0);
      if (!Number.isFinite(value) || value < 0) return "0:00";
      const totalSeconds = Math.floor(value);
      const s = String(totalSeconds % 60).padStart(2, "0");
      const m = String(Math.floor((totalSeconds / 60) % 60)).padStart(2, "0");
      const h = Math.floor(totalSeconds / 3600);
      return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
    },
    clipTimeEditorTimelineSeconds(absoluteSeconds) {
      const value = Number(absoluteSeconds || 0);
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      if (!Number.isFinite(value) || !Number.isFinite(minTime)) return 0;
      return Math.max(0, value - minTime);
    },
    formatClipEditorTimelineSeconds(absoluteSeconds) {
      return this.formatClipEditorSeconds(
        this.clipTimeEditorTimelineSeconds(absoluteSeconds)
      );
    },
    clipTimeEditorHasRenderChanges() {
      const epsilon = 0.001;
      const startDiff = Math.abs(Number(this.clipTimeEditor.draftStart || 0) - Number(this.clipTimeEditor.baseStart || 0));
      const endDiff = Math.abs(Number(this.clipTimeEditor.draftEnd || 0) - Number(this.clipTimeEditor.baseEnd || 0));
      const baseVideoType = this.normalizeClipVideoType(this.clipTimeEditor.baseVideoType);
      const draftVideoType = this.normalizeClipVideoType(this.clipTimeEditor.draftVideoType);
      const basePreviewLayout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor.basePreviewLayout
      );
      const draftPreviewLayout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor.draftPreviewLayout
      );
      const previewLayoutChanged = JSON.stringify(basePreviewLayout) !== JSON.stringify(draftPreviewLayout);
      const subtitlesChanged = Boolean(this.clipTimeEditor.baseSubtitlesEnabled)
        !== Boolean(this.clipTimeEditor.draftSubtitlesEnabled);
      return startDiff > epsilon
        || endDiff > epsilon
        || baseVideoType !== draftVideoType
        || previewLayoutChanged
        || subtitlesChanged
        || this.clipTimeEditorSubtitleStyleTouched;
    },
    clipTimeEditorHasChanges() {
      const draftVideoType = this.normalizeClipVideoType(this.clipTimeEditor.draftVideoType);
      const baseContextPosition = this.cloneClipTimeEditorContextPosition(
        this.clipTimeEditor.baseContextPosition
      );
      const draftContextPosition = this.cloneClipTimeEditorContextPosition(
        this.clipTimeEditor.draftContextPosition
      );
      const baseContextText = String(this.clipTimeEditor.baseContextText || "").trim();
      const draftContextText = String(this.clipTimeEditor.draftContextText || "").trim();
      const baseContextEnabled = Boolean(this.clipTimeEditor.baseContextEnabled);
      const draftContextEnabled = Boolean(this.clipTimeEditor.draftContextEnabled);
      const contextEnabledChanged = draftVideoType === "irl" && draftContextEnabled !== baseContextEnabled;
      const contextTextChanged = draftVideoType === "irl" && draftContextEnabled && draftContextText !== baseContextText;
      const contextChanged = draftVideoType === "irl" && draftContextEnabled && (
        Math.abs(Number(draftContextPosition.x) - Number(baseContextPosition.x)) > CLIP_TIME_EDITOR_CONTEXT_EPSILON
        || Math.abs(Number(draftContextPosition.y) - Number(baseContextPosition.y)) > CLIP_TIME_EDITOR_CONTEXT_EPSILON
      );
      return this.clipTimeEditorHasRenderChanges()
        || contextEnabledChanged
        || contextChanged
        || contextTextChanged
        || this.clipTranscriptHasChanges();
    },
    clipTimeEditorIrlPreviewScale() {
      const stage = this.$refs.clipTimeEditorContextStage;
      const stageRect = stage?.getBoundingClientRect ? stage.getBoundingClientRect() : null;
      const stageWidth = Number(stageRect?.width || 0);
      const fallbackWidth = 200;
      const effectiveWidth = stageWidth > 0 ? stageWidth : fallbackWidth;
      return effectiveWidth / CLIP_TIME_EDITOR_IRL_OUTPUT_WIDTH;
    },
    clipTimeEditorContextOverlayStyle() {
      const normalized = this.clipTimeEditorContextPositionWithSubtitleClearance(
        this.clipTimeEditor?.draftContextPosition
      );
      const scale = this.clipTimeEditorIrlPreviewScale();
      return {
        left: `${normalized.x * 100}%`,
        top: `${normalized.y * 100}%`,
        "--clip-context-font-size": `${CLIP_TIME_EDITOR_IRL_CONTEXT_FONT_SIZE * scale}px`,
        "--clip-context-line-spacing": `${CLIP_TIME_EDITOR_IRL_CONTEXT_LINE_SPACING * scale}px`,
        "--clip-context-box-border": `${CLIP_TIME_EDITOR_IRL_CONTEXT_BOX_BORDER * scale}px`,
        "--clip-context-max-width": `${CLIP_TIME_EDITOR_IRL_OUTPUT_WIDTH * scale}px`,
      };
    },
    clipTimeEditorSourceLayoutHandles() {
      const layout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor?.draftPreviewLayout
      );
      if (this.clipTimeEditorIsJustChattingVideoType) {
        return [{
          key: "just_chatting.crop",
          label: this.tt("vodClips.layoutJustChattingCropLabel", "Video vertical"),
          className: "clip-time-editor-layout-box--yellow",
          rect: layout.just_chatting.crop,
        }];
      }
      if (this.clipTimeEditorIsGameplayVideoType) {
        return [
          {
            key: "gameplay.gameplay",
            label: this.tt("vodClips.layoutGameplayCropLabel", "Gameplay"),
            className: "clip-time-editor-layout-box--yellow",
            rect: layout.gameplay.gameplay,
          },
          {
            key: "gameplay.face",
            label: this.tt("vodClips.layoutFaceCropLabel", "Rostro"),
            className: "clip-time-editor-layout-box--blue",
            rect: layout.gameplay.face,
          },
        ];
      }
      return [];
    },
    clipTimeEditorSourceLayoutHandleStyle(handle) {
      const rect = this.clampClipTimeEditorLayoutRect(handle?.rect);
      return {
        left: `${rect.x * 100}%`,
        top: `${rect.y * 100}%`,
        width: `${rect.width * 100}%`,
        height: `${rect.height * 100}%`,
      };
    },
    getClipTimeEditorLayoutRect(target) {
      const layout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor?.draftPreviewLayout
      );
      if (target === "just_chatting.crop") return layout.just_chatting.crop;
      if (target === "gameplay.gameplay") return layout.gameplay.gameplay;
      if (target === "gameplay.face") return layout.gameplay.face;
      return null;
    },
    setClipTimeEditorLayoutRect(target, rect) {
      const layout = this.normalizeClipTimeEditorPreviewLayoutForVideo(
        this.clipTimeEditor?.draftPreviewLayout
      );
      if (target === "just_chatting.crop") {
        layout.just_chatting.crop = this.clipTimeEditorVerticalSourceRectForVideo(
          this.clipTimeEditorSourceVideoSize().width,
          this.clipTimeEditorSourceVideoSize().height,
          rect
        );
      } else if (target === "gameplay.gameplay") {
        layout.gameplay.gameplay = this.clipTimeEditorVerticalSourceRectForVideo(
          this.clipTimeEditorSourceVideoSize().width,
          this.clipTimeEditorSourceVideoSize().height,
          rect
        );
      } else if (target === "gameplay.face") {
        layout.gameplay.face = this.clipTimeEditorSquareSourceRectForVideo(
          this.clipTimeEditorSourceVideoSize().width,
          this.clipTimeEditorSourceVideoSize().height,
          rect
        );
      }
      this.clipTimeEditor.draftPreviewLayout = layout;
      this.clearClipTimeEditorPreviewCaches();
      this.scheduleClipTimeEditorPreviewRender();
    },
    clipTimeEditorVideoSrc(clip) {
      if (!clip || clip !== this.clipTimeEditor.clip) return "";
      return this.clipTimeEditor.privateMediaUrl;
    },
    clipEditorAbsoluteToVideoSeconds(absoluteSeconds) {
      return Math.max(
        0,
        Number(absoluteSeconds || 0) - Number(this.clipTimeEditor.editWindowStart || 0)
      );
    },
    clipEditorVideoToAbsoluteSeconds(videoSeconds) {
      return (
        Number(this.clipTimeEditor.editWindowStart || 0)
        + Math.max(0, Number(videoSeconds || 0))
      );
    },
    clipTimeEditorRangeSeconds() {
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const maxTime = Number(this.clipTimeEditor.maxTime || 0);
      return Math.max(0, maxTime - minTime);
    },
    clipTimeEditorTrackStyle() {
      const range = this.clipTimeEditorRangeSeconds();
      if (!this.clipTimeEditor.clip || range <= 0) {
        return { "--orig-left": "0%", "--orig-width": "0%" };
      }
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const left = ((Number(this.clipTimeEditor.baseStart || 0) - minTime) / range) * 100;
      const width = ((Number(this.clipTimeEditor.baseEnd || 0) - Number(this.clipTimeEditor.baseStart || 0)) / range) * 100;
      return {
        "--orig-left": `${left}%`,
        "--orig-width": `${width}%`,
      };
    },
    clipTimeEditorSelectionStyle() {
      const range = this.clipTimeEditorRangeSeconds();
      if (!this.clipTimeEditor.clip || range <= 0) {
        return { left: "0%", width: "0%" };
      }
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const left = ((Number(this.clipTimeEditor.draftStart || 0) - minTime) / range) * 100;
      const width = ((Number(this.clipTimeEditor.draftEnd || 0) - Number(this.clipTimeEditor.draftStart || 0)) / range) * 100;
      return {
        left: `${left}%`,
        width: `${width}%`,
      };
    },
    clipTimeEditorMarkers() {
      const range = this.clipTimeEditorRangeSeconds();
      if (!this.clipTimeEditor.clip || range <= 0) return [];
      const roundedRange = Number(range.toFixed(3));
      const key = `${roundedRange}`;
      if (this.clipTimeEditorMarkerCache.majorKey === key) {
        return this.clipTimeEditorMarkerCache.major;
      }
      const markers = [];
      const maxMarkers = 80;
      const step = Math.max(10, Math.ceil(range / maxMarkers / 10) * 10);
      let current = 0;
      while (current <= range) {
        const percent = (current / range) * 100;
        markers.push({
          time: current,
          percent: Math.max(0, Math.min(100, percent)),
        });
        current += step;
      }
      this.clipTimeEditorMarkerCache.majorKey = key;
      this.clipTimeEditorMarkerCache.major = markers;
      return markers;
    },
    clipTimeEditorMinorMarkers() {
      const range = this.clipTimeEditorRangeSeconds();
      if (!this.clipTimeEditor.clip || range <= 0) return [];
      const roundedRange = Number(range.toFixed(3));
      const key = `${roundedRange}`;
      if (this.clipTimeEditorMarkerCache.minorKey === key) {
        return this.clipTimeEditorMarkerCache.minor;
      }
      const markers = [];
      const maxMarkers = 90;
      const step = Math.max(1, Math.ceil(range / maxMarkers));
      let current = step;
      while (current < range) {
        if (current % 10 !== 0) {
          const percent = (current / range) * 100;
          if (percent > 0 && percent < 100) {
            markers.push({
              time: current,
              percent,
            });
          }
        }
        current += step;
      }
      this.clipTimeEditorMarkerCache.minorKey = key;
      this.clipTimeEditorMarkerCache.minor = markers;
      return markers;
    },
    clipTimeEditorSecondsFromClientX(clientX) {
      const track = this.$refs.clipTimeEditorTrack;
      if (!track || !track.getBoundingClientRect) return null;
      const rect = track.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const ratio = rect.width > 0 ? x / rect.width : 0;
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const maxTime = Number(this.clipTimeEditor.maxTime || 0);
      return minTime + ratio * Math.max(0, maxTime - minTime);
    },
    onClipTimeEditorTrackPointerDown(event) {
      if (this.clipTimeEditorSaving) return;
      const clickTime = this.clipTimeEditorSecondsFromClientX(event.clientX);
      if (clickTime == null) return;
      this.stageClipTimeEditorUndoSnapshot();

      const duration = Math.max(
        1,
        Number(this.clipTimeEditor.draftEnd || 0) - Number(this.clipTimeEditor.draftStart || 0)
      );
      let newStart = clickTime - (duration / 2);
      let newEnd = newStart + duration;

      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const maxTime = Number(this.clipTimeEditor.maxTime || 0);
      if (newStart < minTime) {
        newStart = minTime;
        newEnd = minTime + duration;
      }
      if (newEnd > maxTime) {
        newEnd = maxTime;
        newStart = Math.max(minTime, maxTime - duration);
      }

      this.clipTimeEditor.draftStart = newStart;
      this.clipTimeEditor.draftEnd = newEnd;
      this.clampClipTimeEditorRange();
      this.pauseClipTimeEditorPreviewVideos();
      this.previewClipTimeEditorDraft({ shouldPlay: false });
    },
    onClipTimeEditorSelectionPointerDown(event) {
      if (this.clipTimeEditorSaving) return;
      this.startClipTimeEditorDrag("move", event);
    },
    onClipTimeEditorHandlePointerDown(which, event) {
      if (this.clipTimeEditorSaving) return;
      if (which === "start") {
        this.startClipTimeEditorDrag("start", event);
      } else if (which === "end") {
        this.startClipTimeEditorDrag("end", event);
      }
    },
    startClipTimeEditorDrag(mode, event) {
      const track = this.$refs.clipTimeEditorTrack;
      let trackRect = null;
      this.stageClipTimeEditorUndoSnapshot();
      this.pauseClipTimeEditorPreviewVideos();
      if (track && track.setPointerCapture) {
        try {
          track.setPointerCapture(event.pointerId);
        } catch (_err) {
          // noop
        }
      }
      if (track && track.getBoundingClientRect) {
        trackRect = track.getBoundingClientRect();
      }

      this.clipTimeEditor.drag.active = true;
      this.clipTimeEditor.drag.mode = mode;
      this.clipTimeEditor.drag.startX = event.clientX;
      this.clipTimeEditor.drag.startDraftStart = Number(this.clipTimeEditor.draftStart || 0);
      this.clipTimeEditor.drag.startDraftEnd = Number(this.clipTimeEditor.draftEnd || 0);
      this.clipTimeEditor.drag.trackLeft = Number(trackRect?.left || 0);
      this.clipTimeEditor.drag.trackWidth = Number(trackRect?.width || 0);
      this.clipTimeEditor.drag.trackRange = Math.max(
        0,
        Number(this.clipTimeEditor.maxTime || 0) - Number(this.clipTimeEditor.minTime || 0)
      );

      window.addEventListener("pointermove", this.onClipTimeEditorPointerMove, { passive: false });
      window.addEventListener("pointerup", this.onClipTimeEditorPointerUp, { passive: true });
      window.addEventListener("pointercancel", this.onClipTimeEditorPointerUp, { passive: true });
    },
    onClipTimeEditorPointerMove(event) {
      if (!this.clipTimeEditor.drag.active) return;
      event.preventDefault();
      this.clipTimeEditorDragPendingClientX = Number(event.clientX || 0);
      if (this.clipTimeEditorDragFrameRequestId != null) return;
      if (
        typeof window === "undefined"
        || typeof window.requestAnimationFrame !== "function"
      ) {
        this.applyClipTimeEditorDragClientX(this.clipTimeEditorDragPendingClientX);
        return;
      }
      this.clipTimeEditorDragFrameRequestId = window.requestAnimationFrame(() => {
        const pendingClientX = this.clipTimeEditorDragPendingClientX;
        this.clipTimeEditorDragFrameRequestId = null;
        this.clipTimeEditorDragPendingClientX = null;
        this.applyClipTimeEditorDragClientX(pendingClientX);
      });
    },
    applyClipTimeEditorDragClientX(clientX) {
      if (!this.clipTimeEditor.drag.active) return;
      const range = Number(this.clipTimeEditor.drag.trackRange || 0);
      const trackWidth = Number(this.clipTimeEditor.drag.trackWidth || 0);
      if (trackWidth <= 0 || range <= 0) return;
      const dx = Number(clientX || 0) - Number(this.clipTimeEditor.drag.startX || 0);
      const deltaSeconds = (dx / trackWidth) * range;

      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const maxTime = Number(this.clipTimeEditor.maxTime || 0);
      const minDuration = 1;
      const startBase = Number(this.clipTimeEditor.drag.startDraftStart || 0);
      const endBase = Number(this.clipTimeEditor.drag.startDraftEnd || 0);

      if (this.clipTimeEditor.drag.mode === "move") {
        const duration = Math.max(minDuration, endBase - startBase);
        let nextStart = startBase + deltaSeconds;
        let nextEnd = endBase + deltaSeconds;
        if (nextStart < minTime) {
          nextStart = minTime;
          nextEnd = minTime + duration;
        }
        if (nextEnd > maxTime) {
          nextEnd = maxTime;
          nextStart = Math.max(minTime, maxTime - duration);
        }
        this.clipTimeEditor.draftStart = nextStart;
        this.clipTimeEditor.draftEnd = nextEnd;
      } else if (this.clipTimeEditor.drag.mode === "start") {
        let nextStart = startBase + deltaSeconds;
        const currentEnd = Number(this.clipTimeEditor.draftEnd || endBase);
        nextStart = Math.max(minTime, Math.min(nextStart, currentEnd - minDuration));
        this.clipTimeEditor.draftStart = nextStart;
      } else if (this.clipTimeEditor.drag.mode === "end") {
        let nextEnd = endBase + deltaSeconds;
        const currentStart = Number(this.clipTimeEditor.draftStart || startBase);
        nextEnd = Math.min(maxTime, Math.max(nextEnd, currentStart + minDuration));
        this.clipTimeEditor.draftEnd = nextEnd;
      }

      this.clampClipTimeEditorRange();
    },
    cancelClipTimeEditorDragFrame() {
      if (
        this.clipTimeEditorDragFrameRequestId != null
        && typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.clipTimeEditorDragFrameRequestId);
      }
      this.clipTimeEditorDragFrameRequestId = null;
      this.clipTimeEditorDragPendingClientX = null;
    },
    onClipTimeEditorPointerUp() {
      const hadActiveDrag = Boolean(this.clipTimeEditor?.drag?.active);
      if (this.clipTimeEditorDragPendingClientX != null) {
        this.applyClipTimeEditorDragClientX(this.clipTimeEditorDragPendingClientX);
      }
      this.stopClipTimeEditorDrag();
      if (hadActiveDrag) {
        this.previewClipTimeEditorDraft({ shouldPlay: false });
      }
    },
    stopClipTimeEditorDrag() {
      window.removeEventListener("pointermove", this.onClipTimeEditorPointerMove);
      window.removeEventListener("pointerup", this.onClipTimeEditorPointerUp);
      window.removeEventListener("pointercancel", this.onClipTimeEditorPointerUp);
      this.cancelClipTimeEditorDragFrame();
      if (this.clipTimeEditor && this.clipTimeEditor.drag) {
        this.clipTimeEditor.drag.active = false;
        this.clipTimeEditor.drag.mode = null;
        this.clipTimeEditor.drag.trackLeft = 0;
        this.clipTimeEditor.drag.trackWidth = 0;
        this.clipTimeEditor.drag.trackRange = 0;
      }
    },
    onClipTimeEditorLayoutPointerDown(target, event) {
      if (!this.clipTimeEditorShowsSourceLayoutControls || this.clipTimeEditorSaving) return;
      const rect = this.getClipTimeEditorLayoutRect(target);
      const video = this.$refs.clipTimeEditorVideo;
      if (!rect || !video || video.tagName !== "VIDEO") return;
      const videoRect = video.getBoundingClientRect ? video.getBoundingClientRect() : null;
      if (!videoRect || videoRect.width <= 0 || videoRect.height <= 0) return;
      this.stageClipTimeEditorUndoSnapshot();
      const wasPlaying = Boolean(this.clipTimeEditor.isPlaying && !video.paused);
      if (wasPlaying) {
        this.pauseClipTimeEditorPreviewVideos({ render: false });
      }
      this.clipTimeEditor.layoutDrag.active = true;
      this.clipTimeEditor.layoutDrag.target = target;
      this.clipTimeEditor.layoutDrag.startClientX = Number(event.clientX || 0);
      this.clipTimeEditor.layoutDrag.startClientY = Number(event.clientY || 0);
      this.clipTimeEditor.layoutDrag.startRect = { ...rect };
      this.clipTimeEditor.layoutDrag.videoWidth = Number(videoRect.width || 0);
      this.clipTimeEditor.layoutDrag.videoHeight = Number(videoRect.height || 0);
      this.clipTimeEditor.layoutDrag.wasPlaying = wasPlaying;
      window.addEventListener("pointermove", this.onClipTimeEditorLayoutPointerMove, { passive: false });
      window.addEventListener("pointerup", this.onClipTimeEditorLayoutPointerUp, { passive: true });
      window.addEventListener("pointercancel", this.onClipTimeEditorLayoutPointerUp, { passive: true });
    },
    onClipTimeEditorLayoutPointerMove(event) {
      if (!this.clipTimeEditor?.layoutDrag?.active) return;
      event.preventDefault();
      this.clipTimeEditorLayoutDragPendingPoint = {
        clientX: Number(event.clientX || 0),
        clientY: Number(event.clientY || 0),
      };
      if (this.clipTimeEditorLayoutDragFrameRequestId != null) return;
      if (
        typeof window === "undefined"
        || typeof window.requestAnimationFrame !== "function"
      ) {
        this.applyClipTimeEditorLayoutDragPoint(this.clipTimeEditorLayoutDragPendingPoint);
        return;
      }
      this.clipTimeEditorLayoutDragFrameRequestId = window.requestAnimationFrame(() => {
        const pendingPoint = this.clipTimeEditorLayoutDragPendingPoint;
        this.clipTimeEditorLayoutDragFrameRequestId = null;
        this.clipTimeEditorLayoutDragPendingPoint = null;
        this.applyClipTimeEditorLayoutDragPoint(pendingPoint);
      });
    },
    applyClipTimeEditorLayoutDragPoint(point) {
      if (!this.clipTimeEditor?.layoutDrag?.active || !point) return;
      const drag = this.clipTimeEditor.layoutDrag;
      const videoWidth = Number(drag.videoWidth || 0);
      const videoHeight = Number(drag.videoHeight || 0);
      if (videoWidth <= 0 || videoHeight <= 0) return;
      const startRect = drag.startRect || null;
      if (!startRect) return;
      const deltaX = (Number(point.clientX || 0) - Number(drag.startClientX || 0)) / videoWidth;
      const deltaY = (Number(point.clientY || 0) - Number(drag.startClientY || 0)) / videoHeight;
      const width = Number(startRect.width || 0);
      const height = Number(startRect.height || 0);
      const nextRect = {
        x: Math.min(Math.max(0, Number(startRect.x || 0) + deltaX), Math.max(0, 1 - width)),
        y: Math.min(Math.max(0, Number(startRect.y || 0) + deltaY), Math.max(0, 1 - height)),
        width,
        height,
      };
      this.setClipTimeEditorLayoutRect(drag.target, nextRect);
    },
    cancelClipTimeEditorLayoutDragFrame() {
      if (
        this.clipTimeEditorLayoutDragFrameRequestId != null
        && typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.clipTimeEditorLayoutDragFrameRequestId);
      }
      this.clipTimeEditorLayoutDragFrameRequestId = null;
      this.clipTimeEditorLayoutDragPendingPoint = null;
    },
    onClipTimeEditorLayoutPointerUp() {
      const shouldResume = Boolean(this.clipTimeEditor?.layoutDrag?.wasPlaying);
      if (this.clipTimeEditorLayoutDragPendingPoint) {
        this.applyClipTimeEditorLayoutDragPoint(this.clipTimeEditorLayoutDragPendingPoint);
      }
      this.stopClipTimeEditorLayoutDrag();
      this.previewClipTimeEditorDraft({ shouldPlay: shouldResume });
    },
    stopClipTimeEditorLayoutDrag() {
      window.removeEventListener("pointermove", this.onClipTimeEditorLayoutPointerMove);
      window.removeEventListener("pointerup", this.onClipTimeEditorLayoutPointerUp);
      window.removeEventListener("pointercancel", this.onClipTimeEditorLayoutPointerUp);
      this.cancelClipTimeEditorLayoutDragFrame();
      if (!this.clipTimeEditor?.layoutDrag) return;
      this.clipTimeEditor.layoutDrag.active = false;
      this.clipTimeEditor.layoutDrag.target = null;
      this.clipTimeEditor.layoutDrag.startRect = null;
      this.clipTimeEditor.layoutDrag.videoWidth = 0;
      this.clipTimeEditor.layoutDrag.videoHeight = 0;
      this.clipTimeEditor.layoutDrag.wasPlaying = false;
    },
    clampClipTimeEditorRange() {
      const minDuration = 1;
      const minTime = Number(this.clipTimeEditor.minTime || 0);
      const maxTime = Number(this.clipTimeEditor.maxTime || 0);
      let draftStart = Number(this.clipTimeEditor.draftStart || 0);
      let draftEnd = Number(this.clipTimeEditor.draftEnd || 0);

      if (!Number.isFinite(draftStart)) draftStart = minTime;
      if (!Number.isFinite(draftEnd)) draftEnd = Math.max(minTime + minDuration, draftStart + minDuration);

      draftStart = Math.max(minTime, Math.min(draftStart, maxTime));
      draftEnd = Math.max(minTime, Math.min(draftEnd, maxTime));

      if (draftEnd - draftStart < minDuration) {
        draftEnd = Math.min(maxTime, draftStart + minDuration);
        if (draftEnd - draftStart < minDuration) {
          draftStart = Math.max(minTime, draftEnd - minDuration);
        }
      }

      this.clipTimeEditor.draftStart = draftStart;
      this.clipTimeEditor.draftEnd = draftEnd;
    },
    async openClipTimeEditorModal(clip, options = {}) {
      if (!clip || !clip.id) return;
      const syncRoute = options?.syncRoute !== false;
      this.closeMobileWorkspacePanel(false);
      this.stopVideoCardMediaPlayback();
      this.stopClipTimeEditorPreviewRenderer();
      this.cancelClipTimeEditorPlaybackScrubSeek();
      this.clearClipTimeEditorPlaybackLoadingState();
      this.clearClipTimeEditorPreviewCaches();
      this.resetClipTimeEditorMarkerCache();
      this.clearClipTimeEditorUndoSnapshot();
      this.showClipTranscriptEditorModal = false;
      this.clipTranscriptLoading = false;
      this.clipTranscriptError = "";
      this.clipTranscriptBase = null;
      this.clipTranscriptDraft = null;
      this.clipTranscriptModalBlocks = [];
      const baseStart = Number(clip.startSeconds ?? clip.start_seconds ?? 0);
      const baseEnd = Number(clip.endSeconds ?? clip.end_seconds ?? 0);
      if (!Number.isFinite(baseStart) || !Number.isFinite(baseEnd) || baseEnd <= baseStart) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (this.isScheduleCollectionsDestroyed) return;
      if (!session) {
        this.showClipActionSnackbar(this.tt("vodClips.authRequired", "Se requiere autenticación"), "info");
        return;
      }

      const editWindowStart = Number(
        clip.edit_window_start_seconds ?? clip.editWindowStartSeconds
      );
      const editWindowEnd = Number(
        clip.edit_window_end_seconds ?? clip.editWindowEndSeconds
      );
      if (
        !Number.isFinite(editWindowStart)
        || !Number.isFinite(editWindowEnd)
        || editWindowEnd <= editWindowStart
      ) {
        this.showClipActionSnackbar(
          this.clipEditorUnavailableMessage(clip),
          "info"
        );
        return;
      }

      const minTime = editWindowStart;
      const maxTime = editWindowEnd;
      const baseVideoType = this.normalizeClipVideoType(
        clip.effective_video_type ?? clip.effectiveVideoType ?? clip.video_type ?? clip.videoType ?? null
      );
      const currentProfile = useCurrentUserStore().profile;
      const clipSubtitlesEnabled = Boolean(
        clip.effective_subtitles_enabled
        ?? clip.effectiveSubtitlesEnabled
        ?? clip.subtitles_enabled
        ?? clip.subtitlesEnabled
        ?? clip.job?.subtitles_enabled
        ?? clip.job?.subtitlesEnabled
      );
      const baseSubtitlesEnabled = currentProfile?.subtitles_enabled ?? clipSubtitlesEnabled;
      const baseContextText = String(
        clip.context_text ?? clip.contextText ?? ""
      ).trim();
      const baseContextEnabled = Boolean(
        clip.use_context ?? clip.useContext ?? false
      );
      const baseContextPosition = this.cloneClipTimeEditorContextPosition(
        clip.context_position ?? clip.contextPosition ?? null
      );
      const basePreviewLayout = this.cloneClipTimeEditorPreviewLayout(
        clip.preview_layout ?? clip.previewLayout ?? null
      );
      let privateMediaUrl;
      try {
        privateMediaUrl = buildTwitchClipPrivateMediaUrl(session, clip.id);
      } catch (error) {
        this.showClipActionSnackbar(
          error?.message || this.clipEditorUnavailableMessage(),
          "info"
        );
        return;
      }

      this.clipTimeEditor.clip = clip;
      this.clipTimeEditor.privateMediaUrl = privateMediaUrl;
      this.clipTimeEditor.baseStart = baseStart;
      this.clipTimeEditor.baseEnd = baseEnd;
      this.clipTimeEditor.baseVideoType = baseVideoType;
      this.clipTimeEditor.baseSubtitlesEnabled = baseSubtitlesEnabled;
      this.clipTimeEditor.editWindowStart = editWindowStart;
      this.clipTimeEditor.editWindowEnd = editWindowEnd;
      this.clipTimeEditor.minTime = minTime;
      this.clipTimeEditor.maxTime = maxTime;
      this.clipTimeEditor.draftStart = baseStart;
      this.clipTimeEditor.draftEnd = baseEnd;
      this.clipTimeEditor.draftVideoType = baseVideoType;
      this.clipTimeEditor.draftSubtitlesEnabled = baseSubtitlesEnabled;
      this.clipTimeEditorSubtitleStyleTouched = false;
      if (currentProfile?.subtitle_font && currentProfile?.subtitle_active_color) {
        this.clipTimeEditor.draftSubtitleStyle = {
          font: currentProfile.subtitle_font,
          activeColor: currentProfile.subtitle_active_color,
          activeEffect: currentProfile.subtitle_active_effect,
        };
      }
      this.clipTimeEditor.basePreviewLayout = basePreviewLayout;
      this.clipTimeEditor.draftPreviewLayout = this.cloneClipTimeEditorPreviewLayout(basePreviewLayout);
      this.clipTimeEditor.baseContextEnabled = baseContextEnabled;
      this.clipTimeEditor.draftContextEnabled = baseContextEnabled;
      this.clipTimeEditor.baseContextText = baseContextText;
      this.clipTimeEditor.draftContextText = baseContextText;
      this.clipTimeEditor.baseContextPosition = this.cloneClipTimeEditorContextPosition(baseContextPosition);
      this.clipTimeEditor.draftContextPosition = this.cloneClipTimeEditorContextPosition(baseContextPosition);
      this.clipTimeEditor.videoTypeTouched = false;
      this.clipTimeEditor.previewStart = baseStart;
      this.clipTimeEditor.currentAbsolute = baseStart;
      this.clipTimeEditor.videoDuration = null;
      this.clipTimeEditor.isPlaying = false;
      this.clipTimeEditor.isMuted = false;
      this.clipTimeEditor.playbackScrubActive = false;
      this.clipTimeEditor.playbackScrubWasPlaying = false;
      this.clipTimeEditor.errorMessage = "";
      this.clipTimeEditor.drag.active = false;
      this.clipTimeEditor.drag.mode = null;
      this.clipTimeEditor.drag.startX = 0;
      this.clipTimeEditor.drag.startDraftStart = baseStart;
      this.clipTimeEditor.drag.startDraftEnd = baseEnd;
      this.clipTimeEditor.drag.trackLeft = 0;
      this.clipTimeEditor.drag.trackWidth = 0;
      this.clipTimeEditor.drag.trackRange = 0;
      this.clipTimeEditor.contextDrag.active = false;
      this.clipTimeEditor.contextDrag.startClientX = 0;
      this.clipTimeEditor.contextDrag.startClientY = 0;
      this.clipTimeEditor.contextDrag.startX = baseContextPosition.x;
      this.clipTimeEditor.contextDrag.startY = baseContextPosition.y;
      this.clipTimeEditor.contextDrag.stageWidth = 0;
      this.clipTimeEditor.contextDrag.stageHeight = 0;
      this.clipTimeEditor.contextDrag.overlayWidth = 0;
      this.clipTimeEditor.contextDrag.overlayHeight = 0;
      this.clipTimeEditor.layoutDrag.active = false;
      this.clipTimeEditor.layoutDrag.target = null;
      this.clipTimeEditor.layoutDrag.startClientX = 0;
      this.clipTimeEditor.layoutDrag.startClientY = 0;
      this.clipTimeEditor.layoutDrag.startRect = null;
      this.clipTimeEditor.layoutDrag.videoWidth = 0;
      this.clipTimeEditor.layoutDrag.videoHeight = 0;
      this.clipTimeEditorSaving = false;
      this.selectedClipId = clip.id;
      this.showClipTimeEditorModal = true;
      if (syncRoute) {
        await this.syncClipEditorRouteState(true, { clipId: clip.id });
        if (this.isScheduleCollectionsDestroyed) return;
      }

      this.$nextTick(() => {
        this.previewClipTimeEditorDraft();
      });
    },
    closeClipTimeEditorModal(force = false, options = {}) {
      if (this.clipTimeEditorSaving && !force) return;
      const syncRoute = options?.syncRoute !== false;
      const closingClip = this.clipTimeEditor?.clip || null;
      const closingClipId = closingClip?.id || null;
      const closingJobId = String(
        closingClip?.job_id
        ?? closingClip?.jobId
        ?? this.selectedCollectionJobId
        ?? ""
      ).trim();
      this.stopClipTimeEditorDrag();
      this.stopClipTimeEditorLayoutDrag();
      this.stopClipTimeEditorContextDrag();
      this.cancelClipTimeEditorPlaybackScrubSeek();
      this.pauseClipTimeEditorMainVideo({ render: false });
      this.stopClipTimeEditorPreviewRenderer();
      this.releaseClipTimeEditorMediaResources();
      this.clearClipTimeEditorPlaybackLoadingState();
      this.clearClipTimeEditorPreviewCaches();
      this.resetClipTimeEditorMarkerCache();
      this.clearClipTimeEditorUndoSnapshot();
      this.closeClipTranscriptEditor();
      this.clipTranscriptLoading = false;
      this.clipTranscriptBase = null;
      this.clipTranscriptDraft = null;
      this.showClipTimeEditorModal = false;
      this.clipTimeEditorSaving = false;
      this.clipTimeEditor = {
        clip: null,
        privateMediaUrl: "",
        baseStart: 0,
        baseEnd: 0,
        baseVideoType: "auto",
        baseSubtitlesEnabled: true,
        editWindowStart: 0,
        editWindowEnd: 0,
        minTime: 0,
        maxTime: 0,
        draftStart: 0,
        draftEnd: 0,
        draftVideoType: "auto",
        draftSubtitlesEnabled: true,
        draftSubtitleStyle: {
          font: "DejaVu Sans",
          activeColor: "#FFF302",
          activeEffect: "text_color",
        },
        basePreviewLayout: this.defaultClipTimeEditorPreviewLayout(),
        draftPreviewLayout: this.defaultClipTimeEditorPreviewLayout(),
        baseContextEnabled: false,
        draftContextEnabled: false,
        baseContextText: "",
        draftContextText: "",
        baseContextPosition: {
          x: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          y: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
        },
        draftContextPosition: {
          x: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          y: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
        },
        videoTypeTouched: false,
        previewStart: 0,
        currentAbsolute: 0,
        videoDuration: null,
        isPlaying: false,
        isMuted: false,
        playbackScrubActive: false,
        playbackScrubWasPlaying: false,
        errorMessage: "",
        drag: {
          active: false,
          mode: null,
          startX: 0,
          startDraftStart: 0,
          startDraftEnd: 0,
          trackLeft: 0,
          trackWidth: 0,
          trackRange: 0,
        },
        contextDrag: {
          active: false,
          startClientX: 0,
          startClientY: 0,
          startX: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_X,
          startY: CLIP_TIME_EDITOR_CONTEXT_DEFAULT_Y,
          stageWidth: 0,
          stageHeight: 0,
          overlayWidth: 0,
          overlayHeight: 0,
        },
        layoutDrag: {
          active: false,
          target: null,
          startClientX: 0,
          startClientY: 0,
          startRect: null,
          videoWidth: 0,
          videoHeight: 0,
          wasPlaying: false,
        },
      };
      if (closingJobId) {
        this.selectedCollectionJobId = closingJobId;
        if (closingClipId) {
          this.selectedClipId = closingClipId;
        }
        if (syncRoute && this.isCreateClipsRouteContext()) {
          this.syncCollectionSelectionToRoute(closingJobId);
        }
      }
    },
    onClipTimeEditorLoadedMetadata(event) {
      const video = event?.target;
      if (!video) return;
      this.clearClipTimeEditorPreviewCaches();
      video.muted = Boolean(this.clipTimeEditor.isMuted);
      const duration = Number(video.duration || 0);
      if (Number.isFinite(duration) && duration > 0) {
        this.clipTimeEditor.videoDuration = duration;
        const cappedMaxTime = Math.max(
          this.clipTimeEditor.minTime,
          Math.min(
            Number(this.clipTimeEditor.editWindowEnd || 0),
            Number(this.clipTimeEditor.editWindowStart || 0) + duration
          )
        );
        this.clipTimeEditor.maxTime = cappedMaxTime;
        this.clampClipTimeEditorRange();
      }
      this.ensureClipTimeEditorPreviewLayoutForVideo({
        syncBase: this.normalizeClipVideoType(this.clipTimeEditor.baseVideoType)
          === this.normalizeClipVideoType(this.clipTimeEditor.draftVideoType),
      });
      this.previewClipTimeEditorDraft();
    },
    onClipTimeEditorVideoTypeChange() {
      this.clipTimeEditor.videoTypeTouched = true;
      this.clearClipTimeEditorPreviewCaches();
      this.ensureClipTimeEditorPreviewLayoutForVideo();
      this.stopClipTimeEditorLayoutDrag();
      if (!this.clipTimeEditorIsIrlVideoType) {
        this.stopClipTimeEditorContextDrag();
      }
      this.pauseClipTimeEditorPreviewVideos();
      this.$nextTick(() => {
        this.previewClipTimeEditorDraft({ shouldPlay: false });
      });
    },
    setClipTimeEditorVideoType(videoType) {
      const nextType = this.normalizeClipVideoType(videoType);
      if (this.normalizeClipVideoType(this.clipTimeEditor?.draftVideoType) === nextType) return;
      this.clipTimeEditor.draftVideoType = nextType;
      this.onClipTimeEditorVideoTypeChange();
    },
    clampClipTimeEditorPlaybackAbsolute(absoluteSeconds) {
      const draftStart = Number(this.clipTimeEditor.draftStart || 0);
      const draftEnd = Number(this.clipTimeEditor.draftEnd || draftStart);
      const value = Number(absoluteSeconds || draftStart);
      if (!Number.isFinite(value)) return draftStart;
      return Math.max(draftStart, Math.min(draftEnd, value));
    },
    clipTimeEditorPlaybackAbsoluteFromEvent(event) {
      const offsetSeconds = Number(event?.target?.value || 0);
      const draftStart = Number(this.clipTimeEditor.draftStart || 0);
      return this.clampClipTimeEditorPlaybackAbsolute(draftStart + offsetSeconds);
    },
    resolveClipTimeEditorPreviewCanvasElement() {
      const refValue = this.$refs.clipTimeEditorPreviewCanvas;
      const canvas = Array.isArray(refValue) ? refValue[0] : refValue;
      if (!canvas || canvas.tagName !== "CANVAS") return null;
      return canvas;
    },
    resolveClipTimeEditorPreviewStageElement() {
      const stageRef = this.clipTimeEditorIsIrlVideoType
        ? this.$refs.clipTimeEditorContextStage
        : this.$refs.clipTimeEditorGenericStage;
      const stage = Array.isArray(stageRef) ? stageRef[0] : stageRef;
      if (!stage || !stage.getBoundingClientRect) return null;
      return stage;
    },
    clipTimeEditorCanvasSize() {
      if (this.clipTimeEditorPreviewCanvasSize) {
        return this.clipTimeEditorPreviewCanvasSize;
      }
      const canvas = this.resolveClipTimeEditorPreviewCanvasElement();
      const stage = this.resolveClipTimeEditorPreviewStageElement();
      let cssWidth = 0;
      let cssHeight = 0;
      if (stage) {
        const rect = stage.getBoundingClientRect();
        cssWidth = Number(rect.width || 0);
        cssHeight = Number(rect.height || 0);
      }
      if ((cssWidth <= 0 || cssHeight <= 0) && canvas) {
        cssWidth = Number(canvas.clientWidth || 0);
        cssHeight = Number(canvas.clientHeight || 0);
      }
      if (cssWidth <= 0 || cssHeight <= 0) {
        cssWidth = 200;
        cssHeight = (cssWidth * 16) / 9;
      }
      const devicePixelRatio = typeof window !== "undefined"
        ? Number(window.devicePixelRatio || 1)
        : 1;
      const ratio = Math.max(
        1,
        Math.min(
          CLIP_TIME_EDITOR_PREVIEW_MAX_DEVICE_PIXEL_RATIO,
          Number.isFinite(devicePixelRatio) ? devicePixelRatio : 1
        )
      );
      const size = {
        width: Math.max(1, Math.round(cssWidth * ratio)),
        height: Math.max(1, Math.round(cssHeight * ratio)),
      };
      const sizeKey = `${size.width}x${size.height}@${ratio}`;
      if (this.clipTimeEditorPreviewCanvasSizeKey !== sizeKey) {
        this.clipTimeEditorIrlBlurCache = null;
      }
      this.clipTimeEditorPreviewCanvasSizeKey = sizeKey;
      this.clipTimeEditorPreviewCanvasSize = size;
      return size;
    },
    beginClipTimeEditorPlaybackLoadingState(options = {}) {
      const delayMs = Math.max(0, Number(options.delayMs ?? 160));
      this.clipTimeEditorPlaybackSeekOperationId += 1;
      const operationId = this.clipTimeEditorPlaybackSeekOperationId;
      this.cancelClipTimeEditorPlaybackSeekReadyWait();
      if (this.clipTimeEditorPlaybackLoadingTimer) {
        clearTimeout(this.clipTimeEditorPlaybackLoadingTimer);
        this.clipTimeEditorPlaybackLoadingTimer = null;
      }
      this.clipTimeEditorPlaybackLoading = false;
      this.clipTimeEditorPlaybackLoadingTimer = setTimeout(() => {
        if (operationId === this.clipTimeEditorPlaybackSeekOperationId) {
          this.clipTimeEditorPlaybackLoading = true;
        }
      }, delayMs);
      return operationId;
    },
    clearClipTimeEditorPlaybackLoadingState(operationId = null, options = {}) {
      if (
        operationId !== null
        && operationId !== this.clipTimeEditorPlaybackSeekOperationId
      ) {
        return false;
      }
      const shouldInvalidate = operationId === null && options.invalidate !== false;
      if (shouldInvalidate) {
        this.clipTimeEditorPlaybackSeekOperationId += 1;
      }
      if (shouldInvalidate || options.cancelSeekWait === true) {
        this.cancelClipTimeEditorPlaybackSeekReadyWait();
      }
      if (this.clipTimeEditorPlaybackLoadingTimer) {
        clearTimeout(this.clipTimeEditorPlaybackLoadingTimer);
        this.clipTimeEditorPlaybackLoadingTimer = null;
      }
      this.clipTimeEditorPlaybackLoading = false;
      return true;
    },
    cancelClipTimeEditorPlaybackSeekReadyWait() {
      if (typeof this.clipTimeEditorPlaybackSeekReadyCancel !== "function") return;
      const cancelWait = this.clipTimeEditorPlaybackSeekReadyCancel;
      this.clipTimeEditorPlaybackSeekReadyCancel = null;
      cancelWait();
    },
    cancelClipTimeEditorPlaybackScrubSeek() {
      if (
        this.clipTimeEditorPlaybackScrubFrameRequestId != null
        && typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        try {
          window.cancelAnimationFrame(this.clipTimeEditorPlaybackScrubFrameRequestId);
        } catch (_err) {
          // noop
        }
      }
      this.clipTimeEditorPlaybackScrubFrameRequestId = null;
      this.clipTimeEditorPlaybackScrubPendingAbsolute = null;
    },
    applyClipTimeEditorPlaybackScrubSeek() {
      const pendingAbsolute = Number(this.clipTimeEditorPlaybackScrubPendingAbsolute);
      this.clipTimeEditorPlaybackScrubPendingAbsolute = null;
      if (!Number.isFinite(pendingAbsolute)) return;
      const video = this.$refs.clipTimeEditorVideo;
      if (!video || video.tagName !== "VIDEO") return;
      this.clipTimeEditorIrlBlurCache = null;
      try {
        video.currentTime = this.clipEditorAbsoluteToVideoSeconds(pendingAbsolute);
      } catch (_err) {
        // noop
      }
      this.scheduleClipTimeEditorPreviewRender();
    },
    scheduleClipTimeEditorPlaybackScrubSeek(absoluteSeconds) {
      this.clipTimeEditorPlaybackScrubPendingAbsolute = Number(absoluteSeconds);
      if (this.clipTimeEditorPlaybackScrubFrameRequestId != null) return;
      if (
        typeof window === "undefined"
        || typeof window.requestAnimationFrame !== "function"
      ) {
        this.applyClipTimeEditorPlaybackScrubSeek();
        return;
      }
      this.clipTimeEditorPlaybackScrubFrameRequestId = window.requestAnimationFrame(() => {
        this.clipTimeEditorPlaybackScrubFrameRequestId = null;
        this.applyClipTimeEditorPlaybackScrubSeek();
      });
    },
    waitForClipTimeEditorSeekReady(video, operationId, timeoutMs = 900) {
      this.cancelClipTimeEditorPlaybackSeekReadyWait();
      return new Promise((resolve) => {
        if (!video || video.tagName !== "VIDEO") {
          resolve(false);
          return;
        }
        if (operationId !== this.clipTimeEditorPlaybackSeekOperationId) {
          resolve(false);
          return;
        }
        if (!video.seeking && Number(video.readyState || 0) >= 2) {
          resolve(true);
          return;
        }

        let settled = false;
        let timeoutHandle = null;
        const cancelWait = () => {
          finish(false);
        };
        const clearListeners = () => {
          video.removeEventListener("seeked", onReady);
          video.removeEventListener("canplay", onReady);
          video.removeEventListener("loadeddata", onReady);
          video.removeEventListener("error", onReady);
          if (timeoutHandle) {
            clearTimeout(timeoutHandle);
            timeoutHandle = null;
          }
        };
        const finish = (ready) => {
          if (settled) return;
          settled = true;
          clearListeners();
          if (this.clipTimeEditorPlaybackSeekReadyCancel === cancelWait) {
            this.clipTimeEditorPlaybackSeekReadyCancel = null;
          }
          resolve(Boolean(ready));
        };
        const onReady = () => {
          finish(true);
        };
        this.clipTimeEditorPlaybackSeekReadyCancel = cancelWait;

        video.addEventListener("seeked", onReady);
        video.addEventListener("canplay", onReady);
        video.addEventListener("loadeddata", onReady);
        video.addEventListener("error", onReady);
        timeoutHandle = setTimeout(() => {
          finish(true);
        }, Math.max(250, Number(timeoutMs) || 900));
      });
    },
    startClipTimeEditorPreviewRenderer() {
      if (!this.showClipTimeEditorModal) return;
      this.clipTimeEditorPreviewRendererRunning = true;
      this.scheduleClipTimeEditorPreviewRender();
    },
    stopClipTimeEditorPreviewRenderer(options = {}) {
      const shouldRender = options.render === true;
      const video = this.$refs.clipTimeEditorVideo;
      if (this.clipTimeEditorPreviewRenderRequestId != null) {
        if (
          this.clipTimeEditorPreviewRenderRequestType === "video-frame"
          && video
          && video.tagName === "VIDEO"
          && typeof video.cancelVideoFrameCallback === "function"
        ) {
          try {
            video.cancelVideoFrameCallback(this.clipTimeEditorPreviewRenderRequestId);
          } catch (_err) {
            // noop
          }
        } else if (
          this.clipTimeEditorPreviewRenderRequestType === "animation-frame"
          && typeof window !== "undefined"
          && typeof window.cancelAnimationFrame === "function"
        ) {
          try {
            window.cancelAnimationFrame(this.clipTimeEditorPreviewRenderRequestId);
          } catch (_err) {
            // noop
          }
        }
      }
      this.clipTimeEditorPreviewRenderRequestId = null;
      this.clipTimeEditorPreviewRenderRequestType = null;
      this.clipTimeEditorPreviewRendererRunning = false;
      if (shouldRender) {
        this.scheduleClipTimeEditorPreviewRender();
      }
    },
    scheduleClipTimeEditorPreviewRender() {
      if (!this.showClipTimeEditorModal) return;
      if (this.clipTimeEditorPreviewRenderRequestId != null) return;
      const video = this.$refs.clipTimeEditorVideo;
      if (!video || video.tagName !== "VIDEO") return;
      if (
        this.clipTimeEditorPreviewRendererRunning
        && typeof video.requestVideoFrameCallback === "function"
      ) {
        this.clipTimeEditorPreviewRenderRequestType = "video-frame";
        this.clipTimeEditorPreviewRenderRequestId = video.requestVideoFrameCallback(() => {
          this.clipTimeEditorPreviewRenderRequestId = null;
          this.renderClipTimeEditorPreviewFrame();
          if (this.clipTimeEditorPreviewRendererRunning) {
            this.scheduleClipTimeEditorPreviewRender();
          }
        });
        return;
      }
      if (
        typeof window !== "undefined"
        && typeof window.requestAnimationFrame === "function"
      ) {
        this.clipTimeEditorPreviewRenderRequestType = "animation-frame";
        this.clipTimeEditorPreviewRenderRequestId = window.requestAnimationFrame(() => {
          this.clipTimeEditorPreviewRenderRequestId = null;
          this.renderClipTimeEditorPreviewFrame();
          if (this.clipTimeEditorPreviewRendererRunning) {
            this.scheduleClipTimeEditorPreviewRender();
          }
        });
        return;
      }
      this.renderClipTimeEditorPreviewFrame();
      this.clipTimeEditorPreviewRendererRunning = false;
    },
    renderClipTimeEditorPreviewFrame() {
      const video = this.$refs.clipTimeEditorVideo;
      const canvas = this.resolveClipTimeEditorPreviewCanvasElement();
      if (!video || video.tagName !== "VIDEO" || !canvas) return;
      const context = canvas.getContext("2d", { alpha: false }) || canvas.getContext("2d");
      if (!context) return;
      const size = this.clipTimeEditorCanvasSize();
      if (canvas.width !== size.width || canvas.height !== size.height) {
        canvas.width = size.width;
        canvas.height = size.height;
        this.clipTimeEditorPreviewHasFrame = false;
      }
      const canDrawVideoFrame = Number(video.readyState || 0) >= 2
        && Number(video.videoWidth || 0) > 0
        && Number(video.videoHeight || 0) > 0;
      if (!canDrawVideoFrame) {
        if (this.clipTimeEditorIsPodcastVideoType) {
          this.renderClipTimeEditorPodcastUnavailableFrame(context, canvas);
          this.clipTimeEditorPreviewHasFrame = true;
          return;
        }
        // Keep the last valid frame to avoid black flicker while paused/buffering.
        if (!this.clipTimeEditorPreviewHasFrame) {
          context.fillStyle = "#020617";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
        return;
      }
      try {
        if (this.clipTimeEditorIsIrlVideoType) {
          this.renderClipTimeEditorIrlFrame(context, video, canvas);
        } else {
          this.renderClipTimeEditorGenericFrame(context, video, canvas);
        }
        this.clipTimeEditorPreviewHasFrame = true;
      } catch (_err) {
        if (!this.clipTimeEditorPreviewHasFrame) {
          context.fillStyle = "#020617";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    },
    renderClipTimeEditorIrlFrame(ctx, video, canvas) {
      const previewLayout = this.clipTimeEditor?.draftPreviewLayout || this.defaultClipTimeEditorPreviewLayout();
      const irlLayout = previewLayout?.irl || {};
      const backgroundCrop = irlLayout.background || { x: 0, y: 0, width: 1, height: 1 };
      const foregroundCrop = irlLayout.foreground || { x: 0, y: 0, width: 1, height: 1 };
      this.renderClipTimeEditorIrlBlurBackground(ctx, video, canvas, backgroundCrop);

      let foregroundWidth = canvas.width;
      let foregroundHeight = foregroundWidth * (3 / 4);
      if (foregroundHeight > canvas.height) {
        foregroundHeight = canvas.height;
        foregroundWidth = foregroundHeight * (4 / 3);
      }
      const foregroundX = (canvas.width - foregroundWidth) / 2;
      const foregroundY = (canvas.height - foregroundHeight) / 2;
      this.drawClipTimeEditorCover(ctx, video, {
        x: foregroundX,
        y: foregroundY,
        width: foregroundWidth,
        height: foregroundHeight,
      }, foregroundCrop);

      ctx.save();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.12)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(foregroundX, foregroundY + 0.5);
      ctx.lineTo(foregroundX + foregroundWidth, foregroundY + 0.5);
      ctx.moveTo(foregroundX, foregroundY + foregroundHeight - 0.5);
      ctx.lineTo(foregroundX + foregroundWidth, foregroundY + foregroundHeight - 0.5);
      ctx.stroke();
      ctx.restore();
      this.drawClipTimeEditorSubtitlePlaceholder(ctx, canvas, "irl");
    },
    renderClipTimeEditorIrlBlurBackground(ctx, video, canvas, backgroundCrop) {
      const cacheKey = JSON.stringify({
        width: canvas.width,
        height: canvas.height,
        videoWidth: Number(video?.videoWidth || 0),
        videoHeight: Number(video?.videoHeight || 0),
        backgroundCrop,
      });
      const cached = this.clipTimeEditorIrlBlurCache;
      if (cached?.key === cacheKey && cached.canvas) {
        ctx.drawImage(cached.canvas, 0, 0);
        return;
      }
      if (typeof document === "undefined") {
        this.renderClipTimeEditorIrlBlurBackgroundLayer(ctx, video, canvas, backgroundCrop);
        return;
      }
      const cacheCanvas = document.createElement("canvas");
      cacheCanvas.width = canvas.width;
      cacheCanvas.height = canvas.height;
      const cacheContext = cacheCanvas.getContext("2d", { alpha: false }) || cacheCanvas.getContext("2d");
      if (!cacheContext) {
        this.renderClipTimeEditorIrlBlurBackgroundLayer(ctx, video, canvas, backgroundCrop);
        return;
      }
      this.renderClipTimeEditorIrlBlurBackgroundLayer(cacheContext, video, cacheCanvas, backgroundCrop);
      this.clipTimeEditorIrlBlurCache = {
        key: cacheKey,
        canvas: cacheCanvas,
      };
      ctx.drawImage(cacheCanvas, 0, 0);
    },
    renderClipTimeEditorIrlBlurBackgroundLayer(ctx, video, canvas, backgroundCrop) {
      const blurPaddingX = canvas.width * 0.07;
      const blurPaddingY = canvas.height * 0.07;
      ctx.save();
      ctx.filter = "blur(22px) saturate(1.1)";
      this.drawClipTimeEditorCover(ctx, video, {
        x: -blurPaddingX,
        y: -blurPaddingY,
        width: canvas.width + blurPaddingX * 2,
        height: canvas.height + blurPaddingY * 2,
      }, backgroundCrop);
      ctx.restore();

      ctx.save();
      ctx.fillStyle = "rgba(2, 6, 23, 0.2)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    },
    renderClipTimeEditorGenericFrame(ctx, video, canvas) {
      const previewLayout = this.clipTimeEditor?.draftPreviewLayout || this.defaultClipTimeEditorPreviewLayout();
      const normalizedLayout = this.normalizeClipTimeEditorPreviewLayoutForVideo(previewLayout);
      if (this.clipTimeEditorIsPodcastVideoType) {
        this.renderClipTimeEditorPodcastUnavailableFrame(ctx, canvas);
        return;
      }
      if (this.clipTimeEditorIsGameplayVideoType) {
        this.renderClipTimeEditorGameplayFrame(ctx, video, canvas, normalizedLayout.gameplay);
        return;
      }
      const sourceRect = this.clipTimeEditorIsJustChattingVideoType
        ? normalizedLayout.just_chatting.crop
        : normalizedLayout.gameplay.gameplay;
      this.drawClipTimeEditorCover(ctx, video, {
        x: 0,
        y: 0,
        width: canvas.width,
        height: canvas.height,
      }, sourceRect);
      if (this.clipTimeEditorIsJustChattingVideoType) {
        this.drawClipTimeEditorSubtitlePlaceholder(ctx, canvas, "just_chatting");
      }
    },
    renderClipTimeEditorPodcastUnavailableFrame(ctx, canvas) {
      ctx.save();
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(226, 232, 240, 0.92)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${Math.max(16, Math.round(canvas.width * 0.07))}px Inter, Arial, sans-serif`;
      const text = this.tt("vodClips.podcastPreviewUnavailable", "Vista previa no disponible para podcast");
      const maxWidth = canvas.width * 0.82;
      const words = String(text).split(/\s+/).filter(Boolean);
      const lines = [];
      let currentLine = "";
      words.forEach((word) => {
        const nextLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(nextLine).width <= maxWidth || !currentLine) {
          currentLine = nextLine;
          return;
        }
        lines.push(currentLine);
        currentLine = word;
      });
      if (currentLine) lines.push(currentLine);
      const lineHeight = Math.max(22, Math.round(canvas.width * 0.09));
      const firstY = (canvas.height / 2) - (((lines.length - 1) * lineHeight) / 2);
      lines.forEach((line, index) => {
        ctx.fillText(line, canvas.width / 2, firstY + (index * lineHeight), maxWidth);
      });
      ctx.restore();
    },
    renderClipTimeEditorGameplayFrame(ctx, video, canvas, gameplayLayout) {
      const faceRect = gameplayLayout?.face || { x: 0.05, y: 0.05, width: 0.18, height: 0.18 };
      const gameplayRect = gameplayLayout?.gameplay || { x: 0, y: 0, width: 1, height: 1 };
      const faceAspect = this.clipTimeEditorLayoutRectPixelAspect(video, faceRect);
      const gameplayAspect = this.clipTimeEditorLayoutRectPixelAspect(video, gameplayRect);
      const faceNaturalHeight = canvas.width / Math.max(0.0001, faceAspect);
      const gameplayNaturalHeight = canvas.width / Math.max(0.0001, gameplayAspect);
      const fitScale = Math.min(1, canvas.height / Math.max(1, faceNaturalHeight + gameplayNaturalHeight));
      let faceHeight = Math.max(1, Math.round(faceNaturalHeight * fitScale));
      let gameplayHeight = Math.max(1, canvas.height - faceHeight);
      if (gameplayHeight <= 0) {
        gameplayHeight = Math.max(1, Math.round(canvas.height * 0.65));
        faceHeight = Math.max(1, canvas.height - gameplayHeight);
      }
      ctx.fillStyle = "#020617";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const faceTargetRect = {
        x: 0,
        y: 0,
        width: canvas.width,
        height: faceHeight,
      };
      const expandedFaceRect = this.clipTimeEditorExpandedSourceRectForTarget(
        video,
        faceRect,
        faceTargetRect
      );
      this.drawClipTimeEditorCover(ctx, video, faceTargetRect, expandedFaceRect);
      this.drawClipTimeEditorCover(ctx, video, {
        x: 0,
        y: faceHeight,
        width: canvas.width,
        height: gameplayHeight,
      }, gameplayRect);
      this.drawClipTimeEditorSubtitlePlaceholder(ctx, canvas, "gameplay");
    },
    clipTimeEditorLayoutRectPixelAspect(video, rect) {
      const videoWidth = Number(video?.videoWidth || 0);
      const videoHeight = Number(video?.videoHeight || 0);
      const rectWidth = Number(rect?.width || 1);
      const rectHeight = Number(rect?.height || 1);
      if (videoWidth <= 0 || videoHeight <= 0 || rectWidth <= 0 || rectHeight <= 0) {
        return 1;
      }
      return (rectWidth * videoWidth) / (rectHeight * videoHeight);
    },
    drawClipTimeEditorSubtitlePlaceholder(ctx, canvas, videoType) {
      const normalizedVideoType = this.normalizeClipVideoType(videoType);
      if (!this.clipTimeEditor?.draftSubtitlesEnabled) return;
      if (!["gameplay", "just_chatting", "irl"].includes(normalizedVideoType)) return;
      const captionY = normalizedVideoType === "gameplay"
        ? CLIP_TIME_EDITOR_GAMEPLAY_CAPTION_Y
        : (normalizedVideoType === "irl"
          ? CLIP_TIME_EDITOR_IRL_CAPTION_Y
          : CLIP_TIME_EDITOR_JUST_CHATTING_CAPTION_Y);
      const caption = this.clipTimeEditorActiveTranscriptCaption();
      if (!caption.words.length) return;
      const subtitleStyle = this.clipTimeEditor.draftSubtitleStyle;
      const font = subtitleStyle.font;
      const fontFamily = CLIP_TIME_EDITOR_CAPTION_FONT_FAMILIES[font];
      const outputFontSize = CLIP_TIME_EDITOR_CAPTION_FONT_SIZES[font];
      const scale = Math.min(
        canvas.width / CLIP_TIME_EDITOR_CAPTION_OUTPUT_WIDTH,
        canvas.height / CLIP_TIME_EDITOR_CAPTION_OUTPUT_HEIGHT
      );
      const fontSize = Math.max(10, outputFontSize * scale);
      const x = canvas.width / 2;
      const y = canvas.height * captionY;
      ctx.save();
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      ctx.lineJoin = "round";
      const wordMetrics = caption.words.map((word, wordIndex) => {
        const activeScale = (
          subtitleStyle.activeEffect === "text_color"
          && wordIndex === caption.activeIndex
        ) ? 1.2 : 1;
        const wordFontSize = fontSize * activeScale;
        ctx.font = `700 ${wordFontSize}px "${fontFamily}"`;
        return {
          text: word,
          fontSize: wordFontSize,
          width: ctx.measureText(word).width,
        };
      });
      ctx.font = `700 ${fontSize}px "${fontFamily}"`;
      const spaceWidth = ctx.measureText(" ").width;
      const totalWidth = wordMetrics.reduce(
        (width, word) => width + word.width,
        Math.max(0, wordMetrics.length - 1) * spaceWidth
      );
      let cursorX = x - (totalWidth / 2);
      wordMetrics.forEach((word, wordIndex) => {
        ctx.font = `700 ${word.fontSize}px "${fontFamily}"`;
        const isActive = wordIndex === caption.activeIndex;
        if (isActive && subtitleStyle.activeEffect === "background") {
          ctx.lineWidth = Math.max(2, 2 * CLIP_TIME_EDITOR_CAPTION_BACKGROUND_OUTLINE * scale);
          ctx.strokeStyle = subtitleStyle.activeColor;
          ctx.fillStyle = subtitleStyle.activeColor;
          ctx.strokeText(word.text, cursorX, y);
          ctx.fillText(word.text, cursorX, y);
        }
        ctx.lineWidth = Math.max(2, 2 * CLIP_TIME_EDITOR_CAPTION_OUTLINE * scale);
        ctx.strokeStyle = "#000000";
        ctx.fillStyle = isActive && subtitleStyle.activeEffect === "text_color"
          ? subtitleStyle.activeColor
          : "#FFFFFF";
        ctx.strokeText(word.text, cursorX, y);
        ctx.fillText(word.text, cursorX, y);
        cursorX += word.width + spaceWidth;
      });
      ctx.restore();
    },
    clipTimeEditorExpandedSourceRectForTarget(video, sourceRect, targetRect) {
      const videoWidth = Number(video?.videoWidth || 0);
      const videoHeight = Number(video?.videoHeight || 0);
      const targetWidth = Number(targetRect?.width || 0);
      const targetHeight = Number(targetRect?.height || 0);
      if (videoWidth <= 0 || videoHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) {
        return this.clampClipTimeEditorLayoutRect(sourceRect);
      }
      const rect = this.clampClipTimeEditorLayoutRect(sourceRect);
      const sourceX = rect.x * videoWidth;
      const sourceY = rect.y * videoHeight;
      const sourceWidth = rect.width * videoWidth;
      const sourceHeight = rect.height * videoHeight;
      const targetAspect = targetWidth / targetHeight;
      const sourceAspect = sourceWidth / sourceHeight;
      let expandedWidth = sourceWidth;
      let expandedHeight = sourceHeight;
      if (sourceAspect < targetAspect) {
        expandedWidth = sourceHeight * targetAspect;
      } else if (sourceAspect > targetAspect) {
        expandedHeight = sourceWidth / targetAspect;
      }
      if (expandedWidth > videoWidth) {
        expandedWidth = videoWidth;
        expandedHeight = expandedWidth / targetAspect;
      }
      if (expandedHeight > videoHeight) {
        expandedHeight = videoHeight;
        expandedWidth = expandedHeight * targetAspect;
      }
      expandedWidth = Math.max(1, Math.min(videoWidth, expandedWidth));
      expandedHeight = Math.max(1, Math.min(videoHeight, expandedHeight));
      const centerX = sourceX + (sourceWidth / 2);
      const centerY = sourceY + (sourceHeight / 2);
      const expandedX = Math.min(
        Math.max(0, centerX - (expandedWidth / 2)),
        Math.max(0, videoWidth - expandedWidth)
      );
      const expandedY = Math.min(
        Math.max(0, centerY - (expandedHeight / 2)),
        Math.max(0, videoHeight - expandedHeight)
      );
      return {
        x: this.roundClipTimeEditorLayoutValue(expandedX / videoWidth),
        y: this.roundClipTimeEditorLayoutValue(expandedY / videoHeight),
        width: this.roundClipTimeEditorLayoutValue(expandedWidth / videoWidth),
        height: this.roundClipTimeEditorLayoutValue(expandedHeight / videoHeight),
      };
    },
    drawClipTimeEditorCover(ctx, video, targetRect, sourceRect = null) {
      const targetX = Number(targetRect?.x || 0);
      const targetY = Number(targetRect?.y || 0);
      const targetWidth = Number(targetRect?.width || 0);
      const targetHeight = Number(targetRect?.height || 0);
      if (targetWidth <= 0 || targetHeight <= 0) return;

      const videoWidth = Number(video?.videoWidth || 0);
      const videoHeight = Number(video?.videoHeight || 0);
      if (videoWidth <= 0 || videoHeight <= 0) return;

      const rawSourceX = Number(sourceRect?.x ?? 0);
      const rawSourceY = Number(sourceRect?.y ?? 0);
      const rawSourceWidth = Number(sourceRect?.width ?? 1);
      const rawSourceHeight = Number(sourceRect?.height ?? 1);

      const sourceXNormalized = Number.isFinite(rawSourceX) ? Math.max(0, Math.min(1, rawSourceX)) : 0;
      const sourceYNormalized = Number.isFinite(rawSourceY) ? Math.max(0, Math.min(1, rawSourceY)) : 0;
      const sourceWidthNormalized = Number.isFinite(rawSourceWidth)
        ? Math.max(0.0001, Math.min(1 - sourceXNormalized, rawSourceWidth))
        : 1;
      const sourceHeightNormalized = Number.isFinite(rawSourceHeight)
        ? Math.max(0.0001, Math.min(1 - sourceYNormalized, rawSourceHeight))
        : 1;

      const sourceX = sourceXNormalized * videoWidth;
      const sourceY = sourceYNormalized * videoHeight;
      const sourceWidth = sourceWidthNormalized * videoWidth;
      const sourceHeight = sourceHeightNormalized * videoHeight;
      if (sourceWidth <= 0 || sourceHeight <= 0) return;

      const sourceAspect = sourceWidth / sourceHeight;
      const targetAspect = targetWidth / targetHeight;
      let cropX = sourceX;
      let cropY = sourceY;
      let cropWidth = sourceWidth;
      let cropHeight = sourceHeight;
      if (sourceAspect > targetAspect) {
        cropWidth = sourceHeight * targetAspect;
        cropX = sourceX + (sourceWidth - cropWidth) / 2;
      } else if (sourceAspect < targetAspect) {
        cropHeight = sourceWidth / targetAspect;
        cropY = sourceY + (sourceHeight - cropHeight) / 2;
      }
      ctx.drawImage(
        video,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        targetX,
        targetY,
        targetWidth,
        targetHeight
      );
    },
    playClipTimeEditorPreviewVideos() {
      const video = this.$refs.clipTimeEditorVideo;
      if (!video || video.tagName !== "VIDEO") return;
      try {
        video.muted = Boolean(this.clipTimeEditor.isMuted);
        video.play()
          .then(() => {
            this.clearClipTimeEditorPlaybackLoadingState();
            this.clipTimeEditor.isPlaying = true;
            this.startClipTimeEditorPreviewRenderer();
          })
          .catch(() => {
            this.clearClipTimeEditorPlaybackLoadingState();
            this.clipTimeEditor.isPlaying = false;
            this.stopClipTimeEditorPreviewRenderer({ render: true });
          });
      } catch (_err) {
        this.clearClipTimeEditorPlaybackLoadingState();
        this.clipTimeEditor.isPlaying = false;
        this.stopClipTimeEditorPreviewRenderer({ render: true });
      }
    },
    pauseClipTimeEditorPreviewVideos(options = {}) {
      const shouldRender = options.render !== false;
      const video = this.$refs.clipTimeEditorVideo;
      if (video && video.tagName === "VIDEO") {
        try {
          video.pause();
        } catch (_err) {
          // noop
        }
      }
      this.clearClipTimeEditorPlaybackLoadingState();
      this.clipTimeEditor.isPlaying = false;
      this.stopClipTimeEditorPreviewRenderer({ render: shouldRender });
    },
    seekClipTimeEditorPlayback(absoluteSeconds, options = {}) {
      const video = this.$refs.clipTimeEditorVideo;
      const shouldResume = options.shouldResume === true;
      const useLoading = options.useLoading === true;
      const loadingDelayMs = Math.max(0, Number(options.loadingDelayMs ?? 160));
      const nextAbsolute = this.clampClipTimeEditorPlaybackAbsolute(absoluteSeconds);
      this.cancelClipTimeEditorPlaybackScrubSeek();
      this.clipTimeEditorIrlBlurCache = null;
      this.clipTimeEditor.previewStart = nextAbsolute;
      this.clipTimeEditor.currentAbsolute = nextAbsolute;
      if (!video || video.tagName !== "VIDEO") {
        this.clearClipTimeEditorPlaybackLoadingState();
        if (shouldResume) {
          this.playClipTimeEditorPreviewVideos();
        } else {
          this.scheduleClipTimeEditorPreviewRender();
        }
        return;
      }

      if (!useLoading) {
        try {
          video.currentTime = this.clipEditorAbsoluteToVideoSeconds(nextAbsolute);
        } catch (_err) {
          // noop
        }
        this.clearClipTimeEditorPlaybackLoadingState();
        this.scheduleClipTimeEditorPreviewRender();
        if (shouldResume) {
          this.playClipTimeEditorPreviewVideos();
        }
        return;
      }

      const operationId = this.beginClipTimeEditorPlaybackLoadingState({
        delayMs: loadingDelayMs,
      });
      this.stopClipTimeEditorPreviewRenderer();
      this.clipTimeEditor.isPlaying = false;
      try {
        video.currentTime = this.clipEditorAbsoluteToVideoSeconds(nextAbsolute);
      } catch (_err) {
        this.clearClipTimeEditorPlaybackLoadingState(operationId);
        if (shouldResume) {
          this.playClipTimeEditorPreviewVideos();
        } else {
          this.scheduleClipTimeEditorPreviewRender();
        }
        return;
      }
      this.scheduleClipTimeEditorPreviewRender();
      this.waitForClipTimeEditorSeekReady(video, operationId).then(() => {
        if (!this.clearClipTimeEditorPlaybackLoadingState(operationId)) return;
        if (shouldResume) {
          this.playClipTimeEditorPreviewVideos();
          return;
        }
        this.scheduleClipTimeEditorPreviewRender();
      });
    },
    onClipTimeEditorPlay() {
      this.clearClipTimeEditorPlaybackLoadingState();
      this.clipTimeEditor.isPlaying = true;
      this.startClipTimeEditorPreviewRenderer();
    },
    onClipTimeEditorPause() {
      if (!this.clipTimeEditorPlaybackLoading) {
        this.clearClipTimeEditorPlaybackLoadingState();
      }
      this.clipTimeEditor.isPlaying = false;
      this.stopClipTimeEditorPreviewRenderer({ render: true });
    },
    onClipTimeEditorVolumeChange(event) {
      const video = event?.target;
      if (!video) return;
      this.clipTimeEditor.isMuted = Boolean(video.muted);
    },
    toggleClipTimeEditorPlayback() {
      if (this.clipTimeEditorPlaybackLoading) return;
      const video = this.$refs.clipTimeEditorVideo;
      if (!video || video.tagName !== "VIDEO") return;
      if (video.paused || video.ended) {
        const draftStart = Number(this.clipTimeEditor.draftStart || 0);
        const draftEnd = Number(this.clipTimeEditor.draftEnd || draftStart);
        const currentAbsolute = Number(this.clipTimeEditor.currentAbsolute || draftStart);
        if (currentAbsolute < draftStart || currentAbsolute >= draftEnd - 0.05) {
          this.seekClipTimeEditorPlayback(draftStart);
        }
        this.playClipTimeEditorPreviewVideos();
        return;
      }
      this.pauseClipTimeEditorPreviewVideos();
    },
    toggleClipTimeEditorMute() {
      const nextMuted = !Boolean(this.clipTimeEditor.isMuted);
      this.clipTimeEditor.isMuted = nextMuted;
      const video = this.$refs.clipTimeEditorVideo;
      if (!video || video.tagName !== "VIDEO") return;
      video.muted = nextMuted;
    },
    startClipTimeEditorPlaybackScrub() {
      if (this.clipTimeEditorSaving) return;
      if (this.clipTimeEditor.playbackScrubActive) return;
      this.clipTimeEditor.playbackScrubActive = true;
      this.clipTimeEditor.playbackScrubWasPlaying = false;
      this.clearClipTimeEditorPlaybackLoadingState();
      this.pauseClipTimeEditorPreviewVideos({ render: false });
      this.stopClipTimeEditorPreviewRenderer();
    },
    onClipTimeEditorPlaybackScrub(event) {
      if (!this.clipTimeEditor.playbackScrubActive) {
        this.startClipTimeEditorPlaybackScrub();
      }
      const nextAbsolute = this.clipTimeEditorPlaybackAbsoluteFromEvent(event);
      this.clipTimeEditor.previewStart = nextAbsolute;
      this.clipTimeEditor.currentAbsolute = nextAbsolute;
      this.scheduleClipTimeEditorPlaybackScrubSeek(nextAbsolute);
    },
    commitClipTimeEditorPlaybackScrub(event) {
      const nextAbsolute = this.clipTimeEditorPlaybackAbsoluteFromEvent(event);
      this.clipTimeEditor.playbackScrubActive = false;
      this.clipTimeEditor.playbackScrubWasPlaying = false;
      this.cancelClipTimeEditorPlaybackScrubSeek();
      this.clearClipTimeEditorPlaybackLoadingState();
      this.clipTimeEditor.isPlaying = false;
      this.seekClipTimeEditorPlayback(nextAbsolute, {
        useLoading: false,
        shouldResume: false,
      });
    },
    onClipTimeEditorTimeUpdate(event) {
      const video = event?.target;
      if (!video) return;
      const current = this.clipEditorVideoToAbsoluteSeconds(Number(video.currentTime || 0));
      this.clipTimeEditor.currentAbsolute = this.clampClipTimeEditorPlaybackAbsolute(current);
      if (this.clipTimeEditor.playbackScrubActive) {
        this.scheduleClipTimeEditorPreviewRender();
        return;
      }
      const draftStart = Number(this.clipTimeEditor.draftStart || 0);
      const draftEnd = Number(this.clipTimeEditor.draftEnd || 0);
      if (current < draftStart) {
        try {
          video.currentTime = this.clipEditorAbsoluteToVideoSeconds(draftStart);
          this.clipTimeEditor.currentAbsolute = draftStart;
          this.scheduleClipTimeEditorPreviewRender();
        } catch (_err) {
          // noop
        }
        return;
      }
      if (current > draftEnd) {
        this.pauseClipTimeEditorPreviewVideos();
        const resetAbsolute = this.clampClipTimeEditorPlaybackAbsolute(
          Number(this.clipTimeEditor.previewStart || this.clipTimeEditor.draftStart || 0)
        );
        this.seekClipTimeEditorPlayback(resetAbsolute);
        return;
      }
      if (!this.clipTimeEditor.isPlaying) {
        this.scheduleClipTimeEditorPreviewRender();
      }
    },
    pauseClipTimeEditorMainVideo(options = {}) {
      this.pauseClipTimeEditorPreviewVideos(options);
    },
    previewClipTimeEditorDraft(options = {}) {
      const video = this.$refs.clipTimeEditorVideo;
      const shouldPlay = options.shouldPlay !== false;
      const previewStart = Number(this.clipTimeEditor.draftStart || 0);
      this.clipTimeEditor.previewStart = previewStart;
      this.clipTimeEditor.currentAbsolute = previewStart;
      try {
        if (video && video.tagName === "VIDEO") {
          video.muted = Boolean(this.clipTimeEditor.isMuted);
        }
        if (!shouldPlay) {
          this.pauseClipTimeEditorPreviewVideos();
          this.seekClipTimeEditorPlayback(previewStart);
          return;
        }
        this.seekClipTimeEditorPlayback(previewStart, {
          useLoading: true,
          shouldResume: true,
        });
      } catch (_err) {
        // noop
      }
    },
    onClipTimeEditorContextPointerDown(event) {
      if (this.clipTimeEditorSaving) return;
      if (!this.clipTimeEditorAllowsContextEditing) return;
      if (!this.clipTimeEditorContextEnabled) return;
      this.stageClipTimeEditorUndoSnapshot();
      const stage = this.$refs.clipTimeEditorContextStage;
      if (!stage || !stage.getBoundingClientRect) return;
      const stageRect = stage.getBoundingClientRect();
      const overlay = this.$refs.clipTimeEditorContextOverlay;
      const overlayRect = overlay?.getBoundingClientRect ? overlay.getBoundingClientRect() : null;
      if (stageRect.width <= 0 || stageRect.height <= 0) return;
      if (stage.setPointerCapture) {
        try {
          stage.setPointerCapture(event.pointerId);
        } catch (_err) {
          // noop
        }
      }
      const base = this.cloneClipTimeEditorContextPosition(
        this.clipTimeEditor.draftContextPosition
      );
      this.clipTimeEditor.contextDrag.active = true;
      this.clipTimeEditor.contextDrag.startClientX = Number(event.clientX || 0);
      this.clipTimeEditor.contextDrag.startClientY = Number(event.clientY || 0);
      this.clipTimeEditor.contextDrag.startX = Number(base.x || 0);
      this.clipTimeEditor.contextDrag.startY = Number(base.y || 0);
      this.clipTimeEditor.contextDrag.stageWidth = Number(stageRect.width || 0);
      this.clipTimeEditor.contextDrag.stageHeight = Number(stageRect.height || 0);
      this.clipTimeEditor.contextDrag.overlayWidth = Number(overlayRect?.width || 0);
      this.clipTimeEditor.contextDrag.overlayHeight = Number(overlayRect?.height || 0);
      window.addEventListener("pointermove", this.onClipTimeEditorContextPointerMove, { passive: false });
      window.addEventListener("pointerup", this.onClipTimeEditorContextPointerUp, { passive: true });
      window.addEventListener("pointercancel", this.onClipTimeEditorContextPointerUp, { passive: true });
    },
    onClipTimeEditorContextPointerMove(event) {
      if (!this.clipTimeEditor?.contextDrag?.active) return;
      event.preventDefault();
      this.clipTimeEditorContextDragPendingPoint = {
        clientX: Number(event.clientX || 0),
        clientY: Number(event.clientY || 0),
      };
      if (this.clipTimeEditorContextDragFrameRequestId != null) return;
      if (
        typeof window === "undefined"
        || typeof window.requestAnimationFrame !== "function"
      ) {
        this.applyClipTimeEditorContextDragPoint(this.clipTimeEditorContextDragPendingPoint);
        return;
      }
      this.clipTimeEditorContextDragFrameRequestId = window.requestAnimationFrame(() => {
        const pendingPoint = this.clipTimeEditorContextDragPendingPoint;
        this.clipTimeEditorContextDragFrameRequestId = null;
        this.clipTimeEditorContextDragPendingPoint = null;
        this.applyClipTimeEditorContextDragPoint(pendingPoint);
      });
    },
    applyClipTimeEditorContextDragPoint(point) {
      if (!this.clipTimeEditor?.contextDrag?.active || !point) return;
      const stageWidth = Number(this.clipTimeEditor.contextDrag.stageWidth || 0);
      const stageHeight = Number(this.clipTimeEditor.contextDrag.stageHeight || 0);
      if (stageWidth <= 0 || stageHeight <= 0) return;

      const deltaX = (Number(point.clientX || 0) - Number(this.clipTimeEditor.contextDrag.startClientX || 0)) / stageWidth;
      const deltaY = (Number(point.clientY || 0) - Number(this.clipTimeEditor.contextDrag.startClientY || 0)) / stageHeight;

      const rawX = Number(this.clipTimeEditor.contextDrag.startX || 0) + deltaX;
      const rawY = Number(this.clipTimeEditor.contextDrag.startY || 0) + deltaY;

      const halfOverlayWidth = Number(this.clipTimeEditor.contextDrag.overlayWidth || 0) / (2 * stageWidth);
      const overlayHeight = Number(this.clipTimeEditor.contextDrag.overlayHeight || 0) / stageHeight;
      let minX = Math.max(0, halfOverlayWidth);
      let maxX = Math.min(1, 1 - halfOverlayWidth);
      if (maxX < minX) {
        minX = 0.5;
        maxX = 0.5;
      }
      const minY = 0;
      let maxY = Math.max(0, Math.min(1, 1 - overlayHeight));
      if (this.clipTimeEditor?.draftSubtitlesEnabled) {
        const captionTop = CLIP_TIME_EDITOR_IRL_CAPTION_Y
          - (
            (CLIP_TIME_EDITOR_CAPTION_FONT_SIZE + CLIP_TIME_EDITOR_CAPTION_OUTLINE)
            / CLIP_TIME_EDITOR_IRL_OUTPUT_HEIGHT
          );
        maxY = Math.min(maxY, Math.max(0, captionTop - overlayHeight));
      }

      const clampedX = Math.min(maxX, Math.max(minX, rawX));
      const clampedY = Math.min(maxY, Math.max(minY, rawY));
      this.clipTimeEditor.draftContextPosition = {
        x: Number(clampedX.toFixed(6)),
        y: Number(clampedY.toFixed(6)),
      };
    },
    cancelClipTimeEditorContextDragFrame() {
      if (
        this.clipTimeEditorContextDragFrameRequestId != null
        && typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.clipTimeEditorContextDragFrameRequestId);
      }
      this.clipTimeEditorContextDragFrameRequestId = null;
      this.clipTimeEditorContextDragPendingPoint = null;
    },
    onClipTimeEditorContextPointerUp() {
      if (this.clipTimeEditorContextDragPendingPoint) {
        this.applyClipTimeEditorContextDragPoint(this.clipTimeEditorContextDragPendingPoint);
      }
      this.stopClipTimeEditorContextDrag();
    },
    stopClipTimeEditorContextDrag() {
      window.removeEventListener("pointermove", this.onClipTimeEditorContextPointerMove);
      window.removeEventListener("pointerup", this.onClipTimeEditorContextPointerUp);
      window.removeEventListener("pointercancel", this.onClipTimeEditorContextPointerUp);
      this.cancelClipTimeEditorContextDragFrame();
      if (!this.clipTimeEditor?.contextDrag) return;
      this.clipTimeEditor.contextDrag.active = false;
      this.clipTimeEditor.contextDrag.stageWidth = 0;
      this.clipTimeEditor.contextDrag.stageHeight = 0;
      this.clipTimeEditor.contextDrag.overlayWidth = 0;
      this.clipTimeEditor.contextDrag.overlayHeight = 0;
    },
    onClipTimeEditorVideoError() {
      if (!this.showClipTimeEditorModal) return;
      this.closeClipTimeEditorModal(true);
      this.showClipActionSnackbar(
        this.tt(
          "vodClips.premiumEditorVideoLoadFailed",
          "No se pudo cargar el video de edición de este clip."
        ),
        "info"
      );
    },
    async saveClipTimeEditorPersonalizationChanges() {
      const cardRef = this.$refs.clipTimeEditorPersonalizationCard;
      const card = Array.isArray(cardRef) ? cardRef[0] : cardRef;
      if (!card || typeof card.savePendingPersonalizationChanges !== "function") {
        return true;
      }
      return card.savePendingPersonalizationChanges();
    },
    async saveClipTimeEditor() {
      const clip = this.clipTimeEditor.clip;
      if (!clip || !clip.id || this.clipTimeEditorSaving) return;
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }

      this.clipTimeEditorSaving = true;
      this.clipTimeEditor.errorMessage = "";
      try {
        const personalizationSaved = await this.saveClipTimeEditorPersonalizationChanges();
        if (!personalizationSaved) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.personalizationSaveError",
            "No se pudo guardar la personalización antes de re-renderear."
          );
          return;
        }
        const draftVideoType = this.normalizeClipVideoType(this.clipTimeEditor.draftVideoType);
        const draftContextText = this.clipTimeEditorContextTextForSave();
        const nextUseContext = draftVideoType === "irl"
          ? Boolean(this.clipTimeEditor.draftContextEnabled)
          : false;
        const nextContextText = draftVideoType === "irl"
          ? draftContextText
          : null;
        const nextContextPosition = nextUseContext
          ? this.clipTimeEditorContextPositionForSave()
          : null;
        const nextPreviewLayout = ["just_chatting", "gameplay"].includes(draftVideoType)
          ? this.clipTimeEditorPreviewLayoutForSave()
          : null;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("vodClips.authRequired", "Se requiere autenticación"));
        }
        const requestBody = {
          start_seconds: Number(this.clipTimeEditor.draftStart || 0),
          end_seconds: Number(this.clipTimeEditor.draftEnd || 0),
          video_type: draftVideoType,
          use_context: nextUseContext,
          subtitles_enabled: Boolean(this.clipTimeEditor.draftSubtitlesEnabled),
          subtitle_style: {
            font: this.clipTimeEditor.draftSubtitleStyle.font,
            active_color: this.clipTimeEditor.draftSubtitleStyle.activeColor.toUpperCase(),
            active_effect: this.clipTimeEditor.draftSubtitleStyle.activeEffect,
          },
        };
        if (this.clipTranscriptHasChanges()) {
          requestBody.corrected_transcript = this.clipTranscriptPayload(this.clipTranscriptDraft);
        }
        if (nextContextText !== null) {
          requestBody.context_text = nextContextText;
        }
        if (nextContextPosition) {
          requestBody.context_position = nextContextPosition;
        }
        if (nextPreviewLayout) {
          requestBody.preview_layout = nextPreviewLayout;
        }
        const response = await fetch(
          `${resolveApiUrl()}/momo/twitch/clips/${encodeURIComponent(clip.id)}/rerender`,
          {
            method: "POST",
            headers: buildAuthHeaders(session),
            body: JSON.stringify(requestBody),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          const detail = String(errData?.detail || response.statusText || "Error");
          throw new Error(detail);
        }
        const payload = await response.json().catch(() => ({}));
        const nextClipStatus = this.normalizeClipStatus(payload?.clip_status);
        const normalizedVideoType = this.normalizeClipVideoType(payload?.video_type ?? draftVideoType);
        const updatedClip = {
          ...clip,
          status: nextClipStatus,
          start_seconds: Number(this.clipTimeEditor.draftStart || 0),
          end_seconds: Number(this.clipTimeEditor.draftEnd || 0),
          startSeconds: Number(this.clipTimeEditor.draftStart || 0),
          endSeconds: Number(this.clipTimeEditor.draftEnd || 0),
          video_type: normalizedVideoType,
          videoType: normalizedVideoType,
          effective_video_type: normalizedVideoType,
          effectiveVideoType: normalizedVideoType,
          subtitles_enabled: Boolean(payload?.subtitles_enabled),
          subtitlesEnabled: Boolean(payload?.subtitles_enabled),
          effective_subtitles_enabled: Boolean(payload?.subtitles_enabled),
          effectiveSubtitlesEnabled: Boolean(payload?.subtitles_enabled),
          use_context: nextUseContext,
          useContext: nextUseContext,
          preview_layout: nextPreviewLayout,
          previewLayout: nextPreviewLayout,
        };
        if (nextContextPosition) {
          updatedClip.context_position = nextContextPosition;
          updatedClip.contextPosition = nextContextPosition;
        } else {
          updatedClip.context_position = null;
          updatedClip.contextPosition = null;
        }
        if (nextContextText !== null) {
          updatedClip.context_text = nextContextText;
          updatedClip.contextText = nextContextText;
        }
        this.markClipAsRerendering(updatedClip, {
          clipStatus: nextClipStatus,
        });

        this.closeClipTimeEditorModal(true);
        this.showClipActionSnackbar(
          this.tt(
            "vodClips.premiumEditorRerenderQueued",
            "Re-render encolado. El clip se actualizará en unos minutos."
          ),
          "info"
        );
      } catch (error) {
        const detail = String(error?.message || "").toLowerCase();
        if (detail.includes("cancel the scheduled post")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorScheduledBlocked",
            "Cancela la publicación programada para poder editar este clip."
          );
        } else if (detail.includes("already posted")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorPostedBlocked",
            "Este clip ya fue publicado y no se puede editar."
          );
        } else if (detail.includes("premium subscription required")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorRequiresPremium",
            "Necesitas una suscripción premium activa para editar este clip."
          );
        } else if (detail.includes("edit source unavailable")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorVideoLoadFailed",
            "No se pudo cargar el video de edición de este clip."
          );
        } else if (detail.includes("must stay within")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorRangeInvalid",
            "El nuevo rango debe mantenerse dentro de la ventana editable del clip."
          );
        } else if (detail.includes("preview_layout")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.previewLayoutSaveError",
            "No se pudo guardar el encuadre para este clip."
          );
        } else if (detail.includes("context_position") || detail.includes("context_text")) {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.contextSaveError",
            "No se pudo guardar la posición del contexto para este clip."
          );
        } else {
          this.clipTimeEditor.errorMessage = this.tt(
            "vodClips.premiumEditorRerenderError",
            "No se pudo encolar el re-render del clip. Inténtalo de nuevo."
          );
        }
      } finally {
        this.clipTimeEditorSaving = false;
      }
    },
    async handleClipEditorAction(clip) {
      if (!clip) return;
      if (this.isDemoClip(clip)) {
        await this.openClipTimeEditorModal(clip);
        return;
      }
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      if (this.isClipReadyToPost(clip)) {
        this.showClipActionSnackbar(
          this.tt(
            "vodClips.premiumEditorScheduledBlocked",
            "Cancela la publicación programada para poder editar este clip."
          ),
          "info"
        );
        return;
      }

      if (this.isClipPosted(clip)) {
        this.showClipActionSnackbar(
          this.tt(
            "vodClips.premiumEditorPostedBlocked",
            "Este clip ya fue publicado y no se puede editar."
          ),
          "info"
        );
        return;
      }

      const canUsePremiumEditor = await this.resolveScheduleSubscriptionGate();
      if (!canUsePremiumEditor) {
        this.openScheduleSubscriptionPromptModal();
        return;
      }

      if (!this.canEditClipFromSchedule(clip)) {
        this.showClipActionSnackbar(
          this.clipEditorUnavailableMessage(clip),
          "info"
        );
        return;
      }

      await this.openClipTimeEditorModal(clip);
    },
    // Abrir modal de programación
    async openScheduleModal(clip, { editScheduled = false } = {}) {
      if (!clip) return;
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      this.stopVideoCardMediaPlayback();
      this.scheduleEditingExistingClip = editScheduled === true;
      this.scheduleModalStep = this.scheduleEditingExistingClip ? 2 : 1;
      this.selectedClipForSchedule = clip;
      this.clipMetadataDraft = this.buildClipMetadataDraft(clip);
      this.applyWorkspaceMetadataToClipDraft();
      this.scheduleError = null;
      this.postingAssignError = null;
      this.scheduleFacebookPageIdBaseline = "";
      this.scheduleFacebookPages = [];
      this.scheduleFacebookPagesLoadError = "";
      this.scheduleFacebookPagesRequestedForCurrentView = false;
      const { data: { session } } = await supabase.auth.getSession();
      const postingSchedulePromise = this.openPostingScheduleModal(clip, {
        openModal: false,
        useExistingSchedule: this.scheduleEditingExistingClip,
      });
      this.showScheduleModal = true;
      this.isInlineSchedulePanel = true;
      try {
        if (!this.scheduleEditingExistingClip) {
          await this.fetchSchedulePlatformConnections(session);
          this.syncDraftPlatformsWithConnections();
        }
        if (this.clipMetadataDraft && this.clipMetadataDraft.platforms && this.clipMetadataDraft.platforms.length > 0) {
          this.activePlatformTab = this.clipMetadataDraft.platforms[0];
        } else {
          this.activePlatformTab = this.metadataPlatforms[0];
        }
        await postingSchedulePromise;
        console.log("[ScheduleAutoSuggest] openScheduleModal:ready", {
          clipId: clip?.id || null,
          effectiveUserId: this.userId || null,
          isAdminUser: this.isAdminUser,
          isImpersonating: isImpersonating(),
          hasPendingScheduleData: Boolean(this.pendingScheduleData),
          initialDate: this.pendingScheduleData?.date || null,
          initialTimezone: this.postingSelectedTimezone || null,
        });
        if (!this.scheduleEditingExistingClip) {
          await this.applyAdminAutoScheduleSuggestion(session);
        }
      } catch (error) {
        console.warn("No se pudo inicializar la programación de publicación:", error);
      }
      await this.syncYoutubeCategoryIdFromCurrentClip(clip.id);
      this.scheduleFacebookPageIdBaseline = this.getDraftFacebookPageId();
      if (!this.scheduleEditingExistingClip) {
        this.hydrateScheduleFacebookContext(session, { loadPages: true }).catch((error) => {
          console.warn("No se pudo hidratar el contexto de Facebook para schedule:", error);
        });
      }
    },
    isClipReadyToPost(clip) {
      const status = String((clip && clip.status) || "").toLowerCase();
      return status === "5_scheduled_to_post";
    },
    getScopedCollectionClips(jobId) {
      const normalizedJobId = String(jobId || "");
      if (!normalizedJobId) return [];
      return (this.clips || []).filter((clip) => {
        if (!this.isClipActive(clip) || !this.isClipVisibleInSchedule(clip)) {
          return false;
        }
        return String(clip.job_id || "") === normalizedJobId;
      });
    },
    compareClipsByScore(a, b) {
      const aScore = Number(a?.confidence);
      const bScore = Number(b?.confidence);
      const aHasScore = Number.isFinite(aScore);
      const bHasScore = Number.isFinite(bScore);
      if (aHasScore && bHasScore && aScore !== bScore) {
        return bScore - aScore;
      }
      if (aHasScore !== bHasScore) {
        return aHasScore ? -1 : 1;
      }
      return String(a?.id || "").localeCompare(String(b?.id || ""));
    },
    syncCollectionClipOrder(jobId) {
      const normalizedJobId = String(jobId || "");
      if (!normalizedJobId) return;

      const scopedClips = this.getScopedCollectionClips(normalizedJobId);
      const clipIds = new Set(scopedClips.map((clip) => String(clip.id)));
      const clipsById = new Map(scopedClips.map((clip) => [String(clip.id), clip]));
      let order = Array.isArray(this.collectionClipOrderByJob[normalizedJobId])
        ? this.collectionClipOrderByJob[normalizedJobId].map((clipId) => String(clipId))
        : [];

      order = order.filter((clipId) => clipIds.has(clipId));

      if (!order.length && scopedClips.length) {
        order = scopedClips
          .slice()
          .sort((a, b) => this.compareClipsByScore(a, b))
          .map((clip) => String(clip.id));
        this.$set(this.collectionClipOrderByJob, normalizedJobId, order);
        return;
      }

      const existingIds = new Set(order);
      const newcomers = scopedClips
        .filter((clip) => !existingIds.has(String(clip.id)))
        .sort((a, b) => this.compareClipsByScore(a, b));

      newcomers.forEach((clip) => {
        const clipId = String(clip.id);
        const insertAt = order.findIndex((existingId) => {
          const peer = clipsById.get(existingId);
          return peer && this.compareClipsByScore(clip, peer) < 0;
        });
        if (insertAt === -1) {
          order.push(clipId);
        } else {
          order.splice(insertAt, 0, clipId);
        }
      });

      this.$set(this.collectionClipOrderByJob, normalizedJobId, order);
    },
    isClipPosted(clip) {
      const status = String((clip && clip.status) || "").toLowerCase();
      return status === "posted" || status === "6_posted" || status === "7_posted";
    },
    isClipActive(clip) {
      return clip?.clip_active !== false;
    },
    isClipPostedInLastSevenDays(clip) {
      if (!this.isClipPosted(clip)) return false;
      const postingAtRaw = clip?.posting_at;
      if (!postingAtRaw) return false;

      const postingAt = new Date(postingAtRaw);
      if (Number.isNaN(postingAt.getTime())) return false;

      const now = new Date();
      const endOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      );
      const startOfWindow = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0
      );
      startOfWindow.setDate(startOfWindow.getDate() - 7);

      return postingAt >= startOfWindow && postingAt <= endOfToday;
    },
    isClipStatusVisibleInSchedule(clip) {
      if (this.isConverterClip(clip)) {
        return true;
      }
      if (this.isClipLocalRenderReady(clip)) {
        return true;
      }
      if (this.isClipRerendering(clip)) {
        return true;
      }
      const normalized = String(clip?.status || "").toLowerCase();
      if (normalized === "posted" || normalized === "6_posted" || normalized === "7_posted") {
        return this.isClipPostedInLastSevenDays(clip);
      }
      return (
        this.isClipRenderedStatus(normalized)
        || (this.isAdminUser && ["3.1_failed", "4.4_rerender_failed"].includes(normalized))
        || normalized === "5_scheduled_to_post"
      );
    },
    isClipVisibleInSchedule(clip) {
      return this.isClipStatusVisibleInSchedule(clip);
    },
    isClipManageable(clip) {
      return this.isClipReadyToPost(clip) || this.isClipPosted(clip);
    },
    canEditScheduledClip(clip) {
      return this.isAdminUser && this.isClipReadyToPost(clip);
    },
    isTimelineEventPosted(event) {
      if (!event) return false;
      if (event?.extendedProps?.isPosted) return true;
      const status = String(event?.extendedProps?.clipStatus || "").toLowerCase();
      return status === "posted" || status === "6_posted" || status === "7_posted";
    },
    openManageScheduledModal(clip) {
      if (!clip) return;
      if (this.isClipInteractionLocked(clip)) {
        this.showClipRerenderingLockedSnackbar();
        return;
      }
      this.stopVideoCardMediaPlayback();
      this.selectedScheduledClip = clip;
      this.manageScheduledError = null;
      this.showManageScheduledModal = true;
    },
    async openScheduledClipEditorFromManage() {
      const clip = this.selectedScheduledClip;
      if (!this.canEditScheduledClip(clip)) return;
      this.closeManageScheduledModal(true);
      await this.openScheduleModal(clip, { editScheduled: true });
    },
    closeManageScheduledModal(force = false) {
      if (this.manageScheduledCancelling && !force) return;
      this.showManageScheduledModal = false;
      this.selectedScheduledClip = null;
      this.manageScheduledError = null;
    },
    async cancelScheduledUpload() {
      const clip = this.selectedScheduledClip;
      if (!clip || !clip.id) {
        this.manageScheduledError = "No hay clip seleccionado.";
        return;
      }
      if (!this.isClipReadyToPost(clip)) {
        this.manageScheduledError = "Este clip ya no está en estado listo para subir.";
        return;
      }

      try {
        this.manageScheduledCancelling = true;
        this.manageScheduledError = null;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error(this.tt("schedulePost.errorLoginToCancelUpload", "Debes iniciar sesión para cancelar la subida."));
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        const response = await fetch(
          `${apiUrl}/momo/twitch/clips/${encodeURIComponent(clip.id)}`,
          {
            method: "PATCH",
            headers: buildAuthHeaders(session),
            body: JSON.stringify({
              status: "4_rendered",
              posting_at: null,
            }),
          }
        );
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ detail: response.statusText }));
          throw new Error(errData.detail || this.tt("schedulePost.cancelUploadError", "No se pudo cancelar la subida del clip"));
        }

        this.$notify({
          message: `${this.tt("schedulePost.uploadCancelledFor", "Subida cancelada para")} ${clip.clipTitle || this.getClipDisplayName(clip)}.`,
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });

        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });
        this.closeManageScheduledModal(true);
      } catch (err) {
        this.manageScheduledError = err?.message || String(err);
      } finally {
        this.manageScheduledCancelling = false;
      }
    },
    getScheduledClipTimezoneLabel() {
      return this.postingUserTimezone || "UTC";
    },
    getScheduledClipDateLabel(clip) {
      const postingAt = clip && clip.posting_at;
      if (!postingAt) return "-";
      const { dateStr } = this.getPostingDateParts(postingAt);
      return dateStr ? this.formatDate(dateStr) : "-";
    },
    getScheduledClipTimeLabel(clip) {
      const postingAt = clip && clip.posting_at;
      if (!postingAt) return "-";
      const { timeStr } = this.getPostingDateParts(postingAt);
      return timeStr || "-";
    },
    async openPostingScheduleModal(clip, { openModal = true, useExistingSchedule = false } = {}) {
      if (!clip || !clip.id) return;
      if (this.isDemoClip(clip)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      const label = clip.clipTitle || this.getClipDisplayName(clip);
      this.pendingScheduleData = {
        date: this.getPostingTodayInTimezone(),
        clipId: clip.id,
        label,
        clipUpdatedAt: clip.updated_at || null,
        postingUserId: clip.posting_user_id || null,
        isEditingSchedule: useExistingSchedule === true,
      };
      this.postingSelectedPostingHour = null;
      this.postingSelectedPostingMinute = null;
      this.postingSelectedPostingPeriod = "AM";
      this.postingSelectedPostingHourText = "";
      this.postingSelectedPostingMinuteText = "";
      this.postingAssignError = null;

      if (useExistingSchedule && clip.posting_at) {
        const currentUserStore = useCurrentUserStore();
        const timezone =
          this.normalizePostingTimezoneValue(this.postingUserTimezone) ||
          this.normalizePostingTimezoneValue(this.postingSelectedTimezone) ||
          this.normalizePostingTimezoneValue(currentUserStore.timezone) ||
          this.normalizePostingTimezoneValue(this.getStoredPostingTimezone()) ||
          "UTC";
        const { dateStr, timeStr } = this.getPostingDatePartsByTimezone(clip.posting_at, timezone);
        const minutesOfDay = this.parsePostingTimeToMinutes(timeStr);
        if (dateStr && Number.isFinite(minutesOfDay)) {
          const timeSelection = this.buildPostingTimeSelection(minutesOfDay);
          this.postingSelectedTimezone = timezone;
          this.postingUserTimezone = timezone;
          this.postingTimelineStartDate = dateStr;
          this.pendingScheduleData = { ...this.pendingScheduleData, date: dateStr };
          this.postingSelectedPostingHour = timeSelection.hour;
          this.postingSelectedPostingMinute = timeSelection.minute;
          this.postingSelectedPostingPeriod = timeSelection.period;
          this.postingSelectedPostingHourText = timeSelection.hourText;
          this.postingSelectedPostingMinuteText = timeSelection.minuteText;
          this.showPostingScheduleModal = openModal;
          return;
        }
      }

      try {
        const storedTimezone = this.getStoredPostingTimezone();
        if (storedTimezone) {
          this.postingSelectedTimezone = storedTimezone;
          this.postingUserTimezone = storedTimezone;
          this.postingTimelineStartDate = this.getTimelineWindowStartDate();
          this.pendingScheduleData = { ...this.pendingScheduleData, date: this.postingTimelineStartDate };
          this.showPostingScheduleModal = openModal;
          return;
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const currentUserStore = await this.syncCurrentUserContext(session);
          this.postingSelectedTimezone = currentUserStore.timezone || "";
          this.postingUserTimezone = currentUserStore.timezone || "";
        } else {
          this.postingSelectedTimezone = "";
        }
      } catch (e) {
        this.postingSelectedTimezone = "";
      }

      if (!this.postingUserTimezone) {
        this.postingUserTimezone = this.postingSelectedTimezone || "UTC";
      }
      this.postingTimelineStartDate = this.getTimelineWindowStartDate();
      this.pendingScheduleData = { ...this.pendingScheduleData, date: this.postingTimelineStartDate };
      this.showPostingScheduleModal = openModal;
    },
    // Cerrar modal de programación
    closeScheduleModal() {
      if (this.savingMetadata || this.postingAssigning) return;
      this.scheduleModalStep = 1;
      this.scheduleEditingExistingClip = false;
      this.showScheduleModal = false;
      this.isInlineSchedulePanel = false;
      this.showScheduleConnectPromptModal = false;
      this.selectedClipForSchedule = null;
      this.clipMetadataDraft = null;
      this.scheduleError = null;
      this.scheduleFacebookPageIdBaseline = "";
      this.scheduleFacebookPages = [];
      this.scheduleFacebookPagesLoadError = "";
      this.scheduleFacebookPagesRequestedForCurrentView = false;
      this.cancelPostingScheduleModal();
    },
    cancelPostingScheduleModal() {
      if (this.postingAssigning) return;
      this.showPostingScheduleModal = false;
      this.pendingScheduleData = null;
      this.postingSelectedPostingHour = null;
      this.postingSelectedPostingMinute = null;
      this.postingSelectedPostingPeriod = "AM";
      this.postingSelectedTimezone = "";
      this.postingSelectedPostingHourText = "";
      this.postingSelectedPostingMinuteText = "";
      this.postingAssignError = null;
    },
    selectPostingDate(day) {
      if (!day || !day.isoDate || !this.pendingScheduleData) return;
      if (this.isPastPostingTimelineDay(day)) return;
      this.pendingScheduleData = { ...this.pendingScheduleData, date: day.isoDate };
      this.postingAssignError = null;
    },
    isPastPostingTimelineDay(day) {
      const isoDate = String(day?.isoDate || "");
      if (!isoDate) return false;
      const todayIso = this.getPostingTodayInTimezone();
      return isoDate < todayIso;
    },
    canShiftPostingTimeline(direction) {
      const dir = Number(direction) || 0;
      if (!dir || !this.postingTimelineStartDate) return false;
      if (dir > 0) return true;
      const { minIso } = this.getTimelineAllowedRange();
      return this.postingTimelineStartDate > minIso;
    },
    shiftPostingTimeline(direction) {
      const dir = Number(direction) || 0;
      if (!dir || !this.postingTimelineStartDate) return false;
      if (!this.canShiftPostingTimeline(dir)) return false;
      const candidate = this.addDaysToIsoDate(this.postingTimelineStartDate, dir * 6);
      if (!candidate) return false;
      const { minIso: timelineMinIso } = this.getTimelineAllowedRange();
      const clampedGlobal = this.clampTimelineStartIso(candidate, this.getPostingTimelineRenderDaysCount());
      const clamped = clampedGlobal < timelineMinIso ? timelineMinIso : clampedGlobal;
      if (clamped === this.postingTimelineStartDate) return false;
      this.postingTimelineStartDate = clamped;
      return true;
    },
    normalizePostingTimezoneValue(timezoneValue) {
      const normalized = String(timezoneValue || "").trim().toUpperCase();
      return ["EST", "PST", "CST", "MST", "UTC"].includes(normalized) ? normalized : "";
    },
    parsePostingTimeToMinutes(timeStr) {
      const raw = String(timeStr || "").trim();
      const match = raw.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return null;
      const hour = Number(match[1]);
      const minute = Number(match[2]);
      if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
      return hour * 60 + minute;
    },
    clampPostingMinutes(minutesOfDay) {
      const value = Number(minutesOfDay);
      if (!Number.isFinite(value)) return 0;
      if (value < 0) return 0;
      if (value > 1439) return 1439;
      return Math.floor(value);
    },
    buildPostingTimeSelection(minutesOfDay) {
      const clamped = this.clampPostingMinutes(minutesOfDay);
      const hour24 = Math.floor(clamped / 60);
      const minute = clamped % 60;
      const period = hour24 >= 12 ? "PM" : "AM";
      const hour12 = hour24 % 12 || 12;
      return {
        hour: hour12,
        minute,
        period,
        hourText: String(hour12).padStart(2, "0"),
        minuteText: String(minute).padStart(2, "0"),
      };
    },
    getPostingNowPartsByTimezone(timezoneValue) {
      const tzIana = this.getPostingIanaTimezone(timezoneValue);
      const now = new Date();
      const dateStr = new Intl.DateTimeFormat("en-CA", {
        timeZone: tzIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(now);
      const timeStr = new Intl.DateTimeFormat("en-GB", {
        timeZone: tzIana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(now);
      return {
        dateStr,
        minutesOfDay: this.parsePostingTimeToMinutes(timeStr),
      };
    },
    getAdminTimelineUser(payload) {
      const users = Array.isArray(payload?.users) ? payload.users : [];
      const effectiveUserId = String(this.userId || "").trim();
      if (!effectiveUserId) return null;
      const timelineUser = users.find((user) => String(user?.user_id || "").trim() === effectiveUserId) || null;
      console.log("[ScheduleAutoSuggest] getAdminTimelineUser", {
        effectiveUserId,
        usersCount: users.length,
        matched: Boolean(timelineUser),
        matchedUserId: timelineUser ? String(timelineUser?.user_id || "").trim() : null,
      });
      return timelineUser;
    },
    isAdminTimelineEventStatus(status) {
      const normalized = this.normalizeClipStatus(status);
      return normalized === "5_scheduled_to_post" || normalized === "7_posted";
    },
    getAdminTimelineEvents(timelineUser, timezoneValue) {
      const events = Array.isArray(timelineUser?.events) ? timelineUser.events : [];
      const normalizedEvents = events
        .filter((event) => this.isAdminTimelineEventStatus(event?.status))
        .map((event) => {
          const { dateStr, timeStr } = this.getPostingDatePartsByTimezone(event?.posting_at, timezoneValue);
          return {
            ...event,
            localDate: dateStr,
            localTime: timeStr,
            minutesOfDay: this.parsePostingTimeToMinutes(timeStr),
          };
        })
        .filter((event) => event.localDate && event.minutesOfDay !== null)
        .sort((a, b) => {
          if (a.localDate !== b.localDate) return a.localDate.localeCompare(b.localDate);
          return a.minutesOfDay - b.minutesOfDay;
        });
      console.log("[ScheduleAutoSuggest] getAdminTimelineEvents", {
        timelineUserId: String(timelineUser?.user_id || "").trim() || null,
        timezoneValue: timezoneValue || null,
        rawEvents: events.length,
        normalizedEvents: normalizedEvents.length,
        firstEventDate: normalizedEvents[0]?.localDate || null,
        lastEventDate: normalizedEvents[normalizedEvents.length - 1]?.localDate || null,
      });
      return normalizedEvents;
    },
    resetAutoScheduleInputsForManualFirstVideo() {
      if (this.pendingScheduleData) {
        this.pendingScheduleData = {
          ...this.pendingScheduleData,
          date: "",
        };
      }
      this.postingSelectedPostingHour = null;
      this.postingSelectedPostingMinute = null;
      this.postingSelectedPostingPeriod = "AM";
      this.postingSelectedPostingHourText = "";
      this.postingSelectedPostingMinuteText = "";
      this.postingSelectedTimezone = "";
      this.postingUserTimezone = "";
      this.postingTimelineStartDate = this.getTimelineWindowStartDate();
      this.postingAssignError = null;
    },
    buildAdminAutoScheduleSuggestion(timelineUser) {
      const timelineTimezone = this.normalizePostingTimezoneValue(timelineUser?.use_timezone);
      if (!timelineTimezone) {
        console.warn("[ScheduleAutoSuggest] buildAdminAutoScheduleSuggestion:missingTimezone", {
          rawTimezone: timelineUser?.use_timezone || null,
        });
        return null;
      }

      const timelineEvents = this.getAdminTimelineEvents(timelineUser, timelineTimezone);
      if (timelineEvents.length === 0) {
        console.warn("[ScheduleAutoSuggest] buildAdminAutoScheduleSuggestion:noTimelineEvents", {
          timelineUserId: String(timelineUser?.user_id || "").trim() || null,
          timelineTimezone,
        });
        return null;
      }

      const firstEvent = timelineEvents[0];
      const firstEventMinutes = firstEvent.minutesOfDay;
      if (!Number.isFinite(firstEventMinutes)) {
        console.warn("[ScheduleAutoSuggest] buildAdminAutoScheduleSuggestion:invalidFirstEventMinutes", {
          firstEventMinutes,
          firstEventTime: firstEvent?.localTime || null,
        });
        return null;
      }

      const eventsByDay = new Map();
      timelineEvents.forEach((event) => {
        const key = event.localDate;
        if (!key) return;
        const current = eventsByDay.get(key) || { count: 0, firstMinutes: null, lastMinutes: null };
        current.count += 1;
        if (!Number.isFinite(current.firstMinutes) || event.minutesOfDay < current.firstMinutes) {
          current.firstMinutes = event.minutesOfDay;
        }
        if (!Number.isFinite(current.lastMinutes) || event.minutesOfDay > current.lastMinutes) {
          current.lastMinutes = event.minutesOfDay;
        }
        eventsByDay.set(key, current);
      });

      const dayKeys = Array.from(eventsByDay.keys()).sort((a, b) => a.localeCompare(b));
      const lastTimelineDate = dayKeys.length > 0 ? dayKeys[dayKeys.length - 1] : "";
      const todayIso = this.getPostingTodayInTimezoneValue(timelineTimezone);
      const hardcodedGapMinutes = 180;
      const resolveDayStartMinutes = (dayStats, fallbackMinutes = firstEventMinutes) => {
        if (Number.isFinite(dayStats?.firstMinutes)) {
          return this.clampPostingMinutes(dayStats.firstMinutes);
        }
        return this.clampPostingMinutes(fallbackMinutes);
      };

      let targetDate = lastTimelineDate && lastTimelineDate > todayIso ? lastTimelineDate : todayIso;
      const targetDayStats = eventsByDay.get(targetDate) || { count: 0, firstMinutes: null, lastMinutes: null };
      let targetMinutes = resolveDayStartMinutes(targetDayStats);

      if (targetDayStats.count > 0 && targetDayStats.count < 3 && Number.isFinite(targetDayStats.lastMinutes)) {
        const candidateMinutes = targetDayStats.lastMinutes + hardcodedGapMinutes;
        if (candidateMinutes < 1440) {
          targetMinutes = candidateMinutes;
        } else {
          targetDate = this.addDaysToIsoDate(targetDate, 1) || targetDate;
          targetMinutes = resolveDayStartMinutes(targetDayStats);
        }
      } else if (targetDayStats.count >= 3) {
        targetDate = this.addDaysToIsoDate(targetDate, 1) || targetDate;
        targetMinutes = resolveDayStartMinutes(targetDayStats);
      }

      const nowParts = this.getPostingNowPartsByTimezone(timelineTimezone);
      const nowMinutes = Number.isFinite(nowParts.minutesOfDay) ? nowParts.minutesOfDay : 0;
      if (
        targetDate < nowParts.dateStr
        || (targetDate === nowParts.dateStr && targetMinutes <= nowMinutes)
      ) {
        const fallbackDayStats = eventsByDay.get(targetDate) || targetDayStats;
        targetDate = this.addDaysToIsoDate(nowParts.dateStr, 1) || nowParts.dateStr;
        targetMinutes = resolveDayStartMinutes(fallbackDayStats, targetMinutes);
      }

      const timeSelection = this.buildPostingTimeSelection(targetMinutes);
      console.log("[ScheduleAutoSuggest] buildAdminAutoScheduleSuggestion:result", {
        timelineUserId: String(timelineUser?.user_id || "").trim() || null,
        timelineTimezone,
        suggestedDate: targetDate,
        suggestedHour: timeSelection.hour,
        suggestedMinute: timeSelection.minute,
        suggestedPeriod: timeSelection.period,
      });
      return {
        timezone: timelineTimezone,
        date: targetDate,
        ...timeSelection,
      };
    },
    async applyAdminAutoScheduleSuggestion(existingSession = null) {
      const canUseAdminAutoSuggestion = this.isAdminUser || isImpersonating();
      console.log("[ScheduleAutoSuggest] applyAdminAutoScheduleSuggestion:start", {
        canUseAdminAutoSuggestion,
        isAdminUser: this.isAdminUser,
        isImpersonating: isImpersonating(),
        hasPendingScheduleData: Boolean(this.pendingScheduleData),
        effectiveUserId: this.userId || null,
      });
      if (!canUseAdminAutoSuggestion || !this.pendingScheduleData) return;

      try {
        const timelinePayload = await this.fetchAdminPostTimelineForSchedule(existingSession);
        const timelineUser = this.getAdminTimelineUser(timelinePayload);
        if (!timelineUser) {
          console.warn("[ScheduleAutoSuggest] applyAdminAutoScheduleSuggestion:noTimelineUserMatch", {
            effectiveUserId: this.userId || null,
          });
          this.resetAutoScheduleInputsForManualFirstVideo();
          return;
        }

        const suggestion = this.buildAdminAutoScheduleSuggestion(timelineUser);
        if (!suggestion) {
          console.warn("[ScheduleAutoSuggest] applyAdminAutoScheduleSuggestion:noSuggestion", {
            timelineUserId: String(timelineUser?.user_id || "").trim() || null,
          });
          this.resetAutoScheduleInputsForManualFirstVideo();
          return;
        }

        this.postingSelectedTimezone = suggestion.timezone;
        this.postingUserTimezone = suggestion.timezone;
        this.postingTimelineStartDate = this.getTimelineWindowStartDate();
        this.pendingScheduleData = {
          ...this.pendingScheduleData,
          date: suggestion.date,
        };
        this.postingSelectedPostingHour = suggestion.hour;
        this.postingSelectedPostingMinute = suggestion.minute;
        this.postingSelectedPostingPeriod = suggestion.period;
        this.postingSelectedPostingHourText = suggestion.hourText;
        this.postingSelectedPostingMinuteText = suggestion.minuteText;
        this.postingAssignError = null;
        console.log("[ScheduleAutoSuggest] applyAdminAutoScheduleSuggestion:applied", {
          date: suggestion.date,
          timezone: suggestion.timezone,
          hour: suggestion.hour,
          minute: suggestion.minute,
          period: suggestion.period,
        });
      } catch (error) {
        console.warn("No se pudo aplicar la sugerencia automática de programación para admin:", error);
        console.error("[ScheduleAutoSuggest] applyAdminAutoScheduleSuggestion:error", {
          message: error?.message || String(error),
        });
      }
    },
    onPostingHourSelect(event) {
      const rawValue = event?.target?.value;
      if (rawValue === "" || rawValue == null) {
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingHourText = "";
        return;
      }
      const hour = Number(rawValue);
      if (Number.isNaN(hour)) {
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingHourText = "";
        return;
      }
      this.postingSelectedPostingHour = hour;
      this.postingSelectedPostingHourText = String(hour).padStart(2, "0");
    },
    onPostingMinuteSelect(event) {
      const rawValue = event?.target?.value;
      if (rawValue === "" || rawValue == null) {
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingMinuteText = "";
        return;
      }
      const minute = Number(rawValue);
      if (Number.isNaN(minute)) {
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingMinuteText = "";
        return;
      }
      this.postingSelectedPostingMinute = minute;
      this.postingSelectedPostingMinuteText = String(minute).padStart(2, "0");
    },
    validatePostingHourOnInput() {
      if (this.postingSelectedPostingHourText === null || this.postingSelectedPostingHourText === "") {
        this.postingSelectedPostingHour = null;
        return;
      }
      const cleaned = String(this.postingSelectedPostingHourText).replace(/[^\d]/g, "");
      if (cleaned !== this.postingSelectedPostingHourText) this.postingSelectedPostingHourText = cleaned;
      if (!cleaned) {
        this.postingSelectedPostingHour = null;
        return;
      }
      let hour = Number(cleaned);
      if (!Number.isNaN(hour) && hour > 12) hour = 12;
      this.postingSelectedPostingHour = Number.isNaN(hour) ? null : hour;
    },
    validatePostingHour() {
      if (this.postingSelectedPostingHourText === null || this.postingSelectedPostingHourText === "") {
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingHourText = "";
        return;
      }
      const hour = Number(this.postingSelectedPostingHourText);
      if (Number.isNaN(hour)) {
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingHourText = "";
        return;
      }
      if (hour < 1) {
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingHourText = "";
      } else if (hour > 12) {
        this.postingSelectedPostingHour = 12;
        this.postingSelectedPostingHourText = "12";
      } else {
        this.postingSelectedPostingHour = hour;
        this.postingSelectedPostingHourText = String(hour).padStart(2, "0");
      }
    },
    validatePostingMinuteOnInput() {
      if (this.postingSelectedPostingMinuteText === null || this.postingSelectedPostingMinuteText === "") {
        this.postingSelectedPostingMinute = null;
        return;
      }
      const cleaned = String(this.postingSelectedPostingMinuteText).replace(/[^\d]/g, "");
      if (cleaned !== this.postingSelectedPostingMinuteText) this.postingSelectedPostingMinuteText = cleaned;
      if (!cleaned) {
        this.postingSelectedPostingMinute = null;
        return;
      }
      let minute = Number(cleaned);
      if (!Number.isNaN(minute) && minute > 59) minute = 59;
      this.postingSelectedPostingMinute = Number.isNaN(minute) ? null : minute;
    },
    validatePostingMinute() {
      if (this.postingSelectedPostingMinuteText === null || this.postingSelectedPostingMinuteText === "") {
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingMinuteText = "";
        return;
      }
      const minute = Number(this.postingSelectedPostingMinuteText);
      if (Number.isNaN(minute)) {
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingMinuteText = "";
        return;
      }
      if (minute < 0) {
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingMinuteText = "";
      } else if (minute > 59) {
        this.postingSelectedPostingMinute = 59;
        this.postingSelectedPostingMinuteText = "59";
      } else {
        this.postingSelectedPostingMinute = minute;
        this.postingSelectedPostingMinuteText = String(minute).padStart(2, "0");
      }
    },
    getPostingTimezoneOffsetHours(tz) {
      const map = {
        PST: -8,
        MST: -7,
        CST: -6,
        EST: -5,
        UTC: 0,
      };
      return Object.prototype.hasOwnProperty.call(map, tz) ? map[tz] : 0;
    },
    async confirmPostingSchedule(options = {}) {
      const { session: providedSession = null, clipMetadata, clipTags } = options || {};
      if (this.isDemoClip(this.selectedClipForSchedule)) {
        this.showDemoReadOnlySnackbar();
        return;
      }
      if (!this.pendingScheduleData || !this.pendingScheduleData.date) {
        this.postingAssignError = this.tt("schedulePost.errorSelectDate", "Por favor selecciona una fecha");
        return;
      }
      if (this.postingSelectedPostingHour === null || this.postingSelectedPostingMinute === null) {
        this.postingAssignError = this.tt("schedulePost.errorSelectPostingTime", "Por favor selecciona una hora de publicación");
        return;
      }
      if (!this.postingSelectedTimezone) {
        this.postingAssignError = this.tt("schedulePost.errorSelectTimezone", "Por favor selecciona una zona horaria");
        return;
      }
      const { date, clipId, label, clipUpdatedAt } = this.pendingScheduleData;

      try {
        let session = providedSession;
        if (!session) {
          const { data: { session: fetchedSession } } = await supabase.auth.getSession();
          session = fetchedSession;
        }
        if (!session) throw new Error(this.tt("schedulePost.errorLoginToSchedule", "Debes iniciar sesión para programar un clip."));
        this.postingAssigning = true;
        this.postingAssignError = null;
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";

        let videoPostingRandomizerEnabled = false;
        try {
          const userInfoResponse = await fetch(`${apiUrl}/momo/twitch/user-info/timezone`, {
            method: "GET",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          });
          if (userInfoResponse.ok) {
            const userInfoData = await userInfoResponse.json();
            videoPostingRandomizerEnabled = userInfoData?.video_posting_randomizer === true;
          }
        } catch (e) {
          // ignore randomizer fetch errors
        }

        try {
          const timezoneResponse = await fetch(`${apiUrl}/momo/twitch/user-info/timezone`, {
            method: "PATCH",
            headers: buildAuthHeaders(session),
            body: JSON.stringify({
              use_timezone: this.postingSelectedTimezone,
            }),
          });
          if (timezoneResponse.ok) {
            await useCurrentUserStore().refresh(session);
            this.postingUserTimezone = useCurrentUserStore().timezone || this.postingSelectedTimezone;
            this.postingTimelineStartDate = this.getTimelineWindowStartDate();
          }
        } catch (e) {
          // ignore timezone errors
        }
        this.setStoredPostingTimezone(this.postingSelectedTimezone);

        let hours24 = this.postingSelectedPostingHour;
        if (this.postingSelectedPostingPeriod === "PM" && hours24 !== 12) hours24 = hours24 + 12;
        else if (this.postingSelectedPostingPeriod === "AM" && hours24 === 12) hours24 = 0;

        const dateParts = String(date).split("-").map(Number);
        if (dateParts.length !== 3 || dateParts.some((n) => Number.isNaN(n))) {
          throw new Error(this.tt("schedulePost.errorInvalidDate", "Fecha inválida"));
        }
        const [year, month, day] = dateParts;
        const offsetHours = this.getPostingTimezoneOffsetHours(this.postingSelectedTimezone);
        const utcDate = new Date(
          Date.UTC(
            year,
            month - 1,
            day,
            hours24 - offsetHours,
            Number(this.postingSelectedPostingMinute),
            0
          )
        );

        const nowUtc = new Date();
        if (videoPostingRandomizerEnabled && this.pendingScheduleData?.isEditingSchedule !== true) {
          const clipUpdatedAtDate = clipUpdatedAt ? new Date(clipUpdatedAt) : null;
          const clipUpdatedAtMs =
            clipUpdatedAtDate && !Number.isNaN(clipUpdatedAtDate.getTime())
              ? clipUpdatedAtDate.getTime()
              : null;
          const referenceMs = Math.max(nowUtc.getTime(), clipUpdatedAtMs || 0);
          const minimumAllowedMs = referenceMs + 60 * 1000;

          const baseMs = utcDate.getTime();
          const minOffsetByWindow = -15;
          const maxOffsetByWindow = 15;
          const minOffsetByTime = Math.ceil((minimumAllowedMs - baseMs) / (60 * 1000));
          const minValidOffset = Math.max(minOffsetByWindow, minOffsetByTime);
          const maxValidOffset = maxOffsetByWindow;
          const validOffsets = [];

          for (let offset = minValidOffset; offset <= maxValidOffset; offset += 1) {
            if (offset === 0) continue;
            validOffsets.push(offset);
          }

          if (validOffsets.length === 0) {
            this.postingAssignError =
              this.tt("schedulePost.errorTimeTooClose", "La hora seleccionada está demasiado cerca. Elige una hora un poco más tarde.");
            return;
          }

          const randomIndex = Math.floor(Math.random() * validOffsets.length);
          const randomOffsetMinutes = validOffsets[randomIndex];
          utcDate.setUTCMinutes(utcDate.getUTCMinutes() + randomOffsetMinutes);
        }

        if (utcDate.getTime() <= nowUtc.getTime()) {
          this.postingAssignError = this.tt("schedulePost.errorChooseFutureTime", "Por favor elige una hora futura");
          return;
        }
        const scheduledForIso = utcDate.toISOString();

        const clipUpdatePayload = {
          status: "5_scheduled_to_post",
          posting_at: scheduledForIso,
        };
        if (this.pendingScheduleData?.isEditingSchedule === true) {
          if (this.pendingScheduleData.postingUserId) {
            clipUpdatePayload.posting_user_id = this.pendingScheduleData.postingUserId;
          }
        } else {
          clipUpdatePayload.posting_user_id = getEffectiveUserId(session.user.id);
        }
        if (clipMetadata?.title !== undefined) {
          clipUpdatePayload.title = clipMetadata.title;
        }
        if (clipMetadata?.description !== undefined) {
          clipUpdatePayload.clip_description = clipMetadata.description;
        }
        if (clipTags !== undefined) {
          clipUpdatePayload.clip_tags = clipTags;
        }
        if (clipMetadata !== undefined) {
          clipUpdatePayload.clip_metadata = clipMetadata;
        }

        await updateTwitchClip(session, clipId, clipUpdatePayload);

        const timezoneForDisplay =
          this.postingSelectedTimezone || this.postingUserTimezone || "UTC";
        const scheduledDate = new Date(scheduledForIso);
        const tzIanaForDisplay = this.getPostingIanaTimezone(timezoneForDisplay);
        const finalDateStr = new Intl.DateTimeFormat("en-CA", {
          timeZone: tzIanaForDisplay,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(scheduledDate);
        const finalTime24 = new Intl.DateTimeFormat("en-GB", {
          timeZone: tzIanaForDisplay,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(scheduledDate);
        const finalTime12 = this.formatTimelineTime12h(finalTime24).toUpperCase();
        this.$notify({
          message: `${this.tt("schedulePost.scheduledAt", "Programado")}: ${label} · ${this.formatDate(finalDateStr)} ${finalTime12}`,
          type: "success",
        });

        await this.loadClips();
        await this.loadPostingTimeline({ silent: true });
        // Cerrar ambos modales al terminar de programar la publicación
        this.scheduleModalStep = 1;
        this.scheduleEditingExistingClip = false;
        this.showScheduleModal = false;
        this.isInlineSchedulePanel = false;
        this.selectedClipForSchedule = null;
        this.clipMetadataDraft = null;
        this.scheduleError = null;

        this.showPostingScheduleModal = false;
        this.pendingScheduleData = null;
        this.postingSelectedPostingHour = null;
        this.postingSelectedPostingMinute = null;
        this.postingSelectedPostingPeriod = "AM";
        this.postingSelectedTimezone = "";
        this.postingSelectedPostingHourText = "";
        this.postingSelectedPostingMinuteText = "";
        this.postingAssignError = null;
      } catch (err) {
        this.postingAssignError = err?.message || String(err);
      } finally {
        this.postingAssigning = false;
      }
    },
    async saveAndScheduleClip() {
      if (this.savingMetadata || this.postingAssigning) return;
      if (!this.selectedClipForSchedule || !this.clipMetadataDraft) {
        this.scheduleError = this.tt("schedulePost.errorNoClipSelected", "No hay clip seleccionado");
        return;
      }
      if (this.isDemoClip(this.selectedClipForSchedule)) {
        this.showDemoReadOnlySnackbar();
        return;
      }

      try {
        this.savingMetadata = true;
        this.scheduleError = null;

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("schedulePost.errorLoginToSaveMetadata", "Debes iniciar sesión para guardar los metadatos."));
        }

        this.syncWorkspaceMetadataForSchedule();
        await this.saveWorkspaceClipColumns({
          clip: this.selectedClipForSchedule,
          silent: true,
        });

        if (!this.scheduleEditingExistingClip) {
          await this.fetchSchedulePlatformConnections(session);
          this.syncDraftPlatformsWithConnections();
          if (!this.clipMetadataDraft.platforms || this.clipMetadataDraft.platforms.length === 0) {
            throw new Error(
              this.tt(
                "schedulePost.errorNoConnectedPlatforms",
                "No tienes redes sociales conectadas para programar este clip. Conecta al menos una desde tu perfil."
              )
            );
          }
        }

        const selectedPlatforms = Array.isArray(this.clipMetadataDraft.platforms)
          ? this.clipMetadataDraft.platforms
          : [];
        const includesFacebook = selectedPlatforms.includes("facebook");
        if (!this.scheduleEditingExistingClip && includesFacebook) {
          await this.applyDefaultFacebookPageIdToDraft(session);
        }

        const draftFacebookPageId = this.getDraftFacebookPageId();
        if (!this.scheduleEditingExistingClip && includesFacebook && !draftFacebookPageId) {
          throw new Error(
            this.tt(
              "schedulePost.errorFacebookPageIdRequired",
              "Debes seleccionar una página de Facebook antes de programar este clip."
            )
          );
        }
        const baselineFacebookPageId = this.normalizeFacebookPageId(this.scheduleFacebookPageIdBaseline);
        const shouldUpdateFacebookPageId =
          !this.scheduleEditingExistingClip &&
          draftFacebookPageId !== baselineFacebookPageId &&
          draftFacebookPageId !== "";
        if (shouldUpdateFacebookPageId) {
          const facebookPageIdStore = useFacebookPageIdStore();
          const savedPageId = await facebookPageIdStore.updatePageId(draftFacebookPageId, session);
          this.clipMetadataDraft.platform_metadata.facebook.facebook_page_id = savedPageId;
          this.scheduleFacebookPageIdBaseline = savedPageId;
        }

        const clipMetadata = this.buildClipMetadataPayload();
        const clipTags = this.parseHashtagList(this.clipMetadataDraft.hashtags);
        await this.confirmPostingSchedule({
          session,
          clipMetadata,
          clipTags,
        });
      } catch (err) {
        this.scheduleError = err?.message || this.tt("schedulePost.errorSavingMetadata", "Error al guardar los metadatos.");
      } finally {
        this.savingMetadata = false;
      }
    },

  }
}
</script>

<style scoped lang="scss">
/* Integrado con DashboardLayout y TopNavbar — mismo criterio que VODClips */
.schedule-page {
  position: relative;
  min-height: 100vh;
  background: transparent;
  color: var(--clipr-text-primary);
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 0 20px;
  --page-overlay-offset-y: 50px;
  --page-overlay-offset-x: 45px;
}

.schedule-page--embedded {
  min-height: 0;
  gap: var(--schedule-embedded-gap, 16px);
  padding: 0;
}

.schedule-page--embedded > .schedule-row.schedule-row-batches:first-child {
  margin-top: 16px;
}

.schedule-page--embedded .schedule-row.schedule-row-batches::before,
.schedule-page--embedded .schedule-row.schedule-row-batches::after {
  display: none;
}

.schedule-page--embedded .schedule-row.schedule-row-batches {
  background: #746ac712;
  border: 1px solid var(--clipr-border-color);
  box-shadow: none;
}

.schedule-page--embedded .schedule-row-header--streams {
  margin-bottom: 12px;
  padding-bottom: 0;
  border-bottom: 0;
}

.schedule-page--embedded .schedule-row-header--streams .schedule-row-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  background: none;
  -webkit-background-clip: unset;
  background-clip: unset;
  color: var(--clipr-text-primary);
}

.schedule-page--embedded .schedule-row-title--greeting {
  font-size: 1.15rem;
  line-height: 1.3;
}

.schedule-page--embedded .schedule-row-icon {
  width: 34px;
  height: 34px;
  font-size: 15px;
  box-shadow: none;
}

.schedule-page--embedded .schedule-collections-filters {
  gap: 8px;
}

.schedule-page--embedded .schedule-filters-admin-panel {
  border-radius: 12px;
  border-color: rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.44);
  box-shadow: none;
}

.schedule-page--embedded .schedule-filters-toolbar {
  display: none;
}

.schedule-page--embedded .schedule-filters-body {
  padding: 10px 12px;
}

.schedule-page--embedded .schedule-period-pill {
  box-shadow: none;
}

.schedule-page-load-overlay {
  position: absolute;
  inset:
    calc(-1 * var(--page-overlay-offset-y))
    calc(-1 * var(--page-overlay-offset-x))
    calc(-1 * var(--page-overlay-offset-y))
    calc(-1 * var(--page-overlay-offset-x));
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(circle at top, rgba(168, 85, 247, 0.16) 0%, transparent 42%),
    radial-gradient(circle at bottom, rgba(59, 130, 246, 0.12) 0%, transparent 48%),
    rgba(7, 9, 18, 0.93);
  backdrop-filter: blur(6px);
}

.schedule-page--embedded .schedule-page-load-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.schedule-page-load-overlay--error {
  background:
    radial-gradient(circle at top, rgba(239, 68, 68, 0.16) 0%, transparent 44%),
    rgba(10, 8, 14, 0.95);
}

.schedule-page-load-content {
  width: min(520px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
}

.schedule-page-load-spinner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--clipr-border-purple);
  background: rgba(168, 85, 247, 0.12);
  box-shadow: 0 0 0 6px rgba(168, 85, 247, 0.08);
}

.schedule-page-load-spinner i {
  font-size: 24px;
  color: var(--clipr-purple-light);
}

.schedule-page-load-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.schedule-page-load-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--clipr-text-secondary);
}

.schedule-page-load-content--error {
  gap: 14px;
}

.schedule-page-load-spinner--error {
  border-color: rgba(248, 113, 113, 0.55);
  background: rgba(239, 68, 68, 0.14);
  box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.08);
}

.schedule-page-load-spinner--error i {
  color: #f87171;
}

.schedule-page-load-title--error {
  max-width: 520px;
}

.schedule-page-load-refresh-btn {
  min-width: 136px;
}

@media (max-width: 991px) {
  .schedule-page:not(.schedule-page--embedded) {
    --schedule-mobile-rhythm: 1rem;
    --page-overlay-offset-y: 10px;
    --page-overlay-offset-x: 18px;
    margin-left: -15px;
    margin-right: -15px;
    padding: 0 1rem 20px;
    gap: var(--schedule-mobile-rhythm);
  }

  .schedule-page--embedded {
    --schedule-mobile-rhythm: 1rem;
    gap: var(--schedule-embedded-gap, var(--schedule-mobile-rhythm));
  }

  .schedule-page--embedded > .schedule-row,
  .schedule-page--embedded > .schedule-row-timeline {
    margin-top: 0;
    margin-bottom: 0;
  }

  .schedule-page:not(.schedule-page--embedded) > .schedule-row,
  .schedule-page:not(.schedule-page--embedded) > .schedule-row-timeline {
    margin-top: 0;
    margin-bottom: 0;
  }

  .schedule-page--embedded > .schedule-row.schedule-row-videos,
  .schedule-page:not(.schedule-page--embedded) > .schedule-row.schedule-row-videos {
    margin-bottom: 25px;
  }

  .schedule-page .schedule-row-header {
    margin-bottom: var(--schedule-mobile-rhythm);
  }

  .schedule-page .schedule-collections-inline-loading {
    margin-bottom: var(--schedule-mobile-rhythm);
  }
}

.schedule-page .card,
.schedule-page .schedule-collections-card {
  background: transparent;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
}

.schedule-collections-body {
  width: 100%;
}

.schedule-collections-inline-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: var(--clipr-radius-md);
  border: 1px solid var(--clipr-border-color);
  background: rgba(168, 85, 247, 0.08);
  color: var(--clipr-text-secondary);
  font-size: 0.875rem;
}

.schedule-collections-inline-loading .ti-reload {
  font-size: 1.1rem;
  color: var(--clipr-purple-light);
}

.schedule-row {
  position: relative;
  overflow: hidden;
  background: #746ac712;
  border-radius: var(--clipr-radius-lg);
  padding: 18px 20px;
  border: 1px solid var(--clipr-border-color);
  box-shadow: none;
  backdrop-filter: none;
}

.schedule-row.schedule-row-batches {
  background:
    linear-gradient(145deg, rgba(76, 29, 149, 0.14) 0%, rgba(15, 23, 42, 0.55) 42%, rgba(59, 130, 246, 0.07) 100%),
    rgba(116, 106, 199, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.24);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.08),
    0 18px 44px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.schedule-row.schedule-row-batches::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(168, 85, 247, 0.85) 28%,
    rgba(96, 165, 250, 0.75) 72%,
    transparent 100%
  );
  opacity: 0.85;
  pointer-events: none;
}

.schedule-row.schedule-row-batches::after {
  content: "";
  position: absolute;
  top: -90px;
  right: -70px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.16) 0%, transparent 68%);
  pointer-events: none;
}

.schedule-row.schedule-row-batches > * {
  position: relative;
  z-index: 1;
}

.schedule-row.schedule-row-videos {
  padding: 0;
  margin-bottom: 25px;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
}

.schedule-page--embedded .schedule-row.schedule-row-videos {
  background: transparent;
  border: none;
  box-shadow: none;
}

.clip-workspace {
  display: grid;
  grid-template-columns: minmax(230px, 0.72fr) minmax(360px, 1.56fr) minmax(260px, 0.84fr);
  height: 580px;
  max-height: 580px;
  min-height: 580px;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: #0f1115;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.clip-workspace-list,
.clip-workspace-actions {
  min-width: 0;
}

.clip-workspace-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 16px 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  background: #0f1115;
}

.clip-workspace-vod-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 12px;
  padding: 8px;
  overflow: hidden;
  border: 1px solid rgba(192, 132, 252, 0.2);
  border-radius: 11px;
  background: linear-gradient(180deg, #22232a 0%, #181920 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28);
}

.clip-workspace-vod-thumb {
  position: relative;
  width: 70px;
  height: 48px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #9ca3af;
  border-radius: 8px;
  background: #14151a;
}

.clip-workspace-vod-thumb::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(15, 17, 21, 0) 30%, rgba(15, 17, 21, 0.65) 100%);
  pointer-events: none;
}

.clip-workspace-vod-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.clip-workspace-vod-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
}

.clip-workspace-vod-copy strong {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  color: #f9fafb;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
}

.clip-workspace-vod-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin: 0;
}

.clip-workspace-vod-meta-chip {
  padding: 2px 7px;
  border-radius: 999px;
  color: #e5e7eb;
  font-size: 10px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
}

.clip-workspace-vod-meta-time {
  color: #9ca3af;
  font-size: 10px;
}

.clip-workspace-vod-download-actions {
  width: 100%;
  display: flex;
  gap: 8px;
  padding-top: 2px;
}

.clip-workspace-vod-select-all,
.clip-workspace-vod-download-zip {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  padding: 8px 10px;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.clip-workspace-vod-select-all {
  flex: 0 0 auto;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.72);
  color: #d1d5db;
}

.clip-workspace-vod-download-zip {
  flex: 1;
  border: 1px solid rgba(196, 181, 253, 0.7);
  background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
  color: #fff;
  box-shadow: 0 5px 14px rgba(124, 58, 237, 0.28);
}

.clip-workspace-vod-select-all:hover:not(:disabled),
.clip-workspace-vod-download-zip:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: #ddd6fe;
}

.clip-workspace-vod-select-all:hover:not(:disabled) {
  background: rgba(76, 29, 149, 0.42);
}

.clip-workspace-vod-download-zip:hover:not(:disabled) {
  background: linear-gradient(135deg, #8b5cf6 0%, #c084fc 100%);
}

.clip-workspace-vod-select-all:disabled,
.clip-workspace-vod-download-zip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.clip-workspace-list-label {
  margin: 0 0 10px;
  color: #e5e7eb;
  font-size: 12px;
  font-weight: 600;
}

.clip-workspace-list-scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding-right: 2px;
}

.clip-workspace-list-items {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clip-workspace-list-entry {
  position: relative;
}

.clip-workspace-list-entry .clip-workspace-list-item {
  width: 100%;
}

.clip-workspace-list-entry.is-zip-selected .clip-workspace-list-item {
  border-color: rgba(192, 132, 252, 0.78);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2);
}

.clip-workspace-list-checkbox {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 3;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.clip-workspace-list-checkbox input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.clip-workspace-list-checkbox-mark {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 6px;
  background: rgba(9, 12, 22, 0.76);
  color: transparent;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.28);
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.clip-workspace-list-checkbox input:checked + .clip-workspace-list-checkbox-mark {
  border-color: #e9d5ff;
  background: #9333ea;
  color: #fff;
}

.clip-workspace-list-checkbox input:disabled + .clip-workspace-list-checkbox-mark {
  opacity: 0.38;
  cursor: not-allowed;
}

.clip-list-page-enter-active,
.clip-list-page-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.clip-list-page-enter {
  opacity: 0;
  transform: translateX(14px);
}

.clip-list-page-leave-to {
  opacity: 0;
  transform: translateX(-14px);
}

.clip-list-page-leave-active {
  position: absolute;
  width: calc(100% - 2px);
}

.clip-workspace-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}

.clip-workspace-pagination-button {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.clip-workspace-pagination-button:hover:not(:disabled) {
  border-color: rgba(192, 132, 252, 0.45);
  background: rgba(168, 85, 247, 0.2);
}

.clip-workspace-pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.clip-workspace-pagination-label {
  min-width: 92px;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

.collection-pagination {
  width: fit-content;
  min-width: 184px;
  margin: 18px auto 4px;
  padding: 6px;
  gap: 12px;
  border: 1px solid rgba(192, 132, 252, 0.3);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(88, 28, 135, 0.38), rgba(15, 23, 42, 0.78));
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.24);
}

.collection-pagination .clip-workspace-pagination-button {
  width: 34px;
  height: 34px;
  border-color: rgba(216, 180, 254, 0.42);
  border-radius: 9px;
  background: linear-gradient(135deg, #7e22ce, #a855f7);
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.32);
}

.collection-pagination .clip-workspace-pagination-button:hover:not(:disabled) {
  border-color: #f3e8ff;
  background: linear-gradient(135deg, #9333ea, #c084fc);
  box-shadow: 0 5px 16px rgba(192, 132, 252, 0.45);
}

.collection-pagination .clip-workspace-pagination-label {
  min-width: 104px;
  color: #f5f3ff;
  font-size: 12px;
  letter-spacing: 0.01em;
}

.clip-workspace-list-item {
  width: 100%;
  min-width: 0;
  min-height: 68px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  color: #f3f4f6;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  background: linear-gradient(180deg, #23242b 0%, #1a1b21 100%);
  cursor: pointer;
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.clip-workspace-list-item:hover {
  background: linear-gradient(180deg, #2a2b33 0%, #21222a 100%);
  border-color: rgba(192, 132, 252, 0.22);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
}

.clip-workspace-list-item.is-selected {
  position: relative;
  transform: translateX(2px);
  background: linear-gradient(180deg, #2d2a38 0%, #23202f 100%);
  border-color: rgba(216, 180, 254, 0.82);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.35),
    0 8px 22px rgba(88, 28, 135, 0.32);
}

.clip-workspace-list-item.is-selected::before {
  content: "";
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: -1px;
  width: 3px;
  border-radius: 0 999px 999px 0;
  background: #c084fc;
}

.clip-workspace-list-item.is-scheduled-to-post {
  border-color: rgba(245, 158, 11, 0.58);
  box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.16);
}

.clip-workspace-list-item.is-scheduled-to-post:hover {
  border-color: rgba(251, 191, 36, 0.72);
  box-shadow:
    0 0 0 1px rgba(245, 158, 11, 0.24),
    0 4px 14px rgba(0, 0, 0, 0.22);
}

.clip-workspace-list-item.is-scheduled-to-post.is-selected {
  border-color: rgba(251, 191, 36, 0.82);
  box-shadow:
    0 0 0 1px rgba(245, 158, 11, 0.3),
    0 6px 18px rgba(0, 0, 0, 0.28);
}

.clip-workspace-list-item.is-posted {
  border-color: rgba(34, 197, 94, 0.58);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.16);
}

.clip-workspace-list-item.is-posted:hover {
  border-color: rgba(74, 222, 128, 0.72);
  box-shadow:
    0 0 0 1px rgba(34, 197, 94, 0.24),
    0 4px 14px rgba(0, 0, 0, 0.22);
}

.clip-workspace-list-item.is-posted.is-selected {
  border-color: rgba(74, 222, 128, 0.82);
  box-shadow:
    0 0 0 1px rgba(34, 197, 94, 0.3),
    0 6px 18px rgba(0, 0, 0, 0.28);
}

.clip-workspace-list-item.is-rerendering {
  animation: collectionItemPulseRendering 2s ease-in-out infinite;
}

.clip-workspace-list-item.is-rerendering .clip-workspace-list-title,
.clip-workspace-list-item.is-rerendering .clip-workspace-list-meta {
  color: #67e8f9;
}

.clip-workspace-list-thumb {
  position: relative;
  flex: 0 0 48px;
  width: 48px;
  height: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #9ca3af;
  background: #14151a;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 9px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
}

.clip-workspace-list-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.clip-workspace-list-thumb-image--rendering {
  animation: collectionImageRendering 1.8s ease-in-out infinite;
}

.clip-workspace-rerender-overlay {
  border-radius: 8px;
}

.clip-workspace-rerender-overlay .processing-indicator {
  width: 20px;
  height: 20px;
}

.clip-workspace-rerender-overlay .processing-indicator-orbit {
  border-width: 2px;
}

.clip-workspace-rerender-overlay .processing-indicator-core {
  width: 6px;
  height: 6px;
}

.clip-workspace-rerender-overlay .collection-processing-progress {
  left: 4px;
  right: 4px;
  bottom: 4px;
  height: 2px;
}

.clip-workspace-list-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.clip-workspace-list-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  color: #f8fafc;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.clip-workspace-list-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #a8b0bd;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.clip-workspace-list-stats {
  flex: 0 0 52px;
  min-width: 52px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.clip-workspace-list-score.is-placeholder,
.clip-workspace-list-duration.is-placeholder {
  visibility: hidden;
}

.clip-workspace-list-score {
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid rgba(192, 132, 252, 0.28);
  background: rgba(168, 85, 247, 0.12);
  color: #e9d5ff;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.clip-workspace-list-score.score-excellent {
  color: #bbf7d0;
  border-color: rgba(74, 222, 128, 0.35);
  background: rgba(74, 222, 128, 0.1);
}

.clip-workspace-list-score.score-good {
  color: #fed7aa;
  border-color: rgba(253, 186, 116, 0.35);
  background: rgba(253, 186, 116, 0.1);
}

.clip-workspace-list-score.score-low {
  color: #fecaca;
  border-color: rgba(252, 165, 165, 0.35);
  background: rgba(252, 165, 165, 0.1);
}

.clip-workspace-list-duration {
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.45);
  color: #cbd5e1;
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.clip-workspace-preview {
  position: relative;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  gap: 10px;
  background:
    radial-gradient(110% 60% at 50% 0%, rgba(76, 29, 149, 0.1), transparent 58%),
    #0f1115;
}

.clip-workspace-selected-clip-info {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  color: #e5e7eb;
}

.clip-workspace-selected-clip-index,
.clip-workspace-selected-clip-score {
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
}

.clip-workspace-selected-clip-index {
  justify-self: start;
  color: #c4b5fd;
}

.clip-workspace-selected-clip-score {
  justify-self: end;
  color: #fde68a;
}

.clip-workspace-selected-clip-score i {
  margin-right: 3px;
}

.clip-workspace-actions h5 {
  margin: 10px 0 0;
  color: #f3f4f6;
  font-size: 15px;
  font-weight: 700;
}

.clip-workspace-field-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.clip-workspace-char-count {
  color: #6b7280;
  font-size: 10px;
  font-weight: 500;
}

.clip-workspace-player {
  position: relative;
  flex: 1 1 0;
  align-self: center;
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 100%;
  aspect-ratio: 9 / 16;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
  border: 1px solid rgba(216, 180, 254, 0.22);
  border-radius: 16px;
  box-shadow:
    0 24px 54px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.03);
}

.clip-workspace-player video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.clip-workspace-player-video--rendering {
  pointer-events: none;
  animation: collectionImageRendering 1.8s ease-in-out infinite;
}

.clip-workspace-player-poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.clip-workspace-player--rendering {
  animation: collectionItemPulseRendering 2s ease-in-out infinite;
}

.clip-workspace-player-rendering {
  z-index: 3;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
}

.clip-workspace-player-rendering .processing-indicator {
  width: 52px;
  height: 52px;
}

.clip-workspace-player-rendering .processing-indicator-orbit {
  border-width: 3px;
}

.clip-workspace-player-rendering .processing-indicator-core {
  width: 14px;
  height: 14px;
}

.clip-workspace-player-rendering-label {
  position: relative;
  z-index: 2;
  margin: 0;
  color: #e0f2fe;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}

.clip-workspace-player-rendering .collection-processing-progress {
  left: 16px;
  right: 16px;
  bottom: 16px;
}

.clip-workspace-player--empty {
  flex-direction: column;
  gap: 10px;
  color: var(--clipr-text-secondary);
}

.clip-workspace-player--empty i {
  color: #9ca3af;
  font-size: 28px;
}

.clip-workspace-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  padding: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  background: #0f1115;
  overflow-y: auto;
}

.clip-workspace-actions-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.clip-workspace-secondary-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.clip-workspace-action {
  width: 100%;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  color: #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: #1a1b20;
  transition: background 0.16s ease, border-color 0.16s ease;
}

.clip-workspace-action:hover:not(:disabled) {
  background: #22232a;
  border-color: rgba(255, 255, 255, 0.14);
}

.clip-workspace-action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.clip-workspace-action--publish {
  color: #fff;
  border-color: rgba(216, 180, 254, 0.55);
  background: linear-gradient(135deg, #6d28d9, #9333ea);
  box-shadow: 0 8px 22px rgba(109, 40, 217, 0.3);
}

.clip-workspace-action--publish:hover:not(:disabled) {
  background: linear-gradient(135deg, #7e22ce, #a855f7);
  border-color: rgba(243, 232, 255, 0.8);
}

.clip-workspace-action--secondary {
  min-height: 36px;
  padding: 8px 6px;
  color: #cbd5e1;
  font-size: 11px;
  white-space: nowrap;
}

.clip-workspace-action--danger {
  color: #f87171;
  border-color: rgba(248, 113, 113, 0.2);
  background: transparent;
}

.clip-workspace-pro {
  margin-left: auto;
  padding: 1px 5px;
  color: #fef3c7;
  font-size: 9px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.28);
}

.clip-workspace-section {
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  background: rgba(26, 27, 32, 0.5);
}

.clip-workspace-section-title {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 10px 12px;
  color: #e5e7eb;
  font-size: 12px;
  font-weight: 700;
}

.clip-workspace-schedule {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.clip-workspace-schedule-label {
  color: #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.clip-workspace-schedule-platforms {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.clip-workspace-schedule-platform-btn {
  width: 50px;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(30, 41, 59, 0.82);
  opacity: 1;
  filter: grayscale(0.2) brightness(0.92);
  cursor: pointer;
  transition:
    filter 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    transform 0.18s ease;
}

.clip-workspace-schedule-platform-btn:hover:not(:disabled) {
  filter: grayscale(0.05) brightness(1);
  border-color: rgba(192, 132, 252, 0.42);
}

.clip-workspace-schedule-platform-btn.is-selected {
  filter: none;
  border-color: rgba(168, 85, 247, 0.72);
  background: linear-gradient(155deg, rgba(76, 29, 149, 0.48) 0%, rgba(15, 23, 42, 0.96) 100%);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.28),
    0 0 20px rgba(168, 85, 247, 0.38);
}

.clip-workspace-schedule-platform-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.clip-workspace-schedule-platforms-loading,
.clip-workspace-schedule-platforms-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 50px;
  color: #9ca3af;
  font-size: 11px;
  line-height: 1.4;
}

.clip-workspace-schedule-platforms-empty {
  margin: 0;
}

.clip-workspace-schedule-platform-logo {
  width: 26px;
  height: 26px;
  object-fit: contain;
  border-radius: 4px;
  pointer-events: none;
}

.clip-workspace-metadata {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 12px 12px;
  margin: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.clip-workspace-metadata label,
.clip-workspace-schedule-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clip-workspace-schedule-title h5 {
  margin: 0;
}

.clip-workspace-schedule-title button {
  color: var(--clipr-text-secondary);
  border: 0;
  background: transparent;
}

.clip-workspace-schedule-title .clip-workspace-schedule-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
}

.clip-workspace-schedule > label,
.clip-workspace-schedule-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 11px;
  font-weight: 600;
}

.clip-workspace-metadata label {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  margin: 0;
  color: #d1d5db;
  font-size: 12px;
  font-weight: 600;
}

.clip-workspace-metadata .form-control,
.clip-workspace-schedule .form-control {
  min-height: 38px;
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  background: #1a1b20;
  box-shadow: none;
}

.clip-workspace-metadata .form-control[readonly] {
  color: #d1d5db;
  background: #1a1b20;
}

.clip-workspace-metadata .form-control:focus {
  color: #f8fafc;
  border-color: rgba(192, 132, 252, 0.86);
  background: rgba(15, 23, 42, 0.88);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.16);
}

.clip-workspace-metadata .form-control:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.clip-workspace-metadata .clip-workspace-tag-editor {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: 38px;
  min-width: 0;
  gap: 6px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: text;
}

.clip-workspace-metadata .clip-workspace-tag-editor:focus-within {
  color: #f8fafc;
  border-color: rgba(192, 132, 252, 0.86);
  background: rgba(15, 23, 42, 0.88);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.16);
}

.clip-workspace-tag-editor.is-disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.clip-workspace-tag-pill {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  min-height: 24px;
  padding: 2px 8px;
  overflow: hidden;
  color: #e9d5ff;
  border: 1px solid rgba(192, 132, 252, 0.46);
  border-radius: 999px;
  background: rgba(126, 34, 206, 0.24);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clip-workspace-tag-input {
  flex: 1 1 80px;
  min-width: 80px;
  height: 24px;
  padding: 0;
  color: inherit;
  border: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
}

.clip-workspace-tag-input:disabled {
  cursor: not-allowed;
}

.clip-workspace-metadata textarea.form-control,
.clip-workspace-schedule textarea.form-control {
  resize: vertical;
}

.clip-workspace-schedule-submit {
  position: sticky;
  bottom: 0;
  margin-top: auto;
}

.clip-workspace-mobile-toolbar,
.clip-workspace-mobile-bottom-bar,
.clip-workspace-mobile-back,
.clip-workspace-mobile-clip-navigation,
.clip-workspace-mobile-previous,
.clip-workspace-mobile-next,
.clip-workspace-drawer-close,
.clip-workspace-mobile-panel-close,
.clip-workspace-mobile-sheet-backdrop {
  display: none;
}

.clip-workspace-mobile-panel-loading {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #f5f3ff;
  font-size: 13px;
  font-weight: 700;
  background: rgba(15, 17, 21, 0.88);
  backdrop-filter: blur(5px);
}

.clip-workspace-schedule .form-control:focus {
  color: #f8fafc;
  border-color: rgba(192, 132, 252, 0.86);
  background: rgba(15, 23, 42, 0.88);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.16);
}

.clip-workspace-schedule select.form-control,
.clip-workspace-schedule input[type="date"].form-control {
  color-scheme: dark;
}

.clip-workspace-schedule-time-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px solid rgba(168, 85, 247, 0.18);
  border-radius: 14px;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(76, 29, 149, 0.22) 0%, transparent 58%),
    linear-gradient(180deg, rgba(30, 41, 59, 0.62) 0%, rgba(15, 23, 42, 0.88) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.clip-workspace-schedule-time-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.clip-workspace-schedule-time-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 8px;
  align-items: end;
}

.clip-workspace-schedule-time-select {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  min-width: 0;
}

.clip-workspace-schedule-time-select-label {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.clip-workspace-schedule-time-select-wrap {
  position: relative;
}

.clip-workspace-schedule-time-input {
  width: 100%;
  min-height: 52px;
  padding: 0 34px 0 12px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.9);
  color: #f8fafc;
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.clip-workspace-schedule-time-input:hover {
  border-color: rgba(192, 132, 252, 0.42);
}

.clip-workspace-schedule-time-input:focus {
  outline: none;
  border-color: rgba(192, 132, 252, 0.86);
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.16);
}

.clip-workspace-schedule-time-input:invalid,
.clip-workspace-schedule-time-input option[value=""][disabled] {
  color: #9ca3af;
}

.clip-workspace-schedule-time-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 11px;
  pointer-events: none;
}

.clip-workspace-schedule-time-colon {
  align-self: center;
  margin-bottom: 12px;
  color: #e5e7eb;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.clip-workspace-schedule-time-period {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 3px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.72);
}

.clip-workspace-schedule-period-btn {
  min-height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.clip-workspace-schedule-period-btn.is-selected {
  background: linear-gradient(155deg, rgba(76, 29, 149, 0.72) 0%, rgba(109, 40, 217, 0.58) 100%);
  color: #f8fafc;
  box-shadow: 0 0 16px rgba(168, 85, 247, 0.28);
}

.clip-workspace-schedule-period-btn:not(.is-selected):hover {
  color: #e5e7eb;
  background: rgba(30, 41, 59, 0.72);
}

@media (max-width: 991px) {
  .clip-workspace {
    grid-template-columns: minmax(180px, 0.65fr) minmax(300px, 1.35fr);
  }

  .clip-workspace-mobile-toolbar {
    position: absolute;
    top: 14px;
    right: 12px;
    left: 68px;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }

  .clip-workspace-mobile-actions,
  .clip-workspace-mobile-clips,
  .clip-workspace-mobile-back,
  .clip-workspace-mobile-previous,
  .clip-workspace-mobile-next {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 8px 10px;
    color: #f5f3ff;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid rgba(216, 180, 254, 0.26);
    border-radius: 10px;
    background: rgba(24, 22, 34, 0.8);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(12px);
  }

  .clip-workspace-mobile-back {
    margin-right: auto;
    padding-left: 0;
    color: #ddd6fe;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
    white-space: nowrap;
  }

  .clip-workspace-mobile-next {
    width: 36px;
    justify-content: center;
    padding: 8px 0;
  }

  .clip-workspace-mobile-actions {
    border-color: rgba(216, 180, 254, 0.5);
    background: linear-gradient(135deg, rgba(88, 28, 135, 0.92), rgba(76, 29, 149, 0.82));
    box-shadow:
      0 8px 20px rgba(76, 29, 149, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    white-space: nowrap;
  }

  .clip-workspace-mobile-previous:disabled,
  .clip-workspace-mobile-next:disabled {
    opacity: 0.45;
  }

  .clip-workspace-mobile-clips {
    display: none;
  }

  .clip-workspace-mobile-back:hover,
  .clip-workspace-mobile-back:focus-visible {
    color: #f5f3ff;
    background: transparent;
  }

  .clip-workspace-mobile-sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1090;
    display: block;
    padding: 0;
    border: 0;
    background: rgba(2, 6, 23, 0.64);
    cursor: default;
  }

  .clip-workspace-actions {
    display: none;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1100;
    width: min(360px, 92vw);
    padding: 20px 16px;
    border-top: 0;
    border-left: 1px solid rgba(216, 180, 254, 0.22);
    background: #0f1115;
    box-shadow: -18px 0 44px rgba(0, 0, 0, 0.46);
    transform: none;
    touch-action: pan-y;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  .clip-workspace-actions.is-drawer-open {
    display: flex;
  }

  .clip-workspace-drawer-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    width: 32px;
    height: 32px;
    margin: -4px 0 0;
    padding: 0;
    color: #cbd5e1;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
  }
}

@media (max-width: 640px) {
  .clip-workspace {
    grid-template-columns: 1fr;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    border-radius: 0;
  }

  .clip-workspace-list {
    display: none;
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1100;
    height: min(76dvh, 680px);
    padding: 18px 14px calc(14px + env(safe-area-inset-bottom));
    border: 1px solid rgba(216, 180, 254, 0.22);
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -18px 44px rgba(0, 0, 0, 0.46);
    transform: none;
  }

  .clip-workspace-list.is-mobile-panel-open {
    display: flex;
  }

  .clip-workspace-mobile-panel-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    width: 32px;
    height: 32px;
    margin: -4px 0 8px;
    padding: 0;
    color: #cbd5e1;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
  }

  .clip-workspace-list-scroll {
    max-height: none;
    overflow-x: hidden;
  }

  .clip-workspace-preview {
    min-height: 0;
    padding: 68px 12px calc(12px + env(safe-area-inset-bottom));
  }

  .clip-workspace-selected-clip-info {
    gap: 8px;
  }

  .clip-workspace-mobile-clips {
    display: inline-flex;
  }

  .clip-workspace-mobile-clip-navigation {
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    overflow: hidden;
    border: 1px solid rgba(216, 180, 254, 0.26);
    border-radius: 10px;
    background: rgba(24, 22, 34, 0.8);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(12px);
  }

  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-previous,
  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-clips,
  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-next {
    min-height: 34px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-previous,
  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-next {
    width: 32px;
    justify-content: center;
  }

  .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-clips {
    padding: 0 8px;
    color: #f5f3ff;
    white-space: nowrap;
    border-right: 1px solid rgba(216, 180, 254, 0.16);
    border-left: 1px solid rgba(216, 180, 254, 0.16);
  }

  .clip-workspace-mobile-clip-navigation button:disabled {
    opacity: 0.36;
  }

  .clip-workspace-mobile-toolbar {
    left: 66px;
    gap: 4px;
  }

  .schedule-page--collection-detail .clip-workspace-mobile-toolbar {
    left: 12px;
    flex-wrap: wrap;
    align-content: flex-start;
  }

  .schedule-page--collection-detail .clip-workspace-mobile-clip-navigation {
    flex: 1 0 100%;
    width: 100%;
  }

  .schedule-page--collection-detail .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-previous,
  .schedule-page--collection-detail .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-next {
    flex: 0 0 44px;
    width: 44px;
  }

  .schedule-page--collection-detail .clip-workspace-mobile-clip-navigation .clip-workspace-mobile-clips {
    flex: 1 1 auto;
    justify-content: center;
  }

  .schedule-page--collection-detail .clip-workspace-preview {
    padding-top: 108px;
  }

  .clip-workspace-mobile-actions {
    padding-right: 8px;
    padding-left: 8px;
  }

  .clip-workspace-mobile-back {
    max-width: 104px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .clip-workspace-mobile-bottom-bar {
    position: absolute;
    right: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
    left: 12px;
    z-index: 3;
    display: block;
  }

  .clip-workspace-mobile-bottom-bar .clip-workspace-action {
    min-height: 48px;
  }

  .clip-workspace-player,
  .clip-workspace-player.video-card-playback {
    flex: none;
    height: min(68dvh, 560px);
    width: auto;
    max-height: min(68dvh, 560px);
    max-width: min(100%, 300px);
    aspect-ratio: 9 / 16;
  }

  .clip-workspace-actions {
    width: 100%;
    padding: 20px 16px calc(16px + env(safe-area-inset-bottom));
    border-left: 0;
  }

  .clip-workspace-actions.is-drawer-open {
    display: flex;
  }
}

.schedule-row-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.schedule-row-header--streams {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.16);
}

.schedule-row-title-group {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.schedule-row-icon {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 18px;
  color: #e9d5ff;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.35) 0%, rgba(76, 29, 149, 0.2) 100%);
  border: 1px solid rgba(168, 85, 247, 0.4);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.1),
    0 8px 20px rgba(76, 29, 149, 0.28);
}

.schedule-row-title-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule-row-header-left {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-row-header--streams .schedule-period-filter {
  margin-top: 2px;
}

.schedule-row-header-right {
  flex-shrink: 0;
}

.schedule-row-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: var(--clipr-text-primary);
}

.schedule-row-header--streams .schedule-row-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: linear-gradient(90deg, #f3e8ff 0%, #c4b5fd 55%, #93c5fd 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.schedule-row-title--greeting {
  font-size: clamp(1.35rem, 2.6vw, 1.85rem);
  line-height: 1.28;
  letter-spacing: 0.01em;
}

.schedule-row-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--clipr-text-secondary);
}

.schedule-row-header--streams .schedule-row-subtitle {
  font-size: 13px;
  line-height: 1.45;
  max-width: 52ch;
}

.schedule-row-empty--illustrated {
  padding: 28px 20px !important;
}

.schedule-row-empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  font-size: 28px;
  color: #c4b5fd;
  background: linear-gradient(145deg, rgba(168, 85, 247, 0.22) 0%, rgba(15, 23, 42, 0.55) 100%);
  border: 1px solid rgba(168, 85, 247, 0.28);
  box-shadow: 0 10px 28px rgba(76, 29, 149, 0.22);
}

.schedule-row-empty-icon--muted {
  color: #94a3b8;
  background: linear-gradient(145deg, rgba(148, 163, 184, 0.16) 0%, rgba(15, 23, 42, 0.5) 100%);
  border-color: rgba(148, 163, 184, 0.28);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.35);
}

.schedule-row-body {
  width: 100%;
}

.schedule-row-empty {
  color: var(--clipr-text-tertiary);
}

/* Fila jobs: scroll horizontal y cards estilo VODClips */
.collection-row-scroll-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 2px;
}

.collections-row-scroll {
  display: flex;
  align-items: stretch;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-top: 4px;
  padding-bottom: 2px;
  flex: 1;
  min-width: 0;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.collections-row-scroll::-webkit-scrollbar {
  display: none;
}

.scroll-arrow-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: var(--clipr-purple-gradient);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px var(--clipr-purple-glow);
}

.scroll-arrow-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px var(--clipr-purple-overlay-intense);
}

/* Colecciones en lista vertical (sin carrusel lateral). */
.schedule-row-batches .scroll-arrow-btn {
  display: none;
}

.schedule-row-batches .collections-row-scroll {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow-x: visible;
  overflow-y: visible;
  scroll-snap-type: none;
}

.schedule-row-batches .collection-item {
  flex: initial;
  min-width: 0;
  width: auto;
  max-width: none;
  scroll-snap-align: none;
}

.collection-page-enter-active,
.collection-page-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.collection-page-enter {
  opacity: 0;
  transform: translateX(18px);
}

.collection-page-leave-to {
  opacity: 0;
  transform: translateX(-18px);
}

@media (max-width: 1199px) {
  .schedule-row-batches .collections-row-scroll {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .schedule-row-batches .collections-row-scroll {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Clips en lista vertical (sin carrusel lateral). */
.schedule-row-videos .scroll-arrow-btn {
  display: none;
}

.schedule-row-videos .collections-row-scroll.videos-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 17px;
  justify-content: flex-start;
  align-items: stretch;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 15px;
  scroll-snap-type: none;
}

.schedule-row-videos .video-card {
  flex: 0 0 236px;
  align-self: stretch;
  min-width: 0;
  width: 236px;
  max-width: 236px;
  min-height: 320px;
  padding: 11px;
  border-radius: 14px;
  height: 100%;
  --video-card-spacing: 9px;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.18),
    0 0 18px rgba(168, 85, 247, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.28);
}

.schedule-row-videos .video-card:hover {
  transform: none;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.28),
    0 0 22px rgba(168, 85, 247, 0.16),
    0 6px 14px rgba(0, 0, 0, 0.32);
}

.schedule-row-videos .video-card-selected {
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.38),
    0 0 24px rgba(168, 85, 247, 0.2),
    0 8px 18px rgba(0, 0, 0, 0.34);
}

.schedule-row-videos .video-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--video-card-spacing);
  margin-bottom: calc(var(--video-card-spacing) - 2px);
}

.schedule-row-videos .video-card-status {
  min-width: 0;
}

.schedule-row-videos .video-card-status .badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.schedule-row-videos .video-card-score {
  flex: 0 0 auto;
  width: 33px;
  height: 33px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.42);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.3);
  font-variant-numeric: tabular-nums;
}

.schedule-row-videos .video-card-score.score-excellent {
  color: #4ade80;
  border-color: rgba(74, 222, 128, 0.6);
}

.schedule-row-videos .video-card-score.score-good {
  color: #fdba74;
  border-color: rgba(253, 186, 116, 0.65);
}

.schedule-row-videos .video-card-score.score-low {
  color: #fca5a5;
  border-color: rgba(252, 165, 165, 0.65);
}

.schedule-row-videos .video-card-media {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: calc(var(--video-card-spacing) - 1px);
  position: relative;
}

.schedule-row-videos .video-card-media--rendering {
  cursor: wait;
}

.schedule-row-videos .video-card-media--rendered {
  cursor: default;
}

.schedule-row-videos .video-card-thumbnail {
  width: min(108px, 100%);
  margin-left: auto;
  margin-right: auto;
  border-radius: 11px;
  margin-bottom: 0;
}

.schedule-row-videos .video-card-playback:not(.clip-workspace-player),
.schedule-row-videos .video-card-image {
  width: 100%;
  height: 100%;
  display: block;
}

.schedule-row-videos .video-card-placeholder {
  font-size: 22px;
}

.schedule-row-videos .video-card-play-btn {
  width: 32px;
  height: 32px;
  font-size: 12px;
  line-height: 32px;
}

.schedule-row-videos .video-card-meta {
  display: flex;
  flex-direction: column;
  gap: var(--video-card-spacing);
  flex: 1 1 auto;
}

.schedule-row-videos .video-card-title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.schedule-row-videos .video-card-title {
  flex: 1 1 auto;
  font-size: 12px;
  line-height: 1.3;
  min-height: 0;
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  -webkit-box-orient: unset;
}

.schedule-row-videos .video-card-duration {
  flex: 0 0 auto;
  min-width: 40px;
  text-align: right;
  font-size: 11px;
  line-height: 1.3;
  color: var(--clipr-text-secondary);
  font-variant-numeric: tabular-nums;
}

.schedule-row-videos .video-card-duration--empty {
  visibility: hidden;
}

.schedule-row-videos .video-card-actions {
  margin-top: auto;
  gap: var(--video-card-spacing);
  padding-top: var(--video-card-spacing);
}

.schedule-row-videos .video-card-actions-primary,
.schedule-row-videos .video-card-actions-premium--minimal {
  gap: var(--video-card-spacing);
}

.schedule-row-videos .video-card-actions-premium {
  gap: var(--video-card-spacing);
}

.schedule-row-videos .video-card-actions-premium--minimal .video-card-icon-btn {
  height: 32px;
  border-radius: 10px;
}

.schedule-row-videos .video-card-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border-color: rgba(216, 180, 254, 0.56);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.08) inset, 0 9px 16px rgba(15, 23, 42, 0.4);
}

.schedule-row-videos .video-card-icon-btn:hover:not(:disabled) {
  border-color: rgba(233, 213, 255, 0.82);
  box-shadow: 0 11px 20px rgba(109, 40, 217, 0.36);
}

.schedule-row-videos .video-card-icon-btn i {
  font-size: 13px;
}

.schedule-row-videos .video-card-schedule-btn {
  padding: 5px 7px;
  font-size: 10px;
  min-height: 28px;
  gap: 4px;
  justify-content: center;
}

.schedule-row-videos .video-card-schedule-btn i {
  font-size: 10px;
}

.schedule-row-videos .video-card-download-btn,
.schedule-row-videos .video-card-delete-btn,
.schedule-row-videos .video-card-editor-btn {
  padding: 5px 7px;
  font-size: 10px;
  min-height: 28px;
  gap: 4px;
  justify-content: center;
}

.schedule-row-videos .video-card-download-btn i,
.schedule-row-videos .video-card-delete-btn i,
.schedule-row-videos .video-card-editor-btn i {
  font-size: 10px;
}

@media (max-width: 1199px) {
  .schedule-row-videos .video-card {
    flex-basis: 230px;
    width: 230px;
    max-width: 230px;
  }
}

@media (max-width: 991px) {
  .schedule-row-videos .video-card {
    flex-basis: min(100%, 260px);
    width: min(100%, 260px);
    max-width: 260px;
    min-height: 306px;
  }
}

.collection-item {
  display: flex;
  flex-direction: column;
  flex: 0 0 320px;
  min-width: 320px;
  scroll-snap-align: start;
  background: transparent;
  border: 1px solid rgba(139, 92, 246, 0.55);
  border-radius: var(--clipr-radius-lg);
  overflow: visible;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 92, 246, 0.2);
  position: relative;
  z-index: 1;
}

.collection-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.5);
  border-color: rgba(139, 92, 246, 0.8);
}

.collection-item-active {
  background: transparent;
  border-color: var(--clipr-purple-light);
  border-width: 1.5px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 24px var(--clipr-purple-glow);
  z-index: 3;
}

.collection-item-menu-open {
  z-index: 120;
}

.collection-preview {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.35);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2),
    0 0 24px var(--clipr-purple-glow),
    0 0 48px rgba(168, 85, 247, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.3);
  border-radius: var(--clipr-radius-lg) var(--clipr-radius-lg) 0 0;
}

.collection-status-badge-thumbnail {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 4;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  transition: font-size 0.2s ease, padding 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
}

.collection-item:hover .collection-status-badge-thumbnail {
  font-size: 14px;
  padding: 6px 12px;
}

.collection-status-badge-thumbnail.job-status-downloaded {
  background: rgba(168, 85, 247, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-scheduling {
  background: rgba(236, 72, 153, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-rendered {
  background: rgba(34, 197, 94, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-decided {
  background: rgba(59, 130, 246, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-queued,
.collection-status-badge-thumbnail.job-status-running,
.collection-status-badge-thumbnail.job-status-rendering {
  background: rgba(234, 179, 8, 0.9);
  color: #1a1a1f;
}

.collection-status-badge-thumbnail.job-status-failed,
.collection-status-badge-thumbnail.job-status-cancelled {
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-insufficient-tokens {
  background: rgba(249, 115, 22, 0.9);
  color: #fff;
}

.collection-status-badge-thumbnail.job-status-demo {
  background: rgba(20, 184, 166, 0.92);
  color: #042f2e;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.collection-status-badge-thumbnail.job-status-unknown {
  background: rgba(113, 113, 122, 0.9);
  color: #fff;
}

.collection-preview-image {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: #000;
  transition: filter 0.3s ease, transform 0.3s ease;
}

.collection-preview-image--downloading {
  animation: collectionImageDownloading 2.2s ease-in-out infinite;
}

.collection-preview-image--downloaded {
  animation: collectionImageDownloaded 2.6s ease-in-out infinite;
}

.collection-preview-image--rendering {
  animation: collectionImageRendering 1.8s ease-in-out infinite;
}

.collection-preview-image--queued,
.collection-preview-image--decided {
  animation: collectionImageQueued 3s ease-in-out infinite;
}

@keyframes collectionImageDownloading {
  0%,
  100% {
    filter: brightness(0.72) saturate(0.9);
    transform: scale(1);
  }
  50% {
    filter: brightness(0.88) saturate(1.05);
    transform: scale(1.02);
  }
}

@keyframes collectionImageDownloaded {
  0%,
  100% {
    filter: brightness(0.7) saturate(0.85) hue-rotate(0deg);
  }
  50% {
    filter: brightness(0.82) saturate(1.1) hue-rotate(8deg);
  }
}

@keyframes collectionImageRendering {
  0%,
  100% {
    filter: brightness(0.68) saturate(0.9);
    transform: scale(1);
  }
  50% {
    filter: brightness(0.9) saturate(1.15);
    transform: scale(1.03);
  }
}

@keyframes collectionImageQueued {
  0%,
  100% {
    filter: brightness(0.75);
  }
  50% {
    filter: brightness(0.85);
  }
}

.collection-preview-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 20px;
}

.collection-preview-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: rgba(2, 6, 23, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  overflow: hidden;
}

.collection-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  gap: 4px;
  background: rgba(26, 10, 41, 0.65);
}

.collection-content-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.collection-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--clipr-text-primary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.collection-actions {
  position: relative;
  flex: 0 0 auto;
}

.collection-actions-toggle {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.72);
  color: var(--clipr-text-secondary);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.collection-actions-toggle:hover:not(:disabled) {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
  background: rgba(76, 29, 149, 0.45);
}

.collection-actions-menu {
  position: absolute;
  right: 0;
  top: auto;
  bottom: calc(100% + 6px);
  z-index: 60;
  width: 210px;
  border-radius: 10px;
  border: 1px solid var(--clipr-border-purple);
  background: rgba(9, 12, 22, 0.98);
  box-shadow: 0 12px 28px rgba(2, 6, 23, 0.55);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.collection-actions-menu-item {
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--clipr-text-primary);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.collection-actions-menu-item:hover:not(:disabled) {
  background: rgba(147, 51, 234, 0.18);
}

.collection-actions-menu-item--danger {
  color: #fda4af;
}

.collection-actions-menu-item--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.22);
  color: #fecdd3;
}

.collection-actions-menu-item:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.collection-name-running {
  color: #facc15;
}

.collection-name-downloaded {
  color: #c084fc;
}

.collection-name-rendering {
  color: #22d3ee;
}

.collection-name-decided {
  color: #f59e0b;
}

.collection-name-queued {
  color: #ffffff;
}

.collection-name-insufficient-tokens {
  color: #fdba74;
}

.collection-failure-legend {
  margin: -1px 0 1px;
  font-size: 12px;
  color: #fca5a5;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--clipr-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-preview-running,
.collection-preview-downloading {
  color: #facc15;
}

.collection-preview-downloaded {
  color: #c084fc;
}

.collection-preview-rendering {
  color: #22d3ee;
}

.collection-preview-queued {
  color: #ffffff;
}

.collection-preview-decided {
  color: #f59e0b;
}

.collection-item--processing-downloading {
  animation: collectionItemPulseDownloading 2.4s ease-in-out infinite;
}

.collection-item--processing-downloaded {
  animation: collectionItemPulseDownloaded 2.8s ease-in-out infinite;
}

.collection-item--processing-rendering {
  animation: collectionItemPulseRendering 2s ease-in-out infinite;
}

.collection-item--processing-queued,
.collection-item--processing-decided {
  animation: collectionItemPulseQueued 3s ease-in-out infinite;
}

.collection-preview-overlay::before {
  content: "";
  position: absolute;
  inset: -40%;
  pointer-events: none;
  opacity: 0.85;
}

.collection-preview-overlay--downloading::before {
  background: linear-gradient(
    180deg,
    transparent 35%,
    rgba(250, 204, 21, 0.16) 50%,
    transparent 65%
  );
  animation: collectionOverlayFlowDown 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.collection-preview-overlay--downloaded::before {
  background: linear-gradient(
    115deg,
    transparent 28%,
    rgba(192, 132, 252, 0.12) 46%,
    rgba(192, 132, 252, 0.24) 50%,
    rgba(192, 132, 252, 0.12) 54%,
    transparent 72%
  );
  animation: collectionOverlaySweep 2.4s ease-in-out infinite;
}

.collection-preview-overlay--rendering::before {
  background: linear-gradient(
    115deg,
    transparent 28%,
    rgba(34, 211, 238, 0.14) 46%,
    rgba(34, 211, 238, 0.28) 50%,
    rgba(34, 211, 238, 0.14) 54%,
    transparent 72%
  );
  animation: collectionOverlaySweep 1.5s ease-in-out infinite;
}

.collection-preview-overlay--queued::before,
.collection-preview-overlay--decided::before {
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.12), transparent 58%);
  animation: collectionOverlayBreathe 2.6s ease-in-out infinite;
}

.collection-processing-stage {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.collection-processing-progress {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 10px;
  z-index: 2;
  height: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.collection-processing-progress-bar {
  display: block;
  height: 100%;
  width: 38%;
  border-radius: inherit;
  transform: translateX(-120%);
}

.collection-preview-overlay--downloading .collection-processing-progress-bar {
  background: linear-gradient(90deg, transparent, #facc15, transparent);
  animation: collectionProgressSlide 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.collection-preview-overlay--downloaded .collection-processing-progress-bar {
  background: linear-gradient(90deg, transparent, #c084fc, #a855f7, transparent);
  width: 52%;
  animation: collectionProgressPulse 2.2s ease-in-out infinite;
}

.collection-preview-overlay--rendering .collection-processing-progress-bar {
  background: linear-gradient(90deg, transparent, #22d3ee, #06b6d4, transparent);
  animation: collectionProgressSlide 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.collection-preview-overlay--queued .collection-processing-progress-bar,
.collection-preview-overlay--decided .collection-processing-progress-bar {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
  width: 30%;
  animation: collectionProgressSlide 2.4s ease-in-out infinite;
}

.processing-indicator {
  --processing-color: #facc15;
  --processing-glow: rgba(250, 204, 21, 0.45);
  position: relative;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.processing-indicator-orbit,
.processing-indicator-core {
  position: absolute;
  border-radius: 50%;
  box-sizing: border-box;
}

.processing-indicator-orbit {
  inset: 0;
  border: 2.5px solid transparent;
  border-top-color: var(--processing-color);
  border-right-color: var(--processing-color-dim, rgba(250, 204, 21, 0.35));
  filter: drop-shadow(0 0 6px var(--processing-glow));
  animation: processingOrbitSpin 1.15s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
}

.processing-indicator-core {
  width: 10px;
  height: 10px;
  background: var(--processing-color);
  box-shadow: 0 0 14px var(--processing-glow);
  animation: processingCorePulse 1.4s ease-in-out infinite;
}

.processing-indicator--queued {
  --processing-color: #f8fafc;
  --processing-color-dim: rgba(248, 250, 252, 0.35);
  --processing-glow: rgba(248, 250, 252, 0.4);
}

.processing-indicator--downloading {
  --processing-color: #facc15;
  --processing-color-dim: rgba(250, 204, 21, 0.35);
  --processing-glow: rgba(250, 204, 21, 0.5);
}

.processing-indicator--downloaded {
  --processing-color: #c084fc;
  --processing-color-dim: rgba(192, 132, 252, 0.35);
  --processing-glow: rgba(192, 132, 252, 0.55);
}

.processing-indicator--decided {
  --processing-color: #f59e0b;
  --processing-color-dim: rgba(245, 158, 11, 0.35);
  --processing-glow: rgba(245, 158, 11, 0.45);
}

.processing-indicator--rendering {
  --processing-color: #22d3ee;
  --processing-color-dim: rgba(34, 211, 238, 0.35);
  --processing-glow: rgba(34, 211, 238, 0.55);
}

.processing-indicator--downloaded .processing-indicator-orbit {
  animation-duration: 1.65s;
}

.processing-indicator--rendering .processing-indicator-orbit {
  animation-duration: 0.85s;
}

.processing-dots span {
  display: inline-block;
  transform: translateY(0);
  animation: processingDotsBounce 1.35s ease-in-out infinite;
}

.processing-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.processing-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes processingDotsBounce {
  0%,
  70%,
  100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  35% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@keyframes processingOrbitSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes processingCorePulse {
  0%,
  100% {
    transform: scale(0.72);
    opacity: 0.65;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes collectionItemPulseDownloading {
  0%,
  100% {
    border-color: rgba(250, 204, 21, 0.45);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(250, 204, 21, 0.15);
  }
  50% {
    border-color: rgba(250, 204, 21, 0.85);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45), 0 0 18px rgba(250, 204, 21, 0.22);
  }
}

@keyframes collectionItemPulseDownloaded {
  0%,
  100% {
    border-color: rgba(168, 85, 247, 0.5);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(168, 85, 247, 0.2);
  }
  50% {
    border-color: rgba(192, 132, 252, 0.9);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45), 0 0 22px rgba(168, 85, 247, 0.32);
  }
}

@keyframes collectionItemPulseRendering {
  0%,
  100% {
    border-color: rgba(34, 211, 238, 0.45);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 211, 238, 0.16);
  }
  50% {
    border-color: rgba(34, 211, 238, 0.95);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45), 0 0 24px rgba(34, 211, 238, 0.3);
  }
}

@keyframes collectionItemPulseQueued {
  0%,
  100% {
    border-color: rgba(139, 92, 246, 0.55);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 92, 246, 0.2);
  }
  50% {
    border-color: rgba(167, 139, 250, 0.85);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45), 0 0 16px rgba(139, 92, 246, 0.24);
  }
}

@keyframes collectionOverlayFlowDown {
  0% {
    transform: translateY(-28%);
    opacity: 0;
  }
  20%,
  75% {
    opacity: 1;
  }
  100% {
    transform: translateY(28%);
    opacity: 0;
  }
}

@keyframes collectionOverlaySweep {
  0% {
    transform: translateX(-38%) rotate(0deg);
    opacity: 0;
  }
  20%,
  70% {
    opacity: 1;
  }
  100% {
    transform: translateX(38%) rotate(0deg);
    opacity: 0;
  }
}

@keyframes collectionOverlayBreathe {
  0%,
  100% {
    transform: scale(0.92);
    opacity: 0.45;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.9;
  }
}

@keyframes collectionProgressSlide {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}

@keyframes collectionProgressPulse {
  0%,
  100% {
    transform: translateX(18%) scaleX(0.75);
    opacity: 0.55;
  }
  50% {
    transform: translateX(48%) scaleX(1);
    opacity: 1;
  }
}

@keyframes processingDots {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }
  40% {
    opacity: 1;
  }
}

@keyframes processingSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes videoCardRenderingPulse {
  0%,
  100% {
    border-color: rgba(34, 211, 238, 0.42);
    box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.14),
      0 0 18px rgba(34, 211, 238, 0.12),
      0 4px 16px rgba(0, 0, 0, 0.45);
  }
  50% {
    border-color: rgba(34, 211, 238, 0.9);
    box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.32),
      0 0 30px rgba(34, 211, 238, 0.32),
      0 4px 16px rgba(0, 0, 0, 0.45);
  }
}

@keyframes videoCardRenderingSweep {
  0% {
    transform: translateX(-42%) rotate(0deg);
    opacity: 0;
  }
  25%,
  70% {
    opacity: 1;
  }
  100% {
    transform: translateX(42%) rotate(0deg);
    opacity: 0;
  }
}

.collection-updated {
  margin: 0;
  font-size: 12px;
  color: var(--clipr-text-tertiary);
}

.collection-reassign-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 90px;
  color: var(--clipr-text-secondary);
}

.collection-reassign-state--empty {
  flex-direction: column;
  text-align: center;
}

.collection-reassign-scope-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.25);
  margin-bottom: 10px;
}

.collection-reassign-scope-btn {
  min-height: 30px;
  border: none;
  border-radius: 8px;
  padding: 0 10px;
  background: transparent;
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.collection-reassign-scope-btn.active {
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
}

.collection-reassign-search {
  max-width: none;
  margin-bottom: 10px;
}

.collection-reassign-search-input.form-control {
  width: 100%;
  background: rgba(15, 23, 42, 0.7);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
  caret-color: var(--clipr-text-primary);
}

.collection-reassign-search-input.form-control::placeholder {
  color: var(--clipr-text-tertiary);
}

.collection-reassign-search-input.form-control:focus {
  background: rgba(15, 23, 42, 0.9);
  border-color: var(--clipr-border-purple);
  color: var(--clipr-text-primary);
  box-shadow: 0 0 0 2px var(--clipr-purple-glow);
}

.collection-reassign-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.collection-reassign-user {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  border: 1px solid var(--clipr-border-color);
  background: rgba(15, 23, 42, 0.45);
  color: var(--clipr-text-primary);
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.collection-reassign-user:hover:not(:disabled) {
  border-color: var(--clipr-border-purple);
  background: rgba(76, 29, 149, 0.35);
}

.collection-reassign-user:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.collection-reassign-user-name {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.collection-reassign-user-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.collection-reassign-user-copy small {
  color: var(--clipr-text-tertiary);
  font-size: 11px;
  overflow-wrap: anywhere;
}

.collection-reassign-user-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--clipr-text-secondary);
  font-size: 12px;
}

.collection-rerender-failure-hint {
  margin: 10px 0 0;
  color: #fca5a5;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

@media (max-width: 767px) {
  .schedule-row.schedule-row-videos {
    --schedule-videos-mobile-spacing: 15px;
    padding: 0;
  }

  .schedule-row-videos .schedule-row-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: var(--schedule-videos-mobile-spacing);
  }

  .schedule-row-videos .schedule-row-title {
    font-size: 20px;
  }

  .schedule-row-videos .schedule-row-subtitle {
    font-size: 14px;
    line-height: 1.45;
  }

  .schedule-row-videos .schedule-row-header-right {
    align-self: flex-start;
  }

  .schedule-row-videos .batch-header-badge {
    font-size: 13px;
  }

  .schedule-row-batches .collections-row-scroll {
    grid-template-columns: 1fr;
  }

  .schedule-row-videos .collections-row-scroll.videos-strip {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--schedule-videos-mobile-spacing);
    row-gap: var(--schedule-videos-mobile-spacing);
    column-gap: var(--schedule-videos-mobile-spacing);
    overflow-x: hidden;
    overflow-y: hidden;
    padding: 0;
    scroll-snap-type: none;
    touch-action: auto;
  }

  .schedule-row-videos .video-card {
    width: min(100%, 302px);
    max-width: 302px;
    justify-self: center;
    min-height: 334px;
    height: auto;
    margin: 0;
    --video-card-spacing: 11px;
    padding: 13px 13px 12px;
  }

  .schedule-row-videos .video-card-header {
    margin-bottom: 7px;
  }

  .schedule-row-videos .video-card-title {
    font-size: 13px;
    line-height: 1.3;
  }

  .schedule-row-videos .video-card-duration {
    min-width: 42px;
    font-size: 11px;
  }

  .schedule-row-videos .video-card-status .badge {
    min-height: 24px;
    padding: 4px 10px;
    font-size: 10px;
  }

  .schedule-row-videos .video-card-score {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }

  .schedule-row-videos .video-card-thumbnail {
    width: min(119px, 100%);
  }

  .schedule-row-videos .video-card-play-btn {
    width: 34px;
    height: 34px;
    font-size: 13px;
    line-height: 34px;
  }

  .schedule-row-videos .video-card-actions-premium--minimal .video-card-icon-btn {
    height: 33px;
  }

  .schedule-row-videos .video-card-actions-primary,
  .schedule-row-videos .video-card-actions-premium--minimal {
    gap: 9px;
  }

  .schedule-row-videos .video-card-schedule-btn {
    font-size: 10px;
    min-height: 30px;
    padding: 6px 7px;
  }

  .schedule-row-videos .video-card-download-btn,
  .schedule-row-videos .video-card-delete-btn,
  .schedule-row-videos .video-card-editor-btn {
    font-size: 10px;
    min-height: 30px;
    padding: 6px 7px;
  }

  .scroll-arrow-btn {
    display: none;
  }

  .collection-row-scroll-wrap {
    display: block;
    margin-bottom: 0;
  }

  .collections-row-scroll {
    overflow-x: auto;
    overflow-y: visible;
    padding-top: 4px;
    padding-bottom: 4px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
  }

  .collection-item {
    flex-basis: 280px;
    min-width: 280px;
  }

  .collection-content {
    padding: 12px 14px;
  }

  .collection-name {
    font-size: 15px;
  }
}

/* .videos-strip combina con .collections-row-scroll en la fila de clips */

/* Filtros de colecciones (solo panel admin) */
.schedule-collections-filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  padding: 0;
  width: 100%;
}

.schedule-period-filter {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  align-self: flex-start;
}

.schedule-period-filter-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-right: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #a78bfa;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.schedule-period-filter-label i {
  font-size: 12px;
  opacity: 0.9;
}

.schedule-period-pill {
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  border-radius: 999px;
  border: 1px solid rgba(168, 85, 247, 0.3);
  background: rgba(15, 23, 42, 0.62);
  color: #cbd5e1;
  cursor: pointer;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.schedule-period-pill:hover:not(:disabled) {
  border-color: rgba(192, 132, 252, 0.5);
  color: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(76, 29, 149, 0.28);
}

.schedule-period-pill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.schedule-period-pill.is-active {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.58) 0%, rgba(124, 58, 237, 0.38) 100%);
  color: #faf5ff;
  border-color: rgba(192, 132, 252, 0.65);
  box-shadow:
    0 2px 14px rgba(124, 58, 237, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.schedule-filters-admin-panel {
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(168, 85, 247, 0.22);
  background:
    linear-gradient(160deg, rgba(76, 29, 149, 0.12) 0%, rgba(15, 23, 42, 0.62) 55%, rgba(15, 23, 42, 0.48) 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 32px rgba(0, 0, 0, 0.24);
  overflow: hidden;
}

.schedule-filters-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.14);
  background: rgba(15, 23, 42, 0.35);
}

.schedule-filters-toolbar-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.schedule-filters-toolbar-icon {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 13px;
  color: #e9d5ff;
  background: rgba(168, 85, 247, 0.2);
  border: 1px solid rgba(168, 85, 247, 0.32);
}

.schedule-filters-toolbar-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ddd6fe;
}

.schedule-filters-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 16px;
}

.schedule-filters-row {
  display: flex;
  align-items: stretch;
  gap: 12px;
}

.schedule-filters-row--admin {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  background: linear-gradient(135deg, rgba(76, 29, 149, 0.14) 0%, rgba(15, 23, 42, 0.38) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.schedule-filters-row--status {
  padding-top: 0;
  margin-top: -2px;
}

.schedule-filters-admin-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 auto;
  padding-top: 2px;
}

.schedule-filters-admin-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #e9d5ff;
  background: rgba(168, 85, 247, 0.22);
  border: 1px solid rgba(168, 85, 247, 0.35);
}

.schedule-filters-admin-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(220px, 0.8fr);
  gap: 14px 18px;
  flex: 1 1 auto;
  min-width: 0;
}

.schedule-filters-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.schedule-filters-block--status {
  flex: 1 1 auto;
  min-width: 0;
}

.schedule-filters-block-label,
.schedule-clips-filters-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #c4b5fd;
  text-transform: uppercase;
  letter-spacing: 0.07em;
}

.schedule-filters-block-icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 11px;
  color: #e9d5ff;
  background: rgba(168, 85, 247, 0.18);
  border: 1px solid rgba(168, 85, 247, 0.28);
}

.schedule-filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.schedule-filters-chips--status {
  gap: 5px;
}

.schedule-filter-chip {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.65);
  color: var(--clipr-text-secondary);
  cursor: pointer;
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.schedule-filter-chip:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: var(--clipr-text-primary);
  border-color: rgba(148, 163, 184, 0.35);
  transform: translateY(-1px);
}

.schedule-filter-chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.schedule-filter-chip--active {
  background: var(--clipr-purple-overlay-focus);
  color: var(--clipr-purple-light);
  border-color: var(--clipr-purple-overlay-border);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12);
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="all"] {
  background: rgba(148, 163, 184, 0.16);
  color: #cbd5e1;
  border-color: rgba(148, 163, 184, 0.42);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="1_queued"],
.schedule-filter-chip--status.schedule-filter-chip--active[data-status="2_running"] {
  background: rgba(234, 179, 8, 0.18);
  color: #fde68a;
  border-color: rgba(234, 179, 8, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="5_rendering"] {
  background: rgba(34, 211, 238, 0.18);
  color: #a5f3fc;
  border-color: rgba(34, 211, 238, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="3_downloaded"] {
  background: rgba(56, 189, 248, 0.18);
  color: #bae6fd;
  border-color: rgba(56, 189, 248, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="4_decided"] {
  background: var(--clipr-purple-overlay-focus);
  color: var(--clipr-purple-light);
  border-color: var(--clipr-purple-overlay-border);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="6_rendered"],
.schedule-filter-chip--status.schedule-filter-chip--active[data-status="8_published"] {
  background: rgba(34, 197, 94, 0.18);
  color: #86efac;
  border-color: rgba(34, 197, 94, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="7_scheduling"] {
  background: rgba(129, 140, 248, 0.2);
  color: #c7d2fe;
  border-color: rgba(129, 140, 248, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="expired"] {
  background: rgba(251, 146, 60, 0.18);
  color: #fdba74;
  border-color: rgba(251, 146, 60, 0.45);
  box-shadow: none;
}

.schedule-filter-chip--status.schedule-filter-chip--active[data-status="failed"],
.schedule-filter-chip--status.schedule-filter-chip--active[data-status="insufficient_tokens"] {
  background: rgba(248, 113, 113, 0.18);
  color: #fecaca;
  border-color: rgba(248, 113, 113, 0.45);
  box-shadow: none;
}

.schedule-filters-search-wrap {
  position: relative;
  width: 100%;
  max-width: 360px;
}

.schedule-filters-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(196, 181, 253, 0.75);
  font-size: 13px;
  pointer-events: none;
}

.schedule-filters-search-input {
  width: 100%;
  padding: 9px 12px 9px 36px;
  font-size: 12px;
  line-height: 1.3;
  border-radius: 11px;
  border: 1px solid rgba(168, 85, 247, 0.22);
  background: rgba(15, 23, 42, 0.78);
  color: var(--clipr-text-primary);
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease;
}

.schedule-filters-search-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.schedule-filters-search-input:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.55);
  background: rgba(15, 23, 42, 0.9);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.16);
}

.owner-search-wrap {
  position: relative;
  max-width: 280px;
}

.owner-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--clipr-text-secondary);
  font-size: 14px;
  pointer-events: none;
}

.owner-search-input {
  padding-left: 36px;
  background-color: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.owner-search-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.owner-search-input:focus {
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 2px var(--clipr-purple-glow);
}

@media (max-width: 991px) {
  .schedule-filters-admin-grid {
    grid-template-columns: 1fr;
  }

  .schedule-filters-search-wrap {
    max-width: none;
  }
}

@media (max-width: 767px) {
  .schedule-filters-admin-panel {
    border-radius: 12px;
  }

  .schedule-filters-body {
    padding: 12px;
  }

  .schedule-filters-toolbar {
    padding: 10px 12px;
  }

  .schedule-period-filter {
    width: 100%;
  }

  .schedule-period-pill {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;
    padding-left: 10px;
    padding-right: 10px;
  }

  .schedule-row-header--streams {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .schedule-row-header--streams .schedule-row-header-right {
    align-self: flex-start;
  }

  .schedule-row-icon {
    width: 38px;
    height: 38px;
    font-size: 16px;
  }

  .schedule-filters-row--admin {
    flex-direction: column;
    gap: 10px;
  }

  .schedule-filters-admin-rail {
    flex-direction: row;
  }
}

/* Tarjetas de clip alineadas con MakeClips (segment-item-compact + video-thumbnail-wrapper) */
.video-card {
  flex: 0 0 auto;
  align-self: flex-start;
  min-width: 200px;
  max-width: 260px;
  padding: 14px 16px;
  box-sizing: border-box;
  overflow: hidden;
  background: var(--clipr-bg-content);
  border: 1px solid rgba(168, 85, 247, 0.35);
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2),
    0 0 24px var(--clipr-purple-glow),
    0 0 48px rgba(168, 85, 247, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: var(--clipr-transition);
  position: relative;
}

.video-card:hover {
  transform: translateY(-2px);
  border-color: rgba(168, 85, 247, 0.55);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.35),
    0 0 28px var(--clipr-purple-glow),
    0 0 52px rgba(168, 85, 247, 0.16),
    0 8px 24px rgba(0, 0, 0, 0.35);
}

.video-card-selected {
  border: 1.5px solid var(--clipr-purple-light);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.45),
    0 0 32px var(--clipr-purple-glow),
    0 0 56px rgba(168, 85, 247, 0.2),
    0 8px 28px rgba(0, 0, 0, 0.4);
}

.video-card-thumbnail {
  width: 100%;
  height: auto;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  overflow: hidden;
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.35);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2),
    0 0 24px var(--clipr-purple-glow),
    0 0 48px rgba(168, 85, 247, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.3);
  margin-bottom: 10px;
  position: relative;
}

.video-card-thumbnail.video-card-thumbnail-rendering {
  background: #000;
  border-color: rgba(34, 211, 238, 0.45);
  box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.16),
    0 0 20px rgba(34, 211, 238, 0.14),
    0 4px 16px rgba(0, 0, 0, 0.45);
  animation: videoCardRenderingPulse 1.6s ease-in-out infinite;
}

.video-card-thumbnail.video-card-thumbnail-rendered {
  animation: none;
}

.video-card-thumbnail video,
.video-card-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.video-card-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--clipr-purple-light);
  font-size: 40px;
  opacity: 0.6;
  background: var(--clipr-bg-elevated);
  position: relative;
}

.video-card-placeholder i {
  position: static;
  font-size: inherit;
  opacity: inherit;
}

.video-card-image {
  width: 100%;
  height: 100%;
  position: relative;
  background: #000;
}

.video-card-playback {
  width: 100%;
  height: 100%;
  position: relative;
  background: #000;
}

.video-card-playback video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}

.video-card-playback--fullscreen {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

.video-card-playback--fullscreen video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.clip-workspace-player.video-card-playback {
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 100%;
  aspect-ratio: 9 / 16;
}

.video-card-fullscreen-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(196, 181, 253, 0.58);
  background: rgba(15, 23, 42, 0.76);
  color: #ede9fe;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.video-card-fullscreen-nav--prev {
  left: 14px;
}

.video-card-fullscreen-nav--next {
  right: 14px;
}

.video-card-fullscreen-nav i {
  font-size: 18px;
}

.video-card-fullscreen-nav:hover:not(:disabled) {
  border-color: rgba(221, 214, 254, 0.82);
  background: rgba(30, 41, 59, 0.88);
  transform: translateY(-50%) scale(1.04);
}

.video-card-fullscreen-nav:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.video-card-play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 1px solid var(--clipr-border-purple);
  background: rgba(0, 0, 0, 0.55);
  color: var(--clipr-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45), 0 0 20px var(--clipr-purple-overlay-medium);
  transition: var(--clipr-transition);
  padding: 0;
  margin: 0;
  line-height: 54px;
  text-align: center;
}

.video-card-play-btn:hover:not(:disabled) {
  transform: translate(-50%, -50%) scale(1.05);
  background: var(--clipr-purple-overlay-focus);
  border-color: var(--clipr-purple-light);
}

.video-card-play-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.video-card-play-btn i {
  display: inline-block;
  line-height: 1;
  margin: 0;
  padding: 0;
  vertical-align: middle;
  position: relative;
  top: 0;
  left: 0;
  transform: translate(0, 0);
}

.video-card.video-card-rendering {
  cursor: wait;
}

.video-card.video-card-rendering:hover {
  transform: none;
}

.video-card.video-card-rendering .video-card-actions {
  opacity: 0.65;
}

.video-card-rendering-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  background:
    radial-gradient(circle at 50% 42%, rgba(34, 211, 238, 0.2), transparent 46%),
    rgba(0, 0, 0, 0.86);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  pointer-events: auto;
  overflow: hidden;
}

.video-card-rendering-overlay::before {
  content: "";
  position: absolute;
  inset: -35%;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(34, 211, 238, 0.14) 48%,
    rgba(34, 211, 238, 0.26) 50%,
    rgba(34, 211, 238, 0.14) 52%,
    transparent 70%
  );
  animation: videoCardRenderingSweep 1.6s ease-in-out infinite;
  pointer-events: none;
}

.video-card-rendering-overlay .processing-indicator {
  width: 46px;
  height: 46px;
  position: relative;
  z-index: 1;
}

.video-card-rendering-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #22d3ee;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  position: relative;
  z-index: 1;
}

.video-card-rendering-progress {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 12px;
  z-index: 1;
  height: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.video-card-rendering-progress-bar {
  display: block;
  height: 100%;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent, #22d3ee, #06b6d4, transparent);
  animation: collectionProgressSlide 1.05s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.video-card-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.video-card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--clipr-text-primary);
  line-height: 1.35;
  min-height: calc(1.35em * 2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-card-channel {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--clipr-purple-light);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-card-status {
  font-size: 11px;
}

.video-card-status .badge {
  font-weight: 600;
  border-radius: var(--clipr-radius-sm);
}

.video-card-status .badge-status-ready-to-post {
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(52, 211, 153, 0.5);
  color: #6ee7b7;
}

.video-card-status .badge-status-scheduled {
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(96, 165, 250, 0.5);
  color: #93c5fd;
}

.video-card-status .badge-status-posted {
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid rgba(34, 197, 94, 0.45);
  color: #4ade80;
}

.video-card-status .badge-status-rendering {
  background: rgba(34, 211, 238, 0.2);
  border: 1px solid rgba(34, 211, 238, 0.5);
  color: #67e8f9;
  animation: badgeRenderingPulse 2s ease-in-out infinite;
}

@keyframes badgeRenderingPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(34, 211, 238, 0);
  }
  50% {
    box-shadow: 0 0 10px rgba(34, 211, 238, 0.35);
  }
}

.video-card-status .badge-status-post-failed {
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(248, 113, 113, 0.52);
  color: #fca5a5;
}

.video-card-status .badge-status-default {
  background: rgba(148, 163, 184, 0.18);
  border: 1px solid rgba(148, 163, 184, 0.36);
  color: #cbd5e1;
}

.video-card-status .badge-status-demo {
  background: rgba(20, 184, 166, 0.22);
  border: 1px solid rgba(45, 212, 191, 0.55);
  color: #5eead4;
}

.video-card-extra {
  font-size: 12px;
  color: var(--clipr-text-secondary);
}

.video-card-extra small {
  font-size: inherit;
  color: inherit;
}

.video-card-actions {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.video-card-actions--minimal {
  gap: 8px;
  padding-top: 8px;
}

.video-card-actions-primary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.video-card-actions-primary .video-card-icon-btn {
  width: 100%;
  min-width: 0;
  height: 40px;
  border-radius: 12px;
}

.video-card-actions-premium {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
  min-width: 0;
}

.video-card-actions-premium--minimal {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.video-card-actions-premium--minimal .video-card-icon-btn {
  width: 100%;
  min-width: 0;
  height: 40px;
  border-radius: 12px;
}

.video-card-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(168, 85, 247, 0.42);
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.92), rgba(15, 23, 42, 0.92));
  color: #e9d5ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.06) inset, 0 8px 14px rgba(15, 23, 42, 0.35);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.video-card-icon-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(216, 180, 254, 0.78);
  background: linear-gradient(180deg, rgba(51, 65, 85, 0.96), rgba(30, 41, 59, 0.94));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.28);
}

.video-card-icon-btn:active:not(:disabled) {
  transform: translateY(0);
}

.video-card-icon-btn:focus-visible {
  outline: 2px solid rgba(196, 181, 253, 0.95);
  outline-offset: 2px;
}

.video-card-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.video-card-icon-btn i {
  font-size: 16px;
}

.video-card-icon-btn-schedule,
.video-card-icon-btn-editor {
  position: relative;
  overflow: visible;
  border-color: transparent;
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.92), rgba(15, 23, 42, 0.92));
  color: #e9d5ff;
}

.video-card-icon-btn-schedule::before,
.video-card-icon-btn-editor::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, #22d3ee 0%, #8b5cf6 55%, #a855f7 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.video-card-pro-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.58);
  background: linear-gradient(135deg, #22d3ee 0%, #8b5cf6 55%, #a855f7 100%);
  box-shadow: 0 6px 14px rgba(2, 6, 23, 0.55);
  color: #f8fafc;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  line-height: 1.15;
  pointer-events: none;
  z-index: 2;
}

.video-card-icon-btn-fullscreen,
.video-card-icon-btn-mute,
.video-card-icon-btn-download {
  border-color: rgba(167, 139, 250, 0.55);
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.92), rgba(15, 23, 42, 0.92));
  color: #e9d5ff;
}

.video-card-icon-btn-fullscreen:hover:not(:disabled),
.video-card-icon-btn-mute:hover:not(:disabled),
.video-card-icon-btn-download:hover:not(:disabled) {
  border-color: rgba(216, 180, 254, 0.78);
  background: linear-gradient(180deg, rgba(51, 65, 85, 0.96), rgba(30, 41, 59, 0.94));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.34);
}

.video-card-mute-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.video-card-mute-icon-svg {
  display: block;
  width: 20px;
  height: 20px;
  overflow: visible;
}

.video-card-icon-btn-schedule:hover:not(:disabled),
.video-card-icon-btn-editor:hover:not(:disabled) {
  border-color: transparent;
  background: linear-gradient(180deg, rgba(51, 65, 85, 0.96), rgba(30, 41, 59, 0.94));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.34);
}

.video-card-icon-btn-schedule.video-card-icon-btn-manage {
  background: linear-gradient(180deg, rgba(120, 53, 15, 0.96), rgba(92, 39, 11, 0.94));
  color: #fde68a;
  box-shadow: 0 10px 18px rgba(217, 119, 6, 0.34);
}

.video-card-icon-btn-schedule.video-card-icon-btn-manage::before {
  background: linear-gradient(135deg, #fde047 0%, #f59e0b 100%);
}

.video-card-icon-btn-schedule.video-card-icon-btn-manage:hover:not(:disabled) {
  background: linear-gradient(180deg, rgba(146, 64, 14, 0.97), rgba(120, 53, 15, 0.95));
  box-shadow: 0 12px 20px rgba(217, 119, 6, 0.42);
}

.video-card-icon-btn-schedule.video-card-icon-btn-posted {
  background: linear-gradient(180deg, rgba(21, 128, 61, 0.9), rgba(22, 101, 52, 0.92));
  color: #bbf7d0;
  box-shadow: 0 10px 18px rgba(22, 163, 74, 0.32);
}

.video-card-icon-btn-schedule.video-card-icon-btn-posted::before {
  background: linear-gradient(135deg, #4ade80 0%, #16a34a 100%);
}

.video-card-icon-btn-schedule.video-card-icon-btn-posted:hover:not(:disabled) {
  background: linear-gradient(180deg, rgba(22, 163, 74, 0.92), rgba(21, 128, 61, 0.94));
  box-shadow: 0 12px 20px rgba(22, 163, 74, 0.44);
}

.video-card-icon-btn-editor-pro {
  position: relative;
}

.video-card-icon-btn-delete {
  border-color: rgba(248, 113, 113, 0.45);
  background: linear-gradient(180deg, rgba(70, 22, 22, 0.9), rgba(56, 18, 18, 0.92));
  color: #fca5a5;
}

.video-card-icon-btn-delete:hover:not(:disabled) {
  border-color: rgba(252, 165, 165, 0.76);
  background: linear-gradient(180deg, rgba(94, 31, 31, 0.94), rgba(74, 24, 24, 0.92));
  box-shadow: 0 10px 18px rgba(185, 28, 28, 0.3);
}

.video-card-schedule-btn {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: var(--clipr-radius-md);
  background: linear-gradient(135deg, rgba(76, 29, 149, 0.44), rgba(67, 56, 202, 0.36));
  color: #e9d5ff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.35);
  letter-spacing: 0.02em;
  min-height: 38px;
  position: relative;
}

.video-card-schedule-btn::before,
.video-card-editor-btn::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, #22d3ee 0%, #8b5cf6 55%, #a855f7 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.video-card-schedule-btn i {
  font-size: 15px;
  opacity: 0.95;
}

.video-card-schedule-btn:hover:not(:disabled) {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(109, 40, 217, 0.56), rgba(79, 70, 229, 0.5));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.34);
  transform: translateY(-1px);
}

.video-card-schedule-btn:active {
  transform: translateY(0);
}

.video-card-schedule-btn:focus-visible,
.video-card-download-btn:focus-visible,
.video-card-delete-btn:focus-visible,
.video-card-editor-btn:focus-visible {
  outline: 2px solid rgba(196, 181, 253, 0.95);
  outline-offset: 2px;
}

.video-card-schedule-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.video-card-manage-btn {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(161, 98, 7, 0.54), rgba(180, 83, 9, 0.45));
  color: #fef3c7;
  box-shadow: 0 8px 16px rgba(217, 119, 6, 0.3);
}

.video-card-manage-btn::before {
  background: linear-gradient(135deg, #fde047 0%, #f59e0b 100%);
}

.video-card-manage-btn:hover:not(:disabled) {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(180, 83, 9, 0.66), rgba(217, 119, 6, 0.56));
  box-shadow: 0 12px 20px rgba(217, 119, 6, 0.38);
}

.video-card-posted-btn {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(21, 128, 61, 0.58), rgba(22, 101, 52, 0.48));
  color: #dcfce7;
  box-shadow: 0 8px 16px rgba(22, 163, 74, 0.3);
}

.video-card-posted-btn::before {
  background: linear-gradient(135deg, #4ade80 0%, #16a34a 100%);
}

.video-card-posted-btn:hover:not(:disabled) {
  border-color: transparent;
  background: linear-gradient(135deg, rgba(22, 163, 74, 0.68), rgba(21, 128, 61, 0.58));
  box-shadow: 0 12px 20px rgba(22, 163, 74, 0.4);
}

.video-card-download-btn,
.video-card-delete-btn,
.video-card-editor-btn {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-radius: var(--clipr-radius-md);
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  border: 1px solid transparent;
  min-height: 34px;
}

.video-card-download-btn span,
.video-card-delete-btn span,
.video-card-editor-btn span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-card-download-btn i,
.video-card-delete-btn i,
.video-card-editor-btn i {
  font-size: 14px;
  opacity: 0.95;
  flex-shrink: 0;
}

.video-card-editor-btn {
  position: relative;
  border-color: transparent;
  background: linear-gradient(135deg, rgba(76, 29, 149, 0.44), rgba(67, 56, 202, 0.36));
  color: #e9d5ff;
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.35);
}

.video-card-editor-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: transparent;
  background: linear-gradient(135deg, rgba(109, 40, 217, 0.56), rgba(79, 70, 229, 0.5));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.34);
}

.video-card-editor-btn-pro {
  position: relative;
}

.video-card-download-btn {
  border-color: rgba(167, 139, 250, 0.46);
  background: linear-gradient(135deg, rgba(76, 29, 149, 0.44), rgba(67, 56, 202, 0.36));
  color: #e9d5ff;
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.35);
}

.video-card-download-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(216, 180, 254, 0.78);
  background: linear-gradient(135deg, rgba(109, 40, 217, 0.56), rgba(79, 70, 229, 0.5));
  box-shadow: 0 10px 18px rgba(109, 40, 217, 0.34);
}

.video-card-delete-btn {
  border-color: rgba(252, 165, 165, 0.4);
  background: linear-gradient(135deg, rgba(127, 29, 29, 0.48), rgba(153, 27, 27, 0.36));
  color: #fecaca;
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.35);
}

.video-card-delete-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(252, 165, 165, 0.72);
  background: linear-gradient(135deg, rgba(185, 28, 28, 0.6), rgba(153, 27, 27, 0.48));
  box-shadow: 0 10px 18px rgba(185, 28, 28, 0.32);
}

.video-card-download-btn:disabled,
.video-card-delete-btn:disabled,
.video-card-editor-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.video-card-pro-badge--inline {
  position: static;
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 8px;
  line-height: 1.1;
  transform: translateY(-1px);
}

.schedule-editor-page {
  width: min(1320px, 100%);
  margin: 0 auto 18px;
}

.schedule-editor-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: visible;
  background: transparent;
  border: none;
  box-shadow: none;
}

.schedule-editor-page .schedule-editor-shell,
.schedule-editor-page .schedule-editor-shell.confirm-modal--clip-time-editor {
  max-height: none !important;
  height: auto;
}

.schedule-editor-page .schedule-editor-shell .schedule-editor-header {
  border-bottom: none;
  background: transparent;
  padding-bottom: 4px;
}

.schedule-editor-page .schedule-editor-shell .schedule-editor-footer {
  border-top: none;
  background: transparent;
  padding-top: 8px;
}

.schedule-editor-page .schedule-editor-body,
.schedule-editor-page .schedule-editor-shell .clip-time-editor-modal-body {
  flex: none;
  width: 100%;
  max-width: 100%;
  max-height: none !important;
  overflow: visible !important;
}

.schedule-editor-page .schedule-editor-shell .confirm-modal-body {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex: none;
  max-height: none !important;
  overflow: visible !important;
}

.schedule-editor-page .clip-time-editor-screen {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.schedule-editor-header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
}

.schedule-editor-header-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 170px;
}

.schedule-editor-header-link {
  text-decoration: none !important;
}

.confirm-modal--clip-time-editor {
  width: min(860px, 96vw);
  max-height: calc(100dvh - 40px);
}

.confirm-modal--clip-time-editor .confirm-modal-header {
  padding: 20px 24px 14px;
}

.confirm-modal--clip-time-editor .confirm-modal-subtitle {
  max-width: min(620px, calc(100% - 56px));
  margin: 4px auto 0;
  line-height: 1.35;
}

.confirm-modal--clip-time-editor .confirm-modal-body {
  padding: 18px 24px;
}

.confirm-modal--clip-time-editor .confirm-modal-footer {
  gap: 10px;
  padding: 14px 24px 18px;
}

.confirm-modal--clip-time-editor .confirm-modal-btn {
  min-width: 150px;
}

.clip-time-editor-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(100dvh - 190px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.clip-time-editor-screen {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* Tipo de video: centrado, encima de los previews */
.clip-time-editor-screen .make-flow-subtitles-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding: 0 0 10px;
}

.clip-time-editor-screen .make-flow-subtitles-pill {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  width: min(100%, 300px);
  padding: 13px 20px 11px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(165deg, rgba(88, 28, 135, 0.14) 0%, rgba(15, 23, 42, 0.62) 48%, rgba(2, 6, 23, 0.72) 100%);
  box-shadow:
    0 10px 28px rgba(2, 6, 23, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  cursor: pointer;
  user-select: none;
  transition:
    border-color 0.24s ease,
    box-shadow 0.24s ease,
    background 0.24s ease,
    transform 0.24s ease;
}

.clip-time-editor-screen .make-flow-subtitles-pill:hover:not(:has(input:disabled)) {
  transform: translateY(-1px);
  border-color: rgba(196, 181, 253, 0.26);
}

.clip-time-editor-screen .make-flow-subtitles-pill--inactive {
  border-color: rgba(100, 116, 139, 0.18);
}

.clip-time-editor-screen .make-flow-subtitles-pill--active {
  border-color: rgba(34, 211, 238, 0.36);
  background:
    linear-gradient(165deg, rgba(34, 211, 238, 0.14) 0%, rgba(15, 23, 42, 0.68) 52%, rgba(2, 6, 23, 0.78) 100%);
  box-shadow:
    0 12px 30px rgba(34, 211, 238, 0.13),
    0 0 0 1px rgba(34, 211, 238, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

.clip-time-editor-screen .make-flow-subtitles-pill-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
}

.clip-time-editor-screen .make-flow-subtitles-pill-label {
  color: rgba(226, 232, 240, 0.92);
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
}

.clip-time-editor-screen .make-flow-subtitles-pill--inactive .make-flow-subtitles-pill-label {
  color: rgba(148, 163, 184, 0.88);
}

.clip-time-editor-screen .make-flow-subtitles-pill--active .make-flow-subtitles-pill-label {
  color: #f8fafc;
}

.clip-time-editor-screen .make-flow-subtitles-pill-switch {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.clip-time-editor-screen .make-flow-subtitles-pill-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.clip-time-editor-screen .make-flow-subtitles-pill-track {
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: rgba(71, 85, 105, 0.55);
  box-shadow: inset 0 2px 5px rgba(2, 6, 23, 0.35);
  transition: background 0.24s ease, box-shadow 0.24s ease;
}

.clip-time-editor-screen .make-flow-subtitles-pill--active .make-flow-subtitles-pill-track {
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.92), rgba(14, 165, 233, 0.88));
  box-shadow:
    0 0 14px rgba(34, 211, 238, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.clip-time-editor-screen .make-flow-subtitles-pill-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 7px rgba(2, 6, 23, 0.38);
  transition: transform 0.26s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.clip-time-editor-screen .make-flow-subtitles-pill-switch input:checked + .make-flow-subtitles-pill-track .make-flow-subtitles-pill-thumb {
  transform: translateX(20px);
}

.clip-time-editor-screen .make-flow-subtitles-pill-status {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.clip-time-editor-screen .make-flow-subtitles-pill-status.is-off {
  color: rgba(148, 163, 184, 0.62);
}

.clip-time-editor-screen .make-flow-subtitles-pill-status.is-on {
  color: #5eead4;
}

.clip-transcript-open-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 11px 17px;
  border: 1px solid rgba(34, 211, 238, 0.28);
  border-radius: 14px;
  color: #cffafe;
  background: rgba(8, 47, 73, 0.58);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.clip-transcript-open-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(34, 211, 238, 0.58);
  background: rgba(14, 116, 144, 0.42);
}

.clip-transcript-open-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.clip-transcript-modal-overlay {
  z-index: 1080;
}

.clip-transcript-modal {
  width: min(760px, calc(100vw - 28px));
  max-height: min(88dvh, 860px);
}

.clip-transcript-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.clip-transcript-warning {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 13px 14px;
  border: 1px solid rgba(251, 191, 36, 0.28);
  border-radius: 12px;
  color: #fde68a;
  background: rgba(120, 53, 15, 0.18);
}

.clip-transcript-warning i {
  flex: 0 0 auto;
  margin-top: 2px;
}

.clip-transcript-warning p {
  margin: 4px 0 0;
  color: rgba(254, 243, 199, 0.84);
  font-size: 0.8rem;
  line-height: 1.45;
}

.clip-transcript-examples {
  color: #fef3c7;
  font-weight: 700;
}

.clip-transcript-blocks {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.clip-transcript-block {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0;
}

.clip-transcript-block-time {
  color: #67e8f9;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.clip-transcript-textarea {
  min-height: 78px;
  resize: vertical;
  color: #f8fafc;
  border-color: rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.72);
}

.clip-transcript-textarea:focus {
  border-color: rgba(34, 211, 238, 0.62);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.1);
}

.clip-transcript-read-only-note,
.clip-transcript-empty {
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  color: rgba(203, 213, 225, 0.86);
  background: rgba(15, 23, 42, 0.54);
  text-align: center;
}

.clip-transcript-retry-btn {
  margin-left: auto;
  border: 0;
  color: #cffafe;
  background: transparent;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.editor-format {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.editor-format-label {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--clipr-text-secondary);
}

.editor-format .clip-time-editor-video-type-picker {
  width: 100%;
  max-width: min(720px, 100%);
}

.editor-format .clip-time-editor-video-type-help {
  max-width: 620px;
  margin: 2px auto 0;
  text-align: center;
}

.schedule-editor-page .editor-stage {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(196, 181, 253, 0.2);
  background:
    radial-gradient(circle at 100% 0%, rgba(34, 211, 238, 0.07), transparent 42%),
    radial-gradient(circle at 0% 100%, rgba(168, 85, 247, 0.1), transparent 48%),
    rgba(9, 9, 11, 0.78);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 14px 28px rgba(15, 23, 42, 0.34);
}

.schedule-editor-page .editor-stage .clip-time-editor-video-controls,
.schedule-editor-page .editor-stage .editor-trim,
.schedule-editor-page .editor-stage .clip-time-editor-timeline {
  border: none;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.schedule-editor-page .editor-stage .clip-time-editor-video-controls,
.schedule-editor-page .editor-stage .editor-trim {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0;
  margin: 0;
}

.schedule-editor-page .editor-stage .editor-trim {
  gap: 16px;
}

.schedule-editor-page .editor-stage .clip-time-editor-video-controls {
  padding-top: 2px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.schedule-editor-page .editor-stage .editor-trim {
  padding-top: 2px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.schedule-editor-page .editor-stage-controls-host {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* Fila de previews: horizontal (grande) + vertical (siempre visible) */
.editor-preview-row {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.75fr);
  gap: 20px;
  align-items: start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

@media (min-width: 992px) {
  .schedule-editor-page,
  .schedule-editor-page .schedule-editor-shell,
  .schedule-editor-page .schedule-editor-body,
  .schedule-editor-page .clip-time-editor-screen {
    background: transparent !important;
  }

  .editor-preview-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 32px;
    width: 100%;
    max-width: 100%;
  }

  .editor-preview-main {
    flex: 1 1 560px;
    max-width: min(600px, 100%);
  }

  .editor-preview-vertical {
    flex: 0 1 200px;
    max-width: 100%;
  }
}

.editor-preview-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.editor-preview-vertical {
  min-width: 0;
  display: flex;
  justify-content: center;
}

.editor-preview-vertical-stack {
  width: 100%;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.editor-preview-caption {
  margin: 0;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.9);
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.editor-preview-caption--clip {
  align-self: center;
}

.editor-preview-disclaimer {
  margin: 0;
  padding: 0 4px;
  font-size: 10px;
  line-height: 1.35;
  font-weight: 500;
  text-align: center;
  color: rgba(148, 163, 184, 0.78);
}

.editor-preview-vertical .clip-time-editor-context-preview-shell {
  margin-top: 0;
  width: 100%;
}

.editor-preview-vertical .clip-time-editor-context-stage {
  width: 100%;
  max-width: 200px;
}

/* Vista previa vertical genérica (tipos distintos a IRL) */
.editor-vertical-generic {
  position: relative;
  width: 100%;
  max-width: 200px;
  aspect-ratio: 9 / 16;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #020617;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.55);
}

.editor-vertical-generic .clip-time-editor-preview-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

/* Controles de playback + recorte */
.editor-trim {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 18px 20px 20px;
  border-radius: 16px;
  border: 1px solid rgba(196, 181, 253, 0.2);
  background:
    radial-gradient(circle at 100% 0%, rgba(34, 211, 238, 0.07), transparent 42%),
    radial-gradient(circle at 0% 100%, rgba(168, 85, 247, 0.1), transparent 48%),
    rgba(9, 9, 11, 0.78);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 14px 28px rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Formulario de contexto IRL */
.editor-context-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px 20px 20px;
  border-radius: 16px;
  border: 1px solid rgba(196, 181, 253, 0.22);
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.08), transparent 46%),
    radial-gradient(circle at bottom left, rgba(168, 85, 247, 0.12), transparent 50%),
    rgba(9, 9, 11, 0.82);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 14px 28px rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.clip-time-editor-error {
  width: 100%;
}

.clip-time-editor-video-type-picker {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

@media (min-width: 768px) {
  .editor-format .clip-time-editor-video-type-picker {
    max-width: 720px;
  }

  .clip-time-editor-video-type-picker {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.clip-time-editor-video-type-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(15, 23, 42, 0.72);
  color: var(--clipr-text-primary);
  text-align: left;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.clip-time-editor-video-type-option:hover:not(:disabled) {
  border-color: rgba(168, 85, 247, 0.45);
  background: rgba(30, 41, 59, 0.88);
}

.clip-time-editor-video-type-option.is-active {
  border-color: rgba(34, 211, 238, 0.48);
  background: rgba(34, 211, 238, 0.08);
  box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.14);
}

.clip-time-editor-video-type-option:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.clip-time-editor-video-type-option-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #c4b5fd;
  border: 1px solid rgba(196, 181, 253, 0.24);
  background: rgba(168, 85, 247, 0.12);
}

.clip-time-editor-video-type-option.is-active .clip-time-editor-video-type-option-icon {
  color: #67e8f9;
  border-color: rgba(34, 211, 238, 0.34);
  background: rgba(34, 211, 238, 0.14);
}

.clip-time-editor-video-type-option-label {
  min-width: 0;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.25;
}

.clip-time-editor-video-type-guide {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.clip-time-editor-video-type-guide-title {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.clip-time-editor-video-type-guide-description {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 12px;
  line-height: 1.45;
}

.clip-time-editor-video-type-help {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--clipr-text-secondary);
}

.clip-time-editor-context-help {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(226, 232, 240, 0.72);
}

.clip-time-editor-context-help + .clip-time-editor-context-help {
  margin-top: 10px;
}

.clip-time-editor-context-toggle {
  margin: 14px 0 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.55);
  color: var(--clipr-text-primary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.clip-time-editor-context-toggle:hover {
  border-color: rgba(196, 181, 253, 0.28);
  background: rgba(12, 10, 20, 0.72);
}

.clip-time-editor-context-toggle input {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  border-radius: 6px;
  border: 1.5px solid rgba(148, 163, 184, 0.36);
  background: rgba(15, 23, 42, 0.92);
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.clip-time-editor-context-toggle input:hover:not(:disabled) {
  border-color: rgba(196, 181, 253, 0.52);
  background: rgba(30, 27, 46, 0.95);
}

.clip-time-editor-context-toggle input:checked {
  border-color: transparent;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #6d28d9 100%);
  box-shadow:
    0 4px 12px rgba(124, 58, 237, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.clip-time-editor-context-toggle input:checked::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.clip-time-editor-context-toggle input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.28);
}

.clip-time-editor-context-text-group {
  margin-top: 14px;
}

.clip-time-editor-context-text-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c4b5fd;
}

.clip-time-editor-context-textarea {
  width: 100%;
  min-height: 80px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(196, 181, 253, 0.28);
  background: rgba(15, 23, 42, 0.78);
  color: var(--clipr-text-primary);
  resize: vertical;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.clip-time-editor-context-textarea::placeholder {
  color: var(--clipr-text-tertiary);
}

.clip-time-editor-context-textarea:focus {
  border-color: rgba(196, 181, 253, 0.42);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 3px rgba(168, 85, 247, 0.14);
  background: rgba(15, 23, 42, 0.92);
  color: var(--clipr-text-primary);
  outline: none;
}

.clip-time-editor-context-emoji-section {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(9, 9, 11, 0.62);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.clip-time-editor-context-emoji-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 12px;
}

.clip-time-editor-context-emoji-header span {
  font-size: 12px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.clip-time-editor-context-emoji-header small {
  font-size: 11px;
  line-height: 1.4;
  color: rgba(226, 232, 240, 0.62);
}

.clip-time-editor-context-emoji-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 8px;
}

.clip-time-editor-context-emoji-btn {
  min-height: 40px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.55);
  color: var(--clipr-text-primary);
  font-family: "DejaVu Sans", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.clip-time-editor-context-emoji-btn:hover:not(:disabled) {
  border-color: rgba(34, 211, 238, 0.55);
  background: rgba(34, 211, 238, 0.1);
  transform: translateY(-2px);
  box-shadow:
    0 0 0 1px rgba(34, 211, 238, 0.2),
    0 10px 20px rgba(34, 211, 238, 0.12);
}

.clip-time-editor-context-emoji-btn:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.clip-time-editor-context-locked {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.48);
  color: var(--clipr-text-secondary);
  font-size: 12px;
}

.clip-time-editor-context-preview-shell {
  margin-top: 12px;
  width: 100%;
  display: flex;
  justify-content: center;
}

.clip-time-editor-context-stage {
  position: relative;
  width: min(100%, 200px);
  aspect-ratio: 9 / 16;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #020617;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.55);
  contain: layout paint;
  isolation: isolate;
}

.clip-time-editor-preview-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
}

.clip-time-editor-context-overlay {
  position: absolute;
  left: 50%;
  top: 6.25%;
  transform: translate(-50%, 0);
  max-width: min(
    calc(100% - var(--clip-context-box-border, 4px) - var(--clip-context-box-border, 4px)),
    var(--clip-context-max-width, 100%)
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  background: transparent;
  border: 0;
  padding: 0;
  box-shadow: none;
  color: #0f172a;
  font-family: "DejaVu Sans", sans-serif;
  font-size: var(--clip-context-font-size, 10px);
  font-weight: 700;
  line-height: 1;
  text-align: center;
  cursor: move;
  touch-action: none;
  user-select: none;
  z-index: 2;
}

.clip-time-editor-context-overlay-line {
  display: block;
  max-width: 100%;
  padding: var(--clip-context-box-border, 4px);
  border-radius: 0;
  background: #ffffff;
  color: #000000;
  white-space: pre;
}

.clip-time-editor-context-overlay-line + .clip-time-editor-context-overlay-line {
  margin-top: calc(
    var(--clip-context-line-spacing, 2px)
    - var(--clip-context-box-border, 4px)
    - var(--clip-context-box-border, 4px)
  );
}

.clip-time-editor-context-overlay.is-disabled {
  cursor: not-allowed;
  opacity: 0.82;
}

.clip-time-editor-context-empty {
  margin: 10px 0 0;
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(226, 232, 240, 0.62);
  font-style: italic;
  border-radius: 10px;
  border: 1px dashed rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.35);
}

.clip-time-editor-endcard-panel {
  width: 100%;
  min-width: 0;
}

.clip-time-editor-endcard-panel :deep(.personalization-row) {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;
}

.clip-time-editor-endcard-panel :deep(.personalization-col) {
  flex: 0 0 100%;
  max-width: 100%;
  width: 100%;
  margin: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card) {
  border-radius: 16px !important;
  border: 1px solid rgba(196, 181, 253, 0.22) !important;
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.07), transparent 44%),
    radial-gradient(circle at bottom left, rgba(168, 85, 247, 0.1), transparent 48%),
    rgba(9, 9, 11, 0.82) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 14px 28px rgba(15, 23, 42, 0.32) !important;
  color: var(--clipr-text-primary) !important;
  overflow: hidden;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.clip-time-editor-endcard-panel :deep(.members-settings-card.h-100) {
  height: auto !important;
}

.clip-time-editor-endcard-panel :deep(.personalization-col + .personalization-col) {
  border-top: 0;
  padding-top: 0;
  margin-top: 0 !important;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card-header) {
  padding: 16px 18px 0;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card-body) {
  padding: 12px 18px 18px;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card-title) {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--clipr-text-primary) !important;
  background: linear-gradient(120deg, #f8fafc 0%, #ddd6fe 58%, #a5f3fc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent !important;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card-intro) {
  margin-top: 0.45rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: rgba(226, 232, 240, 0.68) !important;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card label),
.clip-time-editor-endcard-panel :deep(.members-settings-card .form-text),
.clip-time-editor-endcard-panel :deep(.members-settings-card .text-muted) {
  color: #c4b5fd !important;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .form-control),
.clip-time-editor-endcard-panel :deep(.members-settings-card .border-input),
.clip-time-editor-endcard-panel :deep(.members-settings-card .custom-logo-file-input) {
  min-height: 44px;
  border-radius: 14px !important;
  border: 1px solid rgba(196, 181, 253, 0.28) !important;
  background: rgba(15, 23, 42, 0.78) !important;
  color: var(--clipr-text-primary) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .form-control:focus),
.clip-time-editor-endcard-panel :deep(.members-settings-card .border-input:focus),
.clip-time-editor-endcard-panel :deep(.members-settings-card .custom-logo-file-input:focus) {
  border-color: rgba(196, 181, 253, 0.42) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 3px rgba(168, 85, 247, 0.14) !important;
  outline: none;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .btn-primary) {
  min-height: 40px;
  padding: 0 18px !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #06b6d4 100%) !important;
  box-shadow: 0 8px 18px rgba(124, 58, 237, 0.22) !important;
  font-weight: 700;
  transition: box-shadow 0.22s ease, transform 0.18s ease;
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .btn-primary:hover:not(:disabled)) {
  box-shadow:
    0 10px 24px rgba(124, 58, 237, 0.32),
    0 0 22px rgba(168, 85, 247, 0.14) !important;
  transform: translateY(-1px);
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .btn-primary:disabled) {
  opacity: 0.55;
}

.clip-time-editor-endcard-panel :deep(.endcard-switch-row .endcard-switch-track) {
  background: rgba(148, 163, 184, 0.28);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.22);
}

.clip-time-editor-endcard-panel :deep(.endcard-switch-row .endcard-switch-track.is-on) {
  background: linear-gradient(135deg, #a855f7 0%, #06b6d4 100%);
  box-shadow:
    0 4px 14px rgba(124, 58, 237, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.clip-time-editor-endcard-panel :deep(.endcard-switch-row .endcard-switch-thumb) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
}

.clip-time-editor-endcard-panel :deep(.endcard-preview-frame) {
  border: 1px solid rgba(196, 181, 253, 0.28);
  background:
    radial-gradient(circle at 50% 18%, rgba(34, 211, 238, 0.08), transparent 52%),
    #000;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 16px 36px rgba(15, 23, 42, 0.55),
    0 0 0 1px rgba(34, 211, 238, 0.08);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.clip-time-editor-endcard-panel :deep(.endcard-preview-frame:not(.is-disabled):hover) {
  border-color: rgba(34, 211, 238, 0.42);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 20px 40px rgba(15, 23, 42, 0.62),
    0 0 28px rgba(34, 211, 238, 0.12);
}

.clip-time-editor-endcard-panel :deep(.endcard-preview-logo-wrap) {
  border-color: rgba(196, 181, 253, 0.32);
  background: rgba(15, 23, 42, 0.88);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.clip-time-editor-endcard-panel :deep(.endcard-preview-handle) {
  color: #67e8f9;
  font-weight: 700;
  text-shadow: 0 0 18px rgba(34, 211, 238, 0.35);
}

.clip-time-editor-endcard-panel :deep(.endcard-preview-disabled-chip) {
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(15, 23, 42, 0.82);
  color: rgba(226, 232, 240, 0.78);
  backdrop-filter: blur(8px);
}

.clip-time-editor-endcard-panel :deep(.members-settings-card .row.align-items-end) {
  row-gap: 12px;
}

.clip-time-editor-endcard-panel :deep(.custom-logo-section) {
  gap: 14px;
}

.clip-time-editor-endcard-panel :deep(.custom-logo-preview-stage) {
  justify-content: center;
}

.clip-time-editor-endcard-panel :deep(.custom-logo-preview-stage--endcard) {
  justify-content: center;
}

.clip-time-editor-endcard-panel :deep(.custom-logo-file-button-row) {
  align-items: center;
  gap: 10px;
}

.clip-time-editor-endcard-panel :deep(.custom-logo-save-btn) {
  min-width: 138px;
}

@media (max-width: 767.98px) {
  .editor-preview-row {
    grid-template-columns: 1fr;
    gap: 16px;
    justify-items: center;
  }

  .editor-preview-main {
    width: 100%;
  }

  .editor-preview-vertical-stack,
  .editor-preview-vertical .clip-time-editor-context-stage,
  .editor-vertical-generic {
    max-width: 200px;
  }
}

@media (max-width: 767.98px) {
  .clip-time-editor-endcard-panel :deep(.custom-logo-file-button-row) {
    flex-wrap: wrap;
  }

  .clip-time-editor-endcard-panel :deep(.custom-logo-save-btn) {
    width: 100%;
    min-width: 0;
  }
}

.clip-time-editor-video-shell {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.82);
  display: flex;
  justify-content: center;
}

.clip-time-editor-video-frame {
  position: relative;
  max-width: 100%;
  max-height: 460px;
  display: inline-flex;
}

.clip-time-editor-preview-loader {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(180deg, rgba(2, 6, 23, 0.48) 0%, rgba(2, 6, 23, 0.62) 100%);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  pointer-events: none;
}

.clip-time-editor-preview-spinner {
  position: relative;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
}

.clip-time-editor-preview-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 3px solid transparent;
}

.clip-time-editor-preview-spinner-ring--outer {
  border-top-color: #22d3ee;
  border-right-color: rgba(34, 211, 238, 0.2);
  animation: clip-time-editor-control-spin 1s linear infinite;
}

.clip-time-editor-preview-spinner-ring--inner {
  inset: 9px;
  border-bottom-color: #a855f7;
  border-left-color: rgba(168, 85, 247, 0.22);
  animation: clip-time-editor-control-spin-reverse 1.35s linear infinite;
}

.clip-time-editor-preview-loader-label {
  margin: 0;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid rgba(34, 211, 238, 0.24);
  background: rgba(15, 23, 42, 0.62);
  color: #67e8f9;
  font-size: 0.63rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
}

.clip-time-editor-video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 460px;
  object-fit: contain;
  display: block;
  background: #0f172a;
}

@media (min-width: 768px) {
  .editor-preview-main .clip-time-editor-video-shell {
    width: 100%;
    max-width: 600px;
  }

  .clip-time-editor-video-frame {
    width: 100%;
    max-height: 360px;
  }

  .clip-time-editor-video {
    width: 100%;
    max-height: 360px;
  }
}

.clip-time-editor-layout-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}

.clip-time-editor-layout-box {
  position: absolute;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 4px;
  border: 2px solid rgba(250, 204, 21, 0.98);
  background: rgba(250, 204, 21, 0.1);
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.55),
    0 10px 24px rgba(15, 23, 42, 0.32);
  cursor: move;
  pointer-events: auto;
  touch-action: none;
  user-select: none;
}

.clip-time-editor-layout-box span {
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.82);
  color: #fde68a;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.clip-time-editor-layout-box--blue {
  border-color: rgba(59, 130, 246, 0.98);
  background: rgba(59, 130, 246, 0.14);
}

.clip-time-editor-layout-box--blue span {
  color: #bfdbfe;
}

.clip-time-editor-video-controls {
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.62);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.clip-time-editor-control-btn {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(196, 181, 253, 0.34);
  border-radius: 999px;
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(168, 85, 247, 0.22), transparent 55%),
    rgba(15, 23, 42, 0.72);
  color: var(--clipr-text-primary);
  min-height: 40px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 4px 14px rgba(2, 6, 23, 0.28);
  transition:
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease,
    transform 0.18s ease;
  white-space: nowrap;
}

.clip-time-editor-control-btn--loading {
  cursor: wait;
  box-shadow:
    0 8px 22px rgba(124, 58, 237, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  animation: clip-time-editor-control-btn-glow 1.6s ease-in-out infinite;
}

.clip-time-editor-control-btn--loading::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    transparent 0%,
    rgba(255, 255, 255, 0.16) 45%,
    transparent 70%
  );
  transform: translateX(-120%);
  animation: clip-time-editor-control-btn-shimmer 1.35s ease-in-out infinite;
  pointer-events: none;
}

.clip-time-editor-control-btn--loading:disabled {
  cursor: wait;
  opacity: 1;
}

.clip-time-editor-control-btn i {
  color: #c4b5fd;
  font-size: 13px;
}

.clip-time-editor-control-spinner {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.clip-time-editor-control-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 2px solid transparent;
}

.clip-time-editor-control-spinner-ring--outer {
  border-top-color: rgba(255, 255, 255, 0.95);
  border-right-color: rgba(255, 255, 255, 0.22);
  animation: clip-time-editor-control-spin 0.85s linear infinite;
}

.clip-time-editor-control-spinner-ring--inner {
  inset: 3px;
  border-bottom-color: rgba(255, 255, 255, 0.82);
  border-left-color: rgba(255, 255, 255, 0.18);
  animation: clip-time-editor-control-spin-reverse 1.15s linear infinite;
}

.clip-time-editor-control-btn:hover:not(:disabled) {
  border-color: rgba(34, 211, 238, 0.52);
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(34, 211, 238, 0.16), transparent 55%),
    rgba(22, 30, 50, 0.88);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 8px 20px rgba(2, 6, 23, 0.38),
    0 0 18px rgba(34, 211, 238, 0.12);
  transform: translateY(-1px);
}

.clip-time-editor-control-btn:hover:not(:disabled) i {
  color: #67e8f9;
}

.clip-time-editor-control-btn:active:not(:disabled) {
  transform: translateY(0);
}

.clip-time-editor-control-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@keyframes clip-time-editor-control-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes clip-time-editor-control-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}

@keyframes clip-time-editor-control-btn-shimmer {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
}

@keyframes clip-time-editor-control-btn-glow {
  0%,
  100% {
    box-shadow:
      0 8px 22px rgba(124, 58, 237, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  }
  50% {
    box-shadow:
      0 10px 26px rgba(6, 182, 212, 0.26),
      0 0 18px rgba(168, 85, 247, 0.22),
      0 0 0 1px rgba(255, 255, 255, 0.12) inset;
  }
}

.clip-time-editor-playback-timeline {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.clip-time-editor-playback-range {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  accent-color: #67e8f9;
  cursor: pointer;
}

.clip-time-editor-playback-range:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.clip-time-editor-playback-time {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: rgba(226, 232, 240, 0.72);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 991.98px) {
  .clip-time-editor-video {
    max-height: 360px;
  }
}

@media (max-width: 575.98px) {
  .clip-time-editor-video {
    max-height: 260px;
  }
}

@media (max-width: 575.98px) {
  .clip-time-editor-video-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .clip-time-editor-control-btn {
    width: 100%;
  }
}

.clip-time-editor-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.clip-time-editor-stat {
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.55);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.18s ease;
}

.clip-time-editor-stat:hover {
  border-color: rgba(196, 181, 253, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 8px 18px rgba(15, 23, 42, 0.28);
  transform: translateY(-1px);
}

.clip-time-editor-stat-label {
  font-size: 10px;
  color: #c4b5fd;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
}

.clip-time-editor-stat-value {
  font-size: 13px;
  color: var(--clipr-text-primary);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.clip-time-editor-timeline {
  border: 1px solid rgba(196, 181, 253, 0.24);
  border-radius: 16px;
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.06), transparent 44%),
    radial-gradient(circle at bottom left, rgba(168, 85, 247, 0.1), transparent 48%),
    rgba(15, 23, 42, 0.88);
  padding: 16px 16px 14px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 8px 24px rgba(15, 23, 42, 0.42);
}

.clip-time-editor-timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  font-size: 10px;
  font-weight: 700;
  color: rgba(226, 232, 240, 0.62);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.clip-time-editor-timeline-header span:nth-child(2) {
  color: #67e8f9;
}

@media (max-width: 767.98px) {
  .schedule-editor-page {
    padding-bottom: calc(132px + env(safe-area-inset-bottom));
  }

  .schedule-editor-page .schedule-editor-shell .clip-time-editor-footer {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 8px;
    padding: 8px 10px 10px;
  }

  .schedule-editor-page .schedule-editor-shell .clip-time-editor-footer .confirm-modal-btn {
    width: auto;
    min-width: 0;
    min-height: 42px;
    flex: 1 1 0;
    padding: 9px 8px;
    font-size: 0.76rem;
  }

  .clip-time-editor-screen {
    padding-bottom: 0;
  }

  .clip-time-editor-screen .make-flow-subtitles-wrap {
    padding: 0 0 8px;
  }

  .clip-time-editor-screen .make-flow-subtitles-pill {
    width: 100%;
    max-width: 300px;
    padding: 12px 16px 10px;
  }

  .clip-transcript-open-btn {
    width: 100%;
    max-width: 300px;
  }

  .clip-transcript-modal {
    width: calc(100vw - 20px);
    max-height: calc(100dvh - 20px);
  }

  .clip-transcript-modal .confirm-modal-header,
  .clip-transcript-modal .confirm-modal-body,
  .clip-transcript-modal .confirm-modal-footer {
    padding-left: 14px;
    padding-right: 14px;
  }

  .clip-transcript-modal .confirm-modal-footer {
    flex-wrap: nowrap;
  }

  .clip-transcript-modal .confirm-modal-btn {
    min-width: 0;
    flex: 1 1 0;
    padding: 10px 8px;
  }

  .editor-trim {
    gap: 0;
    padding: 10px;
    border-radius: 14px;
    background:
      radial-gradient(circle at 100% 0%, rgba(34, 211, 238, 0.05), transparent 46%),
      rgba(9, 9, 11, 0.7);
  }

  .clip-time-editor-grid {
    gap: 6px;
  }

  .clip-time-editor-stat {
    min-width: 0;
    padding: 9px 8px;
    gap: 4px;
    border-radius: 10px;
  }

  .clip-time-editor-stat-label {
    font-size: 8px;
    letter-spacing: 0.04em;
  }

  .clip-time-editor-stat-value {
    font-size: 11px;
  }

  .clip-time-editor-video-controls {
    position: fixed;
    right: 8px;
    bottom: calc(164px + env(safe-area-inset-bottom));
    left: 8px;
    z-index: 20;
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 36px;
    padding: 7px 9px;
    gap: 10px;
    border-color: rgba(103, 232, 249, 0.28);
    border-radius: 14px;
    background:
      radial-gradient(circle at 50% 0%, rgba(34, 211, 238, 0.12), transparent 72%),
      rgba(15, 23, 42, 0.96);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 8px 20px rgba(2, 6, 23, 0.32);
    backdrop-filter: blur(12px);
    align-items: center;
  }

  .clip-time-editor-control-btn {
    width: 36px;
    min-height: 36px;
    padding: 0;
    border-radius: 50%;
    background: rgba(30, 41, 59, 0.86);
    box-shadow: none;
  }

  .clip-time-editor-control-btn i {
    font-size: 12px;
  }

  .clip-time-editor-control-btn > span:last-child {
    display: none;
  }

  .clip-time-editor-playback-timeline {
    gap: 7px;
  }

  .clip-time-editor-playback-range {
    height: 5px;
  }

  .clip-time-editor-playback-time {
    margin-top: 2px;
    font-size: 9px;
    line-height: 1;
  }

  .clip-time-editor-timeline {
    position: fixed;
    right: 8px;
    bottom: calc(68px + env(safe-area-inset-bottom));
    left: 8px;
    z-index: 20;
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.94);
    backdrop-filter: blur(12px);
  }

  .clip-time-editor-timeline-header {
    margin-bottom: 5px;
    font-size: 8px;
  }

  .clip-time-editor-timeline-header span:nth-child(2) {
    display: none;
  }

  .clip-time-editor-track {
    height: 32px;
    border-radius: 8px;
  }

  .clip-time-editor-marker-label,
  .clip-time-editor-selection-label {
    display: none;
  }
}

.clip-time-editor-track {
  position: relative;
  width: 100%;
  height: 60px;
  border-radius: 14px;
  border: 1px solid rgba(196, 181, 253, 0.22);
  background: linear-gradient(
    90deg,
    rgba(9, 9, 11, 0.72) 0%,
    rgba(9, 9, 11, 0.72) var(--orig-left, 0%),
    rgba(34, 211, 238, 0.14) var(--orig-left, 0%),
    rgba(168, 85, 247, 0.18) calc(var(--orig-left, 0%) + var(--orig-width, 0%)),
    rgba(9, 9, 11, 0.72) calc(var(--orig-left, 0%) + var(--orig-width, 0%)),
    rgba(9, 9, 11, 0.72) 100%
  );
  overflow: visible;
  user-select: none;
  touch-action: none;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.35);
}

.clip-time-editor-minor-layer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  clip-path: inset(0 6px 0 6px);
  -webkit-clip-path: inset(0 6px 0 6px);
}

.clip-time-editor-minor-marker {
  position: absolute;
  top: 0;
  height: 14%;
  pointer-events: none;
}

.clip-time-editor-minor-marker-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(34, 211, 238, 0.42) 0%,
    rgba(168, 85, 247, 0.12) 100%
  );
}

.clip-time-editor-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.clip-time-editor-marker-line {
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 100%;
  background: rgba(196, 181, 253, 0.28);
  transform: translateX(-50%);
}

.clip-time-editor-marker-label {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: rgba(226, 232, 240, 0.58);
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.clip-time-editor-selection {
  position: absolute;
  top: 8px;
  bottom: 8px;
  border-radius: 11px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.82) 0%, rgba(6, 182, 212, 0.72) 100%);
  border: 2px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    0 8px 24px rgba(168, 85, 247, 0.38),
    inset 0 0 24px rgba(255, 255, 255, 0.08),
    0 0 20px rgba(34, 211, 238, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: grab;
  z-index: 2;
  overflow: visible;
  animation: schedule-editor-selection-glow 2.4s ease-in-out infinite;
}

.clip-time-editor-selection:active {
  cursor: grabbing;
  animation: none;
}

.clip-time-editor-selection-label {
  font-size: 11px;
  font-weight: 800;
  color: #ffffff;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
  cursor: grab;
  flex: 1;
  order: 2;
  text-align: center;
  padding: 0 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  letter-spacing: 0.03em;
}

.clip-time-editor-handle {
  position: relative;
  flex-shrink: 0;
  width: 16px;
  min-width: 16px;
  height: 46px;
  min-height: 46px;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #e0f2fe 100%);
  border: 2px solid rgba(34, 211, 238, 0.65);
  cursor: ew-resize;
  z-index: 3;
  box-shadow:
    0 2px 10px rgba(34, 211, 238, 0.35),
    0 0 14px rgba(168, 85, 247, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clip-time-editor-handle::after {
  content: "";
  width: 3px;
  height: 18px;
  background: linear-gradient(180deg, #a855f7, #06b6d4);
  border-radius: 2px;
  opacity: 0.85;
}

.clip-time-editor-handle:hover {
  border-color: rgba(34, 211, 238, 0.9);
  box-shadow:
    0 4px 16px rgba(34, 211, 238, 0.45),
    0 0 22px rgba(168, 85, 247, 0.35);
  transform: scale(1.06);
}

.clip-time-editor-handle:active {
  transform: scale(1.02);
}

@keyframes schedule-editor-selection-glow {
  0%,
  100% {
    box-shadow:
      0 8px 24px rgba(168, 85, 247, 0.38),
      inset 0 0 24px rgba(255, 255, 255, 0.08),
      0 0 20px rgba(34, 211, 238, 0.18);
  }
  50% {
    box-shadow:
      0 10px 28px rgba(168, 85, 247, 0.48),
      inset 0 0 28px rgba(255, 255, 255, 0.1),
      0 0 28px rgba(34, 211, 238, 0.28);
  }
}

@media (prefers-reduced-motion: reduce) {
  .clip-time-editor-selection {
    animation: none;
  }

  .clip-time-editor-stat:hover,
  .clip-time-editor-control-btn:hover:not(:disabled),
  .clip-time-editor-endcard-panel :deep(.members-settings-card .btn-primary:hover:not(:disabled)) {
    transform: none;
  }
}

.clip-time-editor-handle-left {
  order: 1;
}

.clip-time-editor-handle-right {
  order: 3;
}

.clip-time-editor-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

/* Modales — alineado con MakeClips.vue (overlay, panel, header, footer) */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .confirm-modal,
.modal-fade-leave-active .confirm-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .confirm-modal,
.modal-fade-leave-to .confirm-modal {
  transform: scale(0.96) translateY(-8px);
  opacity: 0.92;
}

.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background:
    radial-gradient(circle at top, rgba(168, 85, 247, 0.16) 0%, transparent 42%),
    radial-gradient(circle at bottom, rgba(59, 130, 246, 0.12) 0%, transparent 48%),
    rgba(7, 9, 18, 0.93);
  backdrop-filter: blur(10px);
}

.confirm-modal {
  width: min(540px, 100%);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: linear-gradient(
    180deg,
    var(--clipr-purple-overlay-medium) 0%,
    var(--clipr-bg-elevated) 42%
  );
  border: 1px solid var(--clipr-border-color);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.18),
    0 0 28px rgba(168, 85, 247, 0.14),
    0 20px 48px rgba(0, 0, 0, 0.22);
  color: var(--clipr-text-primary);
}

.confirm-modal--schedule-form {
  width: min(920px, 100%);
}

.confirm-modal--schedule-wide {
  width: min(1220px, 96vw);
}

.confirm-modal--schedule-manage {
  width: min(520px, calc(100vw - 32px));
}

.confirm-modal-header {
  position: relative;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--clipr-border-color);
  background: linear-gradient(180deg, rgba(168, 85, 247, 0.14) 0%, transparent 100%);
}

.confirm-modal-header--centered {
  text-align: center;
}

.confirm-modal-header--centered .confirm-modal-header-icon {
  margin: 0 auto 14px;
}

.confirm-modal-header--row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding-right: 52px;
}

.confirm-modal-header--row .confirm-modal-header-icon {
  margin: 0;
}

.confirm-modal-header--row .confirm-modal-title,
.confirm-modal-header--row .confirm-modal-subtitle {
  text-align: left;
}

.confirm-modal-header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--clipr-purple-overlay-strong), rgba(217, 70, 239, 0.2));
  border: 1px solid var(--clipr-purple-overlay-intense);
  color: var(--clipr-purple-light);
  font-size: 22px;
  flex-shrink: 0;
}

.confirm-modal-header-icon--danger {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.35), rgba(127, 29, 29, 0.4));
  border-color: rgba(248, 113, 113, 0.35);
  color: #fecaca;
}

.confirm-modal-title {
  margin: 0 0 4px;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--clipr-text-primary);
  letter-spacing: -0.02em;
}

.confirm-modal-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--clipr-text-secondary);
}

.confirm-modal-header--centered .confirm-modal-subtitle strong {
  color: var(--clipr-text-primary);
}

.confirm-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--clipr-border-color);
  border-radius: 8px;
  background: transparent;
  color: var(--clipr-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal-close:hover:not(:disabled) {
  background: transparent;
  border-color: var(--clipr-border-purple);
  color: var(--clipr-text-primary);
}

.confirm-modal-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.schedule-modal-scroll-body {
  max-height: min(70vh, 720px);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.schedule-modal-stepper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px minmax(0, 1fr) 28px minmax(0, 1fr);
  align-items: start;
  gap: 0;
  padding: 2px 2px 6px;
}

.schedule-modal-stepper-item {
  border: none;
  background: transparent;
  padding: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  color: var(--clipr-text-secondary);
  font: inherit;
  text-align: center;
  cursor: default;
  transition: color 0.2s ease;
}

.schedule-modal-stepper-item:disabled {
  opacity: 1;
  pointer-events: none;
  cursor: default;
  -webkit-text-fill-color: currentColor;
}

.schedule-modal-stepper-item.is-active {
  color: var(--clipr-text-primary);
}

.schedule-modal-stepper-item.is-complete {
  color: #86efac;
}

.schedule-modal-stepper-item.is-next {
  color: #c4b5fd;
}

.schedule-modal-stepper-item.is-pending {
  color: var(--clipr-text-tertiary);
}

.schedule-modal-stepper-divider {
  height: 2px;
  margin: 14px 6px 0;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.35);
  transition: background 0.2s ease;
}

.schedule-modal-stepper-divider.is-complete {
  background: linear-gradient(90deg, rgba(74, 222, 128, 0.92), rgba(22, 163, 74, 0.9));
}

.schedule-modal-stepper-index {
  width: 35px;
  height: 35px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid rgba(148, 163, 184, 0.55);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: var(--clipr-text-tertiary);
  background: rgba(15, 23, 42, 0.78);
  box-shadow: 0 0 0 2px rgba(2, 6, 23, 0.5);
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.schedule-modal-stepper-index i {
  font-size: 11px;
}

.schedule-modal-stepper-item.is-complete .schedule-modal-stepper-index {
  border-color: rgba(74, 222, 128, 0.92);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.95), rgba(21, 128, 61, 0.92));
  color: #fff;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.24);
}

.schedule-modal-stepper-item.is-active .schedule-modal-stepper-index {
  position: relative;
  overflow: visible;
  border-color: rgba(216, 180, 254, 0.95);
  color: #fff;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.98), rgba(124, 58, 237, 0.95));
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.32);
}

.schedule-modal-stepper-item.is-active .schedule-modal-stepper-index > * {
  position: relative;
  z-index: 2;
}

.schedule-modal-stepper-item.is-active .schedule-modal-stepper-index::after {
  content: "";
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  border: 1px solid rgba(196, 181, 253, 0.85);
  box-shadow: 0 0 12px rgba(167, 139, 250, 0.38);
  opacity: 0;
  transform: scale(0.78);
  pointer-events: none;
  animation: scheduleStepRadarPulse 1.5s ease-out infinite;
}

.schedule-modal-stepper-item.is-next .schedule-modal-stepper-index {
  border-color: rgba(196, 181, 253, 0.75);
  color: #ddd6fe;
  background: rgba(76, 29, 149, 0.42);
}

.schedule-modal-stepper-label {
  min-width: 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.2px;
  color: var(--clipr-text-tertiary);
}

.schedule-modal-stepper-item.is-active .schedule-modal-stepper-label {
  color: #fff;
}

.schedule-modal-stepper-item.is-complete .schedule-modal-stepper-label {
  color: #bbf7d0;
}

@keyframes scheduleStepRadarPulse {
  0% {
    opacity: 0.8;
    transform: scale(0.78);
  }
  65% {
    opacity: 0.3;
    transform: scale(1.45);
  }
  100% {
    opacity: 0;
    transform: scale(1.7);
  }
}

.schedule-modal-step-panel {
  min-height: 0;
}

.schedule-modal-step-panel--social,
.schedule-modal-step-panel--general,
.schedule-modal-step-panel--posting {
  display: block;
}

.schedule-modal-step-placeholder {
  min-height: 180px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  border: 1px dashed rgba(148, 163, 184, 0.42);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.45);
  color: var(--clipr-text-secondary);
}

.schedule-modal-step-placeholder i {
  color: #c4b5fd;
}

.schedule-modal-readable-body {
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--clipr-text-primary);
}

.schedule-modal-clip-title {
  margin: 10px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.4;
  color: var(--clipr-text-primary);
}

.delete-confirm-summary {
  background: var(--clipr-purple-overlay-medium);
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-lg);
  padding: 16px 18px;
  margin-bottom: 16px;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.1);
}

.delete-confirm-label {
  display: block;
  color: var(--clipr-text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.45px;
  margin-bottom: 8px;
}

.delete-confirm-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.45;
  color: var(--clipr-text-primary);
}

.delete-confirm-warning-block {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  margin: 0 0 12px;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid rgba(248, 113, 113, 0.45);
  background: rgba(220, 38, 38, 0.12);
  color: var(--clipr-text-primary);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.delete-confirm-warning-block i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 1.15rem;
  color: #f87171;
}

.delete-confirm-warning-block p {
  margin: 0;
}

.delete-confirm-error {
  margin: 12px 0 0;
  color: #b91c1c;
  font-size: 0.9375rem;
  line-height: 1.45;
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
}

.schedule-modal-inline-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid rgba(248, 113, 113, 0.5);
  background: rgba(220, 38, 38, 0.1);
  color: var(--clipr-text-primary);
  font-size: 0.9375rem;
  line-height: 1.45;
}

.schedule-modal-inline-error i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #dc2626;
}

.confirm-modal-footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid var(--clipr-border-color);
  background: var(--clipr-purple-overlay-medium);
}

/* Compacto solo para el modal Schedule clip */
.confirm-modal--schedule-form .confirm-modal-header {
  padding: 20px 20px 12px;
}

.confirm-modal--schedule-form .confirm-modal-header--centered .confirm-modal-header-icon {
  margin: 0 auto 10px;
}

.confirm-modal--schedule-form .confirm-modal-footer {
  gap: 10px;
  padding: 12px 20px 16px;
}

.confirm-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 132px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.confirm-modal-btn.is-loading {
  opacity: 0.85;
  pointer-events: none;
}

.confirm-modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-modal-btn--secondary {
  background: rgba(255, 255, 255, 0.08);
  color: var(--clipr-text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.confirm-modal-btn--secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: var(--clipr-text-primary);
}

.confirm-modal-btn--primary {
  background: linear-gradient(135deg, #a855f7, #d946ef);
  color: #fff;
  box-shadow: 0 4px 14px var(--clipr-purple-overlay-bold);
}

.confirm-modal-btn--primary:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(168, 85, 247, 0.45);
  transform: translateY(-1px);
}

.confirm-modal-btn--primary:disabled {
  transform: none;
}

.confirm-modal-btn--danger {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.92), rgba(153, 27, 27, 0.95));
  color: #fff;
  box-shadow: 0 4px 14px rgba(185, 28, 28, 0.35);
}

.confirm-modal-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
}

.schedule-modal-lede {
  margin-bottom: 16px;
}

.schedule-modal-lede-text {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--clipr-text-secondary);
}

.schedule-modal-posting-block {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(168, 85, 247, 0.28);
}

.schedule-modal-posting-block--step {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.schedule-modal-top-layout {
  display: grid;
  grid-template-columns: 262px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.schedule-modal-left-rail {
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: var(--clipr-radius-lg);
  background: var(--clipr-bg-elevated);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12),
    0 0 20px rgba(168, 85, 247, 0.06);
  padding: 18px 16px;
  margin-right: 2px;
}

.schedule-modal-left-rail--step {
  margin-right: 0;
}

.schedule-modal-left-rail-title {
  margin: 2px 2px 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.45px;
  color: var(--clipr-text-secondary);
}

.schedule-modal-left-rail-hint {
  margin: 0 2px 14px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--clipr-text-tertiary);
}

.schedule-modal-network-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  align-items: stretch;
}

.schedule-modal-network-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;
  gap: 0;
  min-width: 0;
}

.schedule-modal-network-item {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  min-height: 68px;
  padding: 10px 13px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 12px;
  background: linear-gradient(165deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.45) 100%);
  color: inherit;
  text-align: left;
  font: inherit;
  -webkit-appearance: none;
  appearance: none;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 6px 16px rgba(2, 6, 23, 0.35);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, background 0.2s ease, transform 0.18s ease;
}

.schedule-modal-network-item:hover:not(:disabled) {
  border-color: rgba(168, 85, 247, 0.45);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.18),
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 8px 20px rgba(76, 29, 149, 0.26);
  transform: translateY(-1px);
}

.schedule-modal-network-item:active:not(:disabled) {
  transform: translateY(0);
}

.schedule-modal-network-item:focus-visible {
  outline: 2px solid var(--clipr-purple-light);
  outline-offset: 2px;
}

.schedule-modal-network-item:disabled {
  cursor: not-allowed;
}

.schedule-modal-network-item.is-on {
  border-color: rgba(168, 85, 247, 0.42);
  background: linear-gradient(155deg, rgba(76, 29, 149, 0.38) 0%, rgba(15, 23, 42, 0.9) 52%, rgba(30, 41, 59, 0.85) 100%);
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.2),
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 10px 24px rgba(76, 29, 149, 0.28);
}

.schedule-modal-network-item.is-disabled {
  opacity: 0.72;
  border-color: rgba(148, 163, 184, 0.2);
  background: linear-gradient(165deg, rgba(15, 23, 42, 0.72) 0%, rgba(30, 41, 59, 0.58) 100%);
  box-shadow:
    0 0 0 1px rgba(100, 116, 139, 0.12) inset,
    0 6px 14px rgba(2, 6, 23, 0.28);
}

.schedule-modal-network-item.is-loading {
  opacity: 0.95;
}

.schedule-modal-network-main {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}

.schedule-modal-network-item .schedule-social-logo-wrap.schedule-modal-network-logo {
  width: 50px;
  height: 50px;
  min-width: 50px;
  padding: 0;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
}

.schedule-modal-network-item.is-on .schedule-social-logo-wrap.schedule-modal-network-logo {
  border-color: rgba(168, 85, 247, 0.35);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 12px rgba(168, 85, 247, 0.15);
}

.schedule-modal-network-item .schedule-modal-network-logo .schedule-social-logo {
  width: 30px;
  height: 30px;
}

.schedule-modal-network-meta {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  row-gap: 2px;
  align-items: center;
}

.schedule-modal-network-name {
  grid-column: 1;
  grid-row: 1;
  font-size: 14px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  line-height: 1.2;
}

.schedule-modal-network-status {
  grid-column: 1;
  grid-row: 2;
  font-size: 12px;
  line-height: 1.15;
  font-weight: 600;
}

.schedule-modal-network-status.is-connected {
  color: #60a5fa;
}

.schedule-modal-network-status.is-disconnected {
  color: var(--clipr-text-tertiary);
}

.schedule-modal-network-toggle-pill {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  max-width: 100%;
  min-width: 34px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(248, 113, 113, 0.42);
  background: linear-gradient(135deg, rgba(127, 29, 29, 0.24) 0%, rgba(69, 10, 10, 0.42) 100%);
  color: #fca5a5;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
}

.schedule-modal-network-toggle-pill.is-on {
  border-color: rgba(74, 222, 128, 0.52);
  background: linear-gradient(135deg, rgba(20, 83, 45, 0.3) 0%, rgba(22, 101, 52, 0.5) 100%);
  color: #86efac;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.22);
}

.schedule-modal-network-toggle-pill.is-disabled {
  border-color: rgba(248, 113, 113, 0.28);
  background: linear-gradient(135deg, rgba(69, 10, 10, 0.22) 0%, rgba(68, 64, 60, 0.26) 100%);
  color: #fca5a5;
  box-shadow: none;
}

.schedule-modal-network-toggle-pill.is-loading {
  gap: 6px;
  white-space: normal;
  text-align: center;
    max-width: min(100%, 9rem);
  border-color: rgba(167, 139, 250, 0.55);
  color: #e9d5ff;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(30, 41, 59, 0.72) 100%);
}

.schedule-modal-network-toggle-icon {
  font-size: 14px;
  line-height: 1;
}

.schedule-modal-network-toggle-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.schedule-modal-facebook-editor {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  gap: 5px;
  min-height: 64px;
  min-width: 0;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: linear-gradient(165deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.45) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 6px 16px rgba(2, 6, 23, 0.35);
}

.schedule-modal-facebook-editor--grid-slot {
  margin: 0;
}

.schedule-modal-facebook-label {
  display: block;
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.15px;
  color: var(--clipr-text-primary);
}

.schedule-modal-facebook-editor .form-control {
  min-height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(168, 85, 247, 0.32);
  background-color: rgba(2, 6, 23, 0.78);
  color: var(--clipr-text-primary);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.03);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  font-size: 12px;
}

.schedule-modal-facebook-editor select.form-control {
  -webkit-appearance: none;
  appearance: none;
  min-width: 0;
  padding-right: 34px;
  background-image:
    linear-gradient(45deg, transparent 50%, rgba(196, 181, 253, 0.95) 50%),
    linear-gradient(135deg, rgba(196, 181, 253, 0.95) 50%, transparent 50%);
  background-position:
    calc(100% - 16px) calc(50% - 1px),
    calc(100% - 11px) calc(50% - 1px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

.schedule-modal-facebook-editor .form-control:focus {
  border-color: rgba(167, 139, 250, 0.78);
  background-color: rgba(15, 23, 42, 0.92);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.schedule-modal-facebook-editor .form-control:disabled {
  opacity: 0.75;
  color: var(--clipr-text-tertiary);
  background-color: rgba(15, 23, 42, 0.6);
  cursor: not-allowed;
}

.schedule-modal-facebook-editor select.form-control option {
  background: #0f172a;
  color: var(--clipr-text-primary);
}

.schedule-modal-facebook-editor .form-text {
  margin-top: 0;
  font-size: 10px;
  line-height: 1.3;
}

.schedule-modal-main-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-modal-main-column--step {
  width: 100%;
}

.schedule-modal-general-card {
  margin-bottom: 0;
  padding: 18px;
  border: 1px solid rgba(168, 85, 247, 0.26);
  border-radius: 16px;
  background: linear-gradient(
    155deg,
    rgba(15, 23, 42, 0.82) 0%,
    rgba(30, 27, 75, 0.58) 100%
  );
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.1), 0 12px 30px rgba(15, 23, 42, 0.26);
}

.schedule-modal-general-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
  gap: 18px;
  align-items: start;
}

.schedule-modal-general-fields {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(2, 6, 23, 0.42);
}

.schedule-modal-general-fields .form-group label {
  font-size: 14px;
  margin-bottom: 8px;
}

.schedule-modal-compact-field .form-control {
  min-height: 38px;
  padding: 8px 11px;
  font-size: 14px;
  border-radius: 10px;
  border: 1px solid rgba(168, 85, 247, 0.32);
  background-color: rgba(2, 6, 23, 0.78);
  color: var(--clipr-text-primary);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.03);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.schedule-modal-compact-field .form-control::placeholder {
  color: var(--clipr-text-tertiary);
}

.schedule-modal-compact-field .form-control:focus {
  border-color: rgba(167, 139, 250, 0.78);
  background-color: rgba(15, 23, 42, 0.92);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.schedule-modal-compact-field {
  margin-bottom: 0;
}

.schedule-modal-compact-field textarea.form-control {
  min-height: 84px;
  resize: vertical;
}

.schedule-modal-compact-hint {
  margin-top: 6px !important;
  font-size: 12px;
}

.schedule-modal-preview-pane {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-modal-preview-social-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.35);
}

.schedule-modal-preview-social-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--clipr-text-secondary);
  text-transform: uppercase;
}

.schedule-modal-preview-platform-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.schedule-modal-preview-platform-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 26px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(15, 23, 42, 0.65);
  color: var(--clipr-text-secondary);
  font-size: 11px;
  font-weight: 600;
}

.schedule-modal-preview-platform-chip .schedule-social-logo {
  width: 14px;
  height: 14px;
}

.schedule-modal-preview-platform-chip--empty {
  border-style: dashed;
  color: var(--clipr-text-tertiary);
}

.schedule-modal-preview-phone {
  position: relative;
  width: 100%;
  max-width: 320px;
  margin-left: auto;
  border-radius: 20px;
  padding: 9px;
  background: linear-gradient(
    155deg,
    rgba(167, 139, 250, 0.38) 0%,
    rgba(76, 29, 149, 0.38) 38%,
    rgba(15, 23, 42, 0.78) 100%
  );
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.36);
}

.schedule-modal-preview-phone::before {
  content: "";
  position: absolute;
  inset: 1px;
  border-radius: 19px;
  border: 1px solid rgba(216, 180, 254, 0.28);
  pointer-events: none;
}

.schedule-modal-preview-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(168, 85, 247, 0.38);
  background: rgba(15, 23, 42, 0.9);
  isolation: isolate;
}

.schedule-modal-preview-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.schedule-modal-preview-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--clipr-text-tertiary);
  font-size: 11px;
  background: radial-gradient(circle at top, rgba(51, 65, 85, 0.6), rgba(15, 23, 42, 0.95));
}

.schedule-modal-preview-empty i {
  font-size: 22px;
}

.schedule-modal-preview-topbar {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 3;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.28);
  backdrop-filter: blur(3px);
}

.schedule-modal-preview-avatar {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.9), rgba(217, 70, 239, 0.85));
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.schedule-modal-preview-account-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.schedule-modal-preview-account-name {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-modal-preview-account-subtitle {
  color: rgba(226, 232, 240, 0.88);
  font-size: 10px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-modal-preview-gradient {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 58%;
  background: linear-gradient(
    to top,
    rgba(2, 6, 23, 0.94) 0%,
    rgba(2, 6, 23, 0.65) 42%,
    rgba(2, 6, 23, 0.08) 100%
  );
  z-index: 2;
}

.schedule-modal-preview-caption {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.schedule-modal-preview-caption-title {
  margin: 0;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.schedule-modal-preview-caption-description {
  margin: 0;
  color: rgba(226, 232, 240, 0.94);
  font-size: 11px;
  line-height: 1.35;
  max-height: 3.6em;
  overflow: hidden;
}

.schedule-modal-preview-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.schedule-modal-preview-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.2);
  border: 1px solid rgba(168, 85, 247, 0.35);
  color: #e9d5ff;
  font-size: 10px;
  font-weight: 600;
}

.schedule-modal-preview-engagement {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(226, 232, 240, 0.88);
  font-size: 10px;
  font-weight: 600;
}

.schedule-modal-preview-engagement span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.schedule-modal-options-inline {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.schedule-modal-posting-controls {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 10px;
  align-items: start;
}

.schedule-modal-posting-controls--combined {
  position: relative;
  gap: 14px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  background: var(--clipr-bg-elevated);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12),
    0 0 20px rgba(168, 85, 247, 0.06);
}

.schedule-modal-posting-controls--combined::before {
  content: "";
  position: absolute;
  top: 14px;
  bottom: 14px;
  left: calc(42.5% - 0.5px);
  width: 1px;
  background: linear-gradient(
    to bottom,
    rgba(168, 85, 247, 0),
    rgba(168, 85, 247, 0.34) 20%,
    rgba(168, 85, 247, 0.34) 80%,
    rgba(168, 85, 247, 0)
  );
  pointer-events: none;
}

.schedule-modal-posting-controls--combined .assign-cycle-timezone-picker,
.schedule-modal-posting-controls--combined .assign-cycle-time-picker {
  margin-top: 0;
  padding: 2px 4px;
  border: none;
  box-shadow: none;
  background: transparent;
}

.schedule-modal-posting-controls--combined .assign-cycle-time-label {
  margin-bottom: 10px;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.schedule-modal-posting-controls--combined .material-timezone-select {
  min-height: 44px;
  border-radius: 10px;
  border-color: rgba(148, 163, 184, 0.45);
  background-color: rgba(15, 23, 42, 0.84);
}

.schedule-modal-posting-controls--combined .material-time-picker {
  padding: 10px 10px 8px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(15, 23, 42, 0.42);
}

.schedule-modal-posting-controls--combined .material-time-input {
  border-radius: 10px;
  border-color: rgba(148, 163, 184, 0.5);
  background-color: rgba(15, 23, 42, 0.86);
}

.schedule-modal-posting-controls--combined .material-period-btn {
  border-radius: 8px;
}

.assign-cycle-timezone-picker--compact,
.assign-cycle-time-picker--compact {
  margin-top: 0;
  padding: 12px;
}

.assign-cycle-timezone-picker--compact .assign-cycle-time-label,
.assign-cycle-time-picker--compact .assign-cycle-time-label {
  margin-bottom: 8px;
  font-size: 12px;
}

.assign-cycle-time-picker--compact .material-time-picker-main {
  width: 100%;
  justify-content: center;
  margin-bottom: 10px;
  gap: 10px;
}

.assign-cycle-time-picker--compact .material-time-input {
  width: 74px;
  height: 64px;
  line-height: 60px;
  font-size: 23px;
}

.assign-cycle-time-picker--compact .material-time-separator {
  margin: 0 0 16px;
  font-size: 32px;
}

.assign-cycle-time-picker--compact .material-period-btn {
  width: 52px;
  height: 36px;
  line-height: 34px;
  font-size: 13px;
}

.confirm-modal--collection-processing {
  max-width: 440px;
}

.confirm-modal--collection-processing-failed .confirm-modal-header {
  border-bottom-color: rgba(248, 113, 113, 0.2);
}

.collection-processing-modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 4px 8px 8px;
}

.collection-processing-modal-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}

.collection-processing-modal-indicator .processing-indicator {
  width: 52px;
  height: 52px;
}

.collection-processing-modal-error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(248, 113, 113, 0.12);
  color: #f87171;
  font-size: 30px;
}

.collection-processing-modal-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.collection-processing-modal-status--queued,
.collection-processing-modal-status--decided {
  color: #e2e8f0;
  background: rgba(226, 232, 240, 0.12);
}

.collection-processing-modal-status--downloading {
  color: #fde047;
  background: rgba(250, 204, 21, 0.14);
}

.collection-processing-modal-status--downloaded {
  color: #d8b4fe;
  background: rgba(192, 132, 252, 0.14);
}

.collection-processing-modal-status--rendering {
  color: #67e8f9;
  background: rgba(34, 211, 238, 0.14);
}

.collection-processing-modal-status--failed {
  color: #fca5a5;
  background: rgba(248, 113, 113, 0.14);
}

.collection-processing-modal-message {
  margin: 0;
  max-width: 340px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--clipr-text-secondary);
}

.collection-processing-modal-progress {
  width: 100%;
  max-width: 280px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.collection-processing-modal-progress-bar {
  display: block;
  height: 100%;
  width: 38%;
  border-radius: inherit;
  transform: translateX(-120%);
}

.collection-processing-modal-progress-bar--downloading {
  background: linear-gradient(90deg, transparent, #facc15, transparent);
  animation: collectionProgressSlide 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.collection-processing-modal-progress-bar--downloaded {
  background: linear-gradient(90deg, transparent, #c084fc, #a855f7, transparent);
  width: 52%;
  animation: collectionProgressPulse 2.2s ease-in-out infinite;
}

.collection-processing-modal-progress-bar--rendering {
  background: linear-gradient(90deg, transparent, #22d3ee, #06b6d4, transparent);
  animation: collectionProgressSlide 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.collection-processing-modal-progress-bar--queued,
.collection-processing-modal-progress-bar--decided {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
  width: 30%;
  animation: collectionProgressSlide 2.4s ease-in-out infinite;
}

.assign-cycle-question {
  font-size: 15px;
  color: var(--clipr-text-primary);
  margin-bottom: 10px;
}

.assign-cycle-question strong {
  color: var(--clipr-purple-light);
  font-weight: 700;
}

.snackbar-fade-enter-active,
.snackbar-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.snackbar-fade-enter-from,
.snackbar-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

.schedule-snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translate(-50%, 0);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  z-index: 3100;
}

.schedule-snackbar i {
  font-size: 18px;
}

.schedule-snackbar--download {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.95), rgba(99, 102, 241, 0.95));
  border: 1px solid rgba(216, 180, 254, 0.5);
}

.schedule-snackbar--delete {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.95), rgba(153, 27, 27, 0.95));
  border: 1px solid rgba(252, 165, 165, 0.4);
}

.schedule-snackbar--info {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(37, 99, 235, 0.95));
  border: 1px solid rgba(147, 197, 253, 0.45);
}

.confirm-modal--timeline-preview {
  max-width: 760px;
}

.schedule-timeline-preview-video {
  width: 100%;
  max-height: 420px;
  border-radius: var(--clipr-radius-lg);
  background: #000;
  display: block;
}

@media (max-width: 768px) {
  .schedule-snackbar {
    left: 12px;
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom));
    top: auto;
    transform: none;
    justify-content: center;
    padding: 12px 14px;
    font-size: 13px;
  }
}

.scheduled-clip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.scheduled-clip-item {
  background: var(--clipr-purple-overlay-medium);
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-lg);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.scheduled-clip-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: var(--clipr-text-secondary);
}

.scheduled-clip-value {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.45;
  color: var(--clipr-text-primary);
  word-break: break-word;
}

.mt-2 {
  margin-top: 8px;
}

/* Timeline principal (días dinámicos): franja tipo carrusel, altura estable */
.schedule-row-timeline {
  min-height: 0;
}

.calendar-timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.calendar-timeline-title {
  display: flex;
  flex-direction: column;
}

.calendar-timeline-main-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--clipr-text-primary);
}

.calendar-timeline-month {
  font-size: 13px;
  text-transform: uppercase;
  color: var(--clipr-text-tertiary);
}

.calendar-timeline-body {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 148px;
}

.timeline-loading-state {
  width: 100%;
  min-height: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
  background: linear-gradient(
    135deg,
    var(--clipr-bg-elevated) 0%,
    var(--clipr-purple-overlay-medium) 100%
  );
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12);
  text-align: center;
}

.timeline-loading-state--modal {
  min-height: 132px;
}

.timeline-loading-icon {
  font-size: 22px;
  color: var(--clipr-purple-light);
}

.timeline-loading-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--clipr-text-primary);
}

.timeline-loading-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: var(--clipr-text-secondary);
}

.timeline-nav-btn {
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--clipr-transition);
  flex-shrink: 0;
  align-self: center;
}

.timeline-nav-btn:hover {
  background: var(--clipr-purple-overlay-focus);
  border-color: var(--clipr-border-purple);
  transform: translateY(-1px);
}

.timeline-nav-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.timeline-nav-btn:disabled:hover {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
}

.timeline-days {
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-top: 4px;
  padding-bottom: 4px;
  scrollbar-gutter: stable;
  scroll-snap-type: x proximity;
  align-items: stretch;
}

.timeline-days--posting {
  scrollbar-width: thin;
  scrollbar-color: rgba(167, 139, 250, 0.75) rgba(15, 23, 42, 0.55);
}

.timeline-days--posting::-webkit-scrollbar {
  height: 8px;
}

.timeline-days--posting::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.55);
  border-radius: 999px;
}

.timeline-days--posting::-webkit-scrollbar-thumb {
  background: linear-gradient(90deg, rgba(167, 139, 250, 0.9), rgba(124, 58, 237, 0.9));
  border-radius: 999px;
}

.timeline-days--posting::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(90deg, rgba(196, 181, 253, 0.95), rgba(139, 92, 246, 0.95));
}

.timeline-days--posting .timeline-day-card {
  flex: 0 0 132px;
  min-width: 132px;
}

.timeline-day-card {
  flex: 1 1 120px;
  min-width: 0;
  min-height: 132px;
  background: var(--clipr-bg-elevated);
  border-radius: var(--clipr-radius-lg);
  padding: 10px 12px;
  text-align: center;
  box-shadow: none;
  cursor: pointer;
  transition: var(--clipr-transition);
  border: 1px solid var(--clipr-border-color);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  scroll-snap-align: start;
}

.timeline-today-badge {
  align-self: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 999px;
  margin-top: 6px;
  margin-bottom: 2px;
  background: rgba(168, 85, 247, 0.2);
  color: var(--clipr-purple-light);
  border: 1px solid rgba(168, 85, 247, 0.45);
}

.timeline-day-card.is-today:not(.is-selected) {
  border-color: transparent;
  box-shadow: none;
}

.timeline-day-card.has-events {
  background: linear-gradient(
    135deg,
    var(--clipr-bg-elevated) 0%,
    var(--clipr-purple-overlay-medium) 100%
  );
}

.timeline-day-card.is-selected {
  border-color: var(--clipr-purple-light);
  box-shadow: 0 0 0 1px var(--clipr-purple-overlay-border), var(--clipr-shadow-card);
}

.timeline-day-card.is-selected .timeline-day-number {
  color: var(--clipr-purple-light);
}

.timeline-day-card:hover {
  transform: translateY(-2px);
  border-color: var(--clipr-border-hover);
  box-shadow: var(--clipr-shadow-card);
}

.timeline-day-card.is-disabled {
  opacity: 0.52;
  cursor: not-allowed;
  background: rgba(15, 23, 42, 0.58);
  border-color: rgba(148, 163, 184, 0.22);
  box-shadow: none;
}

.timeline-day-card.is-disabled:hover {
  transform: none;
  border-color: rgba(148, 163, 184, 0.22);
  box-shadow: none;
}

.timeline-day-name {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--clipr-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-day-number {
  font-size: 20px;
  font-weight: 700;
  margin: 4px 0;
  color: var(--clipr-text-primary);
}

.timeline-day-events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  min-height: 48px;
}

.timeline-day-event-pill {
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--clipr-purple-gradient);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-day-event-pill--static {
  cursor: default;
}

.timeline-day-event-pill.is-posted {
  background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
}

.timeline-day-empty {
  font-size: 10px;
  color: var(--clipr-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cycle-data-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--clipr-text-tertiary);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.back-button:hover {
  color: var(--clipr-text-primary);
  background-color: rgba(15, 23, 42, 0.95);
}

/* Clip list styles */
.clips-list {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 5px;
}

.mt-3 {
  margin-top: 15px;
}

.mt-4 {
  margin-top: 20px;
}

.mb-2 {
  margin-bottom: 10px;
}

.mb-3 {
  margin-bottom: 15px;
}

.mb-4 {
  margin-bottom: 20px;
}

.assign-cycle-timeline {
  background: var(--clipr-bg-elevated);
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: var(--clipr-radius-lg);
  padding: 14px 12px;
  overflow: visible;
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12),
    0 0 20px rgba(168, 85, 247, 0.06);
}

.assign-cycle-timezone-picker,
.assign-cycle-time-picker {
  margin-top: 20px;
  padding: 15px;
  background: var(--clipr-bg-elevated);
  border-radius: var(--clipr-radius-lg);
  border: 1px solid rgba(168, 85, 247, 0.22);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.08);
}

.assign-cycle-time-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.schedule-timeline-posting-date-label {
  margin-bottom: 6px;
}

.material-timezone-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--clipr-border-color);
  border-radius: 4px;
  background-color: rgba(15, 23, 42, 0.95);
  color: var(--clipr-text-primary);
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s ease;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ccc' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;
}

.material-timezone-select:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.6);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.3);
}

.material-timezone-select:disabled {
  background-color: rgba(15, 23, 42, 0.7);
  color: var(--clipr-text-secondary);
  cursor: not-allowed;
}

/* Material Design Time Picker Styles */
.material-time-picker {
  width: 100%;
}

.material-time-picker-main {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
}

.material-time-separator {
  font-size: 36px;
  font-weight: 400;
  color: var(--clipr-text-primary);
  margin: 0px 1px 22px 1px;
  line-height: 1;
}

.material-time-period {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 8px;
}

.material-period-btn {
  width: 51px;
  height: 36px;
  padding: 0;
  margin: 0;
  border: 2px solid var(--clipr-border-color);
  border-radius: 4px;
  background-color: transparent;
  color: var(--clipr-purple-light);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 32px;
}

.material-period-btn.is-selected {
  background-color: rgba(76, 29, 149, 0.4);
  border-color: var(--clipr-purple-light);
  color: #f9fafb;
}

.material-period-btn:not(.is-selected) {
  color: var(--clipr-text-tertiary);
}

.material-period-btn:hover {
  background-color: rgba(15, 23, 42, 1);
}

.material-time-input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.material-time-input-label {
  font-size: 12px;
  color: var(--clipr-text-tertiary);
  text-transform: uppercase;
  font-weight: 500;
}

.material-time-input {
  width: 86px;
  height: 75px;
  padding: 0;
  margin: 0;
  border: 2px solid var(--clipr-border-color);
  border-radius: 4px;
  background-color: rgba(15, 23, 42, 0.95);
  color: var(--clipr-text-primary);
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;
  line-height: 71px;
  display: block;
}

.material-time-input:focus {
  outline: none;
  border-color: var(--clipr-purple-light);
  box-shadow: 0 0 0 3px var(--clipr-purple-glow);
}

.material-time-input::placeholder {
  color: var(--clipr-text-tertiary);
  font-size: 20px;
}

.material-time-input::-webkit-inner-spin-button,
.material-time-input::-webkit-outer-spin-button {
  opacity: 1;
  height: 30px;
  cursor: pointer;
}

.assign-cycle-time-hint {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #fca5a5;
  font-style: italic;
}

/* Metadata Sections Styles */
.metadata-section-general {
  margin-bottom: 24px;
  background: var(--clipr-bg-elevated);
  border-radius: var(--clipr-radius-lg);
  padding: 20px;
  border: 1px solid rgba(168, 85, 247, 0.22);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.08);
}

.metadata-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--clipr-border-color);
}

.metadata-section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metadata-section-content {
  padding-top: 8px;
}

.metadata-section-content .form-group label,
.metadata-section-content .form-group label strong {
  color: var(--clipr-text-primary);
}

.metadata-section-content .form-group .form-text,
.metadata-section-content .form-group .form-text.text-muted {
  color: var(--clipr-text-secondary);
}

/* Plataformas en el modal de programación: franja única + detalle */
.metadata-platform-tabs {
  margin-top: 0;
}

.metadata-platform-tabs--detail-only {
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--clipr-border-color);
}

.schedule-platform-strip-hint {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--clipr-text-secondary);
}

.schedule-platform-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.schedule-platform-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 12px 12px 12px;
  border-radius: 14px;
  border: 1px solid var(--clipr-border-color);
  background: linear-gradient(
    165deg,
    rgba(15, 23, 42, 0.92) 0%,
    var(--clipr-purple-overlay-medium) 100%
  );
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.schedule-platform-card:not(.is-on) {
  opacity: 0.72;
}

.schedule-platform-card.is-disabled {
  opacity: 0.5;
  border-color: rgba(148, 163, 184, 0.22);
  background: linear-gradient(
    165deg,
    rgba(15, 23, 42, 0.74) 0%,
    rgba(30, 41, 59, 0.68) 100%
  );
}

.schedule-platform-card.is-on {
  opacity: 1;
  border-color: rgba(168, 85, 247, 0.4);
  background: linear-gradient(
    165deg,
    rgba(88, 28, 135, 0.22) 0%,
    rgba(15, 23, 42, 0.88) 55%
  );
}

.schedule-platform-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.schedule-platform-card-title-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule-platform-card-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--clipr-text-primary);
  line-height: 1.2;
  min-width: 0;
  word-break: break-word;
}

.schedule-platform-card-status {
  font-size: 11px;
  line-height: 1.1;
  font-weight: 600;
}

.schedule-platform-card-status.is-connected {
  color: #60a5fa;
}

.schedule-platform-card-status.is-disconnected {
  color: var(--clipr-text-tertiary);
}

.schedule-platform-switch {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.schedule-platform-switch:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.schedule-platform-switch:hover .schedule-platform-switch-track {
  filter: brightness(1.08);
}

.schedule-platform-switch:active {
  transform: scale(0.96);
}

.schedule-platform-switch:focus-visible {
  outline: 2px solid var(--clipr-purple-light);
  outline-offset: 3px;
}

.schedule-platform-switch-track {
  position: relative;
  display: block;
  width: 44px;
  height: 26px;
  border-radius: 999px;
  background: rgba(71, 85, 105, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25) inset;
}

.schedule-platform-card.is-on .schedule-platform-switch-track {
  background: linear-gradient(135deg, #c084fc 0%, #9333ea 100%);
  border-color: rgba(255, 255, 255, 0.22);
  box-shadow: 0 0 12px rgba(168, 85, 247, 0.35);
}

.schedule-platform-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f8fafc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  transition: transform 0.22s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.schedule-platform-card.is-on .schedule-platform-switch-thumb {
  transform: translateX(18px);
}

.schedule-platform-card-config {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  min-height: 88px;
  padding: 16px 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.schedule-platform-card-config:disabled {
  cursor: not-allowed;
  border-color: rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.4);
}

.schedule-platform-card-config:hover {
  border-color: rgba(168, 85, 247, 0.35);
  background: rgba(168, 85, 247, 0.1);
}

.schedule-platform-card.is-disabled .schedule-platform-card-config:hover {
  border-color: rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.4);
}

.schedule-platform-card-config:focus-visible {
  outline: 2px solid var(--clipr-purple-light);
  outline-offset: 2px;
}

.schedule-platform-card-config:active {
  transform: scale(0.99);
}

.schedule-social-logo-wrap--card {
  width: 56px;
  height: 56px;
  padding: 12px;
}

.schedule-social-logo-wrap--card .schedule-social-logo {
  width: 32px;
  height: 32px;
}

.platform-tabs-content {
  min-height: 240px;
}

.platform-tab-panel {
  animation: fadeIn 0.3s ease;
}

.platform-tab-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: var(--clipr-text-tertiary);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Logos de red — mismo patrón que UserProfile/SocialConnectionsCard.vue */
.schedule-social-logo-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(88, 28, 135, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.schedule-social-logo-wrap--tab {
  width: 40px;
  height: 40px;
  padding: 8px;
}

.schedule-social-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
}

.schedule-social-logo-wrap--tab .schedule-social-logo {
  width: 24px;
  height: 24px;
}

/* Form Grid Layout */
.metadata-form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .confirm-modal-overlay {
    position: fixed;
    inset: 0;
    min-height: 100dvh;
    align-items: center;
    justify-content: center;
    padding: 14px;
  }

  .confirm-modal--schedule-form,
  .confirm-modal--schedule-wide {
    width: min(100%, 620px);
    max-height: calc(100dvh - 28px);
    border-radius: 16px;
  }

  .confirm-modal--clip-time-editor {
    width: min(100%, 620px);
    max-height: calc(100dvh - 28px);
  }

  .confirm-modal--schedule-wide {
    height: min(92dvh, 900px);
  }

  .confirm-modal--schedule-form .confirm-modal-header,
  .confirm-modal--schedule-wide .confirm-modal-header {
    padding: 18px 16px 12px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-header {
    padding: 18px 16px 12px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-subtitle {
    max-width: calc(100% - 48px);
  }

  .confirm-modal--schedule-form .confirm-modal-body,
  .confirm-modal--schedule-wide .confirm-modal-body {
    padding: 14px 16px 16px;
    overscroll-behavior: contain;
  }

  .confirm-modal--clip-time-editor .confirm-modal-body {
    padding: 14px 16px 16px;
  }

  .clip-time-editor-modal-body {
    max-height: calc(100dvh - 166px);
  }

  .confirm-modal--schedule-form .confirm-modal-footer,
  .confirm-modal--schedule-wide .confirm-modal-footer {
    gap: 8px;
    padding: 12px 16px 16px;
    flex-wrap: nowrap;
  }

  .confirm-modal--clip-time-editor .confirm-modal-footer {
    gap: 8px;
    padding: 12px 16px 16px;
    flex-wrap: nowrap;
  }

  .confirm-modal--schedule-manage .schedule-manage-footer-actions {
    flex-wrap: nowrap;
    align-items: stretch;
  }

  .confirm-modal--schedule-manage .schedule-manage-footer-actions .confirm-modal-btn {
    min-width: 0;
    flex: 1 1 0;
  }

  .confirm-modal--schedule-form .confirm-modal-header {
    padding: 16px 14px 10px;
  }

  .confirm-modal--schedule-form .confirm-modal-header--centered .confirm-modal-header-icon {
    margin-bottom: 8px;
  }

  .confirm-modal--schedule-form .confirm-modal-footer {
    gap: 7px;
    padding: 10px 14px 12px;
  }

  .confirm-modal--schedule-form .confirm-modal-btn,
  .confirm-modal--schedule-wide .confirm-modal-btn {
    min-width: 0;
    flex: 1 1 0;
    min-height: 44px;
    padding: 10px 14px;
    font-size: 0.88rem;
  }

  .confirm-modal--clip-time-editor .confirm-modal-btn {
    min-width: 0;
    flex: 1 1 0;
    min-height: 44px;
    padding: 10px 14px;
    font-size: 0.88rem;
  }

  .confirm-modal--schedule-form .confirm-modal-close,
  .confirm-modal--schedule-wide .confirm-modal-close {
    width: 32px;
    height: 32px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-close {
    width: 32px;
    height: 32px;
  }

  .confirm-modal--schedule-form .schedule-modal-scroll-body,
  .confirm-modal--schedule-wide .schedule-modal-scroll-body {
    max-height: calc(92dvh - 182px);
  }

  .schedule-modal-stepper {
    grid-template-columns: minmax(0, 1fr) 12px minmax(0, 1fr) 12px minmax(0, 1fr);
    padding: 8px 0 8px;
  }

  .schedule-modal-stepper-item {
    gap: 5px;
  }

  .schedule-modal-stepper-index {
    width: 25px;
    height: 25px;
    font-size: 15px;
  }

  .schedule-modal-stepper-label {
    font-size: 15px;
    line-height: 1.2;
  }

  .schedule-modal-stepper-divider {
    margin: 11px 4px 0;
  }

  .schedule-modal-lede {
    margin-bottom: 14px;
  }

  .schedule-modal-lede-text {
    font-size: 0.88rem;
    line-height: 1.5;
  }

  .metadata-section-general {
    margin-bottom: 16px;
    padding: 14px;
  }

  .metadata-section-header {
    margin-bottom: 12px;
    padding-bottom: 10px;
  }

  .metadata-form-grid {
    gap: 12px;
    margin-bottom: 14px;
  }

  .metadata-subsection {
    margin-top: 16px;
    padding-top: 14px;
  }

  .schedule-modal-options-bottom {
    margin-top: 14px;
  }

  .schedule-modal-posting-block {
    margin-top: 16px;
    padding-top: 14px;
  }

  .schedule-modal-posting-block--step {
    margin-top: 0;
    padding-top: 0;
  }

  .schedule-modal-top-layout {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .schedule-modal-left-rail {
    padding: 12px 10px 10px;
    border-radius: 14px;
    border-color: rgba(168, 85, 247, 0.34);
    background: linear-gradient(
      165deg,
      rgba(17, 24, 39, 0.96) 0%,
      rgba(30, 27, 75, 0.72) 100%
    );
    box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.2), 0 10px 20px rgba(76, 29, 149, 0.2);
  }

  .schedule-modal-left-rail-title {
    margin-bottom: 4px;
    font-size: 11px;
    letter-spacing: 0.4px;
  }

  .schedule-modal-left-rail-hint {
    margin-bottom: 10px;
    font-size: 11px;
    line-height: 1.3;
  }

  .schedule-modal-network-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .schedule-modal-facebook-editor--grid-slot {
    grid-column: auto;
  }

  .schedule-modal-network-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    align-items: stretch;
  }

  .schedule-modal-network-item {
    min-height: 96px;
    padding: 0 19px;
    border-radius: 12px;
    justify-content: center;
  }

  .schedule-modal-network-item .schedule-social-logo-wrap.schedule-modal-network-logo {
    width: 47px;
    height: 47px;
    min-width: 47px;
    border-radius: 10px;
  }

  .schedule-modal-network-item .schedule-modal-network-logo .schedule-social-logo {
    width: 28px;
    height: 28px;
  }

  .schedule-modal-network-main {
    grid-template-columns: auto minmax(0, 1fr);
    justify-items: stretch;
    align-content: center;
    align-items: center;
    gap: 7px;
    text-align: left;
    width: auto;
    max-width: 100%;
    margin: 2px auto;
  }

  .schedule-modal-network-name {
    grid-column: 1;
    grid-row: 1;
    width: auto;
    font-size: 13px;
    line-height: 1.2;
  }

  .schedule-modal-network-status {
    grid-column: 1;
    grid-row: 2;
    width: auto;
    font-size: 11px;
  }

  .schedule-modal-network-meta {
    width: auto;
    max-width: 100%;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    justify-items: start;
    column-gap: 0;
    row-gap: 4px;
    text-align: left;
  }

  .schedule-modal-network-toggle-pill {
    grid-column: 1;
    grid-row: 4;
    justify-self: start;
    align-self: start;
    min-height: 22px;
    padding: 3px 7px;
    font-size: 9px;
    line-height: 1.2;
    text-align: center;
    white-space: normal;
    max-width: 100%;
  }

  .schedule-modal-facebook-editor {
    min-height: 62px;
    padding: 9px 11px;
    gap: 5px;
  }

  .schedule-modal-facebook-label {
    font-size: 12px;
  }

  .schedule-modal-facebook-editor .form-control {
    min-height: 33px;
    font-size: 12px;
  }

  .schedule-modal-main-column {
    gap: 10px;
  }

  .schedule-modal-general-layout {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .schedule-modal-general-fields {
    padding: 12px;
  }

  .schedule-modal-preview-pane {
    max-width: 100%;
  }

  .schedule-modal-preview-phone {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }

  .schedule-modal-posting-controls {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .schedule-modal-posting-controls--combined {
    padding: 8px;
    gap: 8px;
  }

  .schedule-modal-posting-controls--combined::before {
    display: none;
  }

  .schedule-modal-posting-controls--combined .assign-cycle-time-picker {
    border-top: 1px solid rgba(168, 85, 247, 0.24);
    padding-top: 8px;
  }

  .assign-cycle-timeline {
    margin-top: 10px !important;
    padding: 9px 8px;
    border-radius: 10px;
  }

  .calendar-timeline-header {
    margin-bottom: 10px;
  }

  .calendar-timeline-month {
    font-size: 12px;
  }

  .timeline-day-card {
    min-height: 110px;
    padding: 8px;
  }

  .timeline-day-number {
    font-size: 18px;
    margin: 2px 0;
  }

  .timeline-day-events {
    min-height: 40px;
  }

  .assign-cycle-timezone-picker,
  .assign-cycle-time-picker {
    margin-top: 10px;
    padding: 8px;
  }

  .assign-cycle-timezone-picker--compact,
  .assign-cycle-time-picker--compact {
    margin-top: 0;
    padding: 8px;
  }

  .material-time-picker-main {
    width: 100%;
    justify-content: center;
    gap: 12px;
  }

  .material-time-input {
    width: 82px;
    height: 70px;
    line-height: 66px;
    font-size: 25px;
  }

  .material-time-separator {
    font-size: 34px;
    margin-bottom: 18px;
  }

  .material-period-btn {
    width: 56px;
    height: 40px;
    font-size: 13px;
    line-height: 38px;
  }

  .timeline-nav-btn {
    display: none;
  }

  .calendar-timeline-body {
    gap: 0;
  }

  .timeline-days {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    padding-top: 4px;
    padding-bottom: 6px;
  }

  .timeline-day-card {
    flex: 0 0 calc((100% - 8px) / 2);
    min-width: calc((100% - 8px) / 2);
  }

  .metadata-form-grid {
    grid-template-columns: 1fr;
  }

  .schedule-platform-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .confirm-modal-overlay {
    padding: 8px;
  }

  .confirm-modal--schedule-form,
  .confirm-modal--schedule-wide {
    width: 100%;
    max-width: 100%;
    max-height: calc(100dvh - 16px);
    border-radius: 12px;
  }

  .confirm-modal--clip-time-editor {
    width: 100%;
    max-width: 100%;
    max-height: calc(100dvh - 16px);
    border-radius: 12px;
  }

  .confirm-modal--schedule-wide {
    height: calc(100dvh - 16px);
  }

  .confirm-modal--schedule-form .confirm-modal-header,
  .confirm-modal--schedule-wide .confirm-modal-header {
    padding: 14px 12px 10px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-header {
    padding: 14px 12px 10px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-subtitle {
    max-width: calc(100% - 44px);
  }

  .confirm-modal--schedule-form .confirm-modal-body,
  .confirm-modal--schedule-wide .confirm-modal-body {
    padding: 10px 12px 12px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-body {
    padding: 10px 12px 12px;
  }

  .clip-time-editor-modal-body {
    gap: 12px;
    max-height: calc(100dvh - 142px);
  }

  .schedule-modal-stepper {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 10px minmax(0, 1fr) 10px minmax(0, 1fr);
    align-items: start;
    gap: 0;
    overflow: visible;
    padding: 8px 0 8px;
  }

  .schedule-modal-stepper-item {
    min-width: 0;
    gap: 4px;
    flex: initial;
  }

  .schedule-modal-stepper-divider {
    display: block;
    margin: 9px 3px 0;
  }

  .schedule-modal-stepper-index {
    width: 20px;
    height: 20px;
    font-size: 15px;
  }

  .schedule-modal-stepper-label {
    font-size: 12px;
    line-height: 1.15;
  }

  .confirm-modal--schedule-form .confirm-modal-footer,
  .confirm-modal--schedule-wide .confirm-modal-footer {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
    padding: 10px 12px 12px;
  }

  .confirm-modal--clip-time-editor .confirm-modal-footer {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
    padding: 10px 12px 12px;
  }

  .confirm-modal--schedule-manage .schedule-manage-footer-actions {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 6px;
    padding: 8px 10px 10px;
  }

  .confirm-modal--schedule-form .confirm-modal-header {
    padding: 12px 10px 8px;
  }

  .confirm-modal--schedule-form .confirm-modal-header--centered .confirm-modal-header-icon {
    margin-bottom: 6px;
  }

  .confirm-modal--schedule-form .confirm-modal-footer {
    gap: 6px;
    padding: 8px 10px 10px;
  }

  .confirm-modal--schedule-form .schedule-modal-footer-actions {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
  }

  .confirm-modal--schedule-form .confirm-modal-btn,
  .confirm-modal--schedule-wide .confirm-modal-btn {
    width: 100%;
    min-height: 46px;
    font-size: 0.9rem;
    padding: 11px 12px;
  }

  .confirm-modal--schedule-manage .schedule-manage-footer-actions .confirm-modal-btn {
    width: auto;
    min-width: 0;
    flex: 1 1 0;
    min-height: 44px;
    font-size: 0.78rem;
    line-height: 1.15;
    padding: 9px 7px;
    gap: 4px;
    white-space: normal;
    text-align: center;
  }

  .confirm-modal--schedule-manage .schedule-manage-footer-actions .confirm-modal-btn i {
    display: none;
  }

  .confirm-modal--schedule-form .schedule-modal-footer-actions .confirm-modal-btn {
    width: auto;
    min-width: 0;
    flex: 1 1 0;
    font-size: 0.76rem;
    line-height: 1.15;
    padding: 9px 7px;
    gap: 4px;
    white-space: normal;
    text-align: center;
  }

  .confirm-modal--schedule-form .schedule-modal-footer-actions .confirm-modal-btn i {
    display: none;
  }

  .confirm-modal--schedule-form .confirm-modal-close,
  .confirm-modal--schedule-wide .confirm-modal-close {
    width: 32px;
    height: 32px;
    top: 10px;
    right: 10px;
  }

  .confirm-modal-header-icon {
    width: 42px;
    height: 42px;
    font-size: 19px;
  }

  .confirm-modal-title {
    font-size: 1.02rem;
    line-height: 1.25;
  }

  .confirm-modal-subtitle {
    font-size: 0.82rem;
    line-height: 1.45;
  }

  .schedule-platform-strip {
    grid-template-columns: 1fr;
  }

  .schedule-modal-left-rail {
    padding: 9px 8px 8px;
    border-radius: 12px;
  }

  .schedule-modal-network-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
  }

  .schedule-modal-facebook-editor--grid-slot {
    grid-column: auto;
  }

  .schedule-modal-network-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
    align-items: stretch;
  }

  .schedule-modal-network-item {
    min-height: 92px;
    padding: 0 19px;
    justify-content: center;
  }

  .schedule-modal-network-item .schedule-social-logo-wrap.schedule-modal-network-logo {
    width: 43px;
    height: 43px;
    min-width: 43px;
    border-radius: 9px;
  }

  .schedule-modal-network-item .schedule-modal-network-logo .schedule-social-logo {
    width: 25px;
    height: 25px;
  }

  .schedule-modal-network-main {
    grid-template-columns: auto minmax(0, 1fr);
    justify-items: stretch;
    align-content: center;
    align-items: center;
    gap: 6px;
    text-align: left;
    width: auto;
    max-width: 100%;
    margin: 2px auto;
  }

  .schedule-modal-network-name {
    grid-column: 1;
    grid-row: 1;
    width: auto;
    font-size: 12px;
  }

  .schedule-modal-network-status {
    grid-column: 1;
    grid-row: 2;
    width: auto;
    font-size: 10px;
  }

  .schedule-modal-network-meta {
    width: auto;
    max-width: 100%;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    justify-items: start;
    column-gap: 0;
    row-gap: 4px;
    text-align: left;
  }

  .schedule-modal-network-toggle-pill {
    grid-column: 1;
    grid-row: 4;
    justify-self: start;
    align-self: start;
    min-height: 22px;
    padding: 3px 7px;
    font-size: 9px;
    line-height: 1.2;
    text-align: center;
    white-space: normal;
    max-width: 100%;
  }

  .schedule-modal-facebook-editor {
    min-height: 60px;
    padding: 8px 9px;
    gap: 4px;
  }

  .schedule-modal-facebook-label {
    font-size: 11px;
  }

  .schedule-modal-facebook-editor .form-control {
    min-height: 31px;
    font-size: 11px;
  }

  .schedule-modal-facebook-editor .form-text {
    font-size: 9px;
  }

  .schedule-modal-preview-pane {
    max-width: 100%;
  }

  .schedule-modal-preview-phone {
    max-width: 248px;
  }

  .timeline-day-card {
    flex: 0 0 calc((100% - 8px) / 2);
    min-width: calc((100% - 8px) / 2);
    min-height: 98px;
    padding: 7px;
  }

  .timeline-day-number {
    font-size: 16px;
  }

  .timeline-day-events {
    min-height: 34px;
  }

  .schedule-modal-posting-controls {
    gap: 5px;
  }

  .schedule-modal-posting-block--step {
    margin-top: 0;
    padding-top: 0;
  }

  .schedule-modal-posting-controls--combined {
    padding: 7px;
    gap: 7px;
  }

  .material-time-picker-main {
    width: 100%;
    justify-content: center;
    gap: 10px;
  }

  .material-time-input {
    width: 76px;
    height: 62px;
    line-height: 58px;
    font-size: 22px;
  }

  .material-time-separator {
    font-size: 31px;
    margin: 0 0 16px;
  }

  .material-period-btn {
    width: 54px;
    height: 38px;
    font-size: 12px;
    line-height: 36px;
  }

  .material-timezone-select {
    min-height: 44px;
    font-size: 14px;
  }
}

@media (min-width: 769px) and (max-width: 900px) {
  .schedule-platform-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.form-group-full {
  grid-column: 1 / -1;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.form-group label strong {
  color: var(--clipr-text-primary);
  font-weight: 700;
}

.form-group .form-text {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--clipr-text-secondary);
}

.form-group .form-text.text-muted {
  color: var(--clipr-text-secondary);
}

.field-required {
  color: #f87171;
}

/* Metadata Subsections */
.metadata-subsection {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--clipr-border-color);
}

.metadata-subsection-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metadata-subsection-title {
  color: var(--clipr-text-primary);
}

/* Fixed Checkbox Labels */
.checkbox-label-fixed {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 12px;
  background: var(--clipr-bg-elevated);
  border-radius: var(--clipr-radius-lg);
  border: 1px solid rgba(168, 85, 247, 0.18);
  transition: all 0.2s ease;
  position: relative;
  margin: 0;
  color: var(--clipr-text-primary);
}

.checkbox-label-fixed:hover {
  background: var(--clipr-purple-overlay-light);
  border-color: var(--clipr-purple-light);
}

.checkbox-label-fixed .form-check-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  margin: 0;
  width: 20px;
  height: 20px;
  z-index: 2;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.checkbox-label-fixed .form-check-sign {
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--clipr-border-color);
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.9);
  flex-shrink: 0;
  transition: all 0.2s ease;
  z-index: 0;
}

.checkbox-label-fixed input:checked ~ .form-check-sign {
  background: var(--clipr-purple-light);
  border-color: var(--clipr-purple-light);
}

.checkbox-label-fixed input:checked ~ .form-check-sign::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

/* Fix for all form-check-input in platform tabs */
.platform-tab-panel .form-check-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 12px;
  background: var(--clipr-bg-elevated);
  border-radius: var(--clipr-radius-lg);
  border: 1px solid rgba(168, 85, 247, 0.18);
  transition: all 0.2s ease;
  position: relative;
  margin: 0;
  color: var(--clipr-text-primary);
}

.platform-tab-panel .form-check-label:hover {
  background: var(--clipr-purple-overlay-light);
  border-color: var(--clipr-purple-light);
}

.platform-tab-panel .form-check-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  margin: 0;
  width: 20px;
  height: 20px;
  z-index: 2;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.platform-tab-panel .form-check-sign {
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--clipr-border-color);
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.9);
  flex-shrink: 0;
  transition: all 0.2s ease;
  z-index: 0;
}

.platform-tab-panel input:checked ~ .form-check-sign {
  background: var(--clipr-purple-light);
  border-color: var(--clipr-purple-light);
}

.platform-tab-panel input:checked ~ .form-check-sign::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 12px;
  font-weight: bold;
}

.platform-tab-panel .form-group label,
.platform-tab-panel .form-group label strong {
  color: var(--clipr-text-primary);
}

.platform-tab-panel .form-group .form-text,
.platform-tab-panel .form-group .form-text.text-muted {
  color: var(--clipr-text-secondary);
}

/* Enhanced Batch Header */
.batch-header-enhanced {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 1));
  border-radius: 12px;
  border-left: 6px solid var(--clipr-purple-light);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.9);
}

.batch-header-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.batch-header-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.batch-header-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  flex: 1;
  min-width: 0;
}

.batch-header-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.batch-header-badge-success {
  background-color: #22c55e;
  color: #fff;
}

.batch-header-badge-streams {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.95) 0%, rgba(124, 58, 237, 0.9) 100%);
  color: #faf5ff;
  border: 1px solid rgba(233, 213, 255, 0.35);
  box-shadow: 0 6px 18px rgba(76, 29, 149, 0.35);
  text-transform: none;
  letter-spacing: 0.02em;
}

.batch-header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--clipr-text-tertiary);
}

.batch-meta-item {
  display: flex;
  align-items: center;
  color: #666;
}

.batch-meta-separator {
  color: #ccc;
  margin: 0 4px;
}

/* Enhanced Stat Cards */
.stat-card {
  background: rgba(15, 23, 42, 0.95);
  border-radius: 12px;
  padding: 20px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-top: 4px solid transparent;
  position: relative;
  overflow: hidden;
}

.stat-card-sm {
  padding: 14px 12px;
}

.stat-card-sm .stat-icon {
  font-size: 20px;
  margin-bottom: 6px;
}

.stat-card-sm .stat-value {
  font-size: 22px;
  margin-bottom: 3px;
}

.stat-card-sm .stat-label {
  font-size: 11px;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.stat-card-primary {
  border-top-color: var(--clipr-purple-light);
  background: radial-gradient(circle at top left, rgba(168, 85, 247, 0.14), transparent 60%),
    rgba(15, 23, 42, 0.95);
}

.stat-card-success {
  border-top-color: #22c55e;
  background: radial-gradient(circle at top left, rgba(34, 197, 94, 0.14), transparent 60%),
    rgba(15, 23, 42, 0.95);
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 8px;
  opacity: 0.8;
}

.stat-card-primary .stat-icon {
  color: var(--clipr-purple-light);
}

.stat-card-success .stat-icon {
  color: #22c55e;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  margin-bottom: 4px;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: var(--clipr-text-tertiary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Rotating animation for loading icons */
.rotating {
  animation: rotate 1s linear infinite;
  display: inline-block;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Batch Content Table Styles */
.batch-content-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  margin-right: 10px;
}

.batch-content-table-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.9);
  border: 1px solid var(--clipr-border-color);
}

.batch-content-table {
  margin-bottom: 0;
  background: transparent;
  border-collapse: separate;
  border-spacing: 0;
}

.batch-content-table thead {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(15, 23, 42, 1));
  position: sticky;
  top: 0;
  z-index: 10;
}

.batch-content-table thead th {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--clipr-text-tertiary);
  border-bottom: 2px solid var(--clipr-border-color);
  padding: 14px 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.batch-content-table tbody td {
  padding: 12px 10px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(15, 23, 42, 1);
}

.batch-content-table tbody tr:last-child td {
  border-bottom: none;
}

.batch-content-row {
  transition: all 0.2s ease;
  cursor: grab;
  border-bottom: 1px solid #f0f0f0;
}

.batch-content-row:active {
  cursor: grabbing;
}

.batch-content-row:hover {
  background-color: rgba(15, 23, 42, 0.9);
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.batch-content-row.cycle-content-row-dragging {
  opacity: 0.6;
  background-color: rgba(30, 64, 175, 0.8);
  transform: scale(0.98);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.batch-content-info-cell {
  text-align: center;
  padding: 12px 8px !important;
  width: 50px;
  vertical-align: middle;
}

.batch-drag-icon {
  color: var(--clipr-purple-light);
  font-size: 18px;
  opacity: 0.6;
  transition: all 0.2s ease;
  cursor: grab;
}

.batch-content-row:hover .batch-drag-icon {
  opacity: 1;
  color: #c4b5fd;
  transform: scale(1.2);
}

.batch-content-row:active .batch-drag-icon {
  cursor: grabbing;
}

.batch-content-info-tooltip-wrapper {
  position: relative;
  display: inline-block;
  margin-left: 10px;
}

.batch-content-info-icon {
  color: var(--clipr-purple-light);
  font-size: 18px;
  cursor: help;
  transition: all 0.2s ease;
}

.batch-content-info-icon:hover {
  color: #c4b5fd;
  transform: scale(1.1);
}

.batch-content-info-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;
  padding: 12px 16px;
  background-color: rgba(15, 23, 42, 0.98);
  color: #fff;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 280px;
  white-space: normal;
}

.batch-content-info-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: rgba(15, 23, 42, 0.98);
}

.batch-content-info-tooltip-wrapper:hover .batch-content-info-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(-5px);
}

.batch-content-info-tooltip p {
  margin: 0;
  font-size: 12px;
}

.batch-content-info-tooltip p strong {
  font-weight: 600;
}

.confirm-modal--clip-time-editor.is-read-only .clip-time-editor-video-type-picker,
.confirm-modal--clip-time-editor.is-read-only .clip-time-editor-layout-overlay,
.confirm-modal--clip-time-editor.is-read-only .clip-time-editor-track,
.confirm-modal--clip-time-editor.is-read-only .make-flow-subtitles-wrap,
.confirm-modal--clip-time-editor.is-read-only .editor-context-block,
.confirm-modal--clip-time-editor.is-read-only .clip-time-editor-endcard-panel {
  opacity: 0.55;
  pointer-events: none;
}

/* Editor inline: sin caja modal, crece con todo el contenido */
.schedule-editor-page .schedule-editor-shell.confirm-modal--clip-time-editor {
  width: 100%;
  max-width: 100%;
  max-height: none !important;
  height: auto;
  overflow: visible !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.schedule-editor-page .schedule-editor-shell .confirm-modal-body,
.schedule-editor-page .schedule-editor-shell .clip-time-editor-modal-body {
  flex: none !important;
  max-height: none !important;
  overflow: visible !important;
}

@media (max-width: 768px) {
  .clip-time-editor-subtitle-style-modal {
    z-index: 3200;
    padding:
      max(12px, env(safe-area-inset-top))
      12px
      max(12px, env(safe-area-inset-bottom));
    overflow: hidden;
  }

  .clip-time-editor-subtitle-style-modal :deep(.subtitle-style-modal) {
    box-sizing: border-box;
    max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .schedule-editor-page .schedule-editor-shell .clip-time-editor-modal-body {
    max-height: none !important;
    overflow: visible !important;
  }

  .schedule-editor-page .editor-stage .editor-trim {
    gap: 16px;
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }
}
</style>


